/*
 * TimingMarcheCard_v20-4.jsx
 * ──────────────────────────────────────────────────────────────────
 * v20-4 — RAW_DAILY et RS_DATA branchés sur données réelles COFA.PA
 *          (16/07/2026) : les tableaux hardcodés RAW_DAILY (Requête 2)
 *          et RS_DATA (Requête 3) contenaient jusqu'ici des données de
 *          démo NVDA sans rapport avec le symbole affiché. Remplacés
 *          par les échantillons réels reçus (127 barres daily,
 *          2025-12-30→2026-06-30 ; 48 points RS mensuels,
 *          2021-03→2025-02). RAW_MONTHLY (Requête 4) était déjà réel,
 *          inchangé.
 *
 * Prop d = objet plat issu du SELECT de jointure :
 *   timing_marche_summary
 *   JOIN StockDashboard              (profil société)
 *   JOIN StockDashboard_Decisions    (iscore, radar_*)
 *   LEFT JOIN Timing_marche_interpretations (interp_*, narratifs)
 *
 * Séries temporelles (graphiques) — props séparées, à terme fetchées
 * (aujourd'hui encore posées en dur après vérification manuelle) :
 *   timing_marche_chart              → OHLCV + SMA (RAW_DAILY)
 *   monthly_histo_price_calculation  → lr_value, sigma, z_score (RAW_MONTHLY)
 *   Analysis_Relative_Strength       → rs_ratio, rs_return_12m, rs_trend_label (RS_DATA)
 *   RSI(14) : pas de colonne dédiée en base pour le chart daily — calculé
 *   côté front depuis les `close` (fallback rsiFromJS). La mention
 *   "tick_app_rsi14" d'une table séparée (héritée de v15) est obsolète.
 *
 * ⚠️ Écart connu, à corriger côté SQL (voir mapping .md) : la Requête 2
 *   utilise `LIMIT 120`, qui ne couvre que ~5,7 mois de séances daily —
 *   insuffisant pour le chip **1Y** (~252 séances attendues). Proposition :
 *   `LIMIT 260`. Tant que la requête réelle n'a pas été relancée avec
 *   cette nouvelle limite, le chip 1Y affichera le même contenu que 6M
 *   (le filtrage front par cutoff -1 an est correct, c'est l'amont qui
 *   manque de profondeur).
 *
 * Modifications v15 (historique) :
 *   ✓ SR_LEVELS câblé sur d.support_level / resist_level / *_touches / *_vol_ratio
 *   ✓ NVDA_COMPANY câblé sur StockDashboard
 *   ✓ iscore + radarData câblés sur StockDashboard_Decisions
 *   ✓ Diagnostic : gauge, scores 4 piliers, régime/signal/action câblés
 *   ✓ Pilier 1 : SMA, TQS, CAGR, R², tendances câblés + tooltips interp_*
 *   ✓ Pilier 2 : TSMOM, momentum_score, FIP, jours+, M1, YTD câblés + interp_*
 *   ✓ Pilier 3 : wyckoff, volume_trend_20_60, priceM1 câblés + interp_*
 *   ✓ Pilier 4 : tooltips interp_rsi_14 / interp_z_score_timing / interp_coherence
 *   ✓ Footer : last_updated depuis d.updated_at
 * ──────────────────────────────────────────────────────────────────
 */


/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from "react";
import useInvelpsTheme from "../../../hooks/useInvelpsTheme";
import {
  Radar as RechartsRadar, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from "recharts";

// ══════════════════════════════════════════════════════════════════
// 1. TOKENS & THEME
// ══════════════════════════════════════════════════════════════════
const C = {
  green:  "#6f8a5b",
  green2: "#8da57a",
  olive:  "#a9b38a",
  amber:  "#caa85e",
  orange: "#d28a52",
  red:    "#c6523f",
  gold:   "#b59a5b",
  // aliases for edu module
  g:"#6f8a5b", g2:"#8da57a", ol:"#a9b38a",
  am:"#caa85e", or:"#d28a52", r:"#c6523f", edu:"#7a8fc4",
};

// Dark theme tokens used by the education modal
const T = {
  bg:"#09090b", su:"#111114", s2:"#16161b", bo:"#1e1e26", b2:"#2a2a36",
  go:"#c4a35a", gd:"#7a5e28", gp:"#e8d5a0",
  gr:"#3a7a50", gb:"#6dbd8a", gbg:"#0a130c",
  re:"#8a3a3a", rb:"#c47070", rbg:"#130a0a",
  bl:"#3a5a8a", bb:"#6a9aca", blbg:"#0a0d16",
  am:"#c4803a", ambg:"#140e0a", tx:"#dcd8ce", di:"#8a8680", mu:"#3e3c38"
};
const EDU_COL = "#7a8fc4"; // education button color

// Sigma channel colors (distinct dashes)
const SIG = {
  b2p: { col:"#d28a52", dash:"7 3", w:2   },  // +2σ orange
  b1p: { col:"#caa85e", dash:"4 3", w:1.6 },  // +1σ amber
  reg: { col:"#4a7fa0", dash:"8 4", w:1.8 },  // moyenne bleue
  b1n: { col:"#7db8d4", dash:"4 3", w:1.6 },  // -1σ bleu clair
  b2n: { col:"#3a5f7a", dash:"7 3", w:2   },  // -2σ bleu foncé
};

// SMA colors
const SMA_COL = {
  s20:  "#6f8a5b",  // vert Invelps
  s50:  "#caa85e",  // amber
  s200: "#c6523f",  // rouge
};

// RSI colors — volontairement distincts du vert principal pour ressortir sur la charte Invelps
const RSI_COL = {
  line: "#4F8CFF",
  lineSoft: "#7BA7FF",
  fill: "rgba(79,140,255,.22)",
  fillDark: "rgba(79,140,255,.26)",
  badgeBg: "rgba(79,140,255,.16)",
  badgeBorder: "rgba(79,140,255,.50)",
};

// Support / Résistance — valeurs de fallback (données réelles NVDA 2026-04-30)
// Les vraies valeurs sont lues depuis d.support_level / d.resist_level dans le composant
const SR_LEVELS_DEFAULT = {
  support:    { price: 175.56, label: "Support",    tests: 46, col: "#6f8a5b" },
  resistance: { price: 211.34, label: "Résistance", tests: 2,  col: "#c6523f" },
};

// ══════════════════════════════════════════════════════════════════
// COLOR HELPERS (from ProfilDescriptionRadarCard)
// ══════════════════════════════════════════════════════════════════
const STOPS = ["#c6523f","#d28a52","#caa85e","#a9b38a","#8da57a","#6f8a5b"];
function hexToRgb(h) {
  if (typeof h !== "string") return { r: 0, g: 0, b: 0 };
  const f = h.replace("#","");
  const n = parseInt(f.length===3?f.split("").map(c=>c+c).join(""):f,16);
  if (Number.isNaN(n)) return { r: 0, g: 0, b: 0 };
  return {r:(n>>16)&255,g:(n>>8)&255,b:n&255};
}
function lerp(a,b,t){return a+(b-a)*t;}
function lerpColor(a,b,t){
  const ac=hexToRgb(a),bc=hexToRgb(b);
  const cl=x=>Math.max(0,Math.min(255,Math.round(x)));
  return `#${cl(lerp(ac.r,bc.r,t)).toString(16).padStart(2,"0")}${cl(lerp(ac.g,bc.g,t)).toString(16).padStart(2,"0")}${cl(lerp(ac.b,bc.b,t)).toString(16).padStart(2,"0")}`;
}
function getScoreColor(pct){
  const p = Number(pct);
  const safe = Number.isFinite(p) ? p : 50; // filet de sécurité : évite un crash (hexToRgb sur undefined) si un champ numérique attendu arrive en NaN/texte
  const t=Math.max(0,Math.min(1,safe/100));
  const seg=(STOPS.length-1)*t,i=Math.min(STOPS.length-2,Math.floor(seg));
  return lerpColor(STOPS[i],STOPS[i+1],seg-i);
}
// Alias rétro-compatible — même source unique de couleur que les autres cards Invelps
// (AnalyseFondamentaleCard utilise heatColor(), MarketStructureCard utilise getScoreColor())
const colorForPct = getScoreColor;

// getStatusColor : bon/neutre/mauvais → vert/ambre/rouge, pour badges à 3 états
// (RSI, Z-Score, régime, signal, action…) — remplace les ternaires ad-hoc dupliqués
// dans le rendu (cf. §7 doc archi générique Invelps : une seule source de couleur/état).
function getStatusColor(status){
  if (status === "good") return C.green;
  if (status === "bad")  return C.red;
  return C.amber; // "neutral"
}

// getPhaseColor : couleur d'une phase Wyckoff (Accumulation/Markup = bon,
// Distribution/Markdown = mauvais, sinon neutre).
function getPhaseColor(phaseLabel){
  const p = (phaseLabel || "");
  if (p.includes("Distribution") || p.includes("Markdown")) return getStatusColor("bad");
  if (p.includes("Accumulation") || p.includes("Markup"))   return getStatusColor("good");
  return getStatusColor("neutral");
}

// Question par pilier — SOURCE UNIQUE (doc archi générique §8/§9 : "une question
// par section", jamais dupliquée). Utilisée à la fois par PillarCard (badge QUESTION)
// et par le récapitulatif "Ce que les indicateurs révèlent" du render principal.
const PILLAR_QUESTIONS = {
  structure: "La tendance de fond est-elle haussière ?",
  momentum:  "Le mouvement accélère-t-il ou s'essouffle-t-il ?",
  volume:    "Les institutionnels participent-ils au mouvement ?",
  timing:    "Est-ce le bon moment pour entrer ?",
};

// dyn() : remplace la mention littérale "NVDA" par le symbole réel courant
// dans tout texte pédagogique (théorie, QCM, corrections). Les valeurs
// numériques des scénarios restent des cas d'école illustratifs — seul
// le nom de société codé en dur (interdit par le doc archi générique §3)
// est neutralisé. Fallback "NVDA" si aucune donnée live n'est disponible.
function dyn(text, symbol) {
  if (typeof text !== "string" || !symbol || symbol === "NVDA") return text;
  return text.replace(/NVDA/g, symbol);
}

// ══════════════════════════════════════════════════════════════════
// PROFIL DESCRIPTION RADAR CARD
// Reproduction exacte du design invelps.com
// ══════════════════════════════════════════════════════════════════
function RadarAxisTick({x,y,payload}){
  return(
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fontSize={10.5} fontWeight={600}
        style={{fill:"var(--mu)",fontFamily:"ui-sans-serif,system-ui"}}>
        {payload?.value}
      </text>
    </g>
  );
}

function RadarTooltip({active,payload}){
  if(!active||!payload?.length) return null;
  const val=payload[0]?.value;
  return(
    <div style={{
      position:"fixed",zIndex:9999,
      background:"var(--card-bg)",border:"1px solid var(--card-bdr)",
      borderRadius:10,padding:"8px 12px",fontSize:11,boxShadow:"0 4px 12px rgba(0,0,0,.24)"
    }}>
      <div style={{fontFamily:"ui-monospace,monospace",fontSize:13,fontWeight:700,color:colorForPct(val)}}>{val}/100</div>
      <div style={{fontSize:10,marginTop:2,color:"var(--mu)"}}>
        {val>=80?"Excellent":val>=60?"Bon":val>=40?"Moyen":"Faible"}
      </div>
    </div>
  );
}

function ProfilDescriptionRadarCard({company, iscore, radarData, onOpenEdu, onOpenGuide, onOpenCours}) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 320;
  const isLong = company.description.length > PREVIEW;
  const displayed = expanded || !isLong
    ? company.description
    : company.description.slice(0, PREVIEW).trimEnd() + "…";

  const scoreColor = colorForPct(iscore);
  const isMtdUp = company.mtdPct >= 0;
  const isYtdUp = company.ytdPct >= 0;

  const profilRows = [
    {label:"Région",         value:company.region,        tip:"Zone géographique principale d'activité et de génération de revenus."},
    {label:"Bourse",         value:company.exchange,       tip:"Marché boursier sur lequel l'action est cotée et échangée."},
    {label:"Secteur",        value:company.sector,         tip:"Grand domaine d'activité économique. Permet de comparer l'entreprise avec ses pairs sectoriels."},
    {label:"Industrie",      value:company.industry,       tip:"Sous-catégorie du secteur. Aide à identifier les concurrents directs."},
    {label:"Capitalisation", value:company.marketCapClass, tip:"Taille boursière. Large Cap >10 Md$, Mid Cap 2-10 Md$, Small Cap <2 Md$."},
    {label:"Style",          value:company.style,          tip:"Profil d'investissement : Croissance, Valeur ou Core (équilibré)."},
    {label:"Cycle",          value:company.cyclicals,      tip:"Sensibilité au cycle économique. Cyclique suit la conjoncture, Défensif y résiste."},
  ];

  return(
    <div style={{
      background:"var(--card-bg)", border:"1px solid var(--card-bdr)",
      borderRadius:16, overflow:"hidden", boxShadow:"0 2px 10px rgba(16,40,28,.05)",
    }}>

      {/* ── HEADER ── */}
      <div style={{padding:"16px 20px 0"}}>
        {/* Row 1: symbol + price */}
        <div style={{
          display:"flex", justifyContent:"space-between",
          alignItems:"flex-start", gap:12, flexWrap:"wrap",
        }}>
          {/* Left: symbol + name */}
          <div style={{minWidth:0, flex:"1 1 220px"}}>
            <div style={{
              fontSize:"clamp(20px,5vw,26px)", fontWeight:800, letterSpacing:"-.02em",
              lineHeight:1.05, color:"var(--ts)", overflowWrap:"anywhere",
            }}>{company.name}</div>
            <div style={{fontSize:12, color:"var(--mu)", marginTop:3, fontWeight:500}}>
              {company.fullName || "NVIDIA Corporation"}
            </div>
          </div>
          {/* Right: price */}
          <div style={{textAlign:"right", flexShrink:0}}>
            <div style={{
              fontFamily:"ui-monospace,monospace", fontSize:"clamp(20px,5vw,26px)", fontWeight:800,
              lineHeight:1, color:"var(--ts)",
            }}>
              {company.price.toLocaleString("fr-FR",{minimumFractionDigits:2,maximumFractionDigits:2})}{" "}
              <span style={{fontSize:18, fontWeight:700}}>USD</span>
            </div>
          </div>
        </div>

        {/* Row 2: exchange·industry + MTD/YTD + badge */}
        <div style={{
          display:"flex", justifyContent:"space-between",
          alignItems:"center", marginTop:6, flexWrap:"wrap", gap:8,
        }}>
          <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
            <span style={{
              fontSize:12, fontWeight:600, color:C.amber,
            }}>
              {company.exchange} · {company.industry}
            </span>
            <span style={{
              fontSize:12, fontWeight:700,
              color:isMtdUp?C.green:C.red,
            }}>
              {isMtdUp?"+":""}{company.mtdPct.toFixed(1)}% MTD
              <TipIcon text="MTD (Month-to-Date) : variation depuis le 1er du mois. Indicateur de performance court terme."/>
            </span>
            <span style={{
              fontSize:12, fontWeight:700,
              color:isYtdUp?C.green:C.red,
            }}>
              {isYtdUp?"+":""}{company.ytdPct.toFixed(1)}% YTD
              <TipIcon text="YTD (Year-to-Date) : variation depuis le 1er janvier. Performance de l'action sur l'année en cours."/>
            </span>
          </div>
          {/* Excellence badge + Cours button */}
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", justifyContent:"flex-end" }}>
            <span style={{
              fontSize:11, fontWeight:700, padding:"3px 12px", borderRadius:999,
              background:"rgba(181,154,91,.14)", color:C.amber,
              border:"1px solid rgba(181,154,91,.35)",
            }}>Excellence</span>
            {onOpenEdu && (
              <div style={{display:"flex", alignItems:"center", gap:5}}>
                {/* Icône guide de lecture — boussole */}
                <button onClick={onOpenGuide} style={{
                  display:"flex", alignItems:"center", justifyContent:"center",
                  width:28, height:28, borderRadius:8, cursor:"pointer",
                  border:"1px solid var(--sep)", background:"var(--sur)",
                  color:"var(--mu)",
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" opacity=".7"/>
                  </svg>
                </button>
                {/* Bouton cours */}
                <button onClick={() => onOpenCours && onOpenCours()} style={{
                  display:"flex", alignItems:"center", gap:5,
                  padding:"3px 11px", borderRadius:8, cursor:"pointer",
                  border:`1px solid ${C.green}60`, background:`${C.green}14`,
                  color:C.green, fontSize:10, fontWeight:700,
                  fontFamily:"ui-monospace,monospace",
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                    <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
                  </svg>
                  Cours
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mega Cap badge */}
        <div style={{marginTop:10}}>
          <span style={{
            fontSize:11, fontWeight:700, padding:"3px 11px", borderRadius:8,
            background:"rgba(30,40,34,.06)", color:"var(--mu)",
            border:"1px solid var(--sep)",
          }}>{company.marketCapClass}</span>
        </div>

        <div style={{height:1, background:"var(--sep)", marginTop:12}}/>
      </div>

      {/* ── BODY: 3 columns side by side ── */}
      <div style={{
        display:"grid",
        gap:0,
        padding:"16px 20px 20px",
        width:"100%",
      }} className="pdrc-grid">

        {/* COL 1 — Profil */}
        <div className="pdrc-col-sep">
          <div style={{
            fontSize:9.5, fontWeight:800, textTransform:"uppercase",
            letterSpacing:".1em", color:"var(--mu)",
            fontFamily:"ui-monospace,monospace", marginBottom:10,
          }}>Profil</div>
          {profilRows.map(({label,value,tip},i,arr)=>(
            <div key={label}>
              <div style={{
                display:"flex", alignItems:"center",
                justifyContent:"space-between", padding:"7px 0", gap:8,
              }}>
                <span style={{
                  fontSize:11, fontWeight:600, color:"var(--mu)",
                  display:"flex", alignItems:"center", gap:2, flexShrink:0,
                }}>
                  {label}<TipIcon text={tip}/>
                </span>
                <span style={{
                  fontSize:12, fontWeight:700, color:"var(--ts)",
                  textAlign:"right",
                }}>{value}</span>
              </div>
              {i<arr.length-1 && <div style={{height:1,background:"var(--sep)"}}/>}
            </div>
          ))}
        </div>

        {/* COL 2 — Description */}
        <div className="pdrc-col-mid" style={{
          display:"flex", flexDirection:"column", gap:10,
        }}>
          <div style={{
            fontSize:9.5, fontWeight:800, textTransform:"uppercase",
            letterSpacing:".1em", color:"var(--mu)",
            fontFamily:"ui-monospace,monospace",
          }}>Description</div>
          <div style={{
            borderRadius:10, padding:"12px 14px", flex:1,
            background:"var(--sur)", border:"1px solid var(--sep)",
          }}>
            <p style={{fontSize:12, lineHeight:1.72, color:"var(--ts)", margin:0}}>
              {displayed}
            </p>
            {isLong && (
              <button type="button" onClick={()=>setExpanded(v=>!v)}
                style={{
                  marginTop:8, fontSize:11, fontWeight:600,
                  color:C.green, background:"none", border:"none",
                  cursor:"pointer", padding:0,
                }}>
                {expanded?"Réduire ↑":"Voir plus ↓"}
              </button>
            )}
          </div>
        </div>

        {/* COL 3 — IScore + Radar */}
        <div className="pdrc-col-last" style={{
          display:"flex", flexDirection:"column",
        }}>
          {/* IScore header */}
          <div style={{
            display:"flex", alignItems:"center",
            justifyContent:"space-between", marginBottom:12,
          }}>
            <div style={{
              display:"flex", alignItems:"center", gap:4,
              fontSize:9.5, fontWeight:800, textTransform:"uppercase",
              letterSpacing:".1em", color:"var(--mu)",
              fontFamily:"ui-monospace,monospace",
            }}>
              ISCORE
              <TipIcon text="Score global de conviction (0–100) synthétisant 5 axes d'analyse : fondamentaux, valorisation, marché, momentum et risque/rendement. ≥70 = dossier de qualité."/>
            </div>
            <div style={{display:"flex", alignItems:"baseline", gap:3}}>
              <span style={{
                fontFamily:"ui-monospace,monospace", fontSize:28,
                fontWeight:800, lineHeight:1, color:scoreColor,
              }}>{iscore}</span>
              <span style={{fontSize:14, color:"var(--mu)", fontWeight:600}}>/100</span>
            </div>
          </div>

          {/* Radar chart */}
          <div style={{flex:1, minHeight:240}}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="68%" margin={{top:8,right:30,bottom:8,left:30}}>
                <PolarGrid stroke="var(--sep)"/>
                <PolarRadiusAxis
                  angle={90} domain={[0,100]} tickCount={4}
                  tick={{fontSize:8,fill:"var(--mu)",fontFamily:"ui-monospace,monospace"}}
                  axisLine={false}
                />
                <PolarAngleAxis dataKey="axis" tick={(p)=><RadarAxisTick {...p}/>}/>
                <RechartsTooltip content={<RadarTooltip/>}/>
                <RechartsRadar
                  dataKey="value"
                  stroke={C.green} fill={C.green}
                  strokeWidth={2} fillOpacity={0.22}
                  dot={{r:3, fill:C.green, strokeWidth:0}}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════
// TOKENS
// ══════════════════════════════════════════════════════
function applyEduTheme(dark) {
  const r = document.documentElement, s = (k,v) => r.style.setProperty(k,v);
  s("--bg",  dark?"#0b1220":"#ffffff");
  s("--card",dark?"rgba(13,20,38,0.97)":"rgba(255,255,255,0.97)");
  s("--bdr", dark?"rgba(111,138,91,0.18)":"rgba(30,40,34,0.09)");
  s("--ts",  dark?"#e8e4da":"#1f2a24");
  s("--tx",  dark?"#cdc9be":"#2d3b34");
  s("--mu",  dark?"#7a8172":"#6f7b74");
  s("--sep", dark?"rgba(111,138,91,0.12)":"rgba(30,40,34,0.07)");
  s("--sur", dark?"rgba(111,138,91,0.05)":"rgba(0,0,0,0.018)");
  s("--tbg", dark?"rgba(9,13,24,0.98)":"rgba(255,255,255,0.99)");
  s("--tbr", dark?"rgba(202,168,94,0.28)":"rgba(30,40,34,0.12)");
}
// ══════════════════════════════════════════════════════
// TOOLTIPS — ENRICHIS AVEC SOURCES ACADÉMIQUES
// ══════════════════════════════════════════════════════
const TIPS = {

  // ══ PROFIL ══════════════════════════════════════════════
  region: { title:"Région géographique", body:"Zone principale de revenus. Détermine l'exposition aux cycles économiques régionaux, aux taux de change et aux risques géopolitiques. North America = marché le plus liquide et mature.", src:"Invelps · Profil fondamental" },
  bourse: { title:"Place de cotation", body:"Marché boursier où l'action est échangée. Le NASDAQ héberge principalement les valeurs technologiques. Détermine les heures de trading et la réglementation applicable.", src:"Invelps · Profil fondamental" },
  secteur: { title:"Secteur économique (GICS)", body:"Grand domaine d'activité. Permet la comparaison avec les pairs sectoriels. La rotation sectorielle est l'un des leviers principaux des gérants actifs selon Murphy (ch. 15 : analyse inter-marchés).", src:"Murphy, TAOTFM ch. 15" },
  industrie: { title:"Industrie — sous-secteur", body:"Sous-catégorie du secteur. 'Semiconductors' vs 'Software' ont des cycles de valorisation très différents. Les concurrents directs appartiennent à la même industrie.", src:"Invelps · Profil fondamental" },
  capitalisation: { title:"Capitalisation boursière", body:"Cours × Actions en circulation. Mega Cap (>200 Md$) : liquidité maximale. Large Cap (10-200 Md$). Mid Cap (2-10 Md$). Small Cap (<2 Md$) : potentiel supérieur, risque plus élevé. La capitalisation détermine la volatilité structurelle.", src:"Invelps · Classification standard" },
  style: { title:"Style d'investissement", body:"High Quality = rentabilité élevée (ROE, marges fortes) + bilan solide. Growth = forte croissance. Value = décote vs pairs. Blend = équilibré. Le style influence fortement la performance selon la phase du cycle de taux d'intérêt.", src:"Invelps · Facteurs de style (Fama-French, QMJ)" },
  cycle: { title:"Sensibilité cyclique", body:"Cyclique : revenus corrélés à la conjoncture économique (semis, auto, énergie). Défensif : revenus stables quelle que soit la conjoncture (santé, alimentation). Hybride : entre les deux. Crucial pour le positionnement en fin de cycle.", src:"Invelps · Analyse macroéconomique" },
  iscore: { title:"IScore — Score de conviction Invelps", body:"Score composite 0-100 sur 5 dimensions : Fondamentaux (rentabilité, bilan), Valorisation (vs pairs et historique), Marché (structure technique), Momentum (force relative), Risque/Rendement (ratio Sharpe estimé). ≥70 = dossier de qualité. ≥85 = conviction élevée.", src:"Invelps composite · Murphy · Gray & Vogel · Damodaran" },

  // ══ DIAGNOSTIC GLOBAL ═══════════════════════════════════
  score_global: { title:"Score Technique Global", body:"Score composite 100 pts pondéré : Structure de tendance 40% + Momentum 25% + Volumes 20% + Timing 15%. Pondérations calibrées sur l'importance empirique de chaque facteur. 82/100 = signal technique fort — titre dans le top 15% de l'univers.", src:"Murphy TAOTFM · Wyckoff Villahermosa · Gray & Vogel QM · Invelps" },
  regime: { title:"Régime technique", body:"État structurel actuel. 'Haussière mature' = prix au-dessus des 3 SMAs (20/50/200), canal de régression orienté positivement, R² solide. Correspond à la Phase E (markup) de Wyckoff. Régime le plus favorable pour détenir le titre.", src:"Wyckoff, Villahermosa ch. 12 & 20 · Murphy, TAOTFM ch. 1" },
  signal: { title:"Signal principal", body:"'Surveiller' = tendance haussière confirmée mais timing d'entrée sous-optimal actuellement. Le momentum CT diverge du LT. Murphy : 'ne pas agir contre la tendance, mais attendre le meilleur point d'entrée dans la direction haussière'.", src:"Murphy, TAOTFM ch. 4 · Invelps composite" },
  action: { title:"Action suggérée", body:"Recommandation tactique basée sur 3 signaux convergents : Z-Score +1.82σ (sur-extension), RSI 67.4 (proche surachat), TSMOM 3M négatif (divergence CT). Attendre un repli vers SMA20 améliore significativement le ratio risque/rendement.", src:"Murphy ch. 4 & 10 · Gray & Vogel ch. 6 · Invelps" },

  // ══ STRUCTURE DE TENDANCE ════════════════════════════════
  tendance_prim: { title:"Tendance primaire (Dow Theory)", body:"Tendance de fond sur plusieurs mois à années. Haussière = succession de hauts ET bas croissants. C'est le filtre numéro 1 de toute décision. Murphy : 'ne jamais trader contre la tendance primaire.' Elle détermine le biais directionnel — toutes les autres analyses sont secondaires.", src:"Edwards & Magee, TAST ch. 2 · Murphy, TAOTFM ch. 1 · Dow Theory" },
  tendance_5ans: { title:"Tendance long terme — 5 ans", body:"Tendance structurelle multi-annuelle. Haussier sur 5Y = titre dans un cycle de croissance durable. Murphy : 'la tendance LT est la plus importante de toutes.' Les corrections de 20-30% dans une tendance 5Y haussière sont des opportunités d'accumulation, pas des signaux de vente.", src:"Edwards & Magee, TAST ch. 3 · Murphy, TAOTFM ch. 1" },
  tendance_1an: { title:"Tendance secondaire — 1 an", body:"Tendance sur 12 mois. Doit être alignée avec la primaire. Une divergence tendance 1Y / tendance primaire = zone de prudence accrue. Wyckoff : la tendance annuelle correspond souvent à la Phase E (markup) ou au début de la distribution.", src:"Murphy, TAOTFM ch. 4 · Wyckoff, Villahermosa ch. 20" },
  trend_quality: { title:"Trend Quality Score — R² OLS", body:"Régularité géométrique de la progression mesurée par le R² de la régression OLS. Score 92 = R² ≈ 0.92 → 92% de la variance du prix expliquée par la droite de tendance (8% de bruit seulement). Murphy : seuil R² ≥ 0.50 pour signal actionnable, ≥ 0.75 pour tendance solide.", src:"Murphy, TAOTFM ch. 4 · Analyse quantitative de tendance" },
  trend_strength: { title:"Trend Strength Score — Force directionnelle", body:"Intensité combinant la pente β de la régression + sa consistance temporelle. 88/100 = tendance à forte inclinaison maintenue durablement dans le temps. Gann : 'la force d'une tendance se mesure à sa pente et à sa durée.' Un titre avec β fort et régulier est le plus difficile à contrer.", src:"Gann, Reddy ch. 2 · Edwards & Magee, TAST ch. 3 · Invelps" },
  compounder: { title:"Compounder Score — Régularité interannuelle", body:"Mesure si le titre génère une croissance stable d'année en année (type 'compounder'). 81/100 = progression régulière. Covel : 'la régularité du trend importe plus que son amplitude ponctuelle.' Les meilleurs compounders surperforment sur 10+ ans avec une volatilité moindre.", src:"Covel, Trend Following ch. 2 · Gray & Vogel, QM ch. 5 · Invelps" },
  r2: { title:"R² — Coefficient de détermination OLS", body:"R² = 1 − (SS_résiduelle / SS_totale). Mesure quelle part du mouvement du prix est 'tendanciel' vs aléatoire. R² = 0.86 → 86% tendanciel, 14% bruit. <0.30 = signal peu fiable même si la pente β est positive. Murphy : 'le R² valide le β — sans lui, β ne vaut rien'.", src:"Murphy, TAOTFM ch. 4 · Statistiques appliquées" },
  cagr_5y: { title:"CAGR 5 ans — Taux de croissance annuel composé", body:"(Valeur_fin / Valeur_début)^(1/5) − 1. +39.2%/an = 10 000€ investis il y a 5 ans valent ~46 000€ aujourd'hui. Surperforme très largement le S&P500 (~13%/an). Gray & Vogel : le CAGR est la mesure la plus honnête de la performance réelle à long terme.", src:"Gray & Vogel, QM ch. 3 · Finance de marché" },
  sma20: { title:"SMA 20 jours — Support dynamique CT", body:"Moyenne des 20 derniers cours de clôture. Premier support dynamique en tendance haussière. Les replis jusqu'à SMA20 = zones d'achat, pas de vente. Murphy ch. 9 : 'la MA la plus utile pour les entrées tactiques en tendance.' Un recul sous SMA20 en clôture = premier signal d'alerte.", src:"Murphy, TAOTFM ch. 9" },
  sma50: { title:"SMA 50 jours — Support intermédiaire", body:"Moyenne des 50 derniers cours. Deuxième support dynamique, zone de rebond classique. Golden Cross = SMA50 dépasse SMA200 = signal haussier structurel majeur. Death Cross = SMA50 sous SMA200 = signal baissier. Edwards & Magee : 'SMA50 sépare la correction temporaire du retournement'.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  sma200: { title:"SMA 200 jours — Ligne bull/bear fondamentale", body:"La ligne de démarcation institutionnelle entre marché haussier (prix > SMA200) et baissier (prix < SMA200). 97% des gérants long-only n'achètent pas si le titre est sous sa SMA200. Edwards & Magee : 'le test ultime de la tendance primaire.' C'est LA référence mondiale.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  alignement: { title:"Alignement des moyennes mobiles", body:"SMA20 > SMA50 > SMA200 = configuration haussière parfaite — toutes les tendances (CT/MT/LT) pointent dans la même direction. Murphy : 'le test le plus simple et le plus puissant de la santé d'une tendance.' L'inverse signale un bear market structurel.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  regression: { title:"Régression OLS — Droite de tendance théorique", body:"P_t = α + β·t. Ajuste la meilleure droite possible aux prix historiques. β = vitesse en €/période. Mais β sans R² ≥ 0.50 n'est pas exploitable. Murphy ch. 4 : 'une pente positive avec R² faible = tendance théorique qu'on ne peut pas trader en pratique car le chemin est trop erratique'.", src:"Murphy, TAOTFM ch. 4" },
  canal: { title:"Canal statistique ±1σ et ±2σ", body:"Bandes à ±1 et ±2 écarts-types autour de la droite de régression. Par définition : 68% des prix dans ±1σ, 95% dans ±2σ. Prix à +2σ = sur-extension rare (~5% du temps). Pesavento : les niveaux géométriques créent des zones de retournement naturelles exploitables.", src:"Murphy, TAOTFM ch. 4 · Pesavento & Jouflas ch. 2" },
  zscore: { title:"Z-Score — Écart statistique à la moyenne", body:"Z = (Prix − Moyenne) / Écart-type. +1.82σ = 1.82 fois l'écart-type au-dessus de la moyenne annuelle. Zone >+2σ = sur-extension rare (5% des observations). Signal de prudence — pas de vente mécanique. Murphy : 'les extrêmes se corrigent, mais les délais sont imprévisibles'.", src:"Murphy, TAOTFM ch. 4 · Statistiques financières" },
  zscore_chart: { title:"Z-Score glissant (60j) — Panneau graphique", body:"Z-Score calculé sur une fenêtre glissante de 60 jours affiché en sous-graphe. Lignes de référence ±1 et ±2. Un titre durablement au-dessus de +1σ est en tendance forte. Retour de +2σ vers 0 = correction attendue de ~10-15% selon la volatilité historique du titre.", src:"Murphy, TAOTFM ch. 4 · Analyse quantitative" },

  // ══ MOMENTUM ════════════════════════════════════════════
  mom_direction: { title:"Direction du momentum — Lecture multi-horizons", body:"Synthèse directionnelle croisée TSMOM 3M / 6M / 12M. Haussier = les 3 horizons positifs. Retournement baissier = 12M positif mais 6M et 3M négatifs : tendance LT intacte mais moteur CT grippé. Moskowitz, Ooi & Pedersen (JFE 2012) : la divergence 12M+/3M- est la configuration la plus commune avant consolidation. Elle signale l'essoufflement, pas le retournement.", src:"Moskowitz, Ooi & Pedersen, JFE 2012 · Gray & Vogel, QM ch. 3 & 6" },
  mom_pattern: { title:"Pattern du momentum — Dynamique interne", body:"Accélération : 3M > 6M > 12M (le momentum s'intensifie). Essoufflement : 12M > 6M > 3M (la force décroît dans le temps). Transition / Mixte : ordre non monotone, phase de bascule. Gray & Vogel (ch. 6) : l'Essoufflement précède statistiquement la consolidation dans 60% des cas. L'Accélération est le signal le plus favorable pour un nouveau long.", src:"Gray & Vogel, QM ch. 6 · Da, Gurun & Warachka (2014, RFS)" },
  mom_strength: { title:"Intensité du momentum — Force relative dans l'univers", body:"Classement normalisé 0-100 dans l'univers Invelps. Fort (>70) : momentum parmi les meilleurs de l'univers. Faible (<30) : sous-performance relative même si les chiffres absolus sont positifs. Gray & Vogel : la force relative (cross-sectional) est aussi prédictive que la force absolue. Un titre Faible mais en 'Accélération' est un signal à surveiller.", src:"Gray & Vogel, QM ch. 3 · Jegadeesh & Titman (1993, JF)" },
  mom_score: { title:"Momentum Score composite", body:"Score 0-100 pondérant TSMOM 3M (20%) + TSMOM 6M (30%) + TSMOM 12M (50%). Pondérations issues de Gray & Vogel (QM ch. 6). Le TSMOM 12M domine car il capture la persistance documentée sur 200 ans. 74/100 = momentum sain malgré la divergence CT.", src:"Gray & Vogel, QM ch. 3-6 · Moskowitz, Ooi & Pedersen, JFE 2012" },
  leadership: { title:"Leadership sectoriel — Momentum relatif", body:"Mesure si le titre surperforme son secteur sur 6-12 mois. 89/100 = parmi les leaders sectoriels. Gray & Vogel : le momentum RELATIF (cross-sectional) est aussi prédictif que le momentum absolu. Les leaders sectoriels tendent à continuer de surperformer sur 3-12 mois. C'est le signal de 'winner takes all'.", src:"Gray & Vogel, QM ch. 3 · Jegadeesh & Titman (1993, JF)" },
  fip: { title:"FIP — Frog in the Pan (Qualité du momentum)", body:"FIP = SIGN(TSMOM_12M) × (% jours_baissiers − % jours_haussiers). FIP > 0 avec TSMOM > 0 = performance annuelle positive mais construite sur quelques pics haussiers isolés dans une majorité de jours baissiers. Gray & Vogel (QM ch. 6) : ces 'frogs' sous-performent les 'smooth momentum' de 3-5%/an.", src:"Gray & Vogel, QM ch. 6 · Da, Gurun & Warachka (2014, RFS)" },
  tsmom12: { title:"TSMOM 12 mois — Momentum absolu long terme", body:"TSMOM_12M = (P_t / P_{t-252j}) − 1. +62.1% = performance brute sur 12 mois glissants. Moskowitz, Ooi & Pedersen (JFE 2012) documentent la persistance de ce signal sur 200 ans et 40+ marchés. Signal le plus robuste de la finance empirique. La règle : les gagnants récents continuent de gagner.", src:"Gray & Vogel, QM ch. 3 · Moskowitz, Ooi & Pedersen, JFE 2012" },
  tsmom6: { title:"TSMOM 6 mois — Momentum intermédiaire", body:"TSMOM_6M = (P_t / P_{t-126j}) − 1. +11.3% = momentum intermédiaire positif. Signal de validation du TSMOM 12M. Gray & Vogel : un titre avec TSMOM positif sur 6M ET 12M simultanément présente la persistance de momentum la plus forte et la plus fiable statistiquement.", src:"Gray & Vogel, QM ch. 3" },
  tsmom3: { title:"TSMOM 3 mois — Momentum court terme", body:"TSMOM_3M = (P_t / P_{t-63j}) − 1. -2.1% = légère faiblesse récente. Divergence avec TSMOM 12M positif = essoufflement CT dans tendance LT haussière = configuration classique. Gray & Vogel : surveiller le FIP pour distinguer une correction saine d'un vrai retournement de momentum.", src:"Gray & Vogel, QM ch. 6" },
  jours_pos: { title:"% jours de clôture positive", body:"Proportion de séances avec clôture en hausse. 58% = légère dominance des acheteurs. Composante du score FIP : >55% = 'smooth momentum' (de qualité). <50% = 'frog momentum' (risqué malgré bilan positif). Wyckoff : 'la régularité journalière révèle la pression des mains fortes'.", src:"Wyckoff, Studies in Tape Reading · Gray & Vogel, QM ch. 6" },
  m1_return: { title:"Performance mensuelle (M1)", body:"Rendement du dernier mois calendaire complet. +3.2% = accélération récente positive — signal que le momentum CT commence à se redresser après la divergence. Covel : 'les meilleures entrées sont quand le momentum CT et LT convergent à nouveau vers le haut — la tendance reprend de la vigueur'.", src:"Covel, Trend Following ch. 3 · Gray & Vogel, QM ch. 5" },
  ytd: { title:"YTD — Performance depuis le 1er janvier", body:"Year-to-Date = variation totale depuis le 1er janvier. +85.4% = performance exceptionnelle. Gray & Vogel : comparer au percentile dans l'univers pour qualifier le leadership. >80th percentile YTD = signal fort de momentum cross-sectionnel. Ce titre est dans le top 5% de son univers.", src:"Gray & Vogel, QM ch. 3 · Invelps composite" },
  divergence: { title:"Divergence momentum — Signal d'essoufflement", body:"Prix en nouveau haut mais indicateur momentum (RSI, TSMOM 3M) ne confirme pas. Murphy (TAOTFM ch. 10) : 'l'une des configurations les plus précieuses de l'AT — le mouvement perd de sa force interne.' Signal d'essoufflement, pas de retournement certain. Renforce la prudence sans déclencher de vente.", src:"Murphy, TAOTFM ch. 10 · Wilder, New Concepts (1978)" },

  // ══ VOLUME / WYCKOFF ════════════════════════════════════
  vol_score: { title:"Score volumétrique — Participation institutionnelle", body:"Score composite : ratio volume / moyenne + persistance temporelle + qualité institutionnelle. 68/100 = participation correcte mais en dessous des meilleures configurations. Wyckoff Loi 3 : 'le volume est l'effort, le prix est le résultat. L'empreinte des mains fortes ne ment jamais'.", src:"Wyckoff, Villahermosa ch. 9 · Murphy, TAOTFM ch. 7" },
  liq_score: { title:"Score de liquidité du titre", body:"Facilité à acheter/vendre sans impacter significativement le cours (spread faible, profondeur de carnet). 72/100 = liquidité satisfaisante. Murphy : 'un titre illiquide peut produire des faux signaux techniques par manque de volume.' La liquidité conditionne la fiabilité de toute l'analyse.", src:"Murphy, TAOTFM ch. 7 · Invelps composite" },
  vol_5j: { title:"Ratio volume — 5 jours vs moyenne 60J", body:"Volume moyen des 5 dernières séances / moyenne des 60 dernières. 1.40× = activité 40% supérieure à la normale. Wyckoff : un regain de volume sur un support = signal d'absorption institutionnelle (achat discret). Murphy : 'le volume confirme, jamais il n'initie — mais un regain soudain mérite attention'.", src:"Wyckoff, Villahermosa ch. 9 · Murphy, TAOTFM ch. 7" },
  vol_20j: { title:"Ratio volume — 20 jours vs moyenne 60J", body:"Volume moyen des 20 dernières séances vs les 60 précédentes. 1.20× = participation correcte sur le mois. Signal de confirmation de la tendance récente. Wyckoff : un trend haussier soutenu par des volumes constants est plus solide qu'un trend sur volumes déclinants.", src:"Murphy, TAOTFM ch. 7 · Wyckoff, Villahermosa ch. 9" },
  vol_60j: { title:"Ratio volume — 60 jours vs moyenne 120J", body:"Tendance structurelle des volumes sur 2 mois vs les 4 mois précédents. 0.90× = légère baisse de la participation institutionnelle sur le moyen terme. Signal d'alerte sur la durabilité de la tendance. Wyckoff : hausse des prix + baisse des volumes sur 60J = 'No Demand' structurel = fragilité.", src:"Wyckoff, Villahermosa ch. 9 · Murphy, TAOTFM ch. 7" },
  supp_vol: { title:"Support Volume Ratio — Absorption sur les supports", body:"Rapport du volume lors des rebonds sur les supports vs le volume général. 1.05 = acheteurs légèrement plus actifs sur les supports. Wyckoff (Studies in Tape Reading, 1910) : 'No Supply' = rebonds sur supports avec faibles volumes vendeurs = les mains fortes n'allègent pas = signal haussier.", src:"Wyckoff, Studies in Tape Reading (1910) · Wyckoff, Villahermosa ch. 9" },
  res_vol: { title:"Résistance Volume Ratio — Pression sur les résistances", body:"Volume lors des tests de résistance. 0.92 = pression vendeuse modérée. Edwards & Magee (ch. 13) : 'la solidité d'une résistance est proportionnelle au volume échangé lors de sa formation initiale.' Un test de résistance sur faible volume est un signal positif — les vendeurs se font rares.", src:"Edwards & Magee, TAST ch. 13 · Murphy, TAOTFM ch. 4" },
  absorption: { title:"Signal d'absorption — Phase précurseur", body:"Les acheteurs institutionnels absorbent-ils les ventes sur les creux ? 'Moyen' = absorption partielle. Wyckoff (Studies in Tape Reading, 1910) : l'absorption progressive sur les supports est le signal précurseur systématique des phases de markup. C'est la 'cause' qui précède l'effet (Phase D → E).", src:"Wyckoff, Studies in Tape Reading (1910) · Wyckoff, Villahermosa ch. 15 & 19" },
  wyckoff: { title:"Phase du cycle de Wyckoff", body:"'Distribution' = les grandes institutions allègent progressivement après un markup. Phase précédant le markdown. Le prix peut encore progresser temporairement via un UTAD (Upthrust After Distribution — fausse cassure haute). Wyckoff : 'la distribution se fait dans l'euphorie — la foule achète ce que les mains fortes vendent'.", src:"Wyckoff, Villahermosa ch. 17-18 · Wyckoff, Studies in Tape Reading" },
  prix_vol: { title:"Prix / Volume — relation effort/résultat", body:"Murphy (TAOTFM ch. 7) : 'le volume confirme la tendance — son absence la fragilise.' Wyckoff Loi 3 : effort (volume) doit être proportionnel au résultat (prix). Hausse sur volumes déclinants = 'No Demand'. Fort volume sur hausse = confirmation institutionnelle.", src:"Murphy, TAOTFM ch. 7 · Wyckoff, Villahermosa ch. 9 (Loi de l'Effort et du Résultat)" },

  // ══ TIMING / OPPORTUNITÉ ════════════════════════════════
  rsi: { title:"RSI 14 — Relative Strength Index (Wilder, 1978)", body:"RSI = 100 − [100/(1+RS)]. RS = moy. gains 14j / moy. pertes 14j. 67.4 = proche du surachat (>70). Murphy (ch. 10) : en tendance forte, le RSI peut rester 50-80 de façon prolongée — la zone d'achat se déplace à 40-55 (pas 30). La DIVERGENCE RSI/prix est plus fiable que les niveaux absolus.", src:"Murphy, TAOTFM ch. 10 · Wilder, New Concepts in Technical Trading Systems (1978)" },
  opp_score: { title:"Opportunity Score — Qualité du point d'entrée", body:"Score 0-100 évaluant si c'est le bon moment pour initier ou renforcer. Intègre Z-Score, RSI, divergences momentum et rapport prix/niveaux clés (SMA, régression). 58/100 = opportunité passable. <50 = attendre. >70 = bon point d'entrée. Pesavento : 'l'entrée dans la bonne PRZ améliore le R/R de 2×'.", src:"Pesavento & Jouflas, Trade What You See ch. 4 · Invelps composite" },
  timing_score: { title:"Timing Score — Qualité tactique de l'entrée", body:"Score synthétisant la qualité du timing sur 3 horizons (CT/MT/LT). 61/100 = timing passable — tendance de fond favorable mais point d'entrée immédiat sous-optimal. Murphy : 'même dans la meilleure tendance, acheter au mauvais moment détruit le ratio risque/récompense.' Attendre améliore mécaniquement le R/R.", src:"Murphy, TAOTFM ch. 1 & 4 · Invelps composite" },
  tscore: { title:"TScore — Score Technique Global Invelps", body:"Score composite propriétaire intégrant structure (40%), momentum (25%), volumes (20%), timing (15%). 71/100 = signal technique above average. Calibré pour combiner les approches Murphy (trend), Wyckoff (volume) et Gray & Vogel (momentum quantitatif). Top 25% de l'univers analysé.", src:"Murphy TAOTFM · Wyckoff Villahermosa · Gray & Vogel QM · Invelps" },
  coherence: { title:"Coherence Score — Alignement inter-timeframes", body:"Mesure si les signaux CT, MT et LT convergent dans la même direction. 66/100 = légère divergence. Murphy (TAOTFM ch. 15) : 'quand tous les timeframes pointent dans la même direction, le signal est extrêmement fiable et le ratio R/R optimal.' >70 = forte convergence = setup idéal.", src:"Murphy, TAOTFM ch. 15 — analyse multi-temporelle" },
  cross_sma: { title:"Dernier croisement de moyennes mobiles", body:"Golden Cross = SMA50 dépasse SMA200 par le haut = signal haussier structurel de long terme. Death Cross = signal baissier. 'Aucun' = pas de croisement récent. Murphy : 'les croisements de MA confirment la tendance, ne la prédisent pas — souvent le mouvement a déjà commencé de 10-15%'.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  val_status: { title:"Statut de valorisation fondamentale", body:"Positionnement du cours vs juste valeur estimée (DCF + multiples sectoriels). 'Neutre' = prix cohérent avec les fondamentaux. Edwards & Magee : 'la valorisation fixe les bornes du raisonnable, la technique dit où le prix va maintenant.' Fondamental + technique = analyse complète.", src:"Damodaran — Valuation · Murphy, TAOTFM ch. 1 · Invelps" },
  jauge: { title:"Jauge d'opportunité — Synthèse visuelle", body:"Synthèse de l'attractivité du point d'entrée, de 'Faible' (rouge) à 'Excellent' (vert). Construit sur 4 signaux : Z-Score (éloignement statistique), RSI (momentum CT), divergence TSMOM (qualité), rapport prix/SMA (position dans la tendance). Pesavento : 'la confluence de plusieurs signaux dans une zone PRZ maximise le R/R'.", src:"Pesavento & Jouflas ch. 4 · Murphy ch. 4 & 10 · Invelps" },

  // ══ SUPPORT / RÉSISTANCE ════════════════════════════════
  support: { title:"Niveau de support clé", body:"Zone historique où la demande a systématiquement surpassé l'offre. 3 tests réussis = niveau très significatif. Edwards & Magee (TAST ch. 13) : 'un support représente une concentration de mémoire de marché.' Cassure + clôture en dessous + fort volume = support invalidé = nouvelle résistance (rôle inversé).", src:"Edwards & Magee, TAST ch. 13 · Murphy, TAOTFM ch. 4" },
  resistance: { title:"Niveau de résistance clé", body:"Zone historique où l'offre a systématiquement surpassé la demande. 2 tests réussis = niveau significatif. Principe du rôle inversé (Edwards & Magee ch. 13) : résistance cassée avec fort volume = nouveau support. Pesavento : les résistances majeures correspondent souvent à des extensions de Fibonacci.", src:"Edwards & Magee, TAST ch. 13 · Pesavento & Jouflas ch. 3" },

  // ══ GRAPHIQUE — PÉRIODES ════════════════════════════════
  period_3m:  { title:"Vue 3 mois — Tactique court terme", body:"Affiche les 60-65 dernières séances journalières avec bougies OHLCV. Utile pour identifier les points d'entrée tactiques, les cassures de S/R récents et l'état du momentum court terme. Murphy (ch. 3) : 'le court terme précise le timing, le long terme confirme la direction. Ne jamais trader le CT contre le LT.' Combiner 3M + 1Y pour un timing optimal.", src:"Murphy, TAOTFM ch. 3 & 4" },
  period_6m:  { title:"Vue 6 mois — Momentum intermédiaire", body:"Couvre environ 130 séances. Fenêtre idéale pour observer le TSMOM 6M en action sur le graphique et identifier les phases d'accélération / décélération. Moskowitz (JFE 2012) : le momentum 6M est le signal intermédiaire le plus robuste — corrélé positivement avec le momentum 12M et prédictif de la persistance à 3M.", src:"Moskowitz, Ooi & Pedersen, JFE 2012 · Murphy ch. 3" },
  period_1y:  { title:"Vue 1 an — Tendance annuelle et structure", body:"Vue de référence pour l'analyse technique classique. Révèle la structure de tendance annuelle, les Golden/Death Crosses, et la position du prix vis-à-vis de la SMA200. Murphy (ch. 9) : 'La SMA200 est la frontière entre bull et bear market. Un titre au-dessus est dans son marché haussier, en dessous dans son marché baissier.' C'est la vue pivot de l'analyse.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  period_3y:  { title:"Vue 3 ans — Structure de tendance moyen terme", body:"Passe aux données mensuelles. Affiche le canal de régression linéaire (droite + ±1σ / ±2σ) à la place des SMAs journalières. Permet de visualiser les phases Wyckoff sur l'horizon intermédiaire et d'évaluer si le prix est statistiquement surévalué ou sous-évalué par rapport à sa tendance historique. Le Z-Score est directement lisible sur le graphique.", src:"Wyckoff, SOT · Damodaran — Regression channels" },
  period_5y:  { title:"Vue 5 ans — Tendance structurelle long terme", body:"Vue sur laquelle se lisent les phases de compounding. Le canal ±2σ encadre ~95% des observations historiques. Un prix au-dessus de +2σ est statistiquement rare (<2.5%). Gray & Vogel (QM ch. 6) : 'c'est l'horizon qui discrimine les vrais compounders des cycliques.' R² mesure la cohérence de la progression sur cette fenêtre.", src:"Gray & Vogel, Quantitative Momentum ch. 6 · Murphy ch. 3" },
  period_max: { title:"Vue MAX — Historique complet depuis 2005", body:"Permet de replacer le prix actuel dans la totalité de l'historique disponible. Donne le vrai CAGR réalisé, le multiple de prix (price multiple), et la régression longue. À cette échelle, les corrections même sévères apparaissent comme des détails dans une tendance compoundée. Ilmanen (Expected Returns, ch. 7) : 'le long terme révèle la prime de risque réelle.'", src:"Ilmanen, Expected Returns ch. 7 · Gray & Vogel, QM ch. 6" },

  // ══ GRAPHIQUE — TOGGLES ═════════════════════════════════
  toggle_sma20:   { title:"SMA 20 — Moyenne mobile 20 séances", body:"La SMA20 (20 séances) est la moyenne mobile court terme de référence. Elle représente le comportement moyen des prix sur le dernier mois de trading. Un prix au-dessus de la SMA20 indique un momentum CT positif ; en dessous, une faiblesse CT. En tendance haussière forte, la SMA20 sert de premier support dynamique (rebond attendu). Murphy (ch. 9) : 'la SMA20 est le support CT le plus utilisé par les traders professionnels.'", src:"Murphy, TAOTFM ch. 9 · Covel, TF ch. 4" },
  toggle_sma50:   { title:"SMA 50 — Moyenne mobile 50 séances", body:"La SMA50 représente ~2.5 mois de trading. C'est la ligne de démarcation entre correction saine et retournement intermédiaire. Dans une tendance haussière, chaque retour sur SMA50 est une opportunité d'entrée ou de renforcement. Le Golden Cross (SMA50 > SMA200) est le signal de structure haussière moyen terme le plus reconnu. Murphy (ch. 9) : 'le croisement SMA50/SMA200 est l'un des signaux les plus suivis par les institutionnels.'", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  toggle_sma200:  { title:"SMA 200 — Moyenne mobile 200 séances", body:"La SMA200 (~10 mois) est la ligne de partage entre bull et bear market. Au-dessus = bull market (biais haussier systématique). En dessous = bear market (biais baissier). De nombreux fonds institutionnels utilisent la SMA200 comme filtre d'exposition (100% long si prix > SMA200, 0% sinon). Murphy (ch. 9) : 'la SMA200 est la frontière universelle entre tendance haussière et baissière.' CAGR moyen des actions au-dessus de leur SMA200 : +12%/an vs -2%/an en dessous.", src:"Murphy, TAOTFM ch. 9 · Faber, GTAA (2007) · Edwards & Magee ch. 28" },
  toggle_reg:     { title:"Droite de régression linéaire", body:"La régression linéaire modélise la trajectoire tendancielle du prix dans le temps. La droite représente la 'juste valeur' statistique à chaque date selon la tendance historique. R² mesure la qualité de l'ajustement : R²=0.84 signifie que 84% de la variance du prix est expliquée par la tendance linéaire. Plus le R² est élevé, plus la tendance est régulière et prévisible. Damodaran : 'la régression historique fixe les bornes du raisonnable pour un titre en croissance.'", src:"Damodaran — Valuation · Murphy ch. 3 · Invelps (log-régression)" },
  toggle_canal:   { title:"Canal de régression ±1σ / ±2σ", body:"Le canal statistique enveloppe la droite de régression avec ±1 et ±2 écarts-types. ±1σ encadre ~68% des observations historiques (zone de valorisation normale). ±2σ encadre ~95% des observations (zone statistiquement rare). Prix proche de +2σ = surévaluation extrême statistique, retour à la moyenne attendu. Prix proche de -1σ ou -2σ = zone de valeur historique, risque plus favorable. Wilder : 'les bandes statistiques révèlent le régime de valorisation relatif à la tendance.'", src:"Murphy, TAOTFM ch. 21 (Bollinger) · Damodaran · Invelps" },
  toggle_zscore:  { title:"Z-Score — Écart à la régression", body:"Le Z-Score mesure l'éloignement du prix actuel par rapport à la droite de régression en unités d'écart-type. Z=0 = prix sur la droite de régression (valorisation neutre). Z=+1 = +1σ au-dessus (légère survalorisation). Z=+2 = +2σ (survalorisation significative, rare). Z=-1 = -1σ (légère sous-valorisation). Le Z-Score est un outil de mean-reversion : plus il est extrême, plus le retour à la moyenne est statistiquement probable. Damodaran : 'le Z-Score de régression est un indicateur de valorisation relative robuste sur longue période.'", src:"Damodaran — Valuation · Murphy ch. 21 · Invelps" },
  toggle_volume:  { title:"Volume — Participation et conviction", body:"Le volume représente le nombre de titres échangés sur la période. Barre verte = séance haussière (clôture > ouverture). Barre rouge = séance baissière. La ligne pointillée = volume moyen 20 séances. Règle fondamentale de Murphy : 'le volume doit confirmer la tendance.' Hausse de prix + volume supérieur à la moyenne = signal fort (participation institutionnelle). Hausse de prix + volume faible = signal fragile (pas de conviction). Phase Wyckoff UTAD : volume anormalement élevé sur une fausse cassure haussière = signal de distribution.", src:"Murphy, TAOTFM ch. 7 · Wyckoff, SOT · Villahermosa, Wyckoff" },
  toggle_rsi:     { title:"RSI 14 — Relative Strength Index", body:"Le RSI (J. Welles Wilder, 1978) mesure la vitesse et l'amplitude des mouvements de prix sur 14 séances. RSI > 70 = surachat (prix monte trop vite, correction possible). RSI < 30 = survente (prix baisse trop vite, rebond possible). RSI entre 40-60 = zone neutre. En tendance haussière forte, le RSI reste souvent entre 40-80 sans atteindre 30 — c'est un signe de force. La divergence (prix fait un plus haut mais RSI baisse) est l'un des signaux de retournement les plus fiables. Murphy (ch. 10) : 'le RSI est l'oscillateur de référence pour la détection des divergences.'", src:"Wilder, New Concepts (1978) · Murphy, TAOTFM ch. 10" },
  toggle_sr:      { title:"Niveaux Support / Résistance clés", body:"Affiche les zones de support et résistance calculées sur 365 jours glissants. Support = zone historique d'accumulation (la demande dépasse systématiquement l'offre). Résistance = zone de distribution. La significativité d'un niveau est proportionnelle au nombre de tests réussis et au volume associé (Vol. Ratio > 1.2 = zone institutionnelle). Edwards & Magee (TAST ch. 13) : 'un support représente une concentration de mémoire de marché — plus il est testé, plus il est significatif.' Cassure + clôture + volume fort = confirmation + rôle inversé.", src:"Edwards & Magee, TAST ch. 13 · Murphy, TAOTFM ch. 4 · Pesavento ch. 3" },
  toggle_candle:  { title:"Graphique en bougies japonaises", body:"Chaque bougie représente 4 informations : ouverture, plus haut, plus bas, clôture. Corps vert = clôture > ouverture (séance haussière). Corps rouge = clôture < ouverture (séance baissière). Mèche haute = plus haut de séance. Mèche basse = plus bas. Les patterns de bougies (doji, marteau, étoile filante) sont des signaux de retournement ou de continuation. Nison (Japanese Candlestick Charting Techniques, 1991) : 'les bougies révèlent la psychologie du marché à chaque séance — qui contrôle, acheteurs ou vendeurs.'", src:"Nison, JCCT (1991) · Murphy, TAOTFM ch. 3" },
  toggle_line:    { title:"Graphique en ligne — Clôtures", body:"Le graphique en ligne relie uniquement les prix de clôture. Plus simple visuellement que les bougies, il élimine le 'bruit' intra-séance pour révéler la tendance pure. Préféré pour les vues longues (3Y, 5Y, MAX) où le niveau de clôture est plus pertinent que les extrêmes intra-séance. Murphy (ch. 3) : 'les clôtures sont les prix les plus importants de la séance — elles reflètent le consensus final du marché après toutes les nouvelles de la journée.'", src:"Murphy, TAOTFM ch. 3 · Edwards & Magee, TAST ch. 2" },

  // ══ GRAPHIQUE — LÉGENDE ═════════════════════════════════
  legend_sma20:   { title:"SMA 20 — Support court terme", body:"Ligne bleue fine. Représente la moyenne des 20 dernières clôtures. En tendance haussière, un retour sur SMA20 accompagné d'un rebond de volume est souvent le meilleur point d'entrée tactique. Distance actuelle entre le prix et la SMA20 indique le degré d'extension CT.", src:"Murphy, TAOTFM ch. 9" },
  legend_sma50:   { title:"SMA 50 — Ligne de tendance intermédiaire", body:"Ligne verte. La SMA50 est le pivot de la tendance intermédiaire. Un prix qui défend la SMA50 après une correction confirme la solidité de la tendance. Une cassure de la SMA50 est un avertissement intermédiaire. Murphy : 'la SMA50 est le premier niveau de soutien intermédiaire à surveiller.'", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee ch. 28" },
  legend_sma200:  { title:"SMA 200 — Frontière bull/bear market", body:"Ligne orange épaisse. La SMA200 est la ligne de séparation universelle entre bull et bear market. Au-dessus : biais haussier systématique. En dessous : biais baissier. Sa pente (montante, plate, descendante) est aussi importante que la position du prix vis-à-vis d'elle.", src:"Murphy, TAOTFM ch. 9 · Faber, GTAA (2007)" },
  legend_reg:     { title:"Droite de régression — Tendance historique", body:"Ligne bleue en pointillés. Trajectoire mathématique optimale du prix depuis 2005 (régression log-linéaire). Représente la 'juste valeur tendancielle' à chaque date. Un prix proche de la droite = valorisation neutre. S'éloigner de la droite vers le haut = surévaluation statistique croissante.", src:"Damodaran · Invelps (log-régression sur clôtures mensuelles)" },
  legend_b1:      { title:"Canal ±1σ — Zone de valorisation normale", body:"Lignes pointillées fines. Le canal ±1σ encadre environ 68% des observations historiques (loi normale). C'est la zone de 'valorisation normale' de ce titre au regard de sa tendance. Un prix dans ce canal est statistiquement banal — ni surévalué ni sous-évalué par rapport à son histoire.", src:"Murphy, TAOTFM ch. 21 · Damodaran · Invelps" },
  legend_b2:      { title:"Canal ±2σ — Zone statistiquement rare", body:"Lignes pointillées épaisses. Le canal ±2σ encadre ~95% des observations. Un prix dépassant +2σ est dans les 2.5% les plus chers de son histoire (signal de prudence extrême). Un prix sous -2σ est dans les 2.5% les moins chers — opportunité historique rare. Ces niveaux sont les cibles naturelles du mean-reversion.", src:"Murphy, TAOTFM ch. 21 · Damodaran · Invelps" },
  legend_support: { title:"Support clé — Zone mémoire du marché", body:"Ligne pointillée verte. Niveau historique où la demande a systématiquement surpassé l'offre. Plus le nombre de tests est élevé, plus le niveau est significatif. La distance entre le prix actuel et le support est le 'risque naturel' de la position si le support cède.", src:"Edwards & Magee, TAST ch. 13 · Murphy, TAOTFM ch. 4" },
  legend_resist:  { title:"Résistance clé — Prochain obstacle", body:"Ligne pointillée rouge. Zone historique où l'offre a pris le dessus. La cassure d'une résistance avec fort volume est un signal d'achat majeur (principe du rôle inversé). La distance entre le prix actuel et la résistance est le 'potentiel de gain' vers le prochain niveau.", src:"Edwards & Magee, TAST ch. 13 · Pesavento ch. 3" },

};


// ══════════════════════════════════════════════════════
// TOOLTIP COMPONENT
// ══════════════════════════════════════════════════════
function Tip({ title, body, src, children }) {
  const [vis, setVis] = useState(false);
  const [pos, setPos] = useState({ x:0, y:0 });
  const ref = useRef(null);
  const track = e => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };
  return (
    <span ref={ref} style={{ position:"relative", display:"inline-flex", alignItems:"center" }}
      onMouseEnter={e=>{ setVis(true); track(e); }}
      onMouseMove={track}
      onMouseLeave={()=>setVis(false)}>
      {children}
      {vis && (
        <span style={{
          position:"fixed",
          left: Math.min(pos.x + (ref.current?.getBoundingClientRect().left||0) + 14, window.innerWidth - 300),
          top:  Math.max((ref.current?.getBoundingClientRect().top||0) + pos.y - 8, 8),
          zIndex:9999,
          minWidth:220, maxWidth:300,
          background:"var(--card-bg)",
          border:"1px solid var(--card-bdr)",
          borderRadius:10, padding:"10px 14px",
          boxShadow:"0 8px 32px rgba(0,0,0,.28)",
          pointerEvents:"none",
          whiteSpace:"normal", lineHeight:1,
        }}>
          {title && <span style={{
            display:"block", fontFamily:"ui-monospace,monospace", fontSize:9,
            textTransform:"uppercase", letterSpacing:".11em",
            color:C.amber, marginBottom:6, paddingBottom:5,
            borderBottom:"1px solid var(--sep)"
          }}>{title}</span>}
          <span style={{ display:"block", fontSize:11.5, color:"var(--tx)", lineHeight:1.65 }}>{body}</span>
          {src && <span style={{
            display:"block", fontFamily:"ui-monospace,monospace", fontSize:8.5,
            color:"var(--mu)", marginTop:7, paddingTop:6,
            borderTop:"1px solid var(--sep)"
          }}>📖 {src}</span>}
        </span>
      )}
    </span>
  );
}

function I() {
  return (
    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" style={{ opacity:.35, marginLeft:3, flexShrink:0 }}>
      <circle cx="6" cy="6" r="5.4" stroke="currentColor" strokeWidth="1.1"/>
      <rect x="5.3" y="5" width="1.4" height="4.2" rx=".7" fill="currentColor"/>
      <rect x="5.3" y="2.8" width="1.4" height="1.4" rx=".7" fill="currentColor"/>
    </svg>
  );
}

function TipI({ tk }) {
  const t = TIPS[tk];
  if (!t) return null;
  return (
    <Tip title={t.title} body={t.body} src={t.src}>
      <span style={{
        display:"inline-flex", alignItems:"center", justifyContent:"center",
        width:14, height:14, borderRadius:"50%",
        border:`1px solid ${C.am}55`, fontSize:8, color:`${C.am}99`,
        marginLeft:3, flexShrink:0, lineHeight:1, cursor:"help"
      }}>?</span>
    </Tip>
  );
}

// ══════════════════════════════════════════════════════
// COURSE CONTENT — 3 LEVELS, 7 MODULES
// Sources : Murphy TAOTFM · Edwards & Magee TAST
//           Wyckoff (Villahermosa + Tape Reading)
//           Gray & Vogel QM · Covel TF
//           Pesavento & Jouflas · Gann (Reddy)
// ══════════════════════════════════════════════════════
const LEVELS = {
  novice: {
    id:"novice", label:"Novice", icon:"🌱", color:"#6f8a5b",
    desc:"Les fondamentaux visuels. Comprendre sans formules.",
    modules: [
      // ── MODULE INTRO ─────────────────────────────────────────────
      {
        id:"n0", anchor:"n-intro", icon:"🗺",
        sub:"§ Intro — Pourquoi l'analyse technique",
        title:"À quoi sert l'analyse technique ?",
        sources:["Murphy, TAOTFM — Introduction","Edwards & Magee, TAST — Preface","Covel, Trend Following ch. 1","Wyckoff, Studies in Tape Reading (1910)"],
        quote:"« L'analyse technique est l'étude du mouvement des marchés, principalement à travers l'utilisation des graphiques, dans le but de prévoir les futures tendances des prix. » — John J. Murphy, Technical Analysis of the Financial Markets",
        content:[
          {type:"p", text:"L'analyse technique (AT) est l'art de lire ce que le marché dit, ici et maintenant, à travers le prix et les volumes. Contrairement à l'analyse fondamentale qui évalue ce qu'une entreprise VAUT, l'AT évalue dans quelle DIRECTION le marché la pousse. Ce n'est pas un oracle — c'est un cadre de probabilités."},
          {type:"p", text:"Son postulat fondateur, posé par Charles Dow en 1884 et repris par tous ses successeurs : le cours d'une action INTÈGRE DÉJÀ toutes les informations disponibles. Les fondamentaux, les espoirs, les craintes, les rumeurs — tout est déjà dans le prix. Murphy : « Le marché sait tout avant que vous le sachiez. »"},
          {type:"concepts", items:[
            {term:"Les 3 postulats de Dow-Murphy", def:"1) Le marché intègre tout. 2) Les prix évoluent en tendances persistantes. 3) L'histoire se répète — les patterns reviennent car la psychologie humaine est constante.", src:"Murphy, TAOTFM — Introduction"},
            {term:"AT vs Analyse fondamentale", def:"L'AF dit 'ce titre vaut 180€'. L'AT dit 'ce titre va vers 220€ dans les 3 prochains mois'. L'une évalue la valeur, l'autre la direction et le timing. Les deux sont complémentaires, pas opposées.", src:"Murphy, TAOTFM ch. 1"},
            {term:"Le marché comme système de votes", def:"Chaque transaction est un vote : acheteur pense que le prix va monter, vendeur qu'il va baisser. Le graphique reflète le résultat agrégé de millions de ces votes — c'est le consensus en temps réel.", src:"Wyckoff, Studies in Tape Reading"},
            {term:"Probabilités, pas certitudes", def:"L'AT ne prédit pas l'avenir — elle identifie des configurations où la probabilité d'un mouvement donné est statistiquement supérieure à 50%. C'est suffisant pour construire un edge (avantage) systématique.", src:"Covel, Trend Following ch. 1"},
          ]},
          {type:"ibox", color:"#6f8a5b", label:"Pourquoi l'AT fonctionne (réponse de Wyckoff, 1910)",
           text:"Richard Wyckoff observait directement les grands opérateurs de Wall Street au début du 20e siècle. Sa conclusion : les institutions (banques, fonds) doivent acheter ou vendre de telles quantités qu'elles laissent inévitablement des traces dans le prix et le volume. L'AT est l'art de lire ces traces — de déduire ce que les 'mains fortes' font, avant que la foule ne le voie."},
          {type:"ibox", color:"#caa85e", label:"La limite fondamentale : l'AT ne prédit pas, elle réagit",
           text:"Covel (Trend Following, ch. 1) résume parfaitement : « Trend following is reactive, not predictive. » Personne ne sait où ira le marché demain. L'AT ne sert pas à prédire — elle sert à identifier l'état présent du marché et à réagir intelligemment. La différence entre spéculation et investissement structuré, c'est précisément ce cadre de réaction disciplinée."},
          {type:"p", text:"Maintenant la vraie question : face à la multitude d'outils (RSI, MACD, Fibonacci, Wyckoff, Gann, SMAs, régressions…), comment ne pas se perdre ? La réponse tient en une phrase — Murphy l'énonce dès le chapitre 1 : commencez toujours par identifier la tendance primaire, puis descendez vers le court terme."},
          {type:"concepts", items:[
            {term:"La séquence d'analyse en 4 étapes", def:"1. TENDANCE : quelle est la direction primaire ? (SMAs, structure hauts/bas). 2. NIVEAU : où sont les supports/résistances clés ? 3. MOMENTUM : la tendance a-t-elle de la force ? (RSI, volumes, TSMOM). 4. TIMING : est-ce le bon moment pour agir ? (Z-Score, convergence des signaux).", src:"Murphy, TAOTFM ch. 1 & 15"},
            {term:"Le principe de convergence", def:"Un signal est fiable quand plusieurs outils indépendants pointent dans la même direction. Une SMA haussière + RSI positif + volume croissant + support tenu = convergence forte = probabilité élevée.", src:"Murphy, TAOTFM ch. 15 (analyse multi-timeframes)"},
            {term:"Moins d'outils, plus de clarté", def:"Murphy et Covel s'accordent : trop d'indicateurs = paralysie par analyse. Les 3 meilleurs outils simples : 1) La structure de prix (hauts/bas). 2) Les moyennes mobiles (SMA 20/50/200). 3) Le volume. Tout le reste est confirmation.", src:"Murphy, TAOTFM ch. 9 · Covel, Trend Following ch. 3"},
            {term:"Le timeframe détermine la stratégie", def:"Long terme (mensuel/hebdo) = biais directionnel, décision d'allocation. Moyen terme (journalier) = entrées/sorties, gestion de position. Court terme (horaire) = timing précis. On commence TOUJOURS par le LT.", src:"Murphy, TAOTFM ch. 15"},
          ]},
          {type:"ibox", color:"#c6523f", label:"La séquence Invelps : comment lire ce dashboard",
           text:"Ce dashboard implémente exactement la séquence Murphy en 4 niveaux. 1) PROFIL & IScore = contexte fondamental + conviction. 2) DIAGNOSTIC = synthèse de la tendance primaire et du régime. 3) CHART = lecture visuelle de la structure, des SMAs, du canal σ. 4) PILIERS = analyse granulaire de chaque dimension (structure, momentum, volume, timing). Lisez toujours dans cet ordre — du général au particulier."},
        ],
        qcm:[
          {
            q:"Murphy dit que 'le marché intègre tout'. Un analyste fondamental vient de découvrir qu'une entreprise a des problèmes cachés non encore publiés. Pourtant le cours de l'action a baissé de 15% en 3 semaines sans nouvelle officielle. L'AT peut-elle expliquer cela ?",
            choices:[
              {l:"Non — sans information officielle, le cours baisse par hasard. L'AT n'explique pas les mouvements sans cause fondamentale.", c:false,
               f:"Faux. C'est précisément ce que Murphy et Wyckoff expliquent : les insiders (qui connaissent les problèmes) vendent depuis 3 semaines. Leurs ventes laissent des traces dans le graphique. L'AT lit ces traces avant que l'information ne soit publique — c'est l'un de ses avantages fondamentaux."},
              {l:"Oui — les initiés qui connaissent les problèmes vendent depuis 3 semaines. L'AT lit les traces de ces ventes (volume + structure de prix) avant la publication officielle.", c:true,
               f:"Exact. Murphy (Introduction) : 'Le marché sait tout avant que vous le sachiez.' Wyckoff (1910) observait déjà ce phénomène : les grandes ventes institutionnelles laissent des empreintes volumétriques et de prix. L'AT lit ces empreintes en temps réel."},
              {l:"Peut-être — mais l'AT ne peut fonctionner que si on connaît la cause fondamentale.", c:false,
               f:"C'est l'erreur classique. L'AT n'a pas besoin de connaître la cause — elle lit le résultat (le prix et le volume). La cause importe peu pour l'action tactique. Covel : 'I don't need to know why — I need to know what.'"},
            ]
          },
          {
            q:"Face à un graphique NVDA, dans quel ordre Murphy recommande-t-il d'analyser les éléments ?",
            choices:[
              {l:"1. RSI → 2. MACD → 3. Bandes de Bollinger → 4. Tendance", c:false,
               f:"Murphy s'opposerait à cette approche. Commencer par les oscillateurs est une erreur classique de débutant : on cherche des signaux avant de savoir dans quelle direction le marché va. L'oscillateur donne un signal d'achat dans une tendance baissière = catastrophe."},
              {l:"1. Tendance primaire (SMAs, structure) → 2. Supports/résistances clés → 3. Momentum (RSI, volumes) → 4. Timing (Z-Score, convergence)", c:true,
               f:"Parfait. C'est exactement la séquence Murphy (TAOTFM ch. 1 & 15) et la logique de ce dashboard. Du général au particulier. La tendance primaire est le filtre — elle détermine le biais de toutes les décisions suivantes. Sans elle, les signaux CT sont du bruit."},
              {l:"1. Fibonacci → 2. Patterns → 3. Volume → 4. Tendance", c:false,
               f:"Fibonacci et les patterns sont des outils avancés de timing — ils n'ont de sens qu'une fois la tendance et les niveaux clés identifiés. Murphy : 'les outils avancés confirment, ils n'initient pas l'analyse.'"},
            ]
          },
        ]
      },
      // ── MODULES N1-N3 ─────────────────────────────────────────────
      {
        id:"n1", anchor:"n-tendance", icon:"📈",
        sub:"§ N1 — Structure de marché",
        title:"Lire une tendance",
        sources:["Edwards & Magee, TAST ch. 2-3","Wyckoff, Villahermosa ch. 3","Covel, Trend Following ch. 1"],
        quote:"« Le prix suit le chemin de la moindre résistance. La tendance haussière est une série de sommets et de creux croissants. » — Wyckoff / Villahermosa",
        content:[
          {type:"p", text:"Une tendance est la direction persistante des prix dans le temps. Edwards et Magee (1948) définissent précisément : une tendance HAUSSIÈRE est une succession de « hauts de plus en plus hauts ET de bas de plus en plus hauts ». Les deux conditions sont nécessaires. Un seul nouveau haut ne suffit pas."},
          {type:"p", text:"Wyckoff précise que le prix suit toujours « le chemin de la moindre résistance » — il progresse là où la résistance est la plus faible. Michael Covel (Trend Following) va plus loin : « le trend following est réactif, jamais prédictif. » On ne prédit pas la tendance — on la suit."},
          {type:"concepts", items:[
            {term:"Tendance haussière", def:"Hauts et bas TOUS DEUX croissants sur plusieurs cycles. La rupture d'un seul bas précédent est un signal d'alerte structurel.", src:"Edwards & Magee ch. 2"},
            {term:"Tendance baissière", def:"Hauts et bas TOUS DEUX décroissants. Chaque rebond échoue à dépasser le sommet précédent.", src:"Edwards & Magee ch. 2"},
            {term:"Range (consolidation)", def:"Oscillation entre support et résistance sans direction claire. Wyckoff appelle ça la 'construction de la cause' — énergie potentielle accumulée.", src:"Wyckoff, Villahermosa ch. 7"},
            {term:"Retournement vs rebond", def:"Un rebond technique n'est PAS un retournement. Il faut une rupture confirmée de la structure de hauts/bas ET du volume pour parler de retournement.", src:"Murphy, TAOTFM ch. 4"},
          ]},
          {type:"ibox", color:T.am, label:"La théorie de Dow (1884)",
           text:"Charles Dow a posé les bases : une tendance primaire se compose de 3 phases. 1) Accumulation (mains fortes achètent en secret). 2) Participation publique (la tendance est visible). 3) Distribution (mains fortes vendent à la foule enthousiaste). Edwards & Magee ont formalisé cela en 1948. Cette structure est toujours d'actualité."},
          {type:"ibox", color:T.re, label:"Piège classique du débutant",
           text:"Confondre un rebond technique avec un retournement. Après une baisse, chaque rebond semble être 'la reprise'. Vérifiez toujours : est-ce que le nouveau haut DÉPASSE le précédent ? Si non, c'est un rebond dans une tendance baissière, pas un retournement. Covel : 'les marchés testent votre conviction avant de vous récompenser.'"},
        ],
        qcm:[
          {q:"NVDA : H1=180€, B1=165€, H2=205€, B2=170€, H3=195€. Quelle est la structure ?",
           choices:[
            {l:"Tendance haussière confirmée — H2>H1 et B2>B1", c:false,
             f:"Presque. H2(205)>H1(180) ✓ et B2(170)>B1(165) ✓ = haussier jusque-là. Mais H3(195)<H2(205) : la règle des hauts croissants est brisée. C'est un essoufflement (Edwards & Magee : 'non-confirmation') — signal d'alerte, pas encore retournement."},
            {l:"Essoufflement haussier — H3(195) < H2(205) brise la règle des hauts croissants", c:true,
             f:"Exact ! Edwards & Magee (ch. 2) : tendance haussière = CHAQUE haut > haut précédent. H3<H2 signale un essoufflement. Murphy parlerait de 'divergence de structure'. Ce n'est pas encore baissier (les bas restent croissants) mais c'est une alerte majeure."},
            {l:"Tendance baissière — le dernier mouvement est une baisse de 205 à 195", c:false,
             f:"Non. Une baisse de 10 points ne définit pas une tendance baissière. Wyckoff : il faut une succession de hauts ET bas décroissants. Les bas restent croissants (165→170) — structure partiellement intacte."},
           ]
          },
          {q:"Covel (Trend Following) dit : 'Je ne sais pas où ira le marché demain.' Quelle implication pratique ?",
           choices:[
            {l:"Il faut donc ignorer l'AT et faire de l'analyse fondamentale", c:false,
             f:"Non. Covel est un trader pur prix — il n'utilise pas l'analyse fondamentale. Son message : AUCUNE méthode ne prédit l'avenir. L'AT lit l'état présent, elle ne prédit pas."},
            {l:"On réagit au prix réel, on ne prédit pas. On suit la tendance actuelle et on accepte d'être en retard au début.", c:true,
             f:"Parfait. Covel (ch. 1) : 'trend following is reactive, not predictive.' On attend la confirmation de la tendance (parfois 5-10% après le départ) puis on suit. La 'perte' initiale est le coût de la confirmation — c'est normal."},
            {l:"Il faut attendre la certitude absolue avant d'agir", c:false,
             f:"La 'certitude absolue' n'existe pas en bourse. Covel : 'waiting for certainty means never trading.' On accepte l'incertitude, on gère le risque. C'est l'essence du trend following."},
           ]
          },
        ]
      },
      {
        id:"n2", anchor:"n-supports", icon:"🎯",
        sub:"§ N2 — Niveaux clés",
        title:"Supports, Résistances et Rôle inversé",
        sources:["Edwards & Magee, TAST ch. 13","Murphy, TAOTFM ch. 4"],
        quote:"« Ces niveaux critiques changent constamment de rôle — de support à résistance et de résistance à support. C'est l'un des principes les plus importants et les moins compris de l'analyse technique. » — Edwards & Magee, TAST ch. 13",
        content:[
          {type:"p", text:"Edwards et Magee ont consacré tout un chapitre (ch. 13) aux supports et résistances — c'est l'une des sections les plus citées de leur ouvrage fondateur de 1948. Un SUPPORT est une zone de prix où, historiquement, les acheteurs ont surpassé les vendeurs. Une RÉSISTANCE est l'inverse."},
          {type:"p", text:"Le principe du RÔLE INVERSÉ est la découverte clé : quand un support est cassé avec conviction, il devient résistance. Quand une résistance est cassée avec volume, elle devient support. Edwards & Magee l'illustrent avec des dizaines de graphiques dans leur chapitre 13."},
          {type:"concepts", items:[
            {term:"Support", def:"Zone historique où la demande a stoppé la baisse. Chaque test réussi renforce le niveau. Edwards & Magee : le volume lors des touches mesure la 'force' du support.", src:"Edwards & Magee ch. 13"},
            {term:"Résistance", def:"Zone historique où l'offre a stoppé la hausse. Anciens sommets, congestions de prix à volume élevé.", src:"Edwards & Magee ch. 13"},
            {term:"Rôle inversé", def:"Support cassé = nouvelle résistance. Résistance cassée = nouveau support. Validé sur des centaines de graphiques depuis 1948. Murphy le confirme dans TAOTFM ch. 4.", src:"Edwards & Magee ch. 13 · Murphy ch. 4"},
            {term:"Significance (force du niveau)", def:"Dépend de : nombre de touches, volume aux touches, ancienneté. Plus un niveau est testé et tient, plus il est significatif.", src:"Edwards & Magee ch. 13"},
          ]},
          {type:"ibox", color:T.gr, label:"Le volume valide la cassure",
           text:"Murphy (TAOTFM ch. 4) : une cassure de support ou résistance n'a de valeur que si le volume est supérieur à la moyenne des 20 dernières séances. Une cassure sur volume faible = 'fake break' probable. Edwards & Magee parlent de la règle des 3% (confirmer 3% au-delà du niveau). Sur NVDA à 216€ (résistance), une clôture à 222€+ sur fort volume = breakout valide."},
          {type:"ibox", color:T.bl, label:"Zones, pas lignes précises",
           text:"Edwards & Magee insistent : les supports et résistances sont des ZONES, pas des lignes au centime près. Un support à 165€ signifie que tout rebond entre 163 et 167€ est dans la norme. Tracer une ligne exacte à 165.17€ et panicker à 164.80€ est une erreur de novice — les marchés ne sont pas si précis."},
        ],
        qcm:[
          {q:"NVDA a résisté 3 fois à 216€ (résistance). Il clôture à 218€ sur volume 2.5× la moyenne. La semaine suivante il revient tester 216€. Selon Edwards & Magee, comment interpréter ce test ?",
           choices:[
            {l:"Signal de vente — le titre retourne vers sa résistance, risque de rechute", c:false,
             f:"Non. La cassure sur fort volume (2.5×) valide le breakout. Edwards & Magee (ch. 13) : la résistance cassée devient support = rôle inversé. Le retour sur 216€ est un pullback classique sur l'ancien niveau — l'entrée idéale selon la méthode."},
            {l:"Achat sur le test de 216€ — l'ancienne résistance est maintenant support (rôle inversé)", c:true,
             f:"Parfait. C'est le principe du rôle inversé formalisé par Edwards & Magee ch. 13. Le pullback sur 216€ après le breakout sur fort volume est le setup d'entrée classique. Stop sous 214€ (zone). Murphy confirme ce setup dans TAOTFM ch. 4."},
            {l:"Attendre une deuxième clôture au-dessus de 218€ pour confirmer", c:false,
             f:"La cassure initiale (clôture 218€, volume 2.5×) EST la confirmation. Attendre une deuxième cassure revient à entrer avec un retard de 5-10% et un ratio risque/récompense dégradé. Edwards & Magee : la confirmation vient du volume, pas d'un deuxième signal prix."},
           ]
          },
        ]
      },
      {
        id:"n3", anchor:"n-volume", icon:"📊",
        sub:"§ N3 — Volume & Conviction",
        title:"Le volume : l'effort derrière le mouvement",
        sources:["Wyckoff, Villahermosa ch. 9","Murphy, TAOTFM ch. 7","Wyckoff, Studies in Tape Reading"],
        quote:"« En analyse financière, l'effort est représenté par le volume et le résultat par le prix. Si l'effort et le résultat ne correspondent pas, quelque chose ne va pas. » — Wyckoff / Villahermosa, ch. 9",
        content:[
          {type:"p", text:"Richard Wyckoff (1873-1934) a fondé son analyse sur une loi simple : le VOLUME représente l'effort des participants, le PRIX représente le résultat. Un effort doit produire un résultat proportionnel. Si le prix monte vigoureusement sur volume faible — attention. L'effort est disproportionné au résultat."},
          {type:"p", text:"Villahermosa modernise ces concepts : le volume révèle les actions des 'mains fortes' (institutions, smart money) qui ne peuvent pas dissimuler leurs mouvements dans les données volumétriques. Murphy complète : 'le volume doit confirmer la tendance. Une tendance dont le volume est décroissant est suspecte.'"},
          {type:"concepts", items:[
            {term:"Volume conforme", def:"Hausse sur volumes croissants + baisse sur volumes décroissants = tendance saine. Murphy : 'la règle de base.' Tout le reste est une déviation.", src:"Murphy, TAOTFM ch. 7"},
            {term:"Divergence prix/volume", def:"Prix monte mais volume baisse : signal d'essoufflement. Les mains fortes ne participent pas à la hausse. Wyckoff : 'No Demand' — absence de demande institutionnelle.", src:"Wyckoff, Villahermosa ch. 9"},
            {term:"Selling Climax", def:"Pic de volume extrême sur un bas, souvent accompagné d'une actualité négative. Wyckoff : signal de Phase A — l'épuisement de la vente institutionnelle. Potentiellement le bas d'un cycle d'accumulation.", src:"Wyckoff, Villahermosa ch. 15"},
            {term:"No Supply / No Demand", def:"Wyckoff : petits volumes sur les reculs (No Supply = haussier) ou petits volumes sur les rebonds (No Demand = baissier). Les mains fortes s'abstiennent.", src:"Wyckoff, Studies in Tape Reading"},
          ]},
          {type:"ibox", color:T.am, label:"Loi de l'effort et du résultat",
           text:"Wyckoff (Villahermosa ch. 9) : si un titre baisse sur volumes faibles, les gros vendeurs NE vendent pas. La baisse est due à l'absence d'acheteurs, non à des ventes agressives. C'est un signal moins négatif qu'il n'y paraît — potentiellement une zone d'accumulation. À l'inverse, une hausse sur volumes très forts peut être un Buying Climax — signal de distribution imminente."},
          {type:"ibox", color:T.bl, label:"Volumes relatifs, jamais absolus",
           text:"Un volume de 100M titres NVDA est-il fort ou faible ? Cela dépend de la MOYENNE. Sur les 60 dernières séances, NVDA échange ~150M titres/jour. 100M = faible (0.67×). Mais si la moyenne était 40M, alors 100M = très fort (2.5×). Toujours comparer le volume actuel à la moyenne des 20-60 dernières séances."},
        ],
        qcm:[
          {q:"NVDA chute de 3% cette semaine sur volume 40% inférieur à la moyenne 20 jours. Interprétation Wyckoff ?",
           choices:[
            {l:"Signal baissier fort — la baisse sur 3 jours confirme la tendance baissière", c:false,
             f:"Non. L'élément clé est le volume FAIBLE. Wyckoff : une baisse sur volume faible = les gros vendeurs ne sont pas actifs. C'est une baisse par manque d'acheteurs, pas par pression vendeuse institutionnelle. Signal ambigu, pas baissier fort."},
            {l:"No Supply probable — baisse sur volume faible = pas de pression vendeuse institutionnelle. Potentiellement un test sain en tendance haussière.", c:true,
             f:"Exact. Wyckoff (Villahermosa ch. 9) : 'No Supply' = baisse sur volume faible. Les institutions ne vendent pas. La faiblesse est superficielle. En contexte de tendance haussière, c'est souvent un test de support avant la reprise. Murphy confirme : 'une correction sur volume décroissant reste dans la norme.'"},
            {l:"Il faut attendre un rebond avec fort volume pour confirmer quoi que ce soit", c:false,
             f:"Partiellement vrai, mais la conclusion de 'No Supply' est déjà informative. Wyckoff n'attend pas toujours la confirmation — il lit l'état présent. Un rebond sur fort volume serait la confirmation de la Phase D, pas la première lecture."},
           ]
          },
        ]
      },
    ]
  },

  intermediaire: {
    id:"intermediaire", label:"Intermédiaire", icon:"📐", color:"#caa85e",
    desc:"Outils quantitatifs : OLS, RSI, TSMOM. Formules sur données NVDA.",
    modules: [
      {
        id:"i1", anchor:"i-regression", icon:"📐",
        sub:"§ I1 — Quantification de la tendance",
        title:"Régression OLS et Moyennes Mobiles",
        sources:["Murphy, TAOTFM ch. 4 & 9","Edwards & Magee, TAST ch. 28"],
        formula:"P_t = α + β·t       R² = 1 − (SS_res / SS_tot)",
        quote:"« La moyenne mobile ne prédit pas la tendance — elle la confirme. Sa valeur est dans la simplification du signal, pas dans l'anticipation. » — Murphy, TAOTFM ch. 9",
        content:[
          {type:"p", text:"La régression OLS (Ordinary Least Squares) ajuste une droite P_t = α + β·t aux prix historiques. β représente la vitesse théorique de progression (€/semaine ou %/an). Mais Murphy insiste fortement dans TAOTFM ch. 4 : β sans R² n'a aucune valeur. Le R² mesure le 'fit' (0-1). Seuil actionnable : R² ≥ 0.50."},
          {type:"p", text:"Les moyennes mobiles simples (SMA) sont des lissages qui filtrent le bruit à court terme. L'alignement SMA20 > SMA50 > SMA200 est la configuration haussière idéale selon Murphy. Le 'Golden Cross' (SMA50 > SMA200) est l'un des signaux les plus suivis institutionnellement. Edwards & Magee l'appelaient 'la confirmation de la tendance primaire.'"},
          {type:"concepts", items:[
            {term:"Pente OLS (β)", def:"Vitesse théorique de progression. DOIT être validée par R² ≥ 0.50. β positif + R² faible = 'tendance existe mais chemin trop erratique pour agir dessus' (Murphy ch. 4).", src:"Murphy, TAOTFM ch. 4"},
            {term:"R² (coefficient de détermination)", def:"% de la variance du prix expliqué par la tendance linéaire. <0.30 = signal peu fiable. 0.50-0.75 = signal modéré. >0.75 = tendance très ordonnée.", src:"Murphy, TAOTFM ch. 4"},
            {term:"Golden Cross", def:"SMA50 passe au-dessus de SMA200. Signal haussier de long terme. Edwards & Magee : 'la confirmation la plus puissante de la tendance primaire.' Murphy : signal de confirmation, jamais d'anticipation.", src:"Murphy ch. 9 · Edwards & Magee ch. 28"},
            {term:"Alignement des SMA", def:"SMA20>SMA50>SMA200 = ordre haussier parfait. Prix < SMA200 = marché techniquement baissier selon la majorité des institutionnels. La ligne de démarcation bull/bear.", src:"Murphy, TAOTFM ch. 9"},
          ]},
          {type:"ibox", color:T.re, label:"Le piège de β sans R²",
           text:"Exemple : β = +0.097€/sem (positif, encourageant) avec R² = 0.17 (catastrophique). Seulement 17% de la variance est expliquée par la tendance — 83% est du bruit. Murphy (ch. 4) : 'ce titre monte sur le papier mais descend souvent dans la réalité. Le chemin est trop erratique pour être exploitable.' Seuil minimum : R² = 0.50 pour envisager un signal basé sur la régression."},
          {type:"ibox", color:T.gr, label:"Zone de rebond en tendance haussière",
           text:"Murphy (ch. 9) : en tendance haussière forte (SMA20>SMA50>SMA200), les corrections jusqu'à la SMA50 sont des OPPORTUNITÉS D'ACHAT, pas des signaux de vente. La SMA20 est le premier support dynamique. La SMA200 est le dernier rempart bull/bear. Prix qui rebondit sur SMA50 après un test réussi = entrée avec excellent ratio risque/récompense."},
        ],
        qcm:[
          {q:"NVDA : β = +1.2%/sem, R² = 0.72. SMA20=200€, SMA50=187€, SMA200=184€. Prix actuel = 198€. Que conclure ?",
           choices:[
            {l:"Tendance haussière solide et fiable (R²=0.72 > seuil 0.50). Test de SMA20 = entrée potentielle selon Murphy.", c:true,
             f:"Parfait. R²=0.72 > seuil 0.50 → β fiable. Alignement SMA20>SMA50>SMA200 = haussier parfait (Murphy ch. 9). Prix à 198€ teste la SMA20 (200€) = rebond dans la tendance. C'est le setup d'entrée décrit par Murphy ch. 9 : 'pullback sur la première MA en tendance forte.'"},
            {l:"Attention — prix sous SMA20 (200€) → signal baissier de court terme", c:false,
             f:"Murphy distingue 'test' (prix ~SMA20, normal) de 'rupture confirmée' (prix BIEN sous SMA20 sur volume). 198€ vs 200€ = test, pas rupture. L'alignement global reste haussier, R² est solide."},
            {l:"R²=0.72 insuffisant — attendre R²≥0.90 pour agir", c:false,
             f:"Murphy (ch. 4) cite 0.50 comme seuil minimal et 0.65+ comme niveau confortable. 0.72 est un très bon R² — la tendance est fiable statistiquement. 0.90 serait excessivement restrictif et exclurait la plupart des tendances réelles."},
           ]
          },
        ]
      },
      {
        id:"i2", anchor:"i-momentum", icon:"⚡",
        sub:"§ I2 — Mesure du momentum",
        title:"RSI et Time-Series Momentum (TSMOM)",
        sources:["Murphy, TAOTFM ch. 10","Gray & Vogel, QM ch. 3 & 6","Moskowitz, Ooi & Pedersen, JFE 2012"],
        formula:"RSI = 100 − [100 / (1 + RS)]     RS = moy. gains 14j / moy. pertes 14j\nTSMOM_h = (P_t / P_{t-h}) − 1\nFIP = SIGN(TSMOM_12M) × (% jours_négatifs − % jours_positifs)",
        quote:"« Le momentum est la tendance des gagnants à continuer de gagner. Cette anomalie est documentée sur plus de 200 ans et dans 40+ marchés. C'est le plus robuste des facteurs. » — Gray & Vogel, Quantitative Momentum 2016",
        content:[
          {type:"p", text:"Le RSI (Relative Strength Index) a été créé par Welles Wilder en 1978. Murphy insiste : le RSI NE prédit PAS la direction — il qualifie l'état. La vraie puissance du RSI réside dans les DIVERGENCES : si le prix fait de nouveaux hauts mais que le RSI ne confirme pas → signal d'essoufflement."},
          {type:"p", text:"Gray & Vogel (Quantitative Momentum, 2016) introduisent deux métriques clés. Le TSMOM (Time-Series Momentum) mesure le rendement absolu sur 3, 6 ou 12 mois. Moskowitz, Ooi & Pedersen (JFE 2012) ont prouvé sa persistance sur 200 ans. Le FIP (Frog in the Pan) mesure la QUALITÉ du momentum : un momentum positif construit sur des pics haussiers isolés dans une mer de jours baissiers est statistiquement inférieur."},
          {type:"concepts", items:[
            {term:"RSI zones de tendance", def:"En tendance forte, le RSI peut rester >70 pendant des mois. Murphy : 'les zones 30/70 sont pour les marchés en range, pas en tendance. En tendance haussière, acheter autour de 40-50, pas à 30.'", src:"Murphy, TAOTFM ch. 10"},
            {term:"Divergence RSI baissière", def:"Prix fait un nouveau haut mais RSI fait un haut inférieur. Murphy : 'l'un des signaux les plus fiables de l'AT.' Signal d'essoufflement, pas de vente mécanique.", src:"Murphy, TAOTFM ch. 10"},
            {term:"TSMOM (Time-Series Momentum)", def:"Rendement absolu sur h mois. Persistance sur 200+ ans (Moskowitz 2012). TSMOM 12M > 0 = momentum positif. Mais la qualité compte autant que la direction.", src:"Gray & Vogel, QM ch. 3"},
            {term:"FIP (Frog in the Pan)", def:"Score de régularité. FIP > 0 avec TSMOM > 0 = gains concentrés sur quelques pics dans une majorité de jours baissiers. Gray & Vogel : ces 'frogs' sous-performent les 'smooth momentum' de manière statistiquement significative.", src:"Gray & Vogel, QM ch. 6"},
          ]},
          {type:"ibox", color:T.am, label:"RSI en tendance forte : la règle méconnue",
           text:"Murphy (TAOTFM ch. 10) : en tendance haussière très forte, les niveaux d'achat se décalent. Au lieu de 30 (survente classique), la zone d'achat passe à 40-50. Le RSI peut rester entre 50 et 80 pendant des mois en tendance haussière — c'est NORMAL. Sur NVDA en phase de markup, RSI = 67 n'est pas du surachat mais de la vigueur."},
          {type:"ibox", color:T.re, label:"FIP : la grenouille et l'eau qui chauffe",
           text:"Gray & Vogel (QM ch. 6, p.118) : une grenouille dans une eau qui chauffe progressivement ne réagit pas. Identique pour les titres à 'discrete momentum' : TSMOM 12M = +62% mais construit sur quelques séances exceptionnelles et une majorité de jours baissiers. Les participants ne 'voient' pas le danger. Ces titres à FIP élevé sous-performent statistiquement les titres à 'continuous momentum' (progression régulière) sur les 3-12 mois suivants."},
        ],
        qcm:[
          {q:"NVDA : RSI = 68, TSMOM 12M = +62%, TSMOM 3M = -2%. FIP = +0.05 (majorité de jours baissiers malgré bilan annuel positif). Murphy et Gray & Vogel convergent-ils ?",
           choices:[
            {l:"Murphy haussier (RSI<70), Gray & Vogel prudents (FIP élevé = qualité de momentum médiocre). Lecture nuancée : LT haussier mais qualité de CT fragile.", c:true,
             f:"Exact. Murphy lirait RSI=68 comme 'fort mais pas surachat'. Gray & Vogel regarderaient FIP=+0.05 (faible mais positif = 'frog') : momentum positif mais de mauvaise qualité. Conclusion conjointe : tendance LT haussière confirmée, mais prudence sur les entrées à CT. Le momentum de court terme est fragile."},
            {l:"Tout est haussier — TSMOM 12M +62% et RSI < 70 confirment", c:false,
             f:"Gray & Vogel iraient au-delà du TSMOM 12M. Le FIP qualifie la RÉGULARITÉ du momentum. +62% construit sur 4-5 séances exceptionnelles dans une mer de jours baissiers est statistiquement moins robuste qu'un +35% régulier. La qualité compte."},
            {l:"TSMOM 3M négatif (-2%) invalide tout — c'est un signal de vente", c:false,
             f:"Murphy et Gray & Vogel s'accorderaient : TSMOM 3M négatif est une divergence de court terme dans une tendance de fond positive, pas un signal de vente. Gray & Vogel (ch. 3) utilisent TSMOM 12M comme signal principal, 3M comme filtre de qualité."},
           ]
          },
        ]
      },
    ]
  },

  avance: {
    id:"avance", label:"Avancé", icon:"🔬", color:"#c6523f",
    desc:"Wyckoff complet, patterns harmoniques Fibonacci, temps de Gann.",
    modules: [
      {
        id:"a1", anchor:"a-wyckoff", icon:"🔄",
        sub:"§ A1 — Méthode Wyckoff",
        title:"Cycle de marché : 5 phases et événements clés",
        sources:["Wyckoff, Villahermosa ch. 14-20","Wyckoff, Studies in Tape Reading (1910)"],
        formula:"Loi 1 : D > O → Prix ↑  |  O > D → Prix ↓\nLoi 2 : Accumulation → Markup proportionnel (cause → effet)\nLoi 3 : Volume (effort) ∝ Prix (résultat)",
        quote:"« Les grands opérateurs accumulent en secret dans la dépression, distribuent en secret dans l'euphorie. Le volume est leur empreinte digitale. » — Richard D. Wyckoff, Studies in Tape Reading (1910)",
        content:[
          {type:"p", text:"Richard Wyckoff (1873-1934) a développé sa méthode en observant les transactions des 'grands opérateurs' (composite operator) dans les données de prix et volume. Il introduit le concept de 'Composite Operator' : imaginer que toutes les institutions agissent comme un seul opérateur avec une stratégie cohérente. Pas une théorie du complot — une façon de lire les données."},
          {type:"p", text:"Villahermosa (The Wyckoff Methodology in Depth, 2019) modernise ces 5 phases avec des exemples contemporains. La Phase B (construction de la cause) est souvent la plus longue et la plus frustrante pour les traders — le range semble interminable. C'est précisément là que les institutions accumulent ou distribuent discrètement."},
          {type:"concepts", items:[
            {term:"Phase A — Arrêt de tendance", def:"Selling Climax (fort volume + rejet = bas potentiel). Automatic Rally. Secondary Test. La tendance précédente s'épuise. Villahermosa ch. 14-16.", src:"Wyckoff, Villahermosa ch. 14"},
            {term:"Phase B — Construction de la cause", def:"Range latéral. Institutions accumulent discrètement. Volume global décroissant. Tests multiples des bornes du range. La plus longue et la plus trompeuse.", src:"Wyckoff, Villahermosa ch. 17"},
            {term:"Phase C — Spring (test décisif)", def:"Fausse cassure sous le support du range. Volume FAIBLE lors de la cassure = les gros vendeurs ne vendent pas. Retour rapide = Spring confirmé = signal haussier majeur.", src:"Wyckoff, Villahermosa ch. 18"},
            {term:"Phase D — Confirmation (LPS)", def:"Last Point of Support : premier 'higher low' après le Spring. Breakout de la résistance du range sur fort volume. L'entrée optimale selon Wyckoff.", src:"Wyckoff, Villahermosa ch. 19"},
            {term:"Phase E — Markup / Markdown", def:"Tendance établie. Les institutions ont fini d'accumuler. En distribution, la Phase C devient UTAD (fausse cassure haute) avant le markdown.", src:"Wyckoff, Villahermosa ch. 20"},
          ]},
          {type:"ibox", color:T.re, label:"Le Spring : le piège le plus rentable",
           text:"Wyckoff (Villahermosa ch. 18) : le Spring est la cassure temporaire sous le support du range, conçue pour déclencher les stops des longs et piéger les vendeurs. Les 3 critères : (1) cassure sous le support du range, (2) volume FAIBLE lors de la cassure — les gros ne vendent pas, (3) retour rapide au-dessus du support en quelques séances. C'est le setup le plus rentable de la méthode Wyckoff ET le plus dangereux pour les non-initiés."},
          {type:"ibox", color:T.am, label:"UTAD : l'équivalent en distribution",
           text:"En haut de marché, la Phase C de distribution est l'UTAD (Upthrust After Distribution) : fausse cassure AU-DESSUS de la résistance du range, attirant les acheteurs enthousiastes pendant que les institutions leur vendent leurs positions. Volume souvent élevé (les institutions absorbent la demande). Prix redescend rapidement sous la résistance = signal baissier majeur selon Villahermosa (ch. 18)."},
        ],
        qcm:[
          {q:"NVDA est en range 180-216€ depuis 3 mois. Il casse sous 180€ à 174€ sur volume FAIBLE (0.7× la moyenne 20j), puis remonte à 183€ en 3 séances sur volume modéré. Wyckoff ?",
           choices:[
            {l:"Rupture de support confirmée — baisse vers 160-165€ probable. Vendre.", c:false,
             f:"Non. Le volume FAIBLE lors de la cassure est le signal clé. Wyckoff : une vraie rupture baissière = fort volume vendeur. Volume 0.7× = les institutions ne vendent PAS. La baisse est due à l'absence d'acheteurs, pas à des ventes agressives — distinction fondamentale."},
            {l:"Spring de Phase C — cassure sur volume faible + retour rapide = signal haussier majeur. Entrée optimale avec stop sous 174€.", c:true,
             f:"Parfait. Les 3 critères du Spring sont réunis : (1) cassure sous 180€ (support du range), (2) volume 0.7× (faible = No Supply), (3) retour rapide à 183€. C'est le setup d'entrée de Phase C selon Villahermosa (ch. 18). Stop sous le Spring (174€). Objectif minimum : résistance du range (216€)."},
            {l:"Test de Phase B — le range n'est pas terminé, attendre le breakout de 216€", c:false,
             f:"Possible si le Spring n'est pas confirmé par le volume. Mais les critères (cassure + faible volume + retour rapide) pointent vers Phase C. Un test de Phase B reste généralement DANS le range — la cassure sous 180€ est significative."},
           ]
          },
          {q:"Après un markup de 6 mois (NVDA 160→250€), un range 230-255€ se forme pendant 2 mois. NVDA casse à 258€ sur FORT volume mais revient à 240€ en 4 séances. Villahermosa appelle ça ?",
           choices:[
            {l:"Breakout Phase D — volume fort confirme la cassure, pullback normal avant la poursuite", c:false,
             f:"La différence clé : un vrai breakout Phase D RESTE au-dessus de la résistance. Le retour rapide à 240€ (bien sous 255€) INVALIDE le breakout. Villahermosa (ch. 18) : si le prix ne maintient pas au-dessus de la résistance, ce n'est pas un breakout — c'est un UTAD."},
            {l:"UTAD (Upthrust After Distribution) — fausse cassure haussière pour piéger les acheteurs pendant que les institutions distribuent. Signal baissier majeur.", c:true,
             f:"Exact. UTAD = Upthrust After Distribution. Trois critères : (1) cassure au-dessus de la résistance (255€ → 258€), (2) fort volume (institutions VENDENT à la foule enthousiaste), (3) retour rapide sous la résistance. Villahermosa (ch. 18) : l'UTAD est le signal de distribution le plus puissant de la méthode Wyckoff."},
            {l:"Fausse alerte — les marchés testent souvent les résistances plusieurs fois", c:false,
             f:"Un 'test de résistance' reste généralement dans le range ou au niveau de la résistance, pas 3€ au-dessus. La cassure sur fort volume avec retour rapide est structurellement différent d'un simple test."},
           ]
          },
        ]
      },
      {
        id:"a2", anchor:"a-patterns", icon:"🔺",
        sub:"§ A2 — Patterns harmoniques",
        title:"Fibonacci, patterns harmoniques et temps de Gann",
        sources:["Pesavento & Jouflas, Trade What You See ch. 2-5","Gann, Reddy ch. 2-3"],
        formula:"Retracements : 23.6% · 38.2% · 50% · 61.8% · 78.6%\nExtensions  : 100% · 127.2% · 161.8% · 261.8%\nGartley D   : retracement 78.6% de XA",
        quote:"« Toute existence est basée sur une proportion exacte et une relation parfaite. Il n'y a pas de hasard dans la nature, car des principes mathématiques du plus haut ordre sont à la base de toutes choses. » — W.D. Gann",
        content:[
          {type:"p", text:"Larry Pesavento et Leslie Jouflas (Trade What You See, 2007) formalisent les patterns harmoniques — des structures géométriques de prix utilisant les ratios de Fibonacci pour identifier des zones de retournement probable (PRZ = Potential Reversal Zone). Ces ratios (0.618, 0.786, 1.272, 1.618) sont dérivés de la suite de Fibonacci et du nombre d'or φ = 1.618."},
          {type:"p", text:"W.D. Gann (1878-1955), documenté par Hima Reddy (2012), avait une approche différente mais complémentaire. Pour Gann, le TEMPS est aussi important que le PRIX. Ses angles (1×1 = 45° = 1 unité de temps pour 1 unité de prix) représentent l'équilibre parfait. 'Il n'y a pas de hasard dans la nature' — les patterns se répètent parce que la psychologie humaine ne change pas."},
          {type:"concepts", items:[
            {term:"Niveaux de Fibonacci", def:"0.618 (1/φ) et 0.786 (√0.618) sont les plus puissants. Pesavento : 'nous n'obtenons pas toujours un retournement parfait au centime — c'est une ZONE, pas un point.'", src:"Pesavento & Jouflas ch. 2"},
            {term:"Pattern Gartley (1935)", def:"XA → AB = 61.8% de XA → BC entre 38.2-88.6% de AB → CD = 78.6% de XA (zone PRZ). Le plus ancien pattern harmonique formalisé. Pesavento : 'le plus fiable statistiquement.'", src:"Pesavento & Jouflas ch. 5"},
            {term:"PRZ (Potential Reversal Zone)", def:"Convergence de plusieurs ratios Fibonacci dans une zone étroite. Plus de confluences = plus fort le signal. Pesavento : toujours utiliser les ratios EN COMBINAISON, jamais isolément.", src:"Pesavento & Jouflas ch. 4"},
            {term:"Angles de Gann 1×1", def:"45° = 1 unité de prix / 1 unité de temps = équilibre. Un prix au-dessus de l'angle 1×1 = tendance forte. Gann : 'time is more important than price' — le temps d'un mouvement prédit souvent sa durée future.", src:"Gann, Reddy ch. 2-3"},
          ]},
          {type:"ibox", color:T.am, label:"Pourquoi Fibonacci fonctionne",
           text:"Pesavento : pas de magie mathématique — de la psychologie collective. Des millions de traders regardent les mêmes niveaux, créant des self-fulfilling prophecies. Un retracement à 61.8% est un 'niveau d'attention maximale' : les acheteurs disciplinés placent leurs ordres là, créant mécaniquement le rebond. Les niveaux existent parce qu'assez de gens croient en eux — et cette croyance se renforce à chaque retournement confirmé."},
          {type:"ibox", color:T.bl, label:"Gann : le temps, dimension oubliée",
           text:"Gann (Reddy ch. 2) : 'the more time between the top and bottom, the more important it is.' Un mouvement qui dure longtemps laisse une empreinte temporelle proportionnelle. Ses 'carrés de 9' et cycles naturels (90, 180, 270, 360 jours) semblent ésotériques mais reflètent une observation empirique sur la répétition des patterns dans le temps. Hima Reddy (2012) modernise et simplifie l'accès à ces outils."},
        ],
        qcm:[
          {q:"NVDA passe de 160€ (X) à 220€ (A = top). Il corrige à 183€ (B = 61.8% de XA). Remonte à 202€ (C = 38.2% de AB). Redescend. Point D = 78.6% de XA. Où est la zone PRZ et quel pattern ?",
           choices:[
            {l:"PRZ à 172.8€ (78.6% de retracement de XA depuis 220€). Pattern Gartley. Zone d'achat avec stop sous 160€.", c:true,
             f:"Exact. Calcul : XA = 220 − 160 = 60€. 78.6% de 60€ = 47.16€. PRZ = 220 − 47.16 = 172.84€. C'est le Gartley de Pesavento (ch. 5) avec B=61.8% ✓ et D=78.6% ✓. Stop sous X (160€). Objectif minimum : retour vers le point A (220€). Pesavento : 'le Gartley est le pattern le plus fiable statistiquement.'"},
            {l:"PRZ à 183€ (point B = 61.8% de XA). Pattern Bat.", c:false,
             f:"Le point B à 61.8% correspond au Gartley, pas au Bat. Le Bat a B = 38.2-50% de XA. Et le PRZ n'est pas au point B — c'est le point D (la fin du pattern) qui définit la zone d'entrée."},
            {l:"Les patterns Fibonacci sont subjectifs et peu fiables — il faut d'autres confirmations", c:false,
             f:"Pesavento serait en désaccord partiel : les patterns sont subjectifs dans leur identification (skill requis) mais objectifs dans leurs ratios. Le Gartley avec B=61.8% et D=78.6% est précis. Il recommande effectivement des confirmations (volume, bougies de retournement sur la PRZ) mais pas de rejeter les patterns."},
           ]
          },
        ]
      },
    ]
  }
};

// ══════════════════════════════════════════════════════
// CONTEXTS
// ══════════════════════════════════════════════════════
const ScoreCtx = createContext(null);
function ScoreProv({ children }) {
  const [scores, setScores] = useState({});
  const register = useCallback((id, ok) =>
    setScores(p => p[id] !== undefined ? p : { ...p, [id]: ok }), []);
  return (
    <ScoreCtx.Provider value={{ scores, register }}>
      {children}
    </ScoreCtx.Provider>
  );
}

// ══════════════════════════════════════════════════════
// QCM COMPONENT
// ══════════════════════════════════════════════════════
function QCM({ qid, q, choices }) {
  const { scores, register } = useContext(ScoreCtx);
  const [sel, setSel] = useState(null);
  const [fb, setFb] = useState(null);
  const done = scores[qid] !== undefined;
  const ok = scores[qid] === true;
  const BC = done ? (ok ? T.gr : T.re) : T.b2;
  const BG = done ? (ok ? T.gbg : T.rbg) : "#12121a";
  return (
    <div style={{ border:`1px solid ${BC}`, background:BG, marginBottom:"1rem",
      position:"relative", overflow:"hidden", transition:"border-color .3s,background .3s" }}>
      <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3,
        background: done ? (ok ? T.gb : T.rb) : T.gd }}/>
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:".55rem 1rem",
        borderBottom:`1px solid ${T.bo}` }}>
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9, letterSpacing:".14em",
          textTransform:"uppercase", padding:".1rem .4rem",
          border:`1px solid ${T.bb}`, color:T.bb, background:T.blbg }}>QCM</span>
        <span style={{ fontSize:9, color:T.di, fontFamily:"ui-monospace,monospace", flex:1 }}>{qid}</span>
        {done && <span style={{ color:ok?T.gb:T.rb, fontSize:13 }}>{ok?"✓ Correct":"✗ Incorrect"}</span>}
      </div>
      <div style={{ padding:".75rem 1rem .5rem", fontSize:12, color:T.tx, lineHeight:1.65 }}>{q}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:4, padding:"0 1rem .75rem" }}>
        {choices.map((ch, i) => {
          let bc=T.bo, bg=T.bg, co=T.di;
          if (done) {
            if (ch.c) { bc=T.gr; bg=T.gbg; co=T.gb; }
            else if (sel===i) { bc=T.re; bg=T.rbg; co=T.rb; }
          }
          return (
            <div key={i} onClick={() => { if(done)return; setSel(i); register(qid,ch.c); setFb(ch.f); }}
              style={{ display:"flex", alignItems:"flex-start", gap:10, padding:".4rem .7rem",
                border:`1px solid ${bc}`, background:bg, color:co,
                cursor:done?"default":"pointer", fontSize:11, lineHeight:1.55, userSelect:"none" }}>
              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9, border:`1px solid ${bc}`,
                padding:".04rem .28rem", flexShrink:0, marginTop:2, minWidth:18, textAlign:"center" }}>
                {["A","B","C"][i]}
              </span>
              {ch.l}
            </div>
          );
        })}
      </div>
      {fb && done && (
        <div style={{ padding:".55rem 1rem", borderTop:`1px solid ${ok?T.gr:T.re}`,
          background:ok?T.gbg:T.rbg, color:ok?T.gb:T.rb, fontSize:11, lineHeight:1.6 }}>
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9, display:"block",
            marginBottom:3, opacity:.7, textTransform:"uppercase" }}>
            {ok?"✓ Correct — Explication":"✗ Incorrect — Explication"}
          </span>
          {fb}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CHAPTER RENDERER
// ══════════════════════════════════════════════════════
// LiveCaseStudy — "Cas pratique dynamique" (doc archi générique §4/§5) :
// contrairement aux QCM ci-dessus (théorie + scénarios d'école statiques),
// ce bloc lit les vraies valeurs du titre couramment analysé (prop `d`,
// StockDashboard / timing_marche_summary) et génère une question + une
// correction 100% dynamiques. Aucune valeur numérique ni nom de société
// codés en dur ici.
// Correspondance modules LEVELS (n0..a2) → pilier diagnostiqué (structure/
// momentum/volume/timing), utilisée pour savoir où afficher LiveCaseStudy.
const MODULE_TO_PILLAR = {
  n1: "structure", i1: "structure",
  i2: "momentum",
  n3: "volume", a1: "volume",
  n2: "timing", a2: "timing",
};

function LiveCaseStudy({ pillar, d, symbol }) {
  const [reveal, setReveal] = useState(false);
  const f = (v, def) => { const n = parseFloat(v ?? def); return isNaN(n) ? null : n; };

  const PILLAR_META = {
    structure: {
      question: `Sur la base des données réelles de ${symbol}, la structure de tendance est-elle haussière ?`,
      build: () => {
        const price = f(d?.timing_last_price ?? d?.price, null);
        const s20   = f(d?.timing_sma20 ?? d?.sma20, null);
        const s50   = f(d?.timing_sma50 ?? d?.sma50, null);
        const s200  = f(d?.timing_sma200 ?? d?.sma200, null);
        const r2    = f(d?.r_squared, null);
        const facts = [
          price != null && s200 != null ? `Prix ${price.toFixed(2)} ${price > s200 ? ">" : "<"} SMA200 ${s200.toFixed(2)}` : null,
          s20 != null && s50 != null && s200 != null ? `Alignement SMA20/50/200 : ${s20.toFixed(2)} / ${s50.toFixed(2)} / ${s200.toFixed(2)}` : null,
          r2 != null ? `R² de la régression : ${r2.toFixed(2)}` : null,
        ].filter(Boolean);
        const bullish = price != null && s200 != null && price > s200 && s20 > s50 && s50 > s200;
        return { facts, ok: bullish ? "good" : "neutral",
          verdict: bullish
            ? "Structure haussière confirmée : prix au-dessus des 3 SMAs, alignement croissant."
            : "Structure non pleinement haussière : alignement des SMAs ou position du prix à surveiller." };
      },
    },
    momentum: {
      question: `Le momentum de ${symbol} accélère-t-il ou s'essouffle-t-il, sur les chiffres réels ?`,
      build: () => {
        const t12 = f(d?.tsmom_12m, null), t3 = f(d?.tsmom_3m, null), fip = f(d?.fip_score, null);
        const facts = [
          t12 != null ? `TSMOM 12M : ${t12 >= 0 ? "+" : ""}${t12.toFixed(1)}%` : null,
          t3  != null ? `TSMOM 3M : ${t3 >= 0 ? "+" : ""}${t3.toFixed(1)}%` : null,
          fip != null ? `FIP Score : ${fip.toFixed(3)}` : null,
        ].filter(Boolean);
        const accel = t12 != null && t3 != null && t12 > 0 && t3 > 0;
        const essouf = t12 != null && t3 != null && t12 > 0 && t3 < 0;
        return { facts, ok: accel ? "good" : essouf ? "neutral" : "bad",
          verdict: accel ? "Momentum en accélération : 12M et 3M positifs et convergents."
                 : essouf ? "Essoufflement : 12M positif mais 3M négatif — divergence court terme."
                 : "Momentum dégradé sur les deux horizons." };
      },
    },
    volume: {
      question: `Les volumes de ${symbol} confirment-ils la tendance en cours ?`,
      build: () => {
        const vr = f(d?.volume_ratio_20d, null);
        const wyk = d?.wyckoff_context ?? null;
        const facts = [
          vr  != null ? `Ratio volume 20J : ${vr.toFixed(2)}×` : null,
          wyk != null ? `Phase Wyckoff : ${wyk}` : null,
        ].filter(Boolean);
        const isAccum = wyk && (wyk.includes("Accumulation") || wyk.includes("Markup"));
        const isDistrib = wyk && (wyk.includes("Distribution") || wyk.includes("Markdown"));
        return { facts, ok: isAccum ? "good" : isDistrib ? "bad" : "neutral",
          verdict: isAccum ? "Volumes cohérents avec une phase d'accumulation/markup."
                 : isDistrib ? "Volumes cohérents avec une phase de distribution/markdown — prudence."
                 : "Signal volume neutre ou ambigu à ce stade." };
      },
    },
    timing: {
      question: `Le RSI et le Z-Score de ${symbol} indiquent-ils un bon point d'entrée aujourd'hui ?`,
      build: () => {
        const rsi = f(d?.rsi_14, null), z = f(d?.z_score, null);
        const facts = [
          rsi != null ? `RSI 14 : ${rsi.toFixed(1)}` : null,
          z   != null ? `Z-Score : ${z >= 0 ? "+" : ""}${z.toFixed(2)}σ` : null,
        ].filter(Boolean);
        const good = rsi != null && rsi >= 40 && rsi <= 60 && z != null && Math.abs(z) <= 1;
        const bad  = rsi != null && rsi > 70 && z != null && z > 2;
        return { facts, ok: good ? "good" : bad ? "bad" : "neutral",
          verdict: good ? "Confluence favorable pour une entrée." :
                   bad  ? "Double sur-extension (RSI + Z-Score) — timing défavorable." :
                          "Timing intermédiaire — ni confluence idéale, ni sur-extension nette." };
      },
    },
  };

  const meta = PILLAR_META[pillar];
  if (!meta) return null;
  const { facts, ok, verdict } = meta.build();
  const col = getStatusColor(ok);

  return (
    <div style={{ marginTop:"1.5rem", padding:".9rem 1rem", border:`1px solid ${col}44`,
      background:`${col}0d` }}>
      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9, textTransform:"uppercase",
        letterSpacing:".18em", color:col, marginBottom:8 }}>
        Cas pratique en direct — {symbol}
      </div>
      <p style={{ fontSize:12.5, color:T.tx, lineHeight:1.65, marginBottom:8, fontStyle:"italic" }}>
        {meta.question}
      </p>
      {facts.length > 0 && (
        <ul style={{ margin:"0 0 10px", paddingLeft:"1.1rem", fontSize:11.5, color:T.di, lineHeight:1.7 }}>
          {facts.map((fact, i) => <li key={i}>{fact}</li>)}
        </ul>
      )}
      {!reveal ? (
        <button onClick={() => setReveal(true)}
          style={{ padding:".4rem .9rem", border:`1px solid ${col}66`, background:"transparent",
            color:col, fontSize:11, fontWeight:700, cursor:"pointer" }}>
          Voir la lecture Invelps →
        </button>
      ) : (
        <div style={{ fontSize:12, color:col, fontWeight:600, lineHeight:1.6,
          borderTop:`1px solid ${col}33`, paddingTop:8 }}>
          {verdict}
        </div>
      )}
    </div>
  );
}

function Chapter({ module: m, levelColor, D: Dprop, liveData }) {
  const col = levelColor;
  // Use passed theme tokens, fall back to global T for backward compat
  const D = Dprop || T;
  // Symbole réellement analysé (StockDashboard) — fallback NVDA si absent
  const symbol = liveData?.symbol || liveData?.company_name || "NVDA";

  function renderContent(block, bi) {
    if (block.type === "p") return (
      <p key={bi} style={{ fontSize:13, color:T.di, lineHeight:1.75, marginBottom:".75rem" }}>{dyn(block.text, symbol)}</p>
    );
    if (block.type === "concepts") return (
      <div key={bi} style={{ margin:".8rem 0 1.2rem" }}>
        <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9, textTransform:"uppercase",
          letterSpacing:".18em", color:T.mu, marginBottom:6 }}>Concepts clés</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:1,
          background:T.bo, border:`1px solid ${T.bo}` }}>
          {block.items.map((c,i) => (
            <div key={i} style={{ background:T.bg, padding:".75rem .9rem" }}>
              <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8, marginBottom:3 }}>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"1rem", color:T.go }}>{c.term}</span>
                {c.src && <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, color:T.mu, flexShrink:0 }}>📖 {c.src}</span>}
              </div>
              <span style={{ fontSize:11, color:T.di, lineHeight:1.6 }}>{dyn(c.def, symbol)}</span>
            </div>
          ))}
        </div>
      </div>
    );
    if (block.type === "ibox") return (
      <div key={bi} style={{ padding:".85rem 1rem", margin:".8rem 0",
        borderLeft:`3px solid ${block.color}`,
        background:block.color+"18", fontSize:11.5, lineHeight:1.65, color:T.di }}>
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9, letterSpacing:".18em",
          textTransform:"uppercase", opacity:.7, display:"block", marginBottom:5 }}>{block.label}</span>
        {dyn(block.text, symbol)}
      </div>
    );
    return null;
  }

  return (
    <div style={{ background:T.bg, color:T.tx, fontFamily:"Georgia,serif" }}>
      <div style={{ maxWidth:720, margin:"0 auto" }}>

        {/* Masthead */}
        <div style={{ padding:"clamp(1.5rem,4vw,2.5rem) 0 1.8rem",
          borderBottom:`1px solid ${T.bo}` }}>
          <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9, letterSpacing:".22em",
            textTransform:"uppercase", color:T.go, marginBottom:".8rem",
            display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:14, height:1, background:T.go, display:"inline-block" }}/>
            {m.sub}
          </div>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif",
            fontSize:"clamp(1.4rem,4vw,2.2rem)", fontWeight:300,
            lineHeight:1.2, letterSpacing:"-.02em", margin:"0 0 .8rem" }}>
            {m.icon} {m.title}
          </h1>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {m.sources.map(s => (
              <span key={s} style={{ fontFamily:"ui-monospace,monospace", fontSize:9, padding:".15rem .55rem",
                border:`1px solid ${T.b2}`, color:T.di }}>📖 {s}</span>
            ))}
          </div>
        </div>

        {/* Citation */}
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
          fontSize:"1.05rem", color:T.gp, margin:"1.4rem 0 1rem",
          paddingLeft:".9rem", borderLeft:`2px solid ${T.gd}`, lineHeight:1.6 }}>
          {m.quote}
        </div>

        {/* Content blocks */}
        {m.content.map(renderContent)}

        {/* Formula */}
        {m.formula && (
          <div style={{ margin:".8rem 0 1.2rem", padding:".75rem 1rem",
            background:T.s2, border:`1px solid ${T.b2}`,
            display:"flex", alignItems:"flex-start", gap:12 }}>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9, color:T.mu,
              textTransform:"uppercase", letterSpacing:".1em", flexShrink:0, paddingTop:3 }}>Formule</span>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:12, color:T.gp,
              lineHeight:1.55, whiteSpace:"pre-line" }}>{m.formula}</span>
          </div>
        )}

        {/* QCMs */}
        {m.qcm && m.qcm.length > 0 && (
          <div style={{ marginTop:"2rem" }}>
            <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9, textTransform:"uppercase",
              letterSpacing:".18em", color:T.mu, marginBottom:"1rem",
              paddingTop:"1.5rem", borderTop:`1px solid ${T.bo}` }}>
              Exercices — appliqués sur {symbol}
            </div>
            {m.qcm.map((q,i) => (
              <QCM key={i} qid={`${m.id}-q${i+1}`} q={dyn(q.q, symbol)}
                choices={q.choices.map(ch => ({ ...ch, l:dyn(ch.l, symbol), f:dyn(ch.f, symbol) }))}/>
            ))}
          </div>
        )}

        {/* Cas pratique dynamique — construit sur les données RÉELLES du titre
            en cours d'analyse (doc archi générique §4/§5). Corrigé : m.id vaut
            n0/n1/.../a2 dans LEVELS, jamais "structure"/"momentum"/..., donc
            cette section ne s'affichait jamais avant ce mapping. */}
        {liveData && MODULE_TO_PILLAR[m.id] && (
          <LiveCaseStudy pillar={MODULE_TO_PILLAR[m.id]} d={liveData} symbol={symbol}/>
        )}

        {/* Module score */}
        <ModuleScore moduleId={m.id} total={m.qcm?.length||0} D={D}/>
      </div>
    </div>
  );
}

function ModuleScore({ moduleId, total, D: Dprop }) {
  const D = Dprop || T;
  const { scores } = useContext(ScoreCtx);
  if (!total) return null;
  const keys = Array.from({length:total}, (_,i) => `${moduleId}-q${i+1}`);
  const done = keys.filter(k => scores[k] !== undefined);
  if (done.length < total) return null;
  const ok  = done.filter(k => scores[k]).length;
  const pct = Math.round(ok/total*100);
  return (
    <div style={{ marginTop:"2rem", padding:"1.2rem 1.5rem",
      border:`1px solid ${T.bo}`, background:T.su }}>
      <div style={{ display:"flex", alignItems:"baseline", gap:14 }}>
        <span style={{ fontFamily:"'Cormorant Garamond',serif",
          fontSize:"2.4rem", color:T.go, lineHeight:1 }}>{ok}/{total}</span>
        <div>
          <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9, color:T.mu,
            textTransform:"uppercase", letterSpacing:".12em" }}>Score</div>
          <div style={{ height:3, background:T.b2, width:140, marginTop:5 }}>
            <div style={{ height:"100%", background:T.go,
              width:`${pct}%`, transition:"width 1s ease" }}/>
          </div>
        </div>
      </div>
      <div style={{ fontSize:11, color:T.di, marginTop:8 }}>
        {pct===100?"Maîtrise complète ✓":pct>=67?"Bonne compréhension. Relisez les points manqués.":"Relisez ce module avant de continuer."}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// EDU MODAL — adaptive theme + mobile responsive
// ══════════════════════════════════════════════════════
function EduModal({ anchor, onClose, isDark, d }) {
  const symbol = d?.symbol || d?.company_name || "NVDA";
  const [levelId, setLevelId] = useState(() => {
    for (const [lk, lv] of Object.entries(LEVELS))
      for (const m of lv.modules)
        if (m.anchor === anchor) return lk;
    return "novice";
  });
  const [moduleId, setModuleId] = useState(() => {
    for (const lv of Object.values(LEVELS))
      for (const m of lv.modules)
        if (m.anchor === anchor) return m.id;
    return LEVELS.novice.modules[0].id;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Adaptive tokens — match the main page theme
  const D = isDark ? {
    bg:"#0b1220", card:"rgba(13,20,38,0.97)", border:"rgba(111,138,91,.18)",
    ts:"#e8e4da", tx:"#cdc9be", mu:"#7a8172", sep:"rgba(111,138,91,.12)",
    sur:"rgba(111,138,91,.05)", sub:"rgba(11,18,32,0.97)",
    // edu-specific
    bo:"#121f32", b2:"#1a2840", go:C.amber, gd:"#9a7c3a", gp:"#e8d5a0",
    gr:"#3a7a50", gb:C.green, gbg:"rgba(111,138,91,.08)",
    re:"#8a3a3a", rb:C.red, rbg:"rgba(198,82,63,.08)",
    bl:"#3a5a8a", bb:"#6a9aca", blbg:"rgba(122,143,196,.08)",
    di:"#7a8172", s2:"rgba(11,18,32,0.6)",
  } : {
    bg:"#f6f5f1", card:"rgba(255,255,255,0.99)", border:"rgba(30,40,34,.09)",
    ts:"#1f2a24", tx:"#2d3b34", mu:"#6f7b74", sep:"rgba(30,40,34,.07)",
    sur:"rgba(0,0,0,.018)", sub:"rgba(248,246,241,0.99)",
    // edu-specific
    bo:"#d4cfc4", b2:"#c4bfb4", go:C.amber, gd:"#8a6a2a", gp:"#4a3510",
    gr:"#2a6040", gb:C.green, gbg:"rgba(111,138,91,.08)",
    re:"#7a2a2a", rb:C.red, rbg:"rgba(198,82,63,.08)",
    bl:"#2a4a8a", bb:"#3a6aaa", blbg:"rgba(122,143,196,.08)",
    di:"#6f7b74", s2:"rgba(248,246,241,0.5)",
  };

  const level = LEVELS[levelId];
  const activeM = level.modules.find(m => m.id === moduleId) || level.modules[0];

  function switchLevel(lk) {
    setLevelId(lk);
    setModuleId(LEVELS[lk].modules[0].id);
    setSidebarOpen(false);
  }
  function selectModule(id) {
    setModuleId(id);
    setSidebarOpen(false);
  }

  const scrollbarStyle = `
    .edu-sb::-webkit-scrollbar{width:4px}
    .edu-sb::-webkit-scrollbar-thumb{background:${D.gd};border-radius:2px}
    @keyframes eduSlide{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
  `;

  return (
    <ScoreProv>
      <div style={{ position:"fixed", inset:0, zIndex:3000,
        background:"rgba(0,0,0,.6)", display:"flex", flexDirection:"column" }}
        onClick={onClose}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');
          ${scrollbarStyle}
          .edu-modal-body{display:flex;flex:1;overflow:hidden;}
          .edu-sidebar{width:200px;flex-shrink:0;overflow-y:auto;padding:12px 0;border-right:1px solid ${D.bo};}
          @media(max-width:600px){
            .edu-sidebar{
              position:absolute;top:48px;left:0;bottom:0;z-index:10;
              width:240px;
              transform:translateX(-100%);
              transition:transform .22s ease;
              box-shadow:4px 0 20px rgba(0,0,0,.15);
            }
            .edu-sidebar.open{transform:translateX(0);}
          }
          .edu-module-tabs{display:none;}
          @media(max-width:600px){.edu-module-tabs{display:flex;}}
        `}</style>

        <div style={{
          display:"flex", flexDirection:"column",
          background:D.bg, flex:1, maxHeight:"100vh",
          overflow:"hidden", position:"relative",
        }} onClick={e=>e.stopPropagation()}>

          {/* ── HEADER */}
          <div style={{
            display:"flex", alignItems:"center", gap:8, padding:"9px 14px",
            background:D.sub, borderBottom:`1px solid ${D.bo}`,
            flexShrink:0, flexWrap:"wrap",
          }}>
            {/* Mobile hamburger */}
            <button
              onClick={() => setSidebarOpen(v=>!v)}
              style={{
                display:"none", alignItems:"center", justifyContent:"center",
                width:28, height:28, borderRadius:4, flexShrink:0,
                background:"transparent", border:`1px solid ${D.b2}`,
                color:D.mu, cursor:"pointer", fontSize:14,
              }}
              className="edu-ham">
              ☰
            </button>

            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke={EDU_COL} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
              <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
            </svg>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9,
              textTransform:"uppercase", letterSpacing:".16em", color:EDU_COL }}>
              Cours
            </span>

            {/* Level tabs */}
            <div style={{ display:"flex", gap:3 }}>
              {Object.values(LEVELS).map(lv => (
                <button key={lv.id} onClick={() => switchLevel(lv.id)}
                  style={{
                    display:"flex", alignItems:"center", gap:4,
                    padding:"4px 9px", borderRadius:4,
                    border:`1px solid ${levelId===lv.id ? lv.color+"80" : D.b2}`,
                    background: levelId===lv.id ? lv.color+"18" : "transparent",
                    color: levelId===lv.id ? lv.color : D.di,
                    cursor:"pointer", fontFamily:"ui-monospace,monospace",
                    fontSize:9, textTransform:"uppercase", letterSpacing:".06em",
                  }}>
                  <span>{lv.icon}</span>
                  <span style={{ display:"inline" }}>{lv.label}</span>
                </button>
              ))}
            </div>

            <span style={{ marginLeft:"auto", fontFamily:"ui-monospace,monospace",
              fontSize:8, color:D.mu }}>{symbol}</span>

            <button onClick={onClose}
              style={{
                width:26, height:26, borderRadius:4, display:"flex",
                alignItems:"center", justifyContent:"center",
                background:"transparent", border:`1px solid ${D.b2}`,
                color:D.mu, cursor:"pointer", fontSize:15, flexShrink:0,
              }}>×</button>
          </div>

          {/* Mobile module tabs strip */}
          <div className="edu-module-tabs" style={{
            flexShrink:0, background:D.sub, borderBottom:`1px solid ${D.bo}`,
            overflowX:"auto", padding:"0 4px",
            display:"none", // overridden by media query
          }}>
            {level.modules.map(m => (
              <button key={m.id} onClick={() => selectModule(m.id)}
                style={{
                  display:"inline-flex", alignItems:"center", gap:5,
                  padding:"8px 12px", whiteSpace:"nowrap", flexShrink:0,
                  border:"none", background:"transparent", cursor:"pointer",
                  borderBottom:`2px solid ${activeM.id===m.id ? level.color : "transparent"}`,
                  color: activeM.id===m.id ? level.color : D.mu,
                  fontFamily:"ui-monospace,monospace", fontSize:9,
                  textTransform:"uppercase", letterSpacing:".06em",
                }}>
                {m.icon} {m.title}
              </button>
            ))}
          </div>

          {/* ── BODY */}
          <div className="edu-modal-body">

            {/* Sidebar (desktop always visible, mobile slide-in) */}
            <div
              className={`edu-sidebar edu-sb${sidebarOpen ? " open" : ""}`}
              style={{ background:D.sub }}>
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:8,
                textTransform:"uppercase", letterSpacing:".15em",
                color:D.mu, padding:"0 14px 6px" }}>
                {level.icon} {level.label}
              </div>
              <div style={{ fontSize:10, color:D.di, padding:"0 14px 12px",
                borderBottom:`1px solid ${D.bo}`, lineHeight:1.5 }}>
                {dyn(level.desc, symbol)}
              </div>
              <div style={{ paddingTop:8 }}>
                {level.modules.map(m => (
                  <button key={m.id} onClick={() => selectModule(m.id)}
                    style={{
                      display:"flex", alignItems:"flex-start", gap:8,
                      width:"100%", padding:"9px 14px", textAlign:"left",
                      cursor:"pointer", border:"none",
                      borderLeft:`2px solid ${activeM.id===m.id ? level.color : "transparent"}`,
                      background: activeM.id===m.id ? level.color+"14" : "transparent",
                      transition:"background .12s",
                    }}>
                    <span style={{ fontSize:13, flexShrink:0, marginTop:1 }}>{m.icon}</span>
                    <div>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:8,
                        color:D.mu, textTransform:"uppercase",
                        letterSpacing:".06em", marginBottom:2 }}>{m.sub}</div>
                      <div style={{ fontSize:11, lineHeight:1.3,
                        color: activeM.id===m.id ? level.color : D.ts }}>
                        {m.title}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sidebar backdrop on mobile */}
            {sidebarOpen && (
              <div onClick={() => setSidebarOpen(false)}
                style={{
                  position:"absolute", inset:0, zIndex:9,
                  background:"rgba(0,0,0,.4)",
                  display:"none", // overridden by media query
                }}
                className="edu-backdrop"/>
            )}

            {/* Content */}
            <div className="edu-sb"
              style={{ flex:1, minWidth:0, overflowY:"auto",
                padding:"0 clamp(.8rem,3vw,1.6rem) 4rem",
                background:D.bg }}>
              <Chapter key={activeM.id} module={activeM} levelColor={level.color} D={D} liveData={d}/>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-specific styles injected */}
      <style>{`
        @media(max-width:600px){
          .edu-ham{display:flex !important;}
          .edu-module-tabs{display:flex !important; overflow-x:auto;}
          .edu-sidebar.open{transform:translateX(0) !important;}
          .edu-backdrop{display:block !important;}
        }
      `}</style>
    </ScoreProv>
  );
}

// ══════════════════════════════════════════════════════
// GRADUATION CAP BUTTON
// ══════════════════════════════════════════════════════
function EduCap({ onOpen, anchor }) {
  return (
    <Tip title="Cours d'analyse graphique"
      body="3 niveaux (Novice · Intermédiaire · Avancé) — Sources : Murphy, Edwards & Magee, Wyckoff, Gray & Vogel, Covel, Pesavento, Gann. Théorie + QCMs sur données NVDA réelles."
      src="8 ouvrages de référence · 7 modules · 10 QCMs">
      <button onClick={() => onOpen(anchor||"n-tendance")}
        style={{ display:"inline-flex", alignItems:"center", gap:6,
          padding:"5px 12px", borderRadius:8, cursor:"pointer",
          border:`1px solid ${C.edu}66`, background:`${C.edu}15`,
          color:C.edu, fontSize:10, fontWeight:700,
          fontFamily:"ui-monospace,monospace",
          transition:"all .15s" }}
        onMouseEnter={e=>{e.currentTarget.style.background=`${C.edu}28`;}}
        onMouseLeave={e=>{e.currentTarget.style.background=`${C.edu}15`;}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
          <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
        </svg>
        Cours
      </button>
    </Tip>
  );
}


function applyTheme(isDark) {
  const r = document.documentElement;
  if (isDark) {
    // ── MODE NUIT — charte Invelps dark ──
    r.style.setProperty("--bg",       "#0b1220");
    r.style.setProperty("--card-bg",  "rgba(13,20,38,0.97)");
    r.style.setProperty("--card-bdr", "rgba(111,138,91,.18)");
    r.style.setProperty("--ts",       "#e8e4da");
    r.style.setProperty("--tx",       "#cdc9be");
    r.style.setProperty("--mu",       "#7a8172");
    r.style.setProperty("--sep",      "rgba(111,138,91,.12)");
    r.style.setProperty("--sur",      "rgba(111,138,91,.05)");
    r.style.setProperty("--gl",       "rgba(111,138,91,.07)");
    // extra dark tokens
    r.style.setProperty("--surface1", "#0f1a2e");
    r.style.setProperty("--surface2", "#131f30");
    r.style.setProperty("--rim",      "rgba(111,138,91,.22)");
  } else {
    // ── MODE JOUR — charte Invelps light ──
    r.style.setProperty("--bg",       "#ffffff");
    r.style.setProperty("--card-bg",  "rgba(255,255,255,0.98)");
    r.style.setProperty("--card-bdr", "rgba(30,40,34,.10)");
    r.style.setProperty("--ts",       "#1f2a24");
    r.style.setProperty("--tx",       "#2d3b34");
    r.style.setProperty("--mu",       "#6f7b74");
    r.style.setProperty("--sep",      "rgba(30,40,34,.07)");
    r.style.setProperty("--sur",      "rgba(0,0,0,.016)");
    r.style.setProperty("--gl",       "rgba(30,40,34,.055)");
    r.style.setProperty("--surface1", "#f0efe9");
    r.style.setProperty("--surface2", "#eae9e3");
    r.style.setProperty("--rim",      "rgba(30,40,34,.12)");
  }
}

// ══════════════════════════════════════════════════════════════════
// COUCHE DE DONNÉES — REQ1-REQ4 (timing_marche_summary+JOINs / timing_marche_chart /
// Analysis_Relative_Strength / monthly_histo_price_calculation). RAW_TM_DATA
// représente le payload tel qu'il arriverait des 4 requêtes SQL (cf.
// query_TimingMarcheCard_mapping.md). En prod, ce bloc est remplacé par le
// résultat d'un fetch API — la forme (req1/req2[]/req3[]/req4[]) doit rester
// identique pour que les fonctions build* ci-dessous continuent de
// fonctionner sans changement. Même principe que RAW_EF_DATA dans
// EtatsFinanciersCard.
// ══════════════════════════════════════════════════════════════════
const RAW_TM_DATA = {
  req1: {
  symbol: `COFA.PA`,
  calendarYear: 2025,
  company_name: `COFACE SA`,
  price: 14.610000,
  mtd_pct: -5.079400,
  ytd_pct: -4.777100,
  exchange_code: `EPA`,
  sector: `Financials`,
  industry: `Reinsurance`,
  region: `Europe`,
  market_cap_class: `Mid Cap`,
  stock_style: `Income`,
  peter_lynch_category: `Fast Growers`,
  description: `COFACE SA, through its subsidiaries, provides credit insurance products and related services for microenterprises, small and medium enterprises, mid-market companies, international corporations, financial institutions, and clients of distribution partners. The company offers credit insurance products to protect companies against default on payment of its trade receivables. It also provides integrated credit management solutions comprising credit insurance, single risk insurance, business information and debt collection services for insured and uninsured businesses; and factoring services, as well as contract and environmental surety, customs and excise, and legal bonds; and payment guarantees. In addition, the company offers business information services through its ICON portal. It operates in Western Europe, Northern Europe, Central and Eastern Europe, the Mediterranean and Africa, North America, Latin America, and the Asia-Pacific. The company was founded in 1946 and is headquartered in Bois-Colombes, France.`,
  iscore: 60.04,
  radar_fondamentaux: 78.39,
  radar_valorisation: 76.00,
  radar_marche: 56.26,
  radar_momentum: 14.95,
  radar_risque_rendement: 70.45,
  dd_value_creation_score: 53.59,
  dd_momentum_score: 14.95,
  last_price: 15.060000,
  market_bias_label: `Légère pression baissière`,
  tscore: 21.2,
  tscore_signal: `Signal faible · éviter ou alléger`,
  trend_score: 29.40,
  lt_trend_score: 34.90,
  st_trend_score: 29.40,
  trend_quality_score: 21.00,
  trend_direction: `Latérale`,
  slope_all: 0.05386,
  slope_5y: 0.10165,
  slope_1y: -0.08951,
  r_squared: 0.53570,
  cagr: 2.52,
  z_score: 0.1776,
  std_dev: 3.0911,
  std_dev_pct: 20.53,
  crossover_signal: `Zone neutre`,
  valuation_status: `Neutre`,
  sma20: 15.026000,
  sma50: 15.534600,
  sma200: 15.425100,
  sma_alignment: `Compression technique (biais baissier)`,
  price_vs_sma: `Prix sous la SMA200 (faiblesse technique)`,
  golden_cross: 0,
  death_cross: 0,
  deviation_status: `Prix proche de sa moyenne (zone d'équilibre)`,
  average: 10.6049,
  plus_1_sigma: 18.1032,
  plus_2_sigma: 22.6440,
  minus_1_sigma: 11.5707,
  minus_2_sigma: 9.2505,
  momentum_score: 3.26,
  momentum_direction: `Baissier`,
  momentum_pattern: `Transition / Mixte`,
  momentum_strength: `Faible`,
  tsmom_3m: -4.53,
  tsmom_6m: -1.52,
  tsmom_12m: -8.23,
  tsmom_3m_rank: 3,
  tsmom_6m_rank: 3,
  tsmom_12m_rank: 4,
  fip_score: -0.059700,
  fip_rank: 3,
  positive_days_pct: 45.42,
  last_return_m1: 0.735800,
  volume_score: 44.54,
  volume_ratio_20d: 0.7939,
  volume_ratio_60d: 0.7270,
  volume_trend: `Stable`,
  volume_trend_20_60: `Stable`,
  liquidity_score: 0.7100,
  liquidity_note: `VeryLiquid`,
  wyckoff_context: `Neutral`,
  wyckoff_phase: `B`,
  wyckoff_event: `None`,
  dominant_side: `Demand`,
  absorption_distribution: `Neutral`,
  range_low_60d: 14.500000,
  range_high_60d: 16.660000,
  range_pos_pct_60d: 25.93,
  anticipation_bias: `Neutral`,
  anticipation_confidence: 39.23,
  support_level: 14.9000,
  support_last_touch: `2026-06-26`,
  support_touches: 51,
  support_vol_ratio: 0.9181,
  resist_level: 15.7600,
  resist_last_touch: `2026-06-16`,
  resist_touches: 72,
  resist_vol_ratio: 1.1090,
  rsi_14: 44.710000,
  rsi_interpretation: `Neutre`,
  opportunity_score: 25.00,
  timing_quality: `Neutral`,
  coherence_score: -1,
  coherence_label: `Marché en tension — point de bascule`,
  n_confirm_bull: 0,
  n_confirm_bear: 1,
  n_tension: 4,
  n_neutral: 1,
  narrative_diagnostic: `COFA.PA presente une tendance latérale (score 29/100) avec un momentum retournement haussier sur 12 mois (TSMOM12 -4.4%). La structure SMA est en construction haussière progressive. RSI 14 a 32.5 — Zone de faiblesse. Phase Wyckoff : Accumulation.`,
  narrative_conclusion: `TScore global : 25/100 — Signal faible · éviter ou alléger. Marché contradictoire — timing incertain. Configuration negative. Eviter les positions longues.`,
  interp_trend_global: `Tendance degradee (score 29/100). La structure technique est orientee negativement. Prudence.`,
  interp_slope_all: `Pente LT quasi plate (0.05 $/mois). Aucune direction structurelle claire.`,
  interp_slope_5y: `Pente 5 ans stable/laterale (0.10 $/mois). Peu de progression sur 5 ans.`,
  interp_slope_1y: `Pente 1 an stable par rapport au 5 ans (-0.10 vs 0.10 $/mois). Poursuite reguliere.`,
  interp_r_squared: `R² 0.53 — Coherence moderee. Tendance valide mais avec bruit significatif.`,
  interp_cagr: `CAGR 2.47% — Croissance marginalement positive.`,
  interp_price_multiple: `Multiplie par 1.3x sur la periode analysee. Performance positive mais limitee.`,
  interp_sma_alignment: `Structure haussiere progressive : SMA20 > SMA50 > SMA200. Le prix (14.95) est sous la SMA20 — correction technique dans tendance etablie.`,
  interp_price_vs_sma: `Prix 3.8% sous la SMA200. Faiblesse technique confirmee.`,
  interp_crossover_signal: `Zone neutre (entre -1σ et +1σ). Prix dans les bandes statistiques habituelles.`,
  interp_z_score: `Z-Score 0.17 : prix proche de la moyenne historique.`,
  interp_valuation_status: `Statut neutre : prix dans la bande centrale (+/-1σ). Valorisation coherente avec l historique.`,
  interp_trend_score: `Score de tendance : 29/100 — Tendance negative ou absente.`,
  interp_lt_trend_score: `Score LT 35/100 — Structure LT faible.`,
  interp_st_trend_score: `Score CT 29/100 — Faiblesse court terme marquee.`,
  interp_trend_quality_score: `Trend Quality Score : 21/100 — Qualite insuffisante.`,
  interp_momentum_global: `Retournement haussier potentiel : 12M negatif mais 3M positif (+2.1%). Demande confirmation sur 2-3 mois.`,
  interp_tsmom_12m: `TSMOM 12M -4.4% — Momentum annuel negatif. Rang 4/5.`,
  interp_tsmom_6m: `TSMOM 6M +3.1% vs 12M negatif : signal contradictoire. Possible retournement.`,
  interp_tsmom_3m: `TSMOM 3M +2.1% — Momentum CT neutre. Consolidation dans la tendance de fond.`,
  interp_fip_score: `FIP score -0.072 — Equilibre jours positifs/negatifs. Momentum sans direction claire.`,
  interp_positive_days_pct: `44.7% de seances positives — dominance vendeurs. Signal de fragilite.`,
  interp_last_return_m1: `Mois dernier : -5.1% — Recul mensuel. Divergence CT.`,
  interp_momentum_divergence: `Ecart 12M/3M de 6.5pp — Divergence partielle a surveiller.`,
  interp_momentum_score: `Score momentum : 1/100 — Momentum faible. Direction : Retournement haussier.`,
  interp_volume_global: `Volume en hausse mais prix en baisse. Wyckoff : potentiel signal de distribution ou capitulation. Surveiller.`,
  interp_volume_ratio_20j: `Ratio 20J/60J : 2.00x. Participation correcte. Trend haussier soutenu par volumes constants = plus solide (Wyckoff).`,
  interp_volume_ratio_60j: `Ratio 60J/120J : 2.46x. Participation institutionnelle soutenue sur 2 mois. Signal de sante moyen terme.`,
  interp_wyckoff_phase: `Phase Wyckoff B (Accumulation). Les institutions construisent des positions discretement. Villahermosa : la Phase B est la construction de la cause qui precedera le markup.`,
  interp_absorption: `Signal de distribution : fort volume avec baisse de prix. Les mains fortes vendent dans la faiblesse. Prudence.`,
  interp_liquidity: `Bonne liquidite (score 0.69). Execution sans impact de prix materiel.`,
  interp_prix_volume: `Volume eleve sur baisse : potentiel signal de capitulation ou de distribution. Analyser avec la phase Wyckoff.`,
  interp_opportunity_score: `Opportunity Score : 70/100 — Opportunite en formation. A surveiller.`,
  interp_rsi_14: `RSI 14 : 32.5 — Zone de faiblesse. Survente moderee. Verifier si un support technique est proche.`,
  interp_z_score_timing: `Z-Score 0.17 : prix proche de la regression. Bon point d entree statistique.`,
  interp_support: `Aucun support cle exploitable identifie dans la fenetre d analyse (365J).`,
  interp_resistance: `Resistance cle : 16.26 (75 tests). Cassure + fort volume = nouveau support. Distance actuelle : 8.8%.`,
  interp_cross_sma: `Pas de croisement recent mais SMA50 > SMA200. Structure haussiere de fond (Golden Cross passe).`,
  interp_timing_score: `Timing Score : 70/100 — Bon point d entree. RSI/Z-Score/Volume favorables.`,
  interp_coherence: `Coherence inter-piliers : forte convergence baissiere (score -3/6). Eviter les longs.`,
  interp_action: `Action : EVITER ou ALLEGER. TScore 25/100 — Signal technique negatif. Ne pas initier de position longue.`,
  diag_q1_tendance: `Non — cours (14.95) sous la SMA200 (15.53) : tendance primaire baissière.`,
  diag_q1_ok: -1,
  diag_q2_momentum: `Potentiel retournement haussier — TSMOM 12M -4.4% mais 3M +2.1%. Trop tôt pour confirmer : attendre 2–3 mois de TSMOM 3M positifs.`,
  diag_q2_ok: 0,
  diag_q3_volume: `Oui — phase Wyckoff Accumulation, volume 2.00× la moyenne : les institutionnels accumulent activement. Wyckoff : la Loi de l'Effort est vérifiée — participation proportionnelle au mouvement.`,
  diag_q3_ok: 1,
  diag_q4_timing: `Non optimal — RSI 14 = 32.5, Z-Score = 0.17σ. Attendre la confluence : RSI 40–60 + Z-Score 0/+1σ + cours proche de la SMA20 (15.96).`,
  diag_q4_ok: -1,
  lecture_croisee_titre: `Lecture croisée — retournement haussier à confirmer`,
  lecture_croisee_txt: `Signal de retournement potentiel : TSMOM 12M encore négatif (-4.4%) mais TSMOM 3M redevenu positif (+2.1%). C'est le schéma classique d'un retournement haussier en formation, mais trop tôt pour confirmer. Attendre 2–3 mois consécutifs de TSMOM 3M positif avant d'entrer. Phase Wyckoff : Accumulation.`,
  lecture_croisee_ok: 0,
  updated_at: `2026-07-15 18:58:31`,
  },  req2: [
    {date:"2025-12-30",open:15.64,high:15.7,low:15.58,close:15.62,volume:163769,sma20:15.227,sma50:15.1084,sma200:16.0918},
    {date:"2025-12-31",open:15.65,high:15.7,low:15.61,close:15.7,volume:66066,sma20:15.257,sma50:15.1174,sma200:16.0826},
    {date:"2026-01-02",open:15.73,high:15.79,low:15.65,close:15.65,volume:205423,sma20:15.293,sma50:15.1234,sma200:16.0733},
    {date:"2026-01-05",open:15.76,high:16.05,low:15.76,close:16.05,volume:190361,sma20:15.355,sma50:15.1366,sma200:16.0651},
    {date:"2026-01-06",open:16.15,high:16.2,low:15.9,close:15.91,volume:170674,sma20:15.4075,sma50:15.1452,sma200:16.0561},
    {date:"2026-01-07",open:15.97,high:15.99,low:15.5,close:15.72,volume:252510,sma20:15.4455,sma50:15.1516,sma200:16.0457},
    {date:"2026-01-08",open:15.69,high:15.79,low:15.66,close:15.7,volume:167438,sma20:15.48,sma50:15.1564,sma200:16.0349},
    {date:"2026-01-09",open:15.73,high:15.86,low:15.66,close:15.75,volume:266030,sma20:15.518,sma50:15.1624,sma200:16.0244},
    {date:"2026-01-12",open:15.8,high:15.84,low:15.64,close:15.68,volume:134823,sma20:15.556,sma50:15.1676,sma200:16.0141},
    {date:"2026-01-13",open:15.68,high:15.68,low:15.37,close:15.44,volume:178308,sma20:15.5795,sma50:15.1682,sma200:16.0028},
    {date:"2026-01-14",open:15.41,high:15.53,low:15.27,close:15.53,volume:193179,sma20:15.606,sma50:15.1732,sma200:15.9916},
    {date:"2026-01-15",open:15.53,high:15.62,low:15.45,close:15.59,volume:174529,sma20:15.6265,sma50:15.1804,sma200:15.9808},
    {date:"2026-01-16",open:15.62,high:15.64,low:15.49,close:15.49,volume:141038,sma20:15.631,sma50:15.2008,sma200:15.9713},
    {date:"2026-01-19",open:15.3,high:15.57,low:15.26,close:15.5,volume:197359,sma20:15.629,sma50:15.2228,sma200:15.9667},
    {date:"2026-01-20",open:15.46,high:15.5,low:15.35,close:15.41,volume:137382,sma20:15.627,sma50:15.2416,sma200:15.9657},
    {date:"2026-01-21",open:15.38,high:15.58,low:15.32,close:15.36,volume:190926,sma20:15.6175,sma50:15.2578,sma200:15.9618},
    {date:"2026-01-22",open:15.56,high:15.68,low:15.42,close:15.46,volume:230228,sma20:15.613,sma50:15.2728,sma200:15.961},
    {date:"2026-01-23",open:15.44,high:15.51,low:15.33,close:15.4,volume:181076,sma20:15.607,sma50:15.2842,sma200:15.9565},
    {date:"2026-01-26",open:15.52,high:15.55,low:15.33,close:15.34,volume:204422,sma20:15.5955,sma50:15.2954,sma200:15.9526},
    {date:"2026-01-27",open:15.4,high:15.61,low:15.4,close:15.49,volume:188310,sma20:15.5895,sma50:15.3062,sma200:15.9475},
    {date:"2026-01-28",open:15.46,high:15.57,low:15.4,close:15.57,volume:138332,sma20:15.587,sma50:15.3196,sma200:15.941},
    {date:"2026-01-29",open:15.61,high:15.62,low:15.45,close:15.48,volume:148990,sma20:15.576,sma50:15.3306,sma200:15.9333},
    {date:"2026-01-30",open:15.55,high:15.66,low:15.32,close:15.42,volume:223959,sma20:15.5645,sma50:15.3436,sma200:15.9248},
    {date:"2026-02-02",open:15.48,high:15.72,low:15.48,close:15.7,volume:124613,sma20:15.547,sma50:15.3604,sma200:15.9169},
    {date:"2026-02-03",open:15.78,high:15.89,low:15.7,close:15.75,volume:167476,sma20:15.539,sma50:15.379,sma200:15.9082},
    {date:"2026-02-04",open:15.79,high:16.16,low:15.75,close:16.06,volume:269385,sma20:15.556,sma50:15.4022,sma200:15.9012},
    {date:"2026-02-05",open:16.12,high:16.24,low:15.97,close:15.97,volume:248009,sma20:15.5695,sma50:15.425,sma200:15.894},
    {date:"2026-02-06",open:16.01,high:16.18,low:15.95,close:16.18,volume:133559,sma20:15.591,sma50:15.4468,sma200:15.8865},
    {date:"2026-02-09",open:16.24,high:16.28,low:16.09,close:16.2,volume:180963,sma20:15.617,sma50:15.4694,sma200:15.8782},
    {date:"2026-02-10",open:16.23,high:16.23,low:16.08,close:16.11,volume:169276,sma20:15.6505,sma50:15.4866,sma200:15.8687},
    {date:"2026-02-11",open:16.2,high:16.22,low:15.69,close:15.73,volume:318244,sma20:15.6605,sma50:15.4976,sma200:15.8561},
    {date:"2026-02-12",open:15.79,high:16.1,low:15.79,close:15.88,volume:204244,sma20:15.675,sma50:15.5132,sma200:15.8428},
    {date:"2026-02-13",open:15.92,high:16.06,low:15.78,close:15.83,volume:189324,sma20:15.692,sma50:15.5312,sma200:15.8351},
    {date:"2026-02-16",open:15.93,high:16.01,low:15.87,close:15.87,volume:143343,sma20:15.7105,sma50:15.5524,sma200:15.8282},
    {date:"2026-02-17",open:15.89,high:16.28,low:15.87,close:16.1,volume:218077,sma20:15.745,sma50:15.5772,sma200:15.8219},
    {date:"2026-02-18",open:16.26,high:16.3,low:16.14,close:16.3,volume:203339,sma20:15.792,sma50:15.604,sma200:15.8171},
    {date:"2026-02-19",open:16.27,high:16.37,low:16.13,close:16.22,volume:363081,sma20:15.83,sma50:15.6282,sma200:15.812},
    {date:"2026-02-20",open:15.89,high:15.91,low:15.07,close:15.6,volume:1098353,sma20:15.84,sma50:15.6404,sma200:15.8036},
    {date:"2026-02-23",open:15.79,high:15.84,low:15.62,close:15.76,volume:309022,sma20:15.861,sma50:15.6572,sma200:15.7967},
    {date:"2026-02-24",open:15.65,high:15.65,low:15.4,close:15.4,volume:262789,sma20:15.8565,sma50:15.6658,sma200:15.7874},
    {date:"2026-02-25",open:15.56,high:15.77,low:15.51,close:15.72,volume:199628,sma20:15.864,sma50:15.6802,sma200:15.7795},
    {date:"2026-02-26",open:15.68,high:15.81,low:15.48,close:15.78,volume:211822,sma20:15.879,sma50:15.6922,sma200:15.7716},
    {date:"2026-02-27",open:15.75,high:15.8,low:15.65,close:15.66,volume:306858,sma20:15.891,sma50:15.6974,sma200:15.7694},
    {date:"2026-03-02",open:15.18,high:15.34,low:15,close:15.13,volume:421934,sma20:15.8625,sma50:15.6892,sma200:15.7643},
    {date:"2026-03-03",open:15.1,high:15.1,low:14.64,close:14.71,volume:403842,sma20:15.8105,sma50:15.6744,sma200:15.7553},
    {date:"2026-03-04",open:14.7,high:14.98,low:14.7,close:14.8,volume:353696,sma20:15.7475,sma50:15.6594,sma200:15.7495},
    {date:"2026-03-05",open:14.9,high:15.06,low:14.84,close:14.86,volume:231201,sma20:15.692,sma50:15.6456,sma200:15.7428},
    {date:"2026-03-06",open:14.95,high:15.02,low:14.71,close:14.88,volume:206905,sma20:15.627,sma50:15.6328,sma200:15.7357},
    {date:"2026-03-09",open:14.56,high:14.79,low:14.36,close:14.75,volume:336896,sma20:15.5545,sma50:15.6164,sma200:15.728},
    {date:"2026-03-10",open:15.02,high:15.11,low:14.84,close:14.84,volume:235212,sma20:15.491,sma50:15.601,sma200:15.7202},
    {date:"2026-03-11",open:14.78,high:14.9,low:14.74,close:14.87,volume:157668,sma20:15.448,sma50:15.586,sma200:15.7121},
    {date:"2026-03-12",open:14.78,high:14.81,low:14.62,close:14.63,volume:181437,sma20:15.3855,sma50:15.5646,sma200:15.7026},
    {date:"2026-03-13",open:14.6,high:14.69,low:14.48,close:14.5,volume:180017,sma20:15.319,sma50:15.5416,sma200:15.6927},
    {date:"2026-03-16",open:14.46,high:14.63,low:14.41,close:14.57,volume:195998,sma20:15.254,sma50:15.512,sma200:15.6846},
    {date:"2026-03-17",open:14.59,high:14.9,low:14.56,close:14.8,volume:171943,sma20:15.189,sma50:15.4898,sma200:15.6766},
    {date:"2026-03-18",open:14.84,high:14.93,low:14.68,close:14.69,volume:189759,sma20:15.1085,sma50:15.4692,sma200:15.6675},
    {date:"2026-03-19",open:14.6,high:14.7,low:14.53,close:14.62,volume:192701,sma20:15.0285,sma50:15.4476,sma200:15.6585},
    {date:"2026-03-20",open:14.6,high:14.71,low:14.46,close:14.53,volume:419949,sma20:14.975,sma50:15.4232,sma200:15.65},
    {date:"2026-03-23",open:14.39,high:14.73,low:14.19,close:14.52,volume:283378,sma20:14.913,sma50:15.4,sma200:15.641},
    {date:"2026-03-24",open:14.62,high:14.81,low:14.57,close:14.7,volume:205691,sma20:14.878,sma50:15.3852,sma200:15.6326},
    {date:"2026-03-25",open:14.91,high:15.13,low:14.8,close:14.83,volume:281894,sma20:14.8335,sma50:15.3712,sma200:15.6252},
    {date:"2026-03-26",open:14.83,high:14.96,low:14.81,close:14.9,volume:195615,sma20:14.7895,sma50:15.3574,sma200:15.6189},
    {date:"2026-03-27",open:14.9,high:14.9,low:14.62,close:14.68,volume:260371,sma20:14.7405,sma50:15.3412,sma200:15.612},
    {date:"2026-03-30",open:14.7,high:14.9,low:14.65,close:14.86,volume:138684,sma20:14.727,sma50:15.3284,sma200:15.6058},
    {date:"2026-03-31",open:14.95,high:15.05,low:14.9,close:14.92,volume:93549,sma20:14.7375,sma50:15.3186,sma200:15.6011},
    {date:"2026-04-01",open:15.12,high:15.22,low:15.1,close:15.1,volume:144145,sma20:14.7525,sma50:15.3134,sma200:15.5965},
    {date:"2026-04-02",open:15.1,high:15.28,low:15.1,close:15.22,volume:148531,sma20:14.7705,sma50:15.3086,sma200:15.5925},
    {date:"2026-04-07",open:15.31,high:15.54,low:15.26,close:15.31,volume:190487,sma20:14.792,sma50:15.3068,sma200:15.5883},
    {date:"2026-04-08",open:15.79,high:15.83,low:15.57,close:15.63,volume:164930,sma20:14.836,sma50:15.3126,sma200:15.586},
    {date:"2026-04-09",open:15.62,high:15.82,low:15.62,close:15.82,volume:160861,sma20:14.885,sma50:15.3192,sma200:15.5841},
    {date:"2026-04-10",open:15.85,high:15.88,low:15.74,close:15.83,volume:119882,sma20:14.933,sma50:15.3244,sma200:15.5816},
    {date:"2026-04-13",open:15.77,high:15.9,low:15.62,close:15.89,volume:151698,sma20:14.996,sma50:15.3326,sma200:15.5796},
    {date:"2026-04-14",open:15.95,high:16.12,low:15.92,close:16.08,volume:144094,sma20:15.075,sma50:15.3458,sma200:15.579},
    {date:"2026-04-15",open:16.13,high:16.19,low:15.92,close:16,volume:179242,sma20:15.1465,sma50:15.3518,sma200:15.5775},
    {date:"2026-04-16",open:16,high:16.13,low:15.97,close:15.99,volume:180174,sma20:15.206,sma50:15.3566,sma200:15.5753},
    {date:"2026-04-17",open:16.13,high:16.26,low:16.01,close:16.25,volume:211307,sma20:15.284,sma50:15.3604,sma200:15.5751},
    {date:"2026-04-20",open:16.13,high:16.18,low:16.07,close:16.13,volume:215471,sma20:15.3595,sma50:15.3636,sma200:15.5733},
    {date:"2026-04-21",open:16.14,high:16.16,low:16.02,close:16.1,volume:138510,sma20:15.438,sma50:15.362,sma200:15.5715},
    {date:"2026-04-22",open:16.09,high:16.12,low:15.91,close:15.96,volume:211753,sma20:15.51,sma50:15.3572,sma200:15.5673},
    {date:"2026-04-23",open:16.05,high:16.1,low:15.9,close:16.1,volume:154240,sma20:15.58,sma50:15.357,sma200:15.5652},
    {date:"2026-04-24",open:15.99,high:16.02,low:15.74,close:15.85,volume:186735,sma20:15.631,sma50:15.3594,sma200:15.5625},
    {date:"2026-04-27",open:15.86,high:15.9,low:15.76,close:15.78,volume:188031,sma20:15.675,sma50:15.3574,sma200:15.5591},
    {date:"2026-04-28",open:15.84,high:16.01,low:15.82,close:15.97,volume:189103,sma20:15.7395,sma50:15.3602,sma200:15.558},
    {date:"2026-04-29",open:16,high:16.02,low:15.78,close:15.85,volume:122343,sma20:15.789,sma50:15.3598,sma200:15.5559},
    {date:"2026-04-30",open:15.75,high:15.81,low:15.62,close:15.75,volume:117958,sma20:15.8305,sma50:15.3528,sma200:15.5518},
    {date:"2026-05-04",open:15.9,high:15.9,low:15.53,close:15.61,volume:195513,sma20:15.856,sma50:15.339,sma200:15.5464},
    {date:"2026-05-05",open:15.61,high:15.7,low:15.57,close:15.65,volume:123550,sma20:15.8775,sma50:15.3276,sma200:15.5445},
    {date:"2026-05-06",open:15.79,high:16.31,low:15.79,close:16.17,volume:270793,sma20:15.9205,sma50:15.339,sma200:15.5451},
    {date:"2026-05-07",open:16.31,high:16.33,low:16.11,close:16.16,volume:200574,sma20:15.947,sma50:15.347,sma200:15.545},
    {date:"2026-05-08",open:16.09,high:16.13,low:15.91,close:15.99,volume:258241,sma20:15.9555,sma50:15.3588,sma200:15.5442},
    {date:"2026-05-11",open:16.12,high:16.12,low:15.95,close:15.96,volume:221100,sma20:15.962,sma50:15.3636,sma200:15.543},
    {date:"2026-05-12",open:16.03,high:16.1,low:15.81,close:15.87,volume:276993,sma20:15.961,sma50:15.3654,sma200:15.5419},
    {date:"2026-05-13",open:16.16,high:16.5,low:16.08,close:16.2,volume:498706,sma20:15.967,sma50:15.3762,sma200:15.5423},
    {date:"2026-05-14",open:16.31,high:16.66,low:16.29,close:16.48,volume:340118,sma20:15.991,sma50:15.4032,sma200:15.5436},
    {date:"2026-05-15",open:16.45,high:16.59,low:16.31,close:16.52,volume:387804,sma20:16.0175,sma50:15.4394,sma200:15.5447},
    {date:"2026-05-18",open:16.46,high:16.53,low:16.33,close:16.45,volume:251966,sma20:16.0275,sma50:15.4724,sma200:15.548},
    {date:"2026-05-19",open:16.5,high:16.57,low:16.34,close:16.36,volume:206124,sma20:16.039,sma50:15.5024,sma200:15.5495},
    {date:"2026-05-20",open:16.34,high:16.54,low:16.19,close:16.4,volume:257913,sma20:16.054,sma50:15.5328,sma200:15.5517},
    {date:"2026-05-21",open:16.51,high:16.61,low:16.31,close:16.31,volume:299408,sma20:16.0715,sma50:15.564,sma200:15.5531},
    {date:"2026-05-22",open:16.4,high:16.51,low:16.32,close:16.44,volume:368427,sma20:16.0885,sma50:15.596,sma200:15.5554},
    {date:"2026-05-25",open:16.54,high:16.55,low:16.37,close:16.38,volume:460331,sma20:16.115,sma50:15.6262,sma200:15.557},
    {date:"2026-05-26",open:15.21,high:15.35,low:14.99,close:15.15,volume:536597,sma20:16.0835,sma50:15.6366,sma200:15.5524},
    {date:"2026-05-27",open:15.1,high:15.27,low:15.1,close:15.22,volume:252686,sma20:16.046,sma50:15.651,sma200:15.548},
    {date:"2026-05-28",open:15.16,high:15.18,low:14.87,close:14.92,volume:256338,sma20:15.9995,sma50:15.658,sma200:15.5409},
    {date:"2026-05-29",open:14.95,high:14.99,low:14.79,close:14.95,volume:591454,sma20:15.9595,sma50:15.661,sma200:15.534},
    {date:"2026-06-01",open:14.85,high:14.87,low:14.75,close:14.87,volume:320834,sma20:15.9225,sma50:15.6646,sma200:15.526},
    {date:"2026-06-02",open:14.87,high:14.87,low:14.68,close:14.79,volume:293523,sma20:15.8795,sma50:15.668,sma200:15.5176},
    {date:"2026-06-03",open:14.7,high:14.76,low:14.64,close:14.72,volume:305068,sma20:15.807,sma50:15.6718,sma200:15.5081},
    {date:"2026-06-04",open:14.55,high:14.71,low:14.5,close:14.61,volume:244419,sma20:15.7295,sma50:15.6736,sma200:15.4971},
    {date:"2026-06-05",open:14.66,high:14.71,low:14.53,close:14.55,volume:213398,sma20:15.6575,sma50:15.6706,sma200:15.4856},
    {date:"2026-06-08",open:14.52,high:14.82,low:14.5,close:14.64,volume:234802,sma20:15.5915,sma50:15.6668,sma200:15.4749},
    {date:"2026-06-09",open:14.67,high:14.84,low:14.65,close:14.78,volume:190732,sma20:15.537,sma50:15.6644,sma200:15.4654},
    {date:"2026-06-10",open:14.8,high:14.93,low:14.71,close:14.93,volume:222293,sma20:15.4735,sma50:15.6694,sma200:15.4607},
    {date:"2026-06-11",open:14.85,high:15.02,low:14.85,close:15.02,volume:179705,sma20:15.4005,sma50:15.6726,sma200:15.4552},
    {date:"2026-06-12",open:15.1,high:15.18,low:14.98,close:15.04,volume:192631,sma20:15.3265,sma50:15.675,sma200:15.4498},
    {date:"2026-06-15",open:15.23,high:15.28,low:15.16,close:15.26,volume:191439,sma20:15.267,sma50:15.6782,sma200:15.4457},
    {date:"2026-06-16",open:15.31,high:15.76,low:15.3,close:15.47,volume:372130,sma20:15.2225,sma50:15.6832,sma200:15.4438},
    {date:"2026-06-17",open:15.47,high:15.5,low:15.25,close:15.34,volume:150856,sma20:15.1695,sma50:15.6838,sma200:15.4431},
    {date:"2026-06-18",open:15.38,high:15.38,low:15.18,close:15.24,volume:164052,sma20:15.116,sma50:15.676,sma200:15.4423},
    {date:"2026-06-19",open:15.29,high:15.3,low:15.16,close:15.21,volume:339349,sma20:15.0545,sma50:15.6638,sma200:15.4404},
    {date:"2026-06-22",open:15.26,high:15.32,low:15.11,close:15.28,volume:194685,sma20:14.9995,sma50:15.6528,sma200:15.4396},
    {date:"2026-06-23",open:15.23,high:15.27,low:15.11,close:15.22,volume:120943,sma20:15.003,sma50:15.6394,sma200:15.4385},
    {date:"2026-06-24",open:15.17,high:15.18,low:15,close:15.05,volume:103295,sma20:14.9945,sma50:15.6188,sma200:15.4363},
    {date:"2026-06-25",open:15.03,high:15.11,low:14.99,close:15,volume:147341,sma20:14.9985,sma50:15.5988,sma200:15.4334},
    {date:"2026-06-26",open:14.99,high:15.08,low:14.9,close:15.08,volume:123273,sma20:15.005,sma50:15.5806,sma200:15.4307},
    {date:"2026-06-29",open:15.11,high:15.11,low:15,close:15.02,volume:137180,sma20:15.0125,sma50:15.556,sma200:15.4278},
    {date:"2026-06-30",open:15.01,high:15.17,low:14.92,close:15.06,volume:166297,sma20:15.026,sma50:15.5346,sma200:15.4251},
  ],  req3: [
    {date_month:"2021-03",rs_ratio:0.0018253,rs_return_12m:24.384,rs_trend_label:"STRONG_LEADER"},
    {date_month:"2021-04",rs_ratio:0.0019466,rs_return_12m:51.583,rs_trend_label:"STRONG_LEADER"},
    {date_month:"2021-05",rs_ratio:0.0018916,rs_return_12m:55.861,rs_trend_label:"LEADER"},
    {date_month:"2021-06",rs_ratio:0.0018631,rs_return_12m:40.181,rs_trend_label:"LEADER"},
    {date_month:"2021-07",rs_ratio:0.0019024,rs_return_12m:15.345,rs_trend_label:"LEADER"},
    {date_month:"2021-08",rs_ratio:0.0018414,rs_return_12m:16.245,rs_trend_label:"NEUTRAL"},
    {date_month:"2021-09",rs_ratio:0.0019340,rs_return_12m:32.691,rs_trend_label:"LEADER"},
    {date_month:"2021-10",rs_ratio:0.0020898,rs_return_12m:23.052,rs_trend_label:"LEADER"},
    {date_month:"2021-11",rs_ratio:0.0020262,rs_return_12m:10.174,rs_trend_label:"LEADER"},
    {date_month:"2021-12",rs_ratio:0.0019919,rs_return_12m:10.203,rs_trend_label:"LEADER"},
    {date_month:"2022-01",rs_ratio:0.0018768,rs_return_12m:2.782,rs_trend_label:"NEUTRAL"},
    {date_month:"2022-02",rs_ratio:0.0020168,rs_return_12m:5.251,rs_trend_label:"LEADER"},
    {date_month:"2022-03",rs_ratio:0.0018169,rs_return_12m:-0.463,rs_trend_label:"LAGGARD"},
    {date_month:"2022-04",rs_ratio:0.0018584,rs_return_12m:-4.527,rs_trend_label:"LAGGARD"},
    {date_month:"2022-05",rs_ratio:0.0018203,rs_return_12m:-3.769,rs_trend_label:"LAGGARD"},
    {date_month:"2022-06",rs_ratio:0.0017643,rs_return_12m:-5.304,rs_trend_label:"LAGGARD"},
    {date_month:"2022-07",rs_ratio:0.0016196,rs_return_12m:-14.866,rs_trend_label:"LAGGARD"},
    {date_month:"2022-08",rs_ratio:0.0016413,rs_return_12m:-10.863,rs_trend_label:"LAGGARD"},
    {date_month:"2022-09",rs_ratio:0.0016733,rs_return_12m:-13.480,rs_trend_label:"LAGGARD"},
    {date_month:"2022-10",rs_ratio:0.0017778,rs_return_12m:-14.933,rs_trend_label:"LAGGARD"},
    {date_month:"2022-11",rs_ratio:0.0018061,rs_return_12m:-10.860,rs_trend_label:"LAGGARD"},
    {date_month:"2022-12",rs_ratio:0.0020080,rs_return_12m:0.806,rs_trend_label:"LEADER"},
    {date_month:"2023-01",rs_ratio:0.0019664,rs_return_12m:4.775,rs_trend_label:"LEADER"},
    {date_month:"2023-02",rs_ratio:0.0020319,rs_return_12m:0.747,rs_trend_label:"LEADER"},
    {date_month:"2023-03",rs_ratio:0.0019330,rs_return_12m:6.391,rs_trend_label:"LEADER"},
    {date_month:"2023-04",rs_ratio:0.0020389,rs_return_12m:9.710,rs_trend_label:"LEADER"},
    {date_month:"2023-05",rs_ratio:0.0019501,rs_return_12m:7.126,rs_trend_label:"LEADER"},
    {date_month:"2023-06",rs_ratio:0.0018608,rs_return_12m:5.468,rs_trend_label:"NEUTRAL"},
    {date_month:"2023-07",rs_ratio:0.0019542,rs_return_12m:20.657,rs_trend_label:"NEUTRAL"},
    {date_month:"2023-08",rs_ratio:0.0018335,rs_return_12m:11.707,rs_trend_label:"NEUTRAL"},
    {date_month:"2023-09",rs_ratio:0.0017906,rs_return_12m:7.010,rs_trend_label:"NEUTRAL"},
    {date_month:"2023-10",rs_ratio:0.0017483,rs_return_12m:-1.654,rs_trend_label:"LAGGARD"},
    {date_month:"2023-11",rs_ratio:0.0016610,rs_return_12m:-8.035,rs_trend_label:"LAGGARD"},
    {date_month:"2023-12",rs_ratio:0.0017327,rs_return_12m:-13.711,rs_trend_label:"LAGGARD"},
    {date_month:"2024-01",rs_ratio:0.0017707,rs_return_12m:-9.955,rs_trend_label:"LAGGARD"},
    {date_month:"2024-02",rs_ratio:0.0018077,rs_return_12m:-11.034,rs_trend_label:"LAGGARD"},
    {date_month:"2024-03",rs_ratio:0.0019265,rs_return_12m:-0.333,rs_trend_label:"NEUTRAL"},
    {date_month:"2024-04",rs_ratio:0.0019309,rs_return_12m:-5.296,rs_trend_label:"NEUTRAL"},
    {date_month:"2024-05",rs_ratio:0.0019141,rs_return_12m:-1.842,rs_trend_label:"NEUTRAL"},
    {date_month:"2024-06",rs_ratio:0.0018642,rs_return_12m:0.182,rs_trend_label:"LEADER"},
    {date_month:"2024-07",rs_ratio:0.0020147,rs_return_12m:3.099,rs_trend_label:"LEADER"},
    {date_month:"2024-08",rs_ratio:0.0021061,rs_return_12m:14.870,rs_trend_label:"LEADER"},
    {date_month:"2024-09",rs_ratio:0.0021426,rs_return_12m:19.656,rs_trend_label:"LEADER"},
    {date_month:"2024-10",rs_ratio:0.0021947,rs_return_12m:25.527,rs_trend_label:"STRONG_LEADER"},
    {date_month:"2024-11",rs_ratio:0.0021408,rs_return_12m:28.884,rs_trend_label:"STRONG_LEADER"},
    {date_month:"2024-12",rs_ratio:0.0020175,rs_return_12m:16.439,rs_trend_label:"LEADER"},
    {date_month:"2025-01",rs_ratio:0.0020317,rs_return_12m:14.739,rs_trend_label:"LEADER"},
    {date_month:"2025-02",rs_ratio:0.0021007,rs_return_12m:16.209,rs_trend_label:"NEUTRAL"},
  ],  req4: [
    {date_month:"2014-06",stockprice:11.15,lr_value:6.7186,sigma:2.1386,z_score:2.0721,lr_r_squared:0.5071},
    {date_month:"2014-07",stockprice:11.16,lr_value:6.7715,sigma:2.1386,z_score:2.0520,lr_r_squared:0.5071},
    {date_month:"2014-08",stockprice:10.87,lr_value:6.8244,sigma:2.1386,z_score:1.8917,lr_r_squared:0.5071},
    {date_month:"2014-09",stockprice:10.45,lr_value:6.8774,sigma:2.1386,z_score:1.6705,lr_r_squared:0.5071},
    {date_month:"2014-10",stockprice:9.94,lr_value:6.9303,sigma:2.1386,z_score:1.4073,lr_r_squared:0.5071},
    {date_month:"2014-11",stockprice:10.28,lr_value:6.9832,sigma:2.1386,z_score:1.5416,lr_r_squared:0.5071},
    {date_month:"2014-12",stockprice:11.08,lr_value:7.0361,sigma:2.1386,z_score:1.8909,lr_r_squared:0.5071},
    {date_month:"2015-01",stockprice:10.95,lr_value:7.0890,sigma:2.1386,z_score:1.8054,lr_r_squared:0.5071},
    {date_month:"2015-02",stockprice:10.88,lr_value:7.1419,sigma:2.1386,z_score:1.7479,lr_r_squared:0.5071},
    {date_month:"2015-03",stockprice:11.50,lr_value:7.1948,sigma:2.1386,z_score:2.0131,lr_r_squared:0.5071},
    {date_month:"2015-04",stockprice:11.13,lr_value:7.2477,sigma:2.1386,z_score:1.8153,lr_r_squared:0.5071},
    {date_month:"2015-05",stockprice:10.22,lr_value:7.3006,sigma:2.1386,z_score:1.3651,lr_r_squared:0.5071},
    {date_month:"2015-06",stockprice:10.43,lr_value:7.3536,sigma:2.1386,z_score:1.4385,lr_r_squared:0.5071},
    {date_month:"2015-07",stockprice:11.57,lr_value:7.4065,sigma:2.1386,z_score:1.9468,lr_r_squared:0.5071},
    {date_month:"2015-08",stockprice:8.39,lr_value:7.4594,sigma:2.1386,z_score:0.4351,lr_r_squared:0.5071},
    {date_month:"2015-09",stockprice:7.88,lr_value:7.5123,sigma:2.1386,z_score:0.1719,lr_r_squared:0.5071},
    {date_month:"2015-10",stockprice:7.54,lr_value:7.5652,sigma:2.1386,z_score:-0.0118,lr_r_squared:0.5071},
    {date_month:"2015-11",stockprice:9.26,lr_value:7.6181,sigma:2.1386,z_score:0.7677,lr_r_squared:0.5071},
    {date_month:"2015-12",stockprice:9.34,lr_value:7.6710,sigma:2.1386,z_score:0.7804,lr_r_squared:0.5071},
    {date_month:"2016-01",stockprice:7.57,lr_value:7.7239,sigma:2.1386,z_score:-0.0720,lr_r_squared:0.5071},
    {date_month:"2016-02",stockprice:6.61,lr_value:7.7769,sigma:2.1386,z_score:-0.5456,lr_r_squared:0.5071},
    {date_month:"2016-03",stockprice:7.25,lr_value:7.8298,sigma:2.1386,z_score:-0.2711,lr_r_squared:0.5071},
    {date_month:"2016-04",stockprice:7.05,lr_value:7.8827,sigma:2.1386,z_score:-0.3894,lr_r_squared:0.5071},
    {date_month:"2016-05",stockprice:6.88,lr_value:7.9356,sigma:2.1386,z_score:-0.4936,lr_r_squared:0.5071},
    {date_month:"2016-06",stockprice:6.11,lr_value:7.9885,sigma:2.1386,z_score:-0.8784,lr_r_squared:0.5071},
    {date_month:"2016-07",stockprice:4.55,lr_value:8.0414,sigma:2.1386,z_score:-1.6326,lr_r_squared:0.5071},
    {date_month:"2016-08",stockprice:4.78,lr_value:8.0943,sigma:2.1386,z_score:-1.5497,lr_r_squared:0.5071},
    {date_month:"2016-09",stockprice:6.45,lr_value:8.1472,sigma:2.1386,z_score:-0.7936,lr_r_squared:0.5071},
    {date_month:"2016-10",stockprice:5.90,lr_value:8.2002,sigma:2.1386,z_score:-1.0755,lr_r_squared:0.5071},
    {date_month:"2016-11",stockprice:5.58,lr_value:8.2531,sigma:2.1386,z_score:-1.2499,lr_r_squared:0.5071},
    {date_month:"2016-12",stockprice:6.20,lr_value:8.3060,sigma:2.1386,z_score:-0.9847,lr_r_squared:0.5071},
    {date_month:"2017-01",stockprice:5.98,lr_value:8.3589,sigma:2.1386,z_score:-1.1123,lr_r_squared:0.5071},
    {date_month:"2017-02",stockprice:6.89,lr_value:8.4118,sigma:2.1386,z_score:-0.7116,lr_r_squared:0.5071},
    {date_month:"2017-03",stockprice:7.11,lr_value:8.4647,sigma:2.1386,z_score:-0.6335,lr_r_squared:0.5071},
    {date_month:"2017-04",stockprice:7.18,lr_value:8.5176,sigma:2.1386,z_score:-0.6255,lr_r_squared:0.5071},
    {date_month:"2017-05",stockprice:7.29,lr_value:8.5705,sigma:2.1386,z_score:-0.5988,lr_r_squared:0.5071},
    {date_month:"2017-06",stockprice:8.84,lr_value:8.6234,sigma:2.1386,z_score:0.1013,lr_r_squared:0.5071},
    {date_month:"2017-07",stockprice:8.00,lr_value:8.6764,sigma:2.1386,z_score:-0.3163,lr_r_squared:0.5071},
    {date_month:"2017-08",stockprice:8.01,lr_value:8.7293,sigma:2.1386,z_score:-0.3363,lr_r_squared:0.5071},
    {date_month:"2017-09",stockprice:9.26,lr_value:8.7822,sigma:2.1386,z_score:0.2234,lr_r_squared:0.5071},
    {date_month:"2017-10",stockprice:9.38,lr_value:8.8351,sigma:2.1386,z_score:0.2548,lr_r_squared:0.5071},
    {date_month:"2017-11",stockprice:9.30,lr_value:8.8880,sigma:2.1386,z_score:0.1926,lr_r_squared:0.5071},
    {date_month:"2017-12",stockprice:9.37,lr_value:8.9409,sigma:2.1386,z_score:0.2006,lr_r_squared:0.5071},
    {date_month:"2018-01",stockprice:9.24,lr_value:8.9938,sigma:2.1386,z_score:0.1151,lr_r_squared:0.5071},
    {date_month:"2018-02",stockprice:9.59,lr_value:9.0467,sigma:2.1386,z_score:0.2540,lr_r_squared:0.5071},
    {date_month:"2018-03",stockprice:9.29,lr_value:9.0997,sigma:2.1386,z_score:0.0890,lr_r_squared:0.5071},
    {date_month:"2018-04",stockprice:10.52,lr_value:9.1526,sigma:2.1386,z_score:0.6394,lr_r_squared:0.5071},
    {date_month:"2018-05",stockprice:9.10,lr_value:9.2055,sigma:2.1386,z_score:-0.0493,lr_r_squared:0.5071},
    {date_month:"2018-06",stockprice:9.52,lr_value:9.2584,sigma:2.1386,z_score:0.1223,lr_r_squared:0.5071},
    {date_month:"2018-07",stockprice:7.72,lr_value:9.3113,sigma:2.1386,z_score:-0.7441,lr_r_squared:0.5071},
    {date_month:"2018-08",stockprice:8.15,lr_value:9.3642,sigma:2.1386,z_score:-0.5678,lr_r_squared:0.5071},
    {date_month:"2018-09",stockprice:8.17,lr_value:9.4171,sigma:2.1386,z_score:-0.5831,lr_r_squared:0.5071},
    {date_month:"2018-10",stockprice:8.84,lr_value:9.4700,sigma:2.1386,z_score:-0.2946,lr_r_squared:0.5071},
    {date_month:"2018-11",stockprice:8.32,lr_value:9.5229,sigma:2.1386,z_score:-0.5625,lr_r_squared:0.5071},
    {date_month:"2018-12",stockprice:8.11,lr_value:9.5759,sigma:2.1386,z_score:-0.6854,lr_r_squared:0.5071},
    {date_month:"2019-01",stockprice:8.18,lr_value:9.6288,sigma:2.1386,z_score:-0.6774,lr_r_squared:0.5071},
    {date_month:"2019-02",stockprice:8.00,lr_value:9.6817,sigma:2.1386,z_score:-0.7863,lr_r_squared:0.5071},
    {date_month:"2019-03",stockprice:7.88,lr_value:9.7346,sigma:2.1386,z_score:-0.8672,lr_r_squared:0.5071},
    {date_month:"2019-04",stockprice:9.02,lr_value:9.7875,sigma:2.1386,z_score:-0.3589,lr_r_squared:0.5071},
    {date_month:"2019-05",stockprice:8.74,lr_value:9.8404,sigma:2.1386,z_score:-0.5145,lr_r_squared:0.5071},
    {date_month:"2019-06",stockprice:8.87,lr_value:9.8933,sigma:2.1386,z_score:-0.4785,lr_r_squared:0.5071},
    {date_month:"2019-07",stockprice:11.20,lr_value:9.9462,sigma:2.1386,z_score:0.5862,lr_r_squared:0.5071},
    {date_month:"2019-08",stockprice:10.74,lr_value:9.9992,sigma:2.1386,z_score:0.3464,lr_r_squared:0.5071},
    {date_month:"2019-09",stockprice:10.42,lr_value:10.0521,sigma:2.1386,z_score:0.1720,lr_r_squared:0.5071},
    {date_month:"2019-10",stockprice:9.80,lr_value:10.1050,sigma:2.1386,z_score:-0.1426,lr_r_squared:0.5071},
    {date_month:"2019-11",stockprice:10.03,lr_value:10.1579,sigma:2.1386,z_score:-0.0598,lr_r_squared:0.5071},
    {date_month:"2019-12",stockprice:10.97,lr_value:10.2108,sigma:2.1386,z_score:0.3550,lr_r_squared:0.5071},
    {date_month:"2020-01",stockprice:11.22,lr_value:10.2637,sigma:2.1386,z_score:0.4471,lr_r_squared:0.5071},
    {date_month:"2020-02",stockprice:10.60,lr_value:10.3166,sigma:2.1386,z_score:0.1325,lr_r_squared:0.5071},
    {date_month:"2020-03",stockprice:5.85,lr_value:10.3695,sigma:2.1386,z_score:-2.1133,lr_r_squared:0.5071},
    {date_month:"2020-04",stockprice:5.36,lr_value:10.4225,sigma:2.1386,z_score:-2.3672,lr_r_squared:0.5071},
    {date_month:"2020-05",stockprice:5.13,lr_value:10.4754,sigma:2.1386,z_score:-2.4994,lr_r_squared:0.5071},
    {date_month:"2020-06",stockprice:5.84,lr_value:10.5283,sigma:2.1386,z_score:-2.1922,lr_r_squared:0.5071},
    {date_month:"2020-07",stockprice:6.70,lr_value:10.5812,sigma:2.1386,z_score:-1.8148,lr_r_squared:0.5071},
    {date_month:"2020-08",stockprice:6.56,lr_value:10.6341,sigma:2.1386,z_score:-1.9050,lr_r_squared:0.5071},
    {date_month:"2020-09",stockprice:5.97,lr_value:10.6870,sigma:2.1386,z_score:-2.2056,lr_r_squared:0.5071},
    {date_month:"2020-10",stockprice:6.70,lr_value:10.7399,sigma:2.1386,z_score:-1.8890,lr_r_squared:0.5071},
    {date_month:"2020-11",stockprice:8.51,lr_value:10.7928,sigma:2.1386,z_score:-1.0674,lr_r_squared:0.5071},
    {date_month:"2020-12",stockprice:8.21,lr_value:10.8458,sigma:2.1386,z_score:-1.2325,lr_r_squared:0.5071},
    {date_month:"2021-01",stockprice:8.12,lr_value:10.8987,sigma:2.1386,z_score:-1.2993,lr_r_squared:0.5071},
    {date_month:"2021-02",stockprice:9.05,lr_value:10.9516,sigma:2.1386,z_score:-0.8892,lr_r_squared:0.5071},
    {date_month:"2021-03",stockprice:9.44,lr_value:11.0045,sigma:2.1386,z_score:-0.7315,lr_r_squared:0.5071},
    {date_month:"2021-04",stockprice:10.15,lr_value:11.0574,sigma:2.1386,z_score:-0.4243,lr_r_squared:0.5071},
    {date_month:"2021-05",stockprice:9.97,lr_value:11.1103,sigma:2.1386,z_score:-0.5332,lr_r_squared:0.5071},
    {date_month:"2021-06",stockprice:10.22,lr_value:11.1632,sigma:2.1386,z_score:-0.4410,lr_r_squared:0.5071},
    {date_month:"2021-07",stockprice:10.60,lr_value:11.2161,sigma:2.1386,z_score:-0.2881,lr_r_squared:0.5071},
    {date_month:"2021-08",stockprice:10.41,lr_value:11.2690,sigma:2.1386,z_score:-0.4017,lr_r_squared:0.5071},
    {date_month:"2021-09",stockprice:10.89,lr_value:11.3220,sigma:2.1386,z_score:-0.2020,lr_r_squared:0.5071},
    {date_month:"2021-10",stockprice:12.35,lr_value:11.3749,sigma:2.1386,z_score:0.4560,lr_r_squared:0.5071},
    {date_month:"2021-11",stockprice:12.01,lr_value:11.4278,sigma:2.1386,z_score:0.2722,lr_r_squared:0.5071},
    {date_month:"2021-12",stockprice:12.53,lr_value:11.4807,sigma:2.1386,z_score:0.4906,lr_r_squared:0.5071},
    {date_month:"2022-01",stockprice:11.69,lr_value:11.5336,sigma:2.1386,z_score:0.0731,lr_r_squared:0.5071},
    {date_month:"2022-02",stockprice:11.97,lr_value:11.5865,sigma:2.1386,z_score:0.1793,lr_r_squared:0.5071},
    {date_month:"2022-03",stockprice:10.93,lr_value:11.6394,sigma:2.1386,z_score:-0.3317,lr_r_squared:0.5071},
    {date_month:"2022-04",stockprice:11.51,lr_value:11.6923,sigma:2.1386,z_score:-0.0853,lr_r_squared:0.5071},
    {date_month:"2022-05",stockprice:10.97,lr_value:11.7453,sigma:2.1386,z_score:-0.3625,lr_r_squared:0.5071},
    {date_month:"2022-06",stockprice:9.97,lr_value:11.7982,sigma:2.1386,z_score:-0.8548,lr_r_squared:0.5071},
    {date_month:"2022-07",stockprice:10.21,lr_value:11.8511,sigma:2.1386,z_score:-0.7674,lr_r_squared:0.5071},
    {date_month:"2022-08",stockprice:10.00,lr_value:11.9040,sigma:2.1386,z_score:-0.8903,lr_r_squared:0.5071},
    {date_month:"2022-09",stockprice:9.84,lr_value:11.9569,sigma:2.1386,z_score:-0.9898,lr_r_squared:0.5071},
    {date_month:"2022-10",stockprice:11.27,lr_value:12.0098,sigma:2.1386,z_score:-0.3459,lr_r_squared:0.5071},
    {date_month:"2022-11",stockprice:11.69,lr_value:12.0627,sigma:2.1386,z_score:-0.1743,lr_r_squared:0.5071},
    {date_month:"2022-12",stockprice:12.14,lr_value:12.1156,sigma:2.1386,z_score:0.0114,lr_r_squared:0.5071},
    {date_month:"2023-01",stockprice:12.82,lr_value:12.1686,sigma:2.1386,z_score:0.3046,lr_r_squared:0.5071},
    {date_month:"2023-02",stockprice:13.96,lr_value:12.2215,sigma:2.1386,z_score:0.8129,lr_r_squared:0.5071},
    {date_month:"2023-03",stockprice:13.05,lr_value:12.2744,sigma:2.1386,z_score:0.3627,lr_r_squared:0.5071},
    {date_month:"2023-04",stockprice:13.86,lr_value:12.3273,sigma:2.1386,z_score:0.7167,lr_r_squared:0.5071},
    {date_month:"2023-05",stockprice:12.95,lr_value:12.3802,sigma:2.1386,z_score:0.2664,lr_r_squared:0.5071},
    {date_month:"2023-06",stockprice:12.62,lr_value:12.4331,sigma:2.1386,z_score:0.0874,lr_r_squared:0.5071},
    {date_month:"2023-07",stockprice:13.32,lr_value:12.4860,sigma:2.1386,z_score:0.3900,lr_r_squared:0.5071},
    {date_month:"2023-08",stockprice:12.37,lr_value:12.5389,sigma:2.1386,z_score:-0.0790,lr_r_squared:0.5071},
    {date_month:"2023-09",stockprice:12.08,lr_value:12.5918,sigma:2.1386,z_score:-0.2393,lr_r_squared:0.5071},
    {date_month:"2023-10",stockprice:11.38,lr_value:12.6448,sigma:2.1386,z_score:-0.5914,lr_r_squared:0.5071},
    {date_month:"2023-11",stockprice:11.15,lr_value:12.6977,sigma:2.1386,z_score:-0.7237,lr_r_squared:0.5071},
    {date_month:"2023-12",stockprice:11.84,lr_value:12.7506,sigma:2.1386,z_score:-0.4258,lr_r_squared:0.5071},
    {date_month:"2024-01",stockprice:12.53,lr_value:12.8035,sigma:2.1386,z_score:-0.1279,lr_r_squared:0.5071},
    {date_month:"2024-02",stockprice:13.26,lr_value:12.8564,sigma:2.1386,z_score:0.1887,lr_r_squared:0.5071},
    {date_month:"2024-03",stockprice:14.65,lr_value:12.9093,sigma:2.1386,z_score:0.8139,lr_r_squared:0.5071},
    {date_month:"2024-04",stockprice:14.45,lr_value:12.9622,sigma:2.1386,z_score:0.6957,lr_r_squared:0.5071},
    {date_month:"2024-05",stockprice:14.10,lr_value:13.0151,sigma:2.1386,z_score:0.5073,lr_r_squared:0.5071},
    {date_month:"2024-06",stockprice:13.01,lr_value:13.0681,sigma:2.1386,z_score:-0.0271,lr_r_squared:0.5071},
    {date_month:"2024-07",stockprice:14.01,lr_value:13.1210,sigma:2.1386,z_score:0.4157,lr_r_squared:0.5071},
    {date_month:"2024-08",stockprice:14.54,lr_value:13.1739,sigma:2.1386,z_score:0.6388,lr_r_squared:0.5071},
    {date_month:"2024-09",stockprice:14.69,lr_value:13.2268,sigma:2.1386,z_score:0.6842,lr_r_squared:0.5071},
    {date_month:"2024-10",stockprice:14.82,lr_value:13.2797,sigma:2.1386,z_score:0.7202,lr_r_squared:0.5071},
    {date_month:"2024-11",stockprice:14.64,lr_value:13.3326,sigma:2.1386,z_score:0.6113,lr_r_squared:0.5071},
    {date_month:"2024-12",stockprice:14.38,lr_value:13.3855,sigma:2.1386,z_score:0.4650,lr_r_squared:0.5071},
    {date_month:"2025-01",stockprice:15.59,lr_value:13.4384,sigma:2.1386,z_score:1.0060,lr_r_squared:0.5071},
    {date_month:"2025-02",stockprice:16.42,lr_value:13.4914,sigma:2.1386,z_score:1.3694,lr_r_squared:0.5071},
    {date_month:"2025-03",stockprice:17.69,lr_value:13.5443,sigma:2.1386,z_score:1.9385,lr_r_squared:0.5071},
    {date_month:"2025-04",stockprice:18.01,lr_value:13.5972,sigma:2.1386,z_score:2.0634,lr_r_squared:0.5071},
    {date_month:"2025-05",stockprice:16.47,lr_value:13.6501,sigma:2.1386,z_score:1.3186,lr_r_squared:0.5071},
    {date_month:"2025-06",stockprice:16.29,lr_value:13.7030,sigma:2.1386,z_score:1.2097,lr_r_squared:0.5071},
    {date_month:"2025-07",stockprice:16.32,lr_value:13.7559,sigma:2.1386,z_score:1.1989,lr_r_squared:0.5071},
    {date_month:"2025-08",stockprice:16.09,lr_value:13.8088,sigma:2.1386,z_score:1.0667,lr_r_squared:0.5071},
    {date_month:"2025-09",stockprice:15.87,lr_value:13.8617,sigma:2.1386,z_score:0.9390,lr_r_squared:0.5071},
    {date_month:"2025-10",stockprice:15.28,lr_value:13.9146,sigma:2.1386,z_score:0.6384,lr_r_squared:0.5071},
    {date_month:"2025-11",stockprice:15.18,lr_value:13.9676,sigma:2.1386,z_score:0.5669,lr_r_squared:0.5071},
    {date_month:"2025-12",stockprice:15.70,lr_value:14.0205,sigma:2.1386,z_score:0.7853,lr_r_squared:0.5071},
    {date_month:"2026-01",stockprice:15.42,lr_value:14.0734,sigma:2.1386,z_score:0.6297,lr_r_squared:0.5071},
    {date_month:"2026-02",stockprice:15.66,lr_value:14.1263,sigma:2.1386,z_score:0.7171,lr_r_squared:0.5071},
    {date_month:"2026-03",stockprice:14.92,lr_value:null,sigma:null,z_score:null,lr_r_squared:null},
    {date_month:"2026-04",stockprice:15.75,lr_value:null,sigma:null,z_score:null,lr_r_squared:null},
    {date_month:"2026-05",stockprice:14.95,lr_value:null,sigma:null,z_score:null,lr_r_squared:null},
    {date_month:"2026-06",stockprice:15.06,lr_value:null,sigma:null,z_score:null,lr_r_squared:null},
  ],
};

// ── Adaptateurs REQ1-REQ4 → TM_META / RAW_DAILY / RS_DATA / RAW_MONTHLY ─────
// Même principe que buildCompanyMeta/buildEFSeries dans EtatsFinanciersCard :
// req1 est déjà à plat dans la forme consommée par le composant (colonnes
// timing_marche_summary/StockDashboard/StockDashboard_Decisions/
// Timing_marche_interpretations telles quelles), donc buildTimingMeta() est
// un passthrough documenté plutôt qu'un renommage — le point d'entrée unique
// reste utile si des champs calculés/renommés doivent être ajoutés plus tard.
function buildTimingMeta(req1) {
  return { ...req1 };
}

// req2 (timing_marche_chart, colonnes SQL réelles) → forme courte utilisée
// par CandleChart (d/o/h/l/c/v/s20/s50/s200). Pas de colonne rsi_14 en base :
// le RSI(14) est calculé côté front depuis `close` (fallback rsiFromJS).
function buildDailySeries(req2) {
  return [...req2]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(r => ({
      d: r.date, o: r.open, h: r.high, l: r.low, c: r.close, v: r.volume,
      s20: r.sma20, s50: r.sma50, s200: r.sma200,
    }));
}

// req3 (Analysis_Relative_Strength, colonnes SQL réelles) → forme courte
// utilisée par RSChart (d/r/r12/lb). rs_trend_label arrive en base sous
// forme de mot complet (NEUTRAL/LEADER/STRONG_LEADER/LAGGARD/
// STRONG_LAGGARD) ; normalisé ici vers les codes abrégés attendus par
// LB_COLORS/LB_FULL.
const RS_LABEL_CODE = {
  NEUTRAL: "N", LEADER: "L", STRONG_LEADER: "SL",
  LAGGARD: "Lg", STRONG_LAGGARD: "SLg",
};
function buildRSSeries(req3) {
  return [...req3]
    .sort((a, b) => a.date_month.localeCompare(b.date_month))
    .map(r => ({
      d: r.date_month, r: r.rs_ratio, r12: r.rs_return_12m,
      lb: RS_LABEL_CODE[r.rs_trend_label] || "N",
    }));
}

// req4 (monthly_histo_price_calculation, colonnes SQL réelles) → forme
// courte utilisée par le chart long terme (d/c/lr/reg/sg/z/r2/b1p/b2p/b1n/
// b2n). sigma est unique par symbole (pas de bande calculée point par point
// en base) : b1p/b2p/b1n/b2n reconstruits ici = lr_value ± sigma / ± 2×sigma,
// null-safe pour les mois où le batch n'a pas encore tourné.
function buildMonthlySeries(req4) {
  return [...req4]
    .sort((a, b) => a.date_month.localeCompare(b.date_month))
    .map(r => {
      const lr = r.lr_value, sg = r.sigma;
      const band = (v) => (lr == null || sg == null) ? null : +(lr + v * sg).toFixed(4);
      return {
        d: r.date_month, c: r.stockprice, lr, reg: lr, sg, z: r.z_score, r2: r.lr_r_squared,
        b1p: band(1), b2p: band(2), b1n: band(-1), b2n: band(-2),
      };
    });
}

const TM_META      = buildTimingMeta(RAW_TM_DATA.req1);
const RAW_DAILY     = buildDailySeries(RAW_TM_DATA.req2);
const RAW_MONTHLY  = buildMonthlySeries(RAW_TM_DATA.req4);
const RS_DATA        = buildRSSeries(RAW_TM_DATA.req3);

const TimingSeriesContext = createContext({ daily: null, monthly: null, rs: null });
function useTimingSeries() { return useContext(TimingSeriesContext); }


// ══════════════════════════════════════════════════════════════════
// SMA values already in RAW_DAILY from CSV.
// We still compute OLS regression + sigma bands + rolling Z-score.
// ══════════════════════════════════════════════════════════════════
function calcRegression(data) {
  const n  = data.length;
  const cs = data.map(d => d.c);
  const mx = (n-1)/2;
  const my = cs.reduce((a,b)=>a+b,0)/n;
  const ssxy = cs.reduce((s,y,i)=>s+(i-mx)*(y-my),0);
  const ssxx = cs.reduce((s,_,i)=>s+(i-mx)**2,0);
  const sl = ssxy/ssxx, ic = my - sl*mx;
  const res = cs.map((y,i)=>y-(ic+sl*i));
  const sg  = Math.sqrt(res.reduce((s,r)=>s+r*r,0)/n);
  return data.map((_,i)=>({
    reg: +(ic+sl*i).toFixed(2),
    b2p: +(ic+sl*i+2*sg).toFixed(2),
    b1p: +(ic+sl*i+sg).toFixed(2),
    b1n: +(ic+sl*i-sg).toFixed(2),
    b2n: +(ic+sl*i-2*sg).toFixed(2),
  }));
}
function calcZScore(data) {
  const cs = data.map(d => d.c);
  return data.map((_,i)=>{
    const w = Math.min(60, i+1);
    const sl = cs.slice(i-w+1, i+1);
    const zm = sl.reduce((a,b)=>a+b,0)/sl.length;
    const zs = Math.sqrt(sl.reduce((s,y)=>s+(y-zm)**2,0)/sl.length)||1;
    return +((cs[i]-zm)/zs).toFixed(3);
  });
}

const _REG  = calcRegression(RAW_DAILY);
const _Z    = calcZScore(RAW_DAILY);

const DAILY = RAW_DAILY.map((d,i) => ({
  ...d,
  ...(_REG[i]),
  z: _Z[i],
}));


const LB_COLORS = {SL:"#6f8a5b", L:"#8da57a", N:"#8a8680", Lg:"#caa85e", SLg:"#c6523f"};
const LB_FULL   = {SL:"Strong Leader", L:"Leader", N:"Neutral", Lg:"Laggard", SLg:"Strong Laggard"};


// 3. TOOLTIP — position:fixed, viewport coords (v2_2 pattern)
//    Never clipped by overflow:hidden. Fond blanc. z-index 9999.
// ══════════════════════════════════════════════════════════════════
// TipIcon: accepts text as plain string OR as TIPS object {title?, body, src?}
function TipIcon({ text }) {
  if (!text) return null;
  const tipProps = typeof text === "string" ? { body: text } : text;
  return (
    <Tip {...tipProps}>
      <span style={{
        display:"inline-flex", alignItems:"center", justifyContent:"center",
        width:14, height:14, borderRadius:"50%",
        border:"1px solid rgba(181,154,91,.55)",
        fontSize:8, color:"rgba(181,154,91,.85)",
        marginLeft:4, flexShrink:0, lineHeight:1,
        verticalAlign:"middle", cursor:"help",
      }}>?</span>
    </Tip>
  );
}



// ══════════════════════════════════════════════════════════════════
// 4. GAUGE — from ValorisationCard.jsx
//    Demi-cercle W=320/H=170, 5 segments de couleur
// ══════════════════════════════════════════════════════════════════
function Gauge({ value, labelTop, labelBottom }) {
  const W=320, H=170, cx=W/2, cy=H-8, r=130;
  // INVERSÉ : rouge (Faible) à gauche → vert (Élevée) à droite
  const COLORS = ["#c6523f","#d28a52","#caa85e","#a9b38a","#6f8a5b"];
  const clamped = value != null ? Math.max(0, Math.min(1, value)) : 0.5;
  const angle = (-180 + 180*clamped) * Math.PI/180;
  const nx = cx + (r-34)*Math.cos(angle);
  const ny = cy + (r-34)*Math.sin(angle);
  const arc = (a0,a1,rr) => {
    const s=a0*Math.PI/180, e=a1*Math.PI/180;
    const x1=cx+rr*Math.cos(s), y1=cy+rr*Math.sin(s);
    const x2=cx+rr*Math.cos(e), y2=cy+rr*Math.sin(e);
    return `M${x1} ${y1} A${rr} ${rr} 0 ${Math.abs(a1-a0)>180?1:0} 1 ${x2} ${y2}`;
  };
  const step = 180/5;
  // Active arc color : rouge si faible, vert si élevé
  const activeCol = clamped >= 0.7 ? C.green : clamped >= 0.4 ? C.amber : C.red;
  const needleCol = "var(--ts)";
  return (
    <div style={{ width:"100%", maxWidth:"min(280px,100%)", margin:"0 auto", overflow:"hidden" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto", display:"block" }}>
        {/* Track */}
        <path d={arc(-180,0,r)} strokeWidth={18} stroke="rgba(148,163,184,.12)" fill="none" strokeLinecap="round"/>
        {/* Colored segments (rouge gauche → vert droite) */}
        {COLORS.map((col,i)=>(
          <path key={i} d={arc(-180+step*i,-180+step*(i+1),r)}
            strokeWidth={18} stroke={col} fill="none"
            strokeLinecap={i===0||i===4?"round":"butt"} opacity={.55}/>
        ))}
        {/* Separators */}
        {[0.25,0.5,0.75].map(t=>{
          const a=(-180+180*t)*Math.PI/180;
          const x1=cx+(r-24)*Math.cos(a), y1=cy+(r-24)*Math.sin(a);
          const x2=cx+(r-10)*Math.cos(a), y2=cy+(r-10)*Math.sin(a);
          return <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--card-bg)" strokeWidth="2"/>;
        })}
        {/* Axis labels */}
        {[{t:0,label:"Faible"},{t:0.5,label:"Neutre"},{t:1,label:"Élevée"}].map(({t,label})=>{
          const a=(-180+180*t)*Math.PI/180, dist=r+20;
          const x=cx+dist*Math.cos(a), y=cy+dist*Math.sin(a);
          return (
            <text key={t} x={x} y={y+4}
              textAnchor="middle" fontSize="8.5" fontWeight="600"
              fill="var(--mu)" fontFamily="ui-monospace,monospace">{label}</text>
          );
        })}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny}
          strokeWidth={8} stroke={needleCol} strokeLinecap="round" opacity={.85}/>
        <circle cx={cx} cy={cy} r={9} fill={needleCol} opacity={.85}/>
        <circle cx={cx} cy={cy} r={3.5} fill="rgba(255,255,255,0.5)"/>
        {/* Labels */}
        <text x={cx} y={32} textAnchor="middle" fontSize="18" fontWeight="800"
          fill="var(--ts)" fontFamily="ui-monospace,monospace">{labelTop}</text>
        <text x={cx} y={H-26} textAnchor="middle" fontSize="11" fontWeight="600"
          fill="var(--mu)" fontFamily="ui-monospace,monospace">{labelBottom}</text>
      </svg>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 5. MODAL "COMPRENDRE"
//    Fond blanc, backdrop semi-opaque, aucune référence Murphy
// ══════════════════════════════════════════════════════════════════
// ANALYSE modal content — valeurs issues de timing_marche_summary + StockDashboard (NVDA 2026-04-30)
// Chaque section est une fonction(d) → string pour permettre l'injection des données réelles
function buildAnalyse(d) {
  const tss       = Math.round(parseFloat(d?.trend_score         ?? 65.2));
  const tqs       = Math.round(parseFloat(d?.trend_quality_score ?? 46));
  const ltScore   = parseFloat(d?.lt_trend_score  ?? 94.4).toFixed(0);
  const stScore   = parseFloat(d?.st_trend_score  ?? 55.3).toFixed(0);
  const cagr      = parseFloat(d?.cagr            ?? 37.3).toFixed(1);
  const r2        = parseFloat(d?.r_squared       ?? 0.844).toFixed(2);
  const sma20     = parseFloat(d?.sma20           ?? 196.17).toFixed(2);
  const sma50     = parseFloat(d?.sma50           ?? 186.94).toFixed(2);
  const sma200    = parseFloat(d?.sma200          ?? 183.72).toFixed(2);
  const suppLvl   = parseFloat(d?.support_level   ?? 175.56).toFixed(2);
  const suppTests = d?.support_touches            ?? 46;
  const resLvl    = parseFloat(d?.resist_level    ?? 211.34).toFixed(2);
  const resTests  = d?.resist_touches             ?? 2;
  const t12       = parseFloat(d?.tsmom_12m       ?? 60.12).toFixed(1);
  const t6        = parseFloat(d?.tsmom_6m        ?? -6.53).toFixed(1);
  const t3        = parseFloat(d?.tsmom_3m        ?? -6.49).toFixed(1);
  const momScore  = Math.round(parseFloat(d?.dd_momentum_score ?? d?.momentum_score ?? 53.86));
  const ldScore   = Math.round(parseFloat(d?.dd_momentum_score ?? 53.86));
  const fip       = parseFloat(d?.fip_score       ?? -0.034).toFixed(3);
  const joursPos  = parseFloat(d?.positive_days_pct ?? 51.7).toFixed(1);
  const r20d      = parseFloat(d?.volume_ratio_20d ?? 1.4767).toFixed(2);
  const r60d      = parseFloat(d?.volume_ratio_60d ?? 1.2805).toFixed(2);
  const suppVR    = parseFloat(d?.support_vol_ratio ?? 1.043).toFixed(3);
  const wyckoff   = d?.wyckoff_context            ?? "Distribution";
  const wyEv      = d?.wyckoff_event              ?? "UTAD";
  const m1        = parseFloat(d?.last_return_m1  ?? 14.43).toFixed(1);
  const vScore    = Math.round(parseFloat(d?.volume_score ?? 68));
  const rsi14     = parseFloat(d?.rsi_14          ?? 60.8).toFixed(1);
  const zScore    = parseFloat(d?.z_score         ?? 1.317).toFixed(2);
  const oppScore  = parseFloat(d?.opportunity_score ?? 9.21).toFixed(0);
  const timingScore = Math.round(parseFloat(d?.opportunity_score ?? 40)); // scoreTiming ← tms.opportunity_score (cf. mapping .md §2a) ; timing_quality est un libellé texte ("Neutral"), pas un score
  const coherence = parseInt(d?.coherence_score   ?? -1);
  const tscore    = parseFloat(d?.tscore          ?? 32.3).toFixed(0);
  const structColor = colorForPct(tss);
  const momColor    = colorForPct(momScore);
  const volColor    = colorForPct(vScore);
  const timColor    = colorForPct(timingScore);

  return {
    structure: {
      title:"Structure de Tendance", icon:"📈", color:structColor,
      sections:[
        {h:"Qu'est-ce que la structure de tendance ?", p:"La structure de tendance évalue la direction du prix sur trois horizons : long terme (5 ans), annuel et primaire. Une tendance haussière est caractérisée par une succession de hauts et bas croissants — c'est la donnée la plus importante de l'analyse."},
        {h:"Trend Quality & Strength Scores", p:`Le Trend Quality Score (${tqs}/100) mesure la régularité de la progression via le R² de la régression (${r2}). Le Trend Strength Score (${tss}/100) évalue l'intensité directionnelle. Le score LT (${ltScore}/100) confirme une structure long terme solide. Score CT (${stScore}/100) : momentum 1 an modéré.`},
        {h:"Moyennes mobiles et alignement", p:`SMA 20 : ${sma20} · SMA 50 : ${sma50} · SMA 200 : ${sma200}. L'alignement SMA20 > SMA50 > SMA200 est la configuration haussière idéale selon Murphy (TAOTFM ch. 9). Un CAGR de +${cagr}% confirme la qualité de la croissance à long terme.`},
        {h:"Supports et résistances", p:`Support principal : ${suppLvl} (${suppTests} tests réussis) — zone d'accumulation historique majeure. Résistance principale : ${resLvl} (${resTests} tests). La significativité d'un niveau est proportionnelle au nombre de tests et au volume associé (Edwards & Magee, TAST ch. 13).`},
        {h:"Interprétation Invelps", p:`Score ${tss}/100 : ${tss >= 75 ? "structure haussière de haute qualité" : tss >= 55 ? "tendance globalement positive" : "structure sous pression"}. R² ${r2} — ${parseFloat(r2) >= 0.75 ? "très bonne cohérence de tendance" : "cohérence modérée"}. CAGR +${cagr}% sur l'historique complet.`},
      ]
    },
    momentum: {
      title:"Force & Momentum", icon:"⚡", color:momColor,
      sections:[
        {h:"Qu'est-ce que le momentum ?", p:"Le momentum mesure la vitesse et la persistance du mouvement de prix. Un titre en fort momentum tend statistiquement à continuer sa direction sur 3 à 12 mois — c'est l'inertie de marché documentée sur l'ensemble des marchés financiers développés (Moskowitz, Ooi & Pedersen, JFE 2012)."},
        {h:"Time-Series Momentum (TSMOM)", p:`TSMOM 12M (${parseFloat(t12)>=0?"+":""}${t12}%) = momentum long terme ${parseFloat(t12)>=20?"très fort":"modéré"}. TSMOM 6M (${parseFloat(t6)>=0?"+":""}${t6}%) et TSMOM 3M (${parseFloat(t3)>=0?"+":""}${t3}%) : ${parseFloat(t12)>0 && parseFloat(t3)<0?"divergence court terme — tendance LT intacte, essoufflement récent":"horizons alignés"}. Gray & Vogel : surveiller le FIP pour distinguer correction saine de retournement.`},
        {h:"Leadership Score et FIP", p:`Le Leadership Score (${ldScore}/100) indique ${ldScore>=70?"que le titre surperforme son univers":"un leadership modéré dans l'univers"}. Le FIP score (${fip}) reflète l'équilibre jours positifs/négatifs (${joursPos}% de séances positives). Ces métriques mesurent la qualité interne du momentum.`},
        {h:"La divergence de momentum", p:`${parseFloat(t12)>0 && parseFloat(t3)<0?"Divergence confirmée : TSMOM 12M positif mais CT négatif. Ce n'est pas un signal de retournement, mais de prudence — Murphy (TAOTFM ch. 10) : l'une des configurations les plus précieuses de l'AT. Une reprise du TSMOM 3M au-dessus de zéro confirmerait la poursuite.":"Horizons momentum convergents — signal de cohérence directionnelle."}`},
        {h:"Interprétation Invelps", p:`Score ${momScore}/100 : momentum ${momScore>=75?"solide":"modéré à faible"}. Direction : ${d?.momentum_direction ?? "Retournement baissier"}. ${momScore<55?"Signal d'essoufflement — attendre la confirmation d'une reprise CT avant toute entrée.":"Le signal long terme reste dominant."}`},
      ]
    },
    volume: {
      title:"Participation & Volume", icon:"📊", color:volColor,
      sections:[
        {h:"Pourquoi les volumes sont essentiels", p:"Le volume confirme ou infirme la tendance de prix. Une hausse sur volumes croissants est solide — sans conviction institutionnelle large, le mouvement est fragile. Murphy : « le volume confirme la tendance » (TAOTFM ch. 7)."},
        {h:"Les ratios de volume Invelps", p:`Ratio 20J : ${r20d}x ${parseFloat(r20d)>=1.2?"= participation institutionnelle solide sur le mois":"= participation dans la norme"}. Ratio 60J : ${r60d}x ${parseFloat(r60d)>=1.0?"= intérêt soutenu sur 2 mois":"= légère baisse structurelle"}. Support Vol. Ratio : ${suppVR} (${parseFloat(suppVR)>=1.1?"acheteurs plus actifs sur les zones support":"neutre"}). M1 : ${parseFloat(m1)>=0?"+":""}${m1}%.`},
        {h:`Phase Wyckoff : ${wyckoff}${wyEv && wyEv!=="None"?" — "+wyEv:""}`, p:`Le cycle de Wyckoff identifie 4 phases : Accumulation → Markup → Distribution → Markdown. ${wyckoff==="Distribution"?`La ${wyEv==="UTAD"?"phase UTAD (Upthrust After Distribution) signale une fausse cassure haussière — les institutions vendent à la foule enthousiaste. Signal baissier majeur selon Villahermosa.":"Distribution désigne la phase où les grands opérateurs allègent progressivement. La pression vendeuse s'accumule en coulisses."}`:wyckoff==="Accumulation"?"L'Accumulation désigne la phase où les grands opérateurs construisent leurs positions avant la hausse.":"La phase actuelle mérite surveillance."}`},
        {h:"Confirmation ou Divergence Prix/Volume", p:`M1 : ${parseFloat(m1)>=0?"+":""}${m1}% avec ratio volume 20J : ${r20d}x. ${parseFloat(m1)>0 && parseFloat(r20d)>=1.2?"Confirmation Prix/Volume : hausse accompagnée de volumes supérieurs — configuration idéale selon Murphy et Wyckoff.":parseFloat(m1)>0 && parseFloat(r20d)<1.0?"Divergence : hausse sans volume — fragilité à surveiller.":"Contexte volumétrique neutre."}`},
        {h:"Interprétation Invelps", p:`Score ${vScore}/100 : participation ${vScore>=75?"solide":vScore>=55?"acceptable":"sous la moyenne"}. Phase ${wyckoff}${wyEv&&wyEv!=="None"?" ("+wyEv+")":""} — ${wyckoff==="Distribution"?"signal de prudence sur les entrées court terme, pas de signal de vente automatique.":"contexte à surveiller."}`},
      ]
    },
    timing: {
      title:"Timing & Opportunité", icon:"⏱", color:timColor,
      sections:[
        {h:"RSI et zones d'entrée", p:`RSI 14 : ${rsi14} — ${parseFloat(rsi14)>=70?"zone de surachat (>70) : prudence accrue sur les entrées immédiates":parseFloat(rsi14)>=55?"zone de force : momentum positif confirmé":parseFloat(rsi14)<=30?"zone de survente (<30) : potentielle opportunité de rebond":"zone neutre"}. La zone idéale d'achat en tendance haussière se situe entre 40 et 55 — sur correction secondaire.`},
        {h:"Z-Score et canal statistique", p:`Z-Score ${parseFloat(zScore)>=0?"+":""}${zScore} : le prix est ${Math.abs(parseFloat(zScore)).toFixed(2)} écart${Math.abs(parseFloat(zScore))>1?"s":""}-type ${parseFloat(zScore)>=0?"au-dessus":"en dessous"} de sa moyenne historique. Les zones >+2σ sont statistiquement rares (~5% des observations). Statut : ${d?.crossover_signal ?? "+1σ breakout"} — ${d?.valuation_status ?? "Surévalué"}.`},
        {h:"Scores d'opportunité et de timing", p:`L'Opportunity Score (${oppScore}/100) et le Timing Score (${timingScore}/100) indiquent ${parseInt(oppScore)<30?"un point d'entrée défavorable — risque de piège":parseInt(oppScore)<60?"un contexte de tendance forte mais point d'entrée sous-optimal":"un timing favorable"}. ${parseInt(oppScore)<30?"Attendre un repli vers les SMAs améliorerait significativement le ratio risque/rendement.":""}`},
        {h:"Cohérence inter-piliers", p:`Cohérence : ${coherence >= 3 ? "forte convergence haussière" : coherence >= 1 ? "légère convergence haussière" : coherence >= -1 ? "signaux en tension — point de bascule" : "divergence baissière marquée"} (score ${coherence}/6). TScore global : ${tscore}/100 — ${parseInt(tscore)>=75?"signal fort":parseInt(tscore)>=50?"signal mixte":"signal faible"}.`},
        {h:"Interprétation Invelps", p:`Score ${timingScore}/100 : ${timingScore>=60?"timing d'entrée acceptable":timingScore>=40?"timing sous-optimal — patienter pour un meilleur point d'entrée":"timing défavorable — éviter les positions longues"}. Scénario optimal : ${parseFloat(sma20)>0?`attendre un repli vers SMA20 (${sma20}) ou SMA50 (${sma50})`:"attendre une reprise du momentum CT"} avant d'initier ou renforcer une position.`},
      ]
    },
  };
}
// Fallback statique si d non disponible (même structure)
const ANALYSE_STATIC = buildAnalyse(null);

function AnalyseModal({ pillar, onClose, d }) {
  const ANALYSE = buildAnalyse(d);
  const content = ANALYSE[pillar];
  if (!content) return null;
  useEffect(()=>{
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  },[]);
  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:2000,
      background:"rgba(0,0,0,.45)",
      display:"flex", alignItems:"flex-end", justifyContent:"center",
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"var(--card-bg)",
        border:"1px solid var(--card-bdr)",
        borderRadius:"20px 20px 0 0",
        width:"100%", maxWidth:600, maxHeight:"84vh",
        overflowY:"auto", padding:"22px 20px 40px",
        boxShadow:"0 -8px 40px rgba(0,0,0,.25)",
      }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10,
              background:`${content.color}18`, border:`1px solid ${content.color}44`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:18, flexShrink:0 }}>{content.icon}</div>
            <div>
              <div style={{ fontSize:13, fontWeight:800, color:content.color,
                fontFamily:"ui-monospace,monospace" }}>{content.title}</div>
              <div style={{ fontSize:10, color:"var(--mu)", fontWeight:600, marginTop:1 }}>
                Analyse technique Invelps
              </div>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background:"none", border:"1px solid var(--sep)", borderRadius:8,
              width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:13, color:"var(--mu)", cursor:"pointer" }}>✕</button>
        </div>
        {/* Sections */}
        {content.sections.map((s,i) => (
          <div key={i} style={{ marginBottom:16, paddingBottom:16,
            borderBottom: i<content.sections.length-1 ? "1px solid var(--sep)" : "none" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
              <div style={{ width:21, height:21, borderRadius:999, background:content.color,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:9, fontWeight:900, color:"#fff", flexShrink:0 }}>{i+1}</div>
              <div style={{ fontSize:13, fontWeight:800, color:"var(--ts)" }}>{s.h}</div>
            </div>
            <p style={{ fontSize:12, color:"var(--mu)", lineHeight:1.72,
              fontWeight:500, paddingLeft:29 }}>{s.p}</p>
          </div>
        ))}
        <button onClick={onClose}
          style={{ marginTop:14, width:"100%", padding:"12px", borderRadius:12,
            border:"none", background:content.color, color:"#fff",
            fontSize:13, fontWeight:800, cursor:"pointer" }}>Fermer</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 6. X-AXIS LABEL GENERATOR — adaptatif selon la période
// ══════════════════════════════════════════════════════════════════

function buildXLabels(data, range, width = 900) {
  if (!data.length) return [];

  const FR_MONTHS = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];

  // 1) On identifie la première barre disponible de chaque mois.
  const monthTicks = [];
  let lastKey = null;
  data.forEach((d, i) => {
    const dt = new Date(d.d);
    if (Number.isNaN(dt.getTime())) return;
    const key = `${dt.getFullYear()}-${dt.getMonth()}`;
    if (key !== lastKey) {
      lastKey = key;
      monthTicks.push({
        i,
        month: dt.getMonth(),
        year: dt.getFullYear(),
        isJanuary: dt.getMonth() === 0,
      });
    }
  });

  if (!monthTicks.length) return [];

  // 2) Granularité adaptée à l’horizon pour garder l’axe lisible :
  //    - 3M/6M/1Y  → un repère par mois (comportement historique)
  //    - 3Y        → un repère par trimestre (Jan/Avr/Juil/Oct), année en majeur
  //    - 5Y/MAX    → un repère par année (Janvier uniquement)
  const isYearly    = range === "5Y" || range === "MAX";
  const isQuarterly = range === "3Y";

  const kept = monthTicks.filter(t => {
    if (isYearly)    return t.isJanuary;
    if (isQuarterly) return t.month % 3 === 0; // Jan, Avr, Juil, Oct
    return true;
  });

  // Si le filtre écarte le tout premier point (ex: données commençant en cours
  // d'année), on force quand même un premier repère pour ancrer l'axe.
  if (kept.length === 0 || kept[0].i !== monthTicks[0].i) {
    kept.unshift(monthTicks[0]);
  }

  return kept.map((t, idx) => {
    const showYear = t.isJanuary || idx === 0;
    return {
      ...t,
      idx,
      tick: true,
      major: t.isJanuary || idx === 0,
      label: isYearly
        ? `${t.year}`
        : `${FR_MONTHS[t.month]}${showYear ? ` '${String(t.year).slice(2)}` : ""}`,
    };
  });
}

// ══════════════════════════════════════════════════════════════════
// 7. CANDLE CHART v6
//    • SMAs affichées SEULEMENT si période ≤ 1Y (daily mode)
//    • Bougies corrigées (bW recalculé depuis W réel)
//    • Panneaux Volume + RSI14 sous le prix
//    • Axe X adaptatif
// ══════════════════════════════════════════════════════════════════

// Compute RSI14 on data array using closes
function computeRSI14(closes) {
  const period = 14;
  const rsi = new Array(closes.length).fill(null);
  if (closes.length < period + 1) return rsi;
  let avgGain = 0, avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = closes[i] - closes[i-1];
    if (diff > 0) avgGain += diff; else avgLoss -= diff;
  }
  avgGain /= period; avgLoss /= period;
  rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < closes.length; i++) {
    const diff = closes[i] - closes[i-1];
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period-1) + gain) / period;
    avgLoss = (avgLoss * (period-1) + loss) / period;
    rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return rsi;
}

function CandleChart({ data, range, showSMA20, showSMA50, showSMA200, showReg, showBands, showZScore, showVolume, showRSI, showLevels, srLevels, chartType, isDark }) {
  const SR_LEVELS = srLevels || {
    support:    { price: 175.56, label: "Support",    tests: 46, col: "#6f8a5b", volR: 1.043 },
    resistance: { price: 211.34, label: "Résistance", tests: 2,  col: "#c6523f", volR: 1.377 },
  };
  const wrapRef = useRef(null);
  const [W, setW] = useState(360);
  const [hov, setHov] = useState(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(([e]) => setW(Math.max(280, e.contentRect.width || 280)));
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  // SMAs only shown for ≤1Y
  const isShortRange = ["3M","6M","1Y"].includes(range);
  const effectiveSMA20  = showSMA20  && isShortRange;
  const effectiveSMA50  = showSMA50  && isShortRange;
  const effectiveSMA200 = showSMA200 && isShortRange;

  // Panel heights
  const compact = W < 520;
  const priceH  = Math.max(compact ? 190 : 220, Math.min(380, W * (compact ? .56 : .40)));
  const volH    = showVolume ? (compact ? 42 : 52) : 0;
  const rsiH    = showRSI   ? (compact ? 112 : 140) : 0;
  const zH      = showZScore ? (compact ? 50 : 62) : 0;
  const SEP     = compact ? 7 : 10; // gap between panels
  const PAD     = compact ? { t:8, r:46, b:42, l:30 } : { t:8, r:60, b:46, l:42 };

  // Layout Y offsets
  const priceTop = 0;
  const volTop   = priceH + (showVolume ? SEP : 0);
  const rsiTop   = volTop + volH + (showRSI ? SEP : 0);
  const zTop     = rsiTop + rsiH + (showZScore ? SEP : 0);
  const totalH   = priceH
    + (showVolume ? volH + SEP : 0)
    + (showRSI    ? rsiH + SEP : 0)
    + (showZScore ? zH   + SEP : 0)
  ;

  const cW = W - PAD.l - PAD.r;
  const cH = priceH - PAD.t - PAD.b;

  const n    = data.length;
  const slot = cW / Math.max(1, n);
  // Fix: use real pixel width, ensure minimum 1.5px candle
  const bW   = Math.max(1.5, Math.min(12, slot * 0.72));
  const cx   = i => PAD.l + (i + 0.5) * slot;

  // RSI14 — lu directement depuis timing_marche_chart (colonne rsi14)
  // Fallback JS si rsi14 absent (données sans la colonne)
  const rsiFromDB  = useMemo(() => data.map(d => d.rsi14 ?? null), [data]);
  const rsiFromJS  = useMemo(() => {
    if (rsiFromDB.some(v => v !== null)) return null; // DB disponible → pas de calcul JS
    const closes = data.map(d => d.c);
    const period = 14;
    const rsi = new Array(closes.length).fill(null);
    if (closes.length < period + 1) return rsi;
    let avgGain = 0, avgLoss = 0;
    for (let i = 1; i <= period; i++) {
      const diff = closes[i] - closes[i-1];
      if (diff > 0) avgGain += diff; else avgLoss -= diff;
    }
    avgGain /= period; avgLoss /= period;
    rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
    for (let i = period + 1; i < closes.length; i++) {
      const diff = closes[i] - closes[i-1];
      const gain = diff > 0 ? diff : 0;
      const loss = diff < 0 ? -diff : 0;
      avgGain = (avgGain * (period-1) + gain) / period;
      avgLoss = (avgLoss * (period-1) + loss) / period;
      rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
    }
    return rsi;
  }, [data, rsiFromDB]);
  const rsiArr = rsiFromDB.some(v => v !== null) ? rsiFromDB : (rsiFromJS ?? []);

  // Price domain
  const priceDomainVals = data.flatMap(d => {
    const vals = [d.h, d.l];
    if (showBands)      { if (d.b2p) vals.push(d.b2p); if (d.b2n) vals.push(d.b2n); }
    if (effectiveSMA20  && d.s20)  vals.push(d.s20);
    if (effectiveSMA50  && d.s50)  vals.push(d.s50);
    if (effectiveSMA200 && d.s200) vals.push(d.s200);
    return vals;
  }).filter(v => v != null && isFinite(v));
  // Include S/R levels in domain so they don't fall outside chart
  if (showLevels) {
    priceDomainVals.push(SR_LEVELS.support.price, SR_LEVELS.resistance.price);
  }
  const minP   = Math.min(...priceDomainVals) * 0.993;
  const maxP   = Math.max(...priceDomainVals) * 1.007;
  const rangeP = maxP - minP || 1;
  const py = v => PAD.t + cH - ((v - minP) / rangeP * cH);

  // Volume domain
  const maxVol = Math.max(...data.map(d => d.v || 0));

  // RSI domain
  const rsiPH = Math.max(82, rsiH - 28); // pixel height for rsi plot
  const rsiY  = (v) => rsiTop + PAD.t + rsiPH - ((v - 0) / 100 * rsiPH);

  // Z-Score
  const zMin=-3.5, zMax=3.5;
  const zPH = zH - 20;
  const zY = z => zTop + PAD.t + zPH - ((z - zMin) / (zMax - zMin) * zPH);
  const zPath = data.map((d,i) => `${i===0?"M":"L"}${cx(i).toFixed(1)},${zY(d.z ?? 0).toFixed(1)}`).join(" ");
  const lastZ = data[n-1]?.z ?? 0;
  const zCol  = lastZ>2?C.red:lastZ>1?"#9c7a3a":lastZ<-1?"#4a7fa0":"#9c7a3a";

  // RSI line / area paths
  let rsiPath = "";
  let rsiAreaPath = "";
  let rsiStarted = false;
  let rsiFirstX = null;
  let rsiLastX = null;
  data.forEach((d,i) => {
    const rv = rsiArr[i];
    if (rv == null) return;
    const x = cx(i).toFixed(1);
    const y = rsiY(rv).toFixed(1);
    if (!rsiStarted) {
      rsiPath += `M${x},${y} `;
      rsiAreaPath += `M${x},${rsiY(50).toFixed(1)} L${x},${y} `;
      rsiStarted = true;
      rsiFirstX = x;
    } else {
      rsiPath += `L${x},${y} `;
      rsiAreaPath += `L${x},${y} `;
    }
    rsiLastX = x;
  });
  if (rsiStarted) rsiAreaPath += `L${rsiLastX},${rsiY(50).toFixed(1)} L${rsiFirstX},${rsiY(50).toFixed(1)} Z`;
  const lastRSI = rsiArr[n-1];
  const rsiStateCol  = lastRSI >= 70 ? C.red : lastRSI <= 30 ? C.green : RSI_COL.line;

  // SMA path builder
  const smaPath = (key, col) => {
    let path = "", prev = false;
    data.forEach((d,i) => {
      if (d[key] != null) {
        path += prev ? `L${cx(i).toFixed(1)},${py(d[key]).toFixed(1)} ` : `M${cx(i).toFixed(1)},${py(d[key]).toFixed(1)} `;
        prev = true;
      } else { prev = false; }
    });
    return path ? <path d={path} fill="none" stroke={col} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" opacity=".95"/> : null;
  };

  // Sigma dashed path
  const sigPath = (key, cfg) => {
    let path = "", prev = false;
    data.forEach((d,i) => {
      if (d[key] != null) {
        path += prev ? `L${cx(i).toFixed(1)},${py(d[key]).toFixed(1)} ` : `M${cx(i).toFixed(1)},${py(d[key]).toFixed(1)} `;
        prev = true;
      } else { prev = false; }
    });
    return path ? <path d={path} fill="none" stroke={cfg.col} strokeWidth={cfg.w} strokeDasharray={cfg.dash} opacity=".88"/> : null;
  };

  // Line + area
  const linePath = data.map((d,i) => `${i===0?"M":"L"}${cx(i).toFixed(1)},${py(d.c).toFixed(1)}`).join(" ");
  const areaPath = linePath + ` L${cx(n-1).toFixed(1)},${(PAD.t+cH).toFixed(1)} L${cx(0).toFixed(1)},${(PAD.t+cH).toFixed(1)} Z`;

  const yTicks   = Array.from({length:7}, (_,i) => minP + rangeP*i/6);
  const xLabels  = useMemo(() => buildXLabels(data, range, W), [data, range, W]);
  const mutC     = isDark ? "#7a8172" : "#6f7b74";
  const gridC    = isDark ? "rgba(111,138,91,.08)" : "rgba(30,40,34,.05)";

  const hovD     = hov != null ? data[hov] : null;
  const hovLabel = hovD ? new Date(hovD.d).toLocaleDateString("fr-FR",{day:"2-digit",month:"short"}) : "";
  const hovRSI   = hov != null ? rsiArr[hov] : null;

  return (
    <div ref={wrapRef} style={{ width:"100%", overflow:"hidden" }}>
      <svg
        viewBox={`0 0 ${W} ${totalH}`}
        style={{ width:"100%", height:"auto", display:"block", cursor:"crosshair", overflow:"hidden" }}
        onMouseMove={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          const rx = (e.clientX - rect.left) / rect.width * W;
          const i = Math.max(0, Math.min(n-1, Math.round((rx - PAD.l) / slot - 0.5)));
          setHov(i);
        }}
        onMouseLeave={() => setHov(null)}
      >
        <defs>
          <linearGradient id="tm6-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={RSI_COL.line} stopOpacity=".18"/>
            <stop offset="100%" stopColor={RSI_COL.line} stopOpacity="0"/>
          </linearGradient>
          <linearGradient id="tm6-volg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.amber} stopOpacity=".35"/>
            <stop offset="100%" stopColor={C.amber} stopOpacity=".1"/>
          </linearGradient>
          <linearGradient id="tm6-rsig" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={RSI_COL.line} stopOpacity=".34"/>
            <stop offset="100%" stopColor={RSI_COL.line} stopOpacity=".04"/>
          </linearGradient>
          <clipPath id="tm6-price"><rect x={PAD.l} y={PAD.t-2} width={cW} height={cH+4}/></clipPath>
          <clipPath id="tm6-vol"><rect x={PAD.l} y={volTop} width={cW} height={volH+2}/></clipPath>
          <clipPath id="tm6-rsi"><rect x={PAD.l} y={rsiTop} width={cW} height={rsiH+2}/></clipPath>
          <clipPath id="tm6-z"><rect x={PAD.l} y={zTop} width={cW} height={zH+2}/></clipPath>
        </defs>

        {/* ── PRICE PANEL ── */}

        {/* Y price grid */}
        {yTicks.map((v,i) => (
          <line key={i} x1={PAD.l} x2={PAD.l+cW} y1={py(v).toFixed(1)} y2={py(v).toFixed(1)}
            stroke={gridC} strokeWidth=".8"/>
        ))}
        {/* Y price labels */}
        {yTicks.filter((_,i)=>i%2===0).map((v,i) => (
          <text key={i} x={PAD.l-4} y={(py(v)+3).toFixed(1)} textAnchor="end"
            fill={mutC} fontSize="8" fontFamily="ui-monospace,monospace">{Math.round(v)}</text>
        ))}

        {/* Sigma bands */}
        {showBands && (
          <g clipPath="url(#tm6-price)">
            {sigPath("b2p", SIG.b2p)}
            {sigPath("b1p", SIG.b1p)}
            {showReg && sigPath("reg", SIG.reg)}
            {sigPath("b1n", SIG.b1n)}
            {sigPath("b2n", SIG.b2n)}
          </g>
        )}
        {!showBands && showReg && (
          <g clipPath="url(#tm6-price)">{sigPath("reg", SIG.reg)}</g>
        )}

        {/* SMAs — only for short ranges */}
        <g clipPath="url(#tm6-price)">
          {effectiveSMA200 && smaPath("s200", SMA_COL.s200)}
          {effectiveSMA50  && smaPath("s50",  SMA_COL.s50)}
          {effectiveSMA20  && smaPath("s20",  SMA_COL.s20)}
        </g>

        {/* ── SUPPORT / RESISTANCE LEVELS ── */}
        {showLevels && [SR_LEVELS.support, SR_LEVELS.resistance].map(lvl => {
          const y = py(lvl.price);
          const isVisible = y >= PAD.t - 2 && y <= PAD.t + cH + 2;
          if (!isVisible) return null;
          const isSupport = lvl.col === SR_LEVELS.support.col;
          const labelBg   = isSupport ? `${C.green}22` : `${C.red}22`;
          const testStr   = `${lvl.tests} test${lvl.tests > 1 ? "s" : ""}`;
          return (
            <g key={lvl.label}>
              {/* Dashed horizontal line full width */}
              <line
                x1={PAD.l} x2={PAD.l + cW}
                y1={y.toFixed(1)} y2={y.toFixed(1)}
                stroke={lvl.col} strokeWidth="1.5"
                strokeDasharray="6 4" opacity=".8"/>
              {/* Label badge on right side */}
              <rect x={PAD.l + cW + 2} y={(y - 9).toFixed(1)} width={52} height={17} rx={3}
                fill={lvl.col} opacity=".9"/>
              <text x={PAD.l + cW + 28} y={(y + 4).toFixed(1)}
                textAnchor="middle" fontSize="7.5" fontWeight="800"
                fill="#fff" fontFamily="ui-monospace,monospace">
                {lvl.price.toFixed(2)}
              </text>
              {/* Label text left side (inside chart) */}
              <rect x={PAD.l + 4} y={(y - 14).toFixed(1)} width={70} height={13} rx={3}
                fill={labelBg} stroke={lvl.col} strokeWidth=".8" opacity=".9"/>
              <text x={PAD.l + 8} y={(y - 5).toFixed(1)}
                fontSize="7.5" fontWeight="800"
                fill={lvl.col} fontFamily="ui-monospace,monospace">
                {lvl.label} ({testStr})
              </text>
            </g>
          );
        })}

        {/* Price candles or line */}
        <g clipPath="url(#tm6-price)">
          {chartType === "line" ? (
            <>
              <path d={areaPath} fill="url(#tm6-grad)"/>
              <path d={linePath} fill="none" stroke={RSI_COL.line} strokeWidth="2"/>
            </>
          ) : (
            data.map((d,i) => {
              const bull  = d.c >= d.o;
              const col   = bull ? C.green : C.red;
              const x     = cx(i);
              const bodyT = py(Math.max(d.o, d.c));
              const bodyB = py(Math.min(d.o, d.c));
              const bodyH = Math.max(1.5, bodyB - bodyT);
              return (
                <g key={i} opacity={hov!=null&&hov!==i?0.38:1} style={{transition:"opacity .04s"}}>
                  <line x1={x.toFixed(1)} x2={x.toFixed(1)}
                    y1={py(d.h).toFixed(1)} y2={py(d.l).toFixed(1)}
                    stroke={col} strokeWidth="1" opacity=".85"/>
                  <rect
                    x={(x - bW/2).toFixed(1)} y={bodyT.toFixed(1)}
                    width={Math.max(1, bW).toFixed(1)} height={bodyH.toFixed(1)}
                    fill={bull ? col : col}
                    fillOpacity={bull ? 0.88 : 0.95}
                    stroke={col} strokeWidth="0.4"/>
                </g>
              );
            })
          )}
        </g>

        {/* Current price tag */}
        {(() => {
          const last = data[n-1];
          const y = py(last.c);
          return (
            <g>
              <line x1={PAD.l} x2={PAD.l+cW} y1={y.toFixed(1)} y2={y.toFixed(1)}
                stroke={C.red} strokeWidth="1" strokeDasharray="3 3" opacity=".45"/>
              <rect x={PAD.l+cW+2} y={(y-9).toFixed(1)} width={52} height={16} rx={3} fill={C.red}/>
              <text x={PAD.l+cW+28} y={(y+4.5).toFixed(1)} textAnchor="middle"
                fontSize="8.5" fontWeight="800" fill="#fff" fontFamily="ui-monospace,monospace">
                {last.c.toFixed(1)}
              </text>
            </g>
          );
        })()}

        {/* X axis — repères mensuels */}
        {xLabels.map(({i, major}) => (
          <line key={`x-tick-${i}`}
            x1={cx(i).toFixed(1)} x2={cx(i).toFixed(1)}
            y1={(PAD.t + cH + 2).toFixed(1)} y2={(PAD.t + cH + (major ? 10 : 6)).toFixed(1)}
            stroke={major ? C.amber : mutC} strokeWidth={major ? "1" : ".7"} opacity={major ? ".72" : ".38"}/>
        ))}
        {xLabels.filter(({label}) => label).map(({i, label, major}) => {
          const x = cx(i);
          const y = PAD.t + cH + PAD.b - 5;
          const rotate = xLabels.length > 14;
          return (
            <text key={`x-label-${i}`} x={x.toFixed(1)} y={y.toFixed(1)}
              transform={rotate ? `rotate(-45 ${x.toFixed(1)} ${y.toFixed(1)})` : undefined}
              textAnchor={rotate ? "end" : "middle"}
              fill={major ? C.amber : mutC} fontSize={rotate ? "7.2" : "8.3"} fontWeight={major ? "800" : "600"}
              fontFamily="ui-sans-serif">{label}</text>
          );
        })}

        {/* Crosshair */}
        {hov != null && (
          <g>
            <line x1={cx(hov).toFixed(1)} x2={cx(hov).toFixed(1)}
              y1={PAD.t} y2={PAD.t+cH} stroke={C.amber} strokeWidth="1" strokeDasharray="3 3" opacity=".55"/>
            {data[hov] && (
              <line x1={PAD.l} x2={PAD.l+cW}
                y1={py(data[hov].c).toFixed(1)} y2={py(data[hov].c).toFixed(1)}
                stroke={C.amber} strokeWidth="1" strokeDasharray="3 3" opacity=".35"/>
            )}
          </g>
        )}

        {/* OHLC tooltip bubble */}
        {hovD && (() => {
          const bw2 = 142, bh = showRSI ? 98 : 80;
          const bx  = Math.min(cx(hov) + 10, PAD.l + cW - bw2 - 2);
          const by  = Math.max(PAD.t + 2, py(hovD.c) - bh - 6);
          const bull = hovD.c >= hovD.o;
          const priceCol = bull ? (isDark?"#8da57a":C.green) : (isDark?"#e07a6a":C.red);
          return (
            <g>
              <rect x={bx} y={by} width={bw2} height={bh} rx={7}
                fill={isDark?"#0d1a2e":"#fff"}
                stroke={isDark?"rgba(111,138,91,.22)":"rgba(30,40,34,.11)"}
                strokeWidth="1" opacity=".97"/>
              <text x={bx+8} y={by+14} fontSize="9" fontWeight="700"
                fill={isDark?"#e8e4da":"#1f2a24"} fontFamily="ui-monospace,monospace">{hovLabel}</text>
              {[["O",hovD.o],["H",hovD.h],["L",hovD.l],["C",hovD.c]].map(([k,v],ii) => (
                <g key={k}>
                  <text x={bx+8+(ii%2)*68} y={by+28+Math.floor(ii/2)*18}
                    fontSize="8.5" fontWeight="600" fill={mutC} fontFamily="ui-monospace,monospace">{k}</text>
                  <text x={bx+19+(ii%2)*68} y={by+28+Math.floor(ii/2)*18}
                    fontSize="8.5" fontWeight="700" fill={priceCol} fontFamily="ui-monospace,monospace">{v != null ? v.toFixed(2) : "—"}</text>
                </g>
              ))}
              {hovRSI != null && (
                <g>
                  <text x={bx+8} y={by+70} fontSize="8.5" fontWeight="600" fill={mutC} fontFamily="ui-monospace,monospace">RSI 14</text>
                  <text x={bx+52} y={by+70} fontSize="8.5" fontWeight="700"
                    fill={hovRSI>=70?C.red:hovRSI<=30?C.green:RSI_COL.line} fontFamily="ui-monospace,monospace">{hovRSI.toFixed(1)}</text>
                  {hovD.v && (
                    <>
                      <text x={bx+8} y={by+84} fontSize="8.5" fontWeight="600" fill={mutC} fontFamily="ui-monospace,monospace">Vol.</text>
                      <text x={bx+30} y={by+84} fontSize="8.5" fontWeight="700"
                        fill={hovD.c>=hovD.o?C.green:C.red} fontFamily="ui-monospace,monospace">
                        {(hovD.v/1e6).toFixed(1)}M
                      </text>
                    </>
                  )}
                </g>
              )}
            </g>
          );
        })()}

        {/* ── VOLUME PANEL ── */}
        {showVolume && (
          <>
            <line x1={PAD.l} x2={PAD.l+cW} y1={volTop} y2={volTop} stroke={gridC} strokeWidth="1"/>
            <text x={PAD.l+4} y={volTop+11} fontSize="8" fontWeight="700" fill={mutC}>VOL</text>
            <g clipPath="url(#tm6-vol)">
              {data.map((d,i) => {
                if (!d.v) return null;
                const barH = Math.max(1, (d.v / maxVol) * (volH - 14));
                const x    = cx(i);
                const col  = d.c >= d.o ? C.green : C.red;
                const recent = i >= n - Math.ceil(n * 0.15);
                return (
                  <rect key={i}
                    x={(x - bW/2).toFixed(1)}
                    y={(volTop + volH - barH - 2).toFixed(1)}
                    width={Math.max(1, bW).toFixed(1)}
                    height={barH.toFixed(1)}
                    fill={col}
                    opacity={hov===i ? 1 : recent ? 0.7 : 0.45}
                    rx="0.5"/>
                );
              })}
            </g>
            {/* Vol avg line */}
            {(() => {
              const avgV = data.reduce((s,d)=>s+(d.v||0),0)/n;
              const avgY = volTop + volH - (avgV/maxVol)*(volH-14) - 2;
              return <line x1={PAD.l} x2={PAD.l+cW} y1={avgY.toFixed(1)} y2={avgY.toFixed(1)}
                stroke={C.amber} strokeWidth="1" strokeDasharray="4 3" opacity=".5"/>;
            })()}
          </>
        )}

        {/* ── RSI14 PANEL ── */}
        {showRSI && (
          <>
            <line x1={PAD.l} x2={PAD.l+cW} y1={rsiTop} y2={rsiTop} stroke={gridC} strokeWidth="1"/>
            <text x={PAD.l+4} y={rsiTop+13} fontSize="9" fontWeight="800" fill={mutC}>RSI 14</text>
            {lastRSI != null && (
              <g>
                <rect x={PAD.l+cW-96} y={rsiTop+5} width={94} height={22} rx={6}
                  fill={RSI_COL.badgeBg}
                  stroke={RSI_COL.badgeBorder}/>
                <text x={PAD.l+cW-88} y={rsiTop+19} fontSize="9" fontWeight="900" fill={rsiStateCol} fontFamily="ui-monospace,monospace">
                  RSI {lastRSI.toFixed(1)}
                </text>
              </g>
            )}
            {/* Zones RSI : surachat / neutre / survente */}
            <rect x={PAD.l} y={rsiY(100).toFixed(1)} width={cW} height={(rsiY(70)-rsiY(100)).toFixed(1)}
              fill={C.red} opacity=".055" clipPath="url(#tm6-rsi)"/>
            <rect x={PAD.l} y={rsiY(30).toFixed(1)} width={cW} height={(rsiY(0)-rsiY(30)).toFixed(1)}
              fill={C.green} opacity=".055" clipPath="url(#tm6-rsi)"/>
            {/* 70 / 50 / 30 lines */}
            {[70,50,30].map(v=>{
              const y = rsiY(v);
              return (
                <g key={v}>
                  <line x1={PAD.l} x2={PAD.l+cW} y1={y.toFixed(1)} y2={y.toFixed(1)}
                    stroke={v===50?C.amber:v===70?`${C.red}88`:`${C.green}88`}
                    strokeWidth={v===50?"1.4":"1"} strokeDasharray={v===50?"0":"5 4"}/>
                  <text x={PAD.l-4} y={(y+3).toFixed(1)} textAnchor="end"
                    fill={v===70?C.red:v===30?C.green:mutC} fontSize="8.5" fontWeight="800" fontFamily="ui-monospace,monospace">{v}</text>
                  <text x={PAD.l+cW+5} y={(y+3).toFixed(1)} textAnchor="start"
                    fill={v===70?C.red:v===30?C.green:mutC} fontSize="8" fontWeight="700" fontFamily="ui-monospace,monospace">{v}</text>
                </g>
              );
            })}
            {/* RSI fill + courbe principale */}
            {rsiAreaPath && (
              <path d={rsiAreaPath} fill="url(#tm6-rsig)" clipPath="url(#tm6-rsi)"/>
            )}
            <path d={rsiPath} fill="none" stroke={RSI_COL.line} strokeWidth="4"
              clipPath="url(#tm6-rsi)" strokeLinecap="round" strokeLinejoin="round"/>
            <path d={rsiPath} fill="none" stroke="#ffffff" strokeWidth="1.1" opacity={isDark ? ".20" : ".38"}
              clipPath="url(#tm6-rsi)" strokeLinecap="round" strokeLinejoin="round"/>
            {/* Current RSI tag */}
            {lastRSI != null && (() => {
              const y = rsiY(lastRSI);
              return (
                <g>
                  <circle cx={(PAD.l+cW).toFixed(1)} cy={y.toFixed(1)} r="3" fill={RSI_COL.line}/>
                  <rect x={PAD.l+cW+2} y={(y-8).toFixed(1)} width={50} height={14} rx={3}
                    fill={lastRSI>=70?C.red:lastRSI<=30?C.green:RSI_COL.line}/>
                  <text x={PAD.l+cW+27} y={(y+3.5).toFixed(1)} textAnchor="middle"
                    fontSize="8" fontWeight="800" fill="#fff" fontFamily="ui-monospace,monospace">
                    {lastRSI.toFixed(1)}
                  </text>
                </g>
              );
            })()}
            {/* RSI crosshair */}
            {hov!=null && hovRSI!=null && (
              <circle cx={cx(hov).toFixed(1)} cy={rsiY(hovRSI).toFixed(1)} r="3" fill={RSI_COL.line} opacity=".95"/>
            )}
          </>
        )}

        {/* ── Z-SCORE PANEL ── */}
        {showZScore && (
          <>
            <line x1={PAD.l} x2={PAD.l+cW} y1={zTop} y2={zTop} stroke={gridC} strokeWidth="1"/>
            {[-2,-1,0,1,2].map(z=>(
              <line key={z} x1={PAD.l} x2={PAD.l+cW} y1={zY(z).toFixed(1)} y2={zY(z).toFixed(1)}
                stroke={gridC} strokeWidth=".8" strokeDasharray={z===0?"0":"3 4"}/>
            ))}
            <text x={PAD.l+4} y={(zTop+11).toFixed(1)} fontSize="8" fontWeight="700" fill={mutC}>Z-Score</text>
            <polygon
              points={`${PAD.l},${zY(0)} ${zPath} ${PAD.l+cW},${zY(0)}`}
              fill={lastZ>=0?`${C.amber}1f`:"#4a7fa018"} clipPath="url(#tm6-z)"/>
            <polyline points={zPath} fill="none" stroke="#9c7a3a" strokeWidth="2.2"
              strokeLinejoin="round" strokeLinecap="round" clipPath="url(#tm6-z)"/>
            <circle cx={cx(n-1).toFixed(1)} cy={zY(lastZ).toFixed(1)} r="3.5" fill={zCol}/>
            {/* Z tag */}
            <rect x={PAD.l+cW+2} y={(zY(lastZ)-8).toFixed(1)} width={50} height={14} rx={3}
              fill={lastZ>1?"#9c7a3a":"#9c7a3a1a"}
              stroke="#9c7a3a" strokeWidth={lastZ>1?0:.7}/>
            <text x={PAD.l+cW+27} y={(zY(lastZ)+3.5).toFixed(1)} textAnchor="middle"
              fontSize="8" fontWeight="800"
              fill={lastZ>1?"#fff":"#9c7a3a"} fontFamily="ui-monospace,monospace">
              {lastZ>=0?"+":""}{lastZ.toFixed(2)}
            </text>
            {[-2,0,2].map(z=>(
              <text key={z} x={PAD.l-3} y={(zY(z)+3).toFixed(1)} textAnchor="end"
                fill={mutC} fontSize="7.5" fontFamily="ui-monospace,monospace">{z}</text>
            ))}
          </>
        )}
      </svg>
    </div>
  );
}
// ══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
// RS CHART — Relative Strength historique vs indice de référence (RS_DATA réel, COFA.PA)
// Reproduit le composant RSChart de TimingMarcheCard_v2_2.jsx
// 2 modes : RS Ratio (courbe log colorée) + Rendements (barres 12M)
// ══════════════════════════════════════════════════════════════════
function RSChart({ isDark, symbol }) {
  const seriesCtx = useTimingSeries();
  const RS_LIVE = (seriesCtx?.rs && seriesCtx.rs.length) ? seriesCtx.rs : RS_DATA;
  if (!RS_LIVE || !RS_LIVE.length) {
    return <div style={{ padding:24, color:"var(--mu)", fontSize:12 }}>RS indisponible pour ce symbole.</div>;
  }
  const [rsMode, setRsMode] = useState("ratio");
  const [tooltip, setTooltip] = useState(null);
  const wrapRef = useRef(null);
  const [W, setW] = useState(360);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(([e]) => setW(Math.max(280, e.contentRect.width || 280)));
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  const mutC  = isDark ? "#7a8172" : "#6f7b74";
  const gridC = isDark ? "rgba(111,138,91,.08)" : "rgba(30,40,34,.055)";
  const bgC   = isDark ? "#0d1426" : "#fff";
  const txC   = isDark ? "#e8e4da" : "#1f2a24";

  const H = 180;
  const PAD = { t:10, r:8, b:26, l:52 };
  const cW = W - PAD.l - PAD.r;
  const cH = H - PAD.t - PAD.b;
  const n  = RS_LIVE.length;
  const cx = i => PAD.l + (i + 0.5) * (cW / n);

  // ── RS Ratio mode (log scale)
  const ratios = RS_LIVE.map(p => p.r);
  const minR = Math.min(...ratios), maxR = Math.max(...ratios);
  const logMin = Math.log(minR), logMax = Math.log(maxR);
  const pyR = v => PAD.t + cH - ((Math.log(Math.max(v, 0.00001)) - logMin) / (logMax - logMin) * cH);
  const yTicksR = [0.0001, 0.0005, 0.001, 0.0014, 0.0016, 0.0018, 0.002, 0.0022, 0.0024, 0.003, 0.005, 0.007]
    .filter(v => v >= minR * 0.5 && v <= maxR * 2);

  // ── Rendements 12M mode
  const ret12s = RS_LIVE.map(p => p.r12 || 0);
  const minRet = Math.min(...ret12s), maxRet = Math.max(...ret12s), rngRet = maxRet - minRet || 1;
  const pyRet = v => PAD.t + cH - ((v - minRet) / rngRet * cH);
  const zeroY = pyRet(0);

  // X labels (years) — dérivées dynamiquement de la plage réelle de RS_LIVE
  // (au lieu d'une liste 2006-2024 codée en dur, héritée du fixture NVDA)
  const rsYearStart = parseInt(RS_LIVE[0].d.slice(0, 4), 10);
  const rsYearEnd   = parseInt(RS_LIVE[n - 1].d.slice(0, 4), 10);
  const xYears = [];
  for (let y = rsYearStart; y <= rsYearEnd; y++) xYears.push(String(y));
  const xLbls  = xYears.map(y => {
    const i = RS_LIVE.findIndex(p => p.d.startsWith(y));
    return i >= 0 ? { y, i } : null;
  }).filter(Boolean);

  // Tooltip hover
  const handleMouseMove = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rx = (e.clientX - rect.left) / rect.width * W;
    const i  = Math.max(0, Math.min(n - 1, Math.floor((rx - PAD.l) / (cW / n))));
    setTooltip({ i, pt: RS_LIVE[i] });
  };

  const sym = symbol || "le titre";
  const RS_MODES = [
    { id:"ratio",     label:"RS Ratio",   tip:`Ratio ${sym}/indice de référence en échelle logarithmique. Une hausse = ${sym} surperforme l'indice.` },
    { id:"rendement", label:"Rendements", tip:`Surperformance relative de ${sym} vs indice de référence sur 12 mois glissants. Barres colorées par label RS (Strong Leader → Strong Laggard).` },
  ];

  return (
    <div ref={wrapRef} style={{ width:"100%" }}>
      {/* Controls */}
      <div style={{ display:"flex", gap:6, marginBottom:10, alignItems:"center", flexWrap:"wrap" }}>
        {RS_MODES.map(m => (
          <Tip key={m.id} body={m.tip}>
            <button onClick={() => setRsMode(m.id)} style={{
              padding:"4px 10px", borderRadius:4,
              border:`1px solid ${rsMode===m.id ? C.amber+"66" : "var(--card-bdr)"}`,
              background: rsMode===m.id ? `${C.amber}18` : "transparent",
              cursor:"pointer", fontSize:10, fontWeight:800,
              color: rsMode===m.id ? C.amber : "var(--mu)",
              fontFamily:"ui-monospace,monospace", textTransform:"uppercase", letterSpacing:".06em",
            }}>{m.label}</button>
          </Tip>
        ))}
        <span style={{ marginLeft:"auto", fontSize:9, fontFamily:"ui-monospace,monospace", color:"var(--mu)" }}>
          {n} pts mensuel · {RS_LIVE[0].d}→{RS_LIVE[n-1].d}
        </span>
      </div>

      {/* Chart SVG */}
      <div style={{ position:"relative" }} onMouseLeave={() => setTooltip(null)}>
        <svg viewBox={`0 0 ${W} ${H}`}
          style={{ width:"100%", height:"auto", display:"block", cursor:"crosshair" }}
          onMouseMove={handleMouseMove}>
          <defs>
            <linearGradient id="rsg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.green} stopOpacity=".18"/>
              <stop offset="100%" stopColor={C.green} stopOpacity="0"/>
            </linearGradient>
          </defs>

          {/* ── MODE RATIO ── */}
          {rsMode === "ratio" && (<>
            {/* Grid */}
            {yTicksR.map((v, i) => (
              <line key={i} x1={PAD.l} x2={W - PAD.r}
                y1={pyR(v).toFixed(1)} y2={pyR(v).toFixed(1)}
                stroke={gridC} strokeWidth=".5" strokeDasharray="3 3"/>
            ))}
            {yTicksR.map((v, i) => (
              <text key={i} x={PAD.l - 3} y={(pyR(v) + 3).toFixed(1)}
                textAnchor="end" fill={mutC} fontSize="7" fontFamily="ui-monospace,monospace">
                {v.toFixed(4)}
              </text>
            ))}
            {/* Area fill */}
            <path
              d={RS_LIVE.map((p,i) => `${i===0?"M":"L"}${cx(i).toFixed(1)},${pyR(p.r).toFixed(1)}`).join(" ")
                + ` L${cx(n-1).toFixed(1)},${(PAD.t+cH).toFixed(1)} L${cx(0).toFixed(1)},${(PAD.t+cH).toFixed(1)} Z`}
              fill="url(#rsg)" opacity=".25"/>
            {/* Colored segments by label */}
            {RS_LIVE.map((pt, i) => {
              if (i === 0) return null;
              return (
                <line key={i}
                  x1={cx(i-1).toFixed(1)} y1={pyR(RS_LIVE[i-1].r).toFixed(1)}
                  x2={cx(i).toFixed(1)}   y2={pyR(pt.r).toFixed(1)}
                  stroke={LB_COLORS[pt.lb] || "#8a8680"}
                  strokeWidth="1.5" opacity=".88"/>
              );
            })}
            {/* Crosshair */}
            {tooltip && (
              <line x1={cx(tooltip.i).toFixed(1)} x2={cx(tooltip.i).toFixed(1)}
                y1={PAD.t} y2={PAD.t + cH}
                stroke={C.amber} strokeWidth=".8" strokeDasharray="3 2"/>
            )}
          </>)}

          {/* ── MODE RENDEMENTS ── */}
          {rsMode === "rendement" && (<>
            {[-60,-40,-20,0,20,40,60].filter(v => v >= minRet - 5 && v <= maxRet + 5).map((v, i) => (
              <line key={i} x1={PAD.l} x2={W - PAD.r}
                y1={pyRet(v).toFixed(1)} y2={pyRet(v).toFixed(1)}
                stroke={v === 0 ? mutC : gridC}
                strokeWidth={v === 0 ? .8 : .5}
                strokeDasharray={v === 0 ? "" : "3 3"}/>
            ))}
            {[-40,-20,0,20,40].filter(v => v >= minRet - 5 && v <= maxRet + 5).map((v, i) => (
              <text key={i} x={PAD.l - 3} y={(pyRet(v) + 3.5).toFixed(1)}
                textAnchor="end" fill={mutC} fontSize="7" fontFamily="ui-monospace,monospace">{v}%</text>
            ))}
            {RS_LIVE.map((pt, i) => {
              const v  = pt.r12 || 0;
              const bh = Math.abs(pyRet(v) - zeroY);
              const by = v >= 0 ? pyRet(v) : zeroY;
              const bw = Math.max(1, cW / n - 0.3);
              return (
                <rect key={i}
                  x={(cx(i) - bw/2).toFixed(1)} y={by.toFixed(1)}
                  width={bw.toFixed(1)} height={Math.max(1, bh).toFixed(1)}
                  fill={LB_COLORS[pt.lb] || "#8a8680"} opacity=".78"/>
              );
            })}
            {tooltip && (
              <line x1={cx(tooltip.i).toFixed(1)} x2={cx(tooltip.i).toFixed(1)}
                y1={PAD.t} y2={PAD.t + cH}
                stroke={C.amber} strokeWidth=".8" strokeDasharray="3 2"/>
            )}
          </>)}

          {/* X axis labels */}
          {xLbls.map(({ y, i }) => (
            <text key={y} x={cx(i).toFixed(1)} y={H - 4}
              textAnchor="middle" fill={mutC} fontSize="7.5" fontFamily="ui-monospace,monospace">{y}</text>
          ))}
        </svg>

        {/* Tooltip overlay */}
        {tooltip && (
          <div style={{
            position:"absolute", top:4, left:8, pointerEvents:"none",
            background:bgC, border:`1px solid rgba(30,40,34,.12)`,
            borderRadius:8, padding:"6px 10px",
            fontSize:9, fontFamily:"ui-monospace,monospace",
            color:txC, minWidth:140, zIndex:10,
            boxShadow:"0 4px 14px rgba(0,0,0,.12)",
          }}>
            <div style={{ color:C.amber, marginBottom:4, fontSize:10, fontWeight:700 }}>{tooltip.pt.d}</div>
            <div style={{ display:"grid", gridTemplateColumns:"auto auto", gap:"2px 10px" }}>
              {rsMode==="ratio" && <>
                <span style={{ color:"var(--mu)" }}>RS Ratio</span>
                <span>{tooltip.pt.r?.toFixed(6)}</span>
                <span style={{ color:"var(--mu)" }}>Label</span>
                <span style={{ color:LB_COLORS[tooltip.pt.lb], fontWeight:700 }}>
                  {LB_FULL[tooltip.pt.lb] || tooltip.pt.lb}
                </span>
                {tooltip.pt.r12 != null && <>
                  <span style={{ color:"var(--mu)" }}>Rend 12M</span>
                  <span style={{ color:tooltip.pt.r12 >= 0 ? C.green : C.red, fontWeight:700 }}>
                    {tooltip.pt.r12 >= 0 ? "+" : ""}{tooltip.pt.r12?.toFixed(1)}%
                  </span>
                </>}
              </>}
              {rsMode==="rendement" && <>
                <span style={{ color:"var(--mu)" }}>Rend 12M</span>
                <span style={{ color:(tooltip.pt.r12||0) >= 0 ? C.green : C.red, fontWeight:700 }}>
                  {(tooltip.pt.r12||0) >= 0 ? "+" : ""}{(tooltip.pt.r12||0).toFixed(1)}%
                </span>
                <span style={{ color:"var(--mu)" }}>Rend 1M</span>
                <span style={{ color:(tooltip.pt.r1||0) >= 0 ? C.green : C.red }}>
                  {(tooltip.pt.r1||0) >= 0 ? "+" : ""}{(tooltip.pt.r1||0).toFixed(1)}%
                </span>
                <span style={{ color:"var(--mu)" }}>Label</span>
                <span style={{ color:LB_COLORS[tooltip.pt.lb], fontWeight:700 }}>
                  {LB_FULL[tooltip.pt.lb] || tooltip.pt.lb}
                </span>
              </>}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display:"flex", gap:10, marginTop:6, flexWrap:"wrap" }}>
        {Object.entries(LB_FULL).map(([lb, label]) => (
          <div key={lb} style={{ display:"flex", alignItems:"center", gap:4 }}>
            {rsMode === "ratio"
              ? <div style={{ width:16, height:2, background:LB_COLORS[lb], borderRadius:1 }}/>
              : <div style={{ width:10, height:10, borderRadius:4, background:LB_COLORS[lb] }}/>
            }
            <span style={{ fontSize:8, color:"var(--mu)", fontFamily:"ui-monospace,monospace" }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MRow({ label, value, color, tip }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
      padding:"5px 2px", borderBottom:"1px solid var(--sep)", gap:8, cursor:"default" }}
      onMouseEnter={e=>e.currentTarget.style.background="var(--sur)"}
      onMouseLeave={e=>e.currentTarget.style.background=""}>
      <span style={{ fontSize:11, color:"var(--mu)", fontWeight:600,
        display:"flex", alignItems:"center" }}>
        {label}{tip && <TipIcon text={tip}/>}
      </span>
      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11,
        fontWeight:700, color:color||"var(--ts)", flexShrink:0 }}>{value}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 9. PILLAR CARD
// ══════════════════════════════════════════════════════════════════
function PillarCard({ number, title, score, color, pillar, tip, d, children, onGuide }) {
  const [modal, setModal] = useState(null);
  const diagQ = PILLAR_QUESTIONS[pillar] || null;
  return (
    <>
      <div style={{ background:"var(--card-bg)", borderRadius:16, border:"1px solid var(--card-bdr)",
        borderTop:`3px solid ${color}`, padding:14,
        boxShadow:"0 2px 8px rgba(16,40,28,.05)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom: diagQ ? 6 : 10 }}>
          <div style={{ width:22, height:22, borderRadius:999, background:color,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:10, fontWeight:900, color:"#fff", flexShrink:0 }}>{number}</div>
          <span style={{ flex:1, fontFamily:"ui-monospace,monospace", fontSize:10,
            fontWeight:800, color, letterSpacing:".07em", lineHeight:1.2 }}>
            {title}{tip && <TipIcon text={tip}/>}
          </span>
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:14,
            fontWeight:800, color, flexShrink:0 }}>{score}/100</span>
        </div>
        {/* Question diagnostique par axe */}
        {diagQ && (
          <div style={{ display:"flex", alignItems:"flex-start", gap:6, marginBottom:10,
            padding:"6px 10px", borderRadius:8,
            background:`${color}09`, border:`1px solid ${color}22` }}>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:7, fontWeight:800,
              color, flexShrink:0, marginTop:1, letterSpacing:".05em" }}>QUESTION</span>
            <span style={{ fontSize:9.5, color:"var(--ts)", fontStyle:"italic",
              lineHeight:1.45, flex:1 }}>{diagQ}</span>
            {onGuide && (
              <button onClick={() => onGuide(pillar)} title="Guide d'analyse"
                style={{ marginLeft:"auto", flexShrink:0, width:18, height:18,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  background:"transparent", border:`1px solid ${color}30`,
                  borderRadius:4, cursor:"pointer", fontSize:9, color }}>🧭</button>
            )}
          </div>
        )}
        {children}
        <div style={{ marginTop:12 }}>
          {/* Bouton analyse */}
          <button
            onClick={() => setModal(pillar)}
            style={{ width:"100%", padding:"9px 12px", borderRadius:10,
              border:`1px solid ${color}44`, background:`${color}0d`,
              fontSize:11, fontWeight:800, color, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"space-between",
              transition:"background .12s" }}
            onMouseEnter={e=>e.currentTarget.style.background=`${color}1e`}
            onMouseLeave={e=>e.currentTarget.style.background=`${color}0d`}>
            <span>◈ Comprendre cette analyse</span><span>›</span>
          </button>
        </div>
      </div>
      {modal && <AnalyseModal pillar={modal} onClose={()=>setModal(null)} d={d}/>}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════
// GUIDE DE LECTURE MODAL (Niveau 5)
// ══════════════════════════════════════════════════════════════════
// ── GUIDE D'ANALYSE TECHNIQUE PAR PILIER ────────────────────────────────────
// Questions clés, indicateurs, seuils interprétatifs et logique analytique
// Calqué sur l'architecture CompassModal de AnalyseFondamentaleCard
const TECH_PILLARS = [
  { key:"structure", label:"Structure de Tendance", abbr:"STRUCT", icon:"◈", weight:40, color:C.green,  ordre:1 },
  { key:"momentum",  label:"Force / Momentum",      abbr:"MOM",    icon:"⚡", weight:25, color:C.amber,  ordre:2 },
  { key:"volume",    label:"Participation / Volume", abbr:"VOL",    icon:"◎", weight:20, color:C.olive,  ordre:3 },
  { key:"timing",    label:"Timing / Opportunité",  abbr:"TIMING", icon:"⊕", weight:15, color:C.orange, ordre:4 },
];

const TECH_ANALYSIS_ORDER = ["structure","momentum","volume","timing"];

const TECH_GUIDE = {
  structure: {
    ordre: 1,
    titre: "La tendance de fond est-elle haussière ?",
    intro: "Murphy (TAOTFM ch. 1) : 'la règle d'or de l'analyse technique est de ne jamais trader contre la tendance principale.' La première question est toujours directionnelle : dans quel sens se déplace le prix sur les trois horizons temporels ? La réponse conditionne tous les signaux suivants — un signal haussier sur un titre en tendance baissière reste un piège.",
    questions: [
      {
        q: "La tendance primaire est-elle haussière ?",
        indicateurs: ["SMA 200", "Pente OLS (β)", "R²"],
        seuil: "Prix > SMA200 = marché haussier · β positif + R² ≥ 0.50 = tendance fiable · R² < 0.30 = tendance peu exploitable",
        logique: "La SMA200 (~200 séances, ~10 mois) est la frontière universelle entre bull et bear market. Murphy (ch. 9) : 'un titre au-dessus de sa SMA200 est dans son marché haussier structural, en dessous dans son marché baissier.' La pente OLS (β) sans R² n'a pas de valeur — un β positif avec R² = 0.17 signifie que 83% de la variance est du bruit : la tendance existe sur le papier, pas dans la réalité.",
      },
      {
        q: "Les moyennes mobiles confirment-elles la direction à court et moyen terme ?",
        indicateurs: ["SMA 20", "SMA 50", "Golden Cross", "Death Cross", "Alignement des SMA"],
        seuil: "SMA20 > SMA50 > SMA200 = alignement haussier parfait · Golden Cross (SMA50 > SMA200) = confirmation LT · Death Cross = retournement baissier",
        logique: "L'alignement ordonné SMA20 > SMA50 > SMA200 est la configuration haussière de référence selon Murphy (ch. 9). Le Golden Cross — SMA50 qui franchit la SMA200 par le haut — est le signal de confirmation structurelle le plus suivi par les institutionnels. Edwards & Magee l'appelaient 'la confirmation la plus puissante de la tendance primaire.' À l'inverse, le Death Cross précède statistiquement des baisses prolongées.",
      },
      {
        q: "La progression est-elle régulière ou erratique ?",
        indicateurs: ["R²", "CAGR 5Y", "Canal de régression ±1σ / ±2σ"],
        seuil: "R² > 0.75 = tendance très ordonnée · 0.50–0.75 = exploitable · < 0.50 = chemin trop erratique · Prix dans ±1σ = zone neutre · Prix > +2σ = sur-extension rare",
        logique: "Le R² (coefficient de détermination) mesure quelle fraction de la variance du prix est expliquée par la tendance linéaire. Murphy (ch. 4) : 'c'est le signal de confiance de la régression.' Le canal ±1σ / ±2σ encadre respectivement ~68% et ~95% des observations historiques. Gray & Vogel (QM ch. 6) : les vrais compounders progressent de façon régulière — CAGR élevé ET R² élevé. Un titre très volatile avec R² faible amplifie le risque de mauvais timing.",
      },
    ],
    conclusion_analytique: "Séquence d'analyse : (1) SMA200 — au-dessus ou en dessous ? C'est le filtre de premier niveau. (2) Alignement SMA20/50/200 — le court terme confirme-t-il le long terme ? (3) R² — la tendance est-elle régulière assez pour être exploitable ? Un titre haussier (SMA200 +) avec alignement parfait et R² > 0.65 représente la structure la plus solide. Ne jamais se fier à β sans vérifier R² — Murphy (ch. 4) insiste : 'un CAGR élevé sur un chemin chaotique ne vaut rien.'",
  },

  momentum: {
    ordre: 2,
    titre: "Le mouvement accélère-t-il ou s'essouffle-t-il ?",
    intro: "Moskowitz, Ooi & Pedersen (Journal of Financial Economics, 2012) ont prouvé la persistance du momentum sur 200 ans d'histoire et plus de 40 marchés : les gagnants récents continuent statistiquement de surperformer. Gray & Vogel (Quantitative Momentum, 2016) ont affiné cette découverte : la direction du momentum ne suffit pas — sa régularité (qualité) est tout aussi prédictive. Un momentum positif mais erratique sous-performe un momentum positif et régulier.",
    questions: [
      {
        q: "Le TSMOM est-il positif et cohérent sur les trois horizons ?",
        indicateurs: ["TSMOM 12M = (P_t / P_{t−252j}) − 1", "TSMOM 6M = (P_t / P_{t−126j}) − 1", "TSMOM 3M = (P_t / P_{t−63j}) − 1"],
        seuil: "TSMOM 12M > 0 = signal principal · 12M + 6M + 3M tous positifs = confirmation forte · 12M+ / 3M− = essoufflement · 12M− = biais baissier",
        logique: "Moskowitz (JFE 2012) : le TSMOM 12M est le signal le plus robuste et le plus documenté — il capture la persistance du momentum institutionnel. Gray & Vogel (ch. 6) : la configuration 12M positif / 3M négatif est la plus commune avant consolidation — elle signale un essoufflement à court terme dans une tendance de fond intacte. Ce n'est pas un signal de vente, c'est un signal de patience.",
      },
      {
        q: "Le momentum est-il de qualité — régulier ou construit sur des pics isolés ?",
        indicateurs: ["FIP = SIGN(TSMOM_12M) × (% jours baissiers − % jours haussiers)", "% de jours de clôture positive", "Pattern : Accélération / Essoufflement / Transition"],
        seuil: "FIP ≤ 0 = 'smooth momentum' (régulier, robuste) · FIP > 0 = 'frog in the pan' (erratique, fragile) · % jours positifs > 55% = continuité saine · Pattern Accélération (3M > 6M > 12M) = signal idéal",
        logique: "Da, Gurun & Warachka (Review of Financial Studies, 2014) ont introduit le FIP : une grenouille dans l'eau qui chauffe progressivement ne réagit pas. TSMOM 12M = +60% mais construit sur 5 séances exceptionnelles au milieu d'une mer de jours baissiers — c'est un 'frog'. Gray & Vogel (ch. 6, p.118) : ces titres sous-performent les 'smooth momentum' de 3 à 5%/an sur les 3-12 mois suivants. Le FIP qualifie la conviction derrière le chiffre.",
      },
      {
        q: "Le titre surperforme-t-il son marché de référence ?",
        indicateurs: ["RS Ratio = P_titre / P_indice (base 1)", "RS Return 12M vs NASDAQ", "Position dans le classement sectoriel"],
        seuil: "RS Ratio en hausse = leadership croissant · RS Ratio > 1 et montant = force relative confirmée · RS Return 12M > 0% = surperformance annuelle",
        logique: "Jegadeesh & Titman (Journal of Finance, 1993) : les titres qui surperforment leur indice sur 3-12 mois tendent à continuer de surperformer sur les 3-12 mois suivants. C'est le momentum cross-sectionnel (relatif), distinct du TSMOM absolu mais complémentaire. Gray & Vogel : un titre avec TSMOM positif ET RS Ratio en hausse cumule les deux signaux de momentum les plus robustes empiriquement.",
      },
    ],
    conclusion_analytique: "Hiérarchie analytique : TSMOM 12M (direction) → FIP (qualité) → RS Ratio (force relative). Lire le momentum en cascade : si le 12M est négatif, le reste de l'analyse momentum n'a pas de sens haussier. Si le 12M est positif, chercher la confirmation du FIP (régularité) et du RS (leadership). La meilleure configuration : TSMOM 12M positif + FIP ≤ 0 + RS Ratio en hausse. La pire : TSMOM 12M positif + FIP élevé + RS Ratio en baisse — le chiffre brut ment sur la santé réelle du momentum.",
  },

  volume: {
    ordre: 3,
    titre: "Les institutionnels participent-ils au mouvement ?",
    intro: "Wyckoff (Studies in Tape Reading, 1910) a posé trois lois fondamentales dont la Loi 3 : 'l'effort (volume) doit être proportionnel au résultat (prix).' Murphy (TAOTFM ch. 7) : 'le volume confirme la tendance — il ne l'initie jamais, mais son absence la fragilise.' Le volume est l'empreinte digitale des mains fortes : là où le volume s'accumule, les institutions agissent.",
    questions: [
      {
        q: "Les volumes sont-ils en hausse ou en baisse par rapport à leur moyenne ?",
        indicateurs: ["Volume Ratio 5J / moy. 60J", "Volume Ratio 20J / moy. 60J", "Volume Ratio 60J / moy. 120J"],
        seuil: "Ratio > 1.2 = participation élevée (signal fort) · 0.85–1.2 = participation normale · < 0.85 = participation faible (signal fragile) · Ratio 60J décroissant = tendance structurelle de désengagement",
        logique: "Murphy (ch. 7) : 'hausse des prix + volume supérieur à la moyenne = signal fort — les institutionnels participent. Hausse des prix + volume faible = No Demand — la progression manque de conviction.' La Loi 3 de Wyckoff : si le prix monte mais que le volume baisse sur 20 séances (ratio < 0.85), c'est une divergence Effort/Résultat — la tendance se fragilise sans crier gare.",
      },
      {
        q: "Les acheteurs absorbent-ils les vendeurs sur les supports clés ?",
        indicateurs: ["Support Volume Ratio (vol. rebonds / vol. moyen)", "Résistance Volume Ratio", "Signal d'absorption (Absorption / Neutral / Distribution)"],
        seuil: "Support Vol. Ratio > 1.05 = absorption active · Résistance Vol. Ratio < 1.0 = pression vendeuse faible = résistance fragile · Signal 'Absorption' = mains fortes actives",
        logique: "Wyckoff (ch. 18-19, Villahermosa) : 'No Supply' = rebonds sur les supports accompagnés de volumes vendeurs faibles — les institutions ne soldent pas leurs positions. Le Support Volume Ratio > 1 mesure exactement cela : les acheteurs sont proportionnellement plus actifs lors des rebonds que dans le marché global. C'est la 'cause' que Wyckoff appelle accumulation discrète — la Phase B avant le markup.",
      },
      {
        q: "Dans quelle phase du cycle de Wyckoff se trouve le titre ?",
        indicateurs: ["Phase Wyckoff (Accumulation / Markup / Distribution / Markdown)", "Wyckoff Event (Spring / UTAD / Breakout…)", "Tendance Volume 20J / 60J (Rising / Falling / Neutral)"],
        seuil: "Accumulation Phase B/C = construction de cause · Markup Phase D/E = tendance en cours · Distribution Phase B/C = alerte · UTAD (Upthrust After Distribution) = piège haussier avant markdown",
        logique: "Villahermosa (The Wyckoff Methodology in Depth, 2019, ch. 14-20) : le cycle Wyckoff est la lecture la plus granulaire de la relation prix/volume. Distribution = les institutions allègent dans l'euphorie pendant que le public achète. L'UTAD est la fausse cassure haute conçue pour piéger les derniers acheteurs — le volume y est fort mais le prix retombe rapidement. C'est le signal baissier le plus dangereux à confondre avec un breakout.",
      },
    ],
    conclusion_analytique: "Ordre d'analyse volumétrique : (1) Volume Ratio 20J — le flux global confirme-t-il la tendance ? (2) Support Vol. Ratio — les mains fortes absorbent-elles les corrections ? (3) Phase Wyckoff — à quel stade du cycle sommes-nous ? Murphy : 'on n'a jamais tort de vérifier le volume avant d'agir sur un signal de prix.' La divergence Prix↑ / Volume↓ est l'un des signaux précurseurs les plus fiables de retournement selon les trois lois de Wyckoff.",
  },

  timing: {
    ordre: 4,
    titre: "Est-ce le bon moment pour entrer ?",
    intro: "Murphy (TAOTFM ch. 1) : 'même dans la meilleure tendance, acheter au mauvais moment détruit le ratio risque/récompense.' Le timing ne contredit pas la tendance — il précise le meilleur moment pour y entrer. Wilder (New Concepts in Technical Trading Systems, 1978) et Pesavento (Trade What You See) ont formalisé des outils qui mesurent objectivement l'état du momentum à court terme et l'éloignement du prix par rapport à sa valeur d'équilibre statistique.",
    questions: [
      {
        q: "Le RSI indique-t-il une zone de surachat ou de survente ?",
        indicateurs: ["RSI 14 = 100 − [100 / (1 + RS)]", "RS = moy. gains 14j / moy. pertes 14j", "Divergence RSI / Prix"],
        seuil: "RSI > 70 = surachat (correction possible) · RSI < 30 = survente (rebond possible) · En tendance forte : zone d'achat décalée à 40–55 · Divergence baissière (prix↑ + RSI↓) = essoufflement interne",
        logique: "Wilder (1978) : le RSI mesure la vitesse et l'amplitude des mouvements de prix sur 14 séances. Murphy (ch. 10) insiste sur une nuance critique : en tendance haussière très forte, le RSI peut rester entre 50 et 80 pendant des mois — appliquer mécaniquement le seuil 70 conduit à vendre trop tôt. La VRAIE puissance du RSI réside dans la divergence : prix fait un nouveau plus haut mais RSI fait un plus haut inférieur = la force interne s'érode. Murphy : 'l'une des configurations les plus précieuses de l'analyse technique.'",
      },
      {
        q: "Le prix est-il éloigné de sa droite de régression historique ?",
        indicateurs: ["Z-Score = (Prix − Droite de régression) / σ résiduel", "Position dans le canal : −2σ / −1σ / Neutre / +1σ / +2σ", "Valorisation relative (Sous-évalué / Neutre / Surévalué)"],
        seuil: "Z-Score > +2σ = sur-extension statistiquement rare (~2.5% des observations) · +1 à +2σ = attention croissante · −1 à 0σ = zone neutre à favorable · < −1σ = zone d'opportunité historique",
        logique: "Le Z-Score de régression est distinct du RSI : il mesure non pas le momentum du mouvement mais l'éloignement du prix par rapport à sa trajectoire tendancielle de long terme. Un Z-Score à +2σ signifie que le prix a quitté sa trajectoire statistique normale — le retour à la moyenne est probable, même si le délai est imprévisible. Murphy (ch. 4) : 'les bandes statistiques révèlent si un titre est temporairement trop cher par rapport à son histoire.' Combiner RSI (momentum CT) + Z-Score (éloignement LT) donne une image complète du timing.",
      },
      {
        q: "Les signaux de court, moyen et long terme convergent-ils dans la même direction ?",
        indicateurs: ["Golden Cross / Death Cross (SMA50/SMA200)", "Position prix / SMA20 / SMA50", "Signal sigma (+2σ breakout / −1σ pullback / Zone neutre)"],
        seuil: "Prix > SMA20 > SMA50 > SMA200 = convergence haussière totale · Pullback sur SMA20 en tendance = meilleur point d'entrée · Breakout +2σ = signal fort mais risque d'extension",
        logique: "Murphy (TAOTFM ch. 15) : 'quand le court, le moyen et le long terme pointent dans la même direction, le signal est extrêmement fiable.' La convergence est rare — c'est précisément pour cela qu'elle est précieuse. Un pullback sur la SMA20 dans une tendance haussière (SMA20 > SMA50 > SMA200) est le timing idéal décrit au ch. 9 : le prix revient tester son premier niveau de support dynamique avant de repartir. C'est l'entrée avec le meilleur ratio risque/récompense.",
      },
    ],
    conclusion_analytique: "Séquence de timing : RSI → Z-Score → Convergence des SMAs. Si les trois signaux sont défavorables simultanément (RSI > 70 + Z-Score > +2σ + prix très au-dessus des SMAs), le ratio risque/récompense est dégradé même pour un titre en excellente tendance. Le timing optimal selon Murphy et Pesavento : RSI entre 40 et 60 (zone de force, pas de surachat) + Z-Score entre 0 et +1σ (pas sur-étendu) + pullback sur SMA20 ou SMA50 (support dynamique testé). Cette confluence est rare — attendre vaut mieux qu'entrer au mauvais prix.",
  },
};

function GuideModal({ onClose, currentPillar, d }) {
  // Toujours ouvrir sur la grille des piliers
  const [selectedPillar, setSelectedPillar] = useState(null);
  const validKeys = ["structure","momentum","volume","timing"];
  const highlightedPillar = validKeys.includes(currentPillar) ? currentPillar : null;
  // doc archi générique §5/§6 : GUIDE lit la même structure de données que
  // Main/Cours pour construire "Ce que vous voyez" avec les vraies valeurs.
  const symbol = d?.symbol || d?.company_name || "NVDA";

  const guide  = selectedPillar ? TECH_GUIDE[selectedPillar]           : null;
  const pillar = selectedPillar ? TECH_PILLARS.find(p => p.key === selectedPillar) : null;

  const selectPillar = key => setSelectedPillar(key);
  const goHome       = ()  => setSelectedPillar(null);

  const idx     = selectedPillar ? TECH_ANALYSIS_ORDER.indexOf(selectedPillar) : -1;
  const prevKey = idx > 0                                ? TECH_ANALYSIS_ORDER[idx - 1] : null;
  const nextKey = idx < TECH_ANALYSIS_ORDER.length - 1  ? TECH_ANALYSIS_ORDER[idx + 1] : null;
  const prevP   = prevKey ? TECH_PILLARS.find(p => p.key === prevKey) : null;
  const nextP   = nextKey ? TECH_PILLARS.find(p => p.key === nextKey) : null;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", h); };
  }, [onClose]);

  // ── Grille d'accueil ─────────────────────────────────────────────────────
  const PillarGrid = () => (
    <div style={{ flex:1, overflowY:"auto" }}>
      {/* Intro */}
      <div style={{ padding:"12px 16px 10px", borderBottom:"1px solid var(--sep)",
        background:`${C.green}06` }}>
        <div style={{ fontFamily:"ui-monospace,monospace", fontSize:7.5, color:C.green,
          textTransform:"uppercase", letterSpacing:".12em", marginBottom:6, fontWeight:700 }}>
          Comment utiliser ce guide
        </div>
        <p style={{ fontSize:"clamp(10px,1.3vw,11.5px)", color:"var(--tx)", lineHeight:1.7, margin:0 }}>
          L'analyse technique suit une logique de <strong style={{color:"var(--ts)"}}>filtre
          séquentiel</strong> : commencer par la direction (SMA200, alignement, R²), puis la
          force (TSMOM, FIP, RS Ratio), puis la conviction du volume (ratios, Wyckoff), et conclure
          par le timing d'entrée (RSI, Z-Score, convergence SMAs). Un signal haussier sur un titre
          baissier reste un piège — Murphy ch. 1.
        </p>
      </div>
      {/* Grille 2×2 */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, padding:"12px 14px" }}>
        {TECH_PILLARS.map((p, i) => {
          const isCurrent = highlightedPillar === p.key;
          return (
            <button key={p.key} onClick={() => selectPillar(p.key)} style={{
              display:"flex", flexDirection:"column", alignItems:"flex-start",
              padding:"10px 12px", gap:4,
              border:`1.5px solid ${isCurrent ? p.color+"80" : p.color+"40"}`,
              background: isCurrent ? `${p.color}09` : `${p.color}06`,
              borderRadius:10, cursor:"pointer", transition:"all .15s", position:"relative",
              outline: isCurrent ? `2px solid ${p.color}35` : "none", outlineOffset:1,
            }}>
              <div style={{ position:"absolute", top:6, right:8,
                fontFamily:"ui-monospace,monospace", fontSize:7, fontWeight:800,
                color:"var(--mu)", opacity:.6 }}>{i + 1}</div>
              {isCurrent && (
                <div style={{ position:"absolute", top:6, left:8,
                  fontFamily:"ui-monospace,monospace", fontSize:6, fontWeight:700,
                  color:"#fff", background:p.color, padding:"1px 5px", borderRadius:4 }}>actif</div>
              )}
              <span style={{ fontSize:15, color:p.color, marginTop: isCurrent ? 8 : 0 }}>{p.icon}</span>
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:"clamp(7.5px,1.8vw,9.5px)",
                fontWeight:700, color:p.color, lineHeight:1.2 }}>{p.label}</div>
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:7, color:"var(--mu)" }}>
                {p.weight}% · {{
                  structure:"SMA · R²",
                  momentum: "TSMOM · FIP · RS",
                  volume:   "Vol. Ratio · Wyckoff",
                  timing:   "RSI · Z-Score · SMAs",
                }[p.key]}
              </div>
              <div style={{ fontSize:"clamp(8px,1.1vw,9.5px)", color:"var(--tx)", lineHeight:1.4,
                textAlign:"left" }}>
                {TECH_GUIDE[p.key]?.titre}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="compass-overlay" style={{
      position:"fixed", inset:0, zIndex:9000,
      width:"100vw", height:"100vh",
      background:"rgba(0,0,0,.55)",
      backdropFilter:"blur(5px)", display:"flex", alignItems:"center", justifyContent:"center",
      padding:"clamp(8px,3vw,20px)", boxSizing:"border-box",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="compass-modal" style={{
        background:"var(--card-bg)", border:"1px solid var(--card-bdr)",
        borderRadius:"clamp(10px,1.5vw,18px)",
        width:"min(960px,96vw)", height:"min(680px,88vh)",
        display:"flex", flexDirection:"column",
        boxShadow:"0 24px 80px rgba(0,0,0,.3)",
        overflow:"hidden",
      }}>

        {/* ══ HEADER — une seule ligne, identique au screenshot ══ */}
        <div style={{
          padding:"10px 14px", borderBottom:"1px solid var(--sep)",
          display:"flex", alignItems:"center", gap:8, flexShrink:0,
          background: selectedPillar && pillar
            ? `linear-gradient(90deg,${pillar.color}10 0%,transparent 60%)`
            : `linear-gradient(90deg,${C.green}10 0%,transparent 60%)`,
        }}>

          {/* ← Piliers (visible seulement sur vue pilier) */}
          {selectedPillar ? (
            <button onClick={goHome} style={{
              display:"flex", alignItems:"center", gap:5, padding:"5px 10px",
              background:"var(--sur)", border:"1px solid var(--sep)",
              borderRadius:8, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap",
            }}>
              <span style={{ fontSize:12, color:"var(--mu)" }}>←</span>
              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, color:"var(--mu)" }}>Piliers</span>
            </button>
          ) : (
            <div style={{ width:32, height:32, borderRadius:8,
              background:`${C.green}18`, border:`1px solid ${C.green}28`,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              🧭
            </div>
          )}

          {/* Badge N/4 + icône + label + poids + score — inline */}
          <div style={{ flex:1, minWidth:0, display:"flex", alignItems:"center",
            gap:6, flexWrap:"nowrap", overflow:"hidden" }}>
            {selectedPillar && pillar ? (
              <>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:7.5, fontWeight:800,
                  color:"#fff", background:pillar.color,
                  padding:"2px 7px", borderRadius:4, flexShrink:0 }}>
                  {idx + 1} / 4
                </span>
                <span style={{ fontSize:11, color:pillar.color, flexShrink:0 }}>{pillar.icon}</span>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:"clamp(9px,1.5vw,12px)",
                  fontWeight:700, color:pillar.color,
                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                  {pillar.label}
                </span>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, color:"var(--mu)",
                  whiteSpace:"nowrap", flexShrink:0 }}>
                  · {pillar.weight}% du TScore
                </span>
              </>
            ) : (
              <div>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:"clamp(9px,1.5vw,12px)",
                  fontWeight:700, color:"var(--ts)" }}>Guide d'analyse technique</div>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:8, color:"var(--mu)", marginTop:1 }}>
                  4 piliers · Filtre séquentiel · TScore Invelps
                </div>
              </div>
            )}
          </div>

          {/* ← → navigation (visible seulement sur vue pilier) */}
          {selectedPillar && (
            <div style={{ display:"flex", gap:4, flexShrink:0 }}>
              <button onClick={() => prevP && selectPillar(prevKey)} disabled={!prevP} style={{
                width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
                background: prevP ? "var(--sur)" : "transparent",
                border:`1px solid ${prevP ? "var(--sep)" : "transparent"}`,
                borderRadius:8, cursor: prevP ? "pointer" : "default",
                fontSize:13, color: prevP ? prevP.color : "var(--mu)", opacity: prevP ? 1 : .3,
              }}>←</button>
              <button onClick={() => nextP && selectPillar(nextKey)} disabled={!nextP} style={{
                width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
                background: nextP ? `${nextP.color}15` : "transparent",
                border:`1px solid ${nextP ? nextP.color+"40" : "transparent"}`,
                borderRadius:8, cursor: nextP ? "pointer" : "default",
                fontSize:13, color: nextP ? nextP.color : "var(--mu)", opacity: nextP ? 1 : .3,
              }}>→</button>
            </div>
          )}

          {/* × fermer */}
          <button onClick={onClose} style={{
            width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
            background:"var(--sur)", border:"1px solid var(--sep)",
            borderRadius:8, cursor:"pointer", fontSize:16, color:"var(--mu)", flexShrink:0,
          }}>×</button>
        </div>

        {/* ══ CORPS ══ */}
        <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>

          {/* ── Vue accueil ── */}
          {!selectedPillar && (
            <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
              <PillarGrid/>
            </div>
          )}

          {/* ── Vue pilier : scroll direct, sans sidebar ── */}
          {selectedPillar && guide && pillar && (
            <div style={{ flex:1, overflowY:"auto", padding:"clamp(14px,2vw,20px) clamp(14px,2.5vw,24px)" }}>

              {/* Titre principal + intro — style screenshot */}
              <div style={{ marginBottom:16, paddingBottom:14, borderBottom:"1px solid var(--sep)" }}>
                <h2 style={{ fontFamily:"ui-sans-serif,system-ui,sans-serif",
                  fontSize:"clamp(16px,3vw,20px)", fontWeight:700,
                  color:"var(--ts)", lineHeight:1.25, margin:"0 0 10px" }}>
                  {guide.titre}
                </h2>
                <p style={{ fontSize:"clamp(10.5px,1.4vw,12px)", color:"var(--tx)",
                  lineHeight:1.75, margin:0 }}>
                  {guide.intro}
                </p>
              </div>

              {/* Label QUESTIONS À SE POSER */}
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:7.5, fontWeight:700, color:C.amber,
                textTransform:"uppercase", letterSpacing:".14em", marginBottom:10 }}>
                Questions à se poser
              </div>

              {/* Cards Q */}
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
                {guide.questions.map((item, qi) => (
                  <div key={qi} style={{
                    border:"1px solid var(--sep)",
                    borderLeft:`3px solid ${pillar.color}`,
                    borderRadius:"0 10px 10px 0",
                    background:"var(--sur)",
                    padding:"10px 14px",
                  }}>
                    {/* Badge Q + question */}
                    <div style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:8 }}>
                      <span style={{
                        fontFamily:"ui-monospace,monospace", fontSize:7, fontWeight:700,
                        color:"#fff", background:pillar.color,
                        padding:"2px 6px", borderRadius:4,
                        flexShrink:0, marginTop:1,
                      }}>Q{qi + 1}</span>
                      <span style={{ fontSize:"clamp(11px,1.5vw,13px)", fontWeight:700,
                        color:"var(--ts)", lineHeight:1.3 }}>{item.q}</span>
                    </div>

                    {/* INDICATEURS : chips */}
                    <div style={{ display:"flex", alignItems:"center", gap:6,
                      flexWrap:"wrap", marginBottom:8 }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:7,
                        color:"var(--mu)", textTransform:"uppercase",
                        letterSpacing:".05em" }}>Indicateurs :</span>
                      {item.indicateurs.map((ind, ri) => (
                        <span key={ri} style={{
                          fontFamily:"ui-monospace,monospace", fontSize:8.5, fontWeight:700,
                          color:pillar.color,
                          background:`${pillar.color}12`,
                          border:`1px solid ${pillar.color}35`,
                          padding:"2px 8px", borderRadius:4,
                        }}>{ind}</span>
                      ))}
                    </div>

                    {/* Bloc seuils — fond crème/teinté amber */}
                    <div style={{
                      background:`${C.amber}09`, border:`1px solid ${C.amber}22`,
                      borderRadius:8, padding:"6px 10px", marginBottom:8,
                    }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:7, color:C.amber,
                        textTransform:"uppercase", letterSpacing:".08em",
                        marginRight:6, fontWeight:700 }}>Seuils ·</span>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9, color:"var(--tx)",
                        lineHeight:1.6 }}>{item.seuil}</span>
                    </div>

                    {/* Logique — italique gris */}
                    <p style={{ fontSize:"clamp(9.5px,1.2vw,11px)", color:"var(--mu)",
                      lineHeight:1.7, margin:0, fontStyle:"italic" }}>
                      {item.logique}
                    </p>
                  </div>
                ))}
              </div>

              {/* Conclusion analytique */}
              <div style={{
                background:`${C.green}09`, border:`1px solid ${C.green}28`,
                borderRadius:10, padding:"11px 14px", marginBottom:14,
              }}>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:7.5, fontWeight:700,
                  color:C.green2, textTransform:"uppercase",
                  letterSpacing:".12em", marginBottom:6 }}>
                  ◈ Conclusion analytique
                </div>
                <p style={{ fontSize:"clamp(10px,1.3vw,11.5px)", color:"var(--tx)",
                  lineHeight:1.72, margin:0 }}>
                  {guide.conclusion_analytique}
                </p>
              </div>

              {/* Ce que vous voyez — doc archi générique §5 : GUIDE doit expliquer
                  les éléments affichés avec les vraies valeurs du titre en cours,
                  pas seulement la théorie générique ci-dessus. */}
              {d && <LiveCaseStudy pillar={selectedPillar} d={d} symbol={symbol}/>}

              {/* Navigation bas de page */}
              <div style={{ display:"flex", justifyContent:"space-between",
                alignItems:"center", paddingTop:10, borderTop:"1px solid var(--sep)" }}>
                {prevP ? (
                  <button onClick={() => selectPillar(prevKey)} style={{
                    display:"flex", alignItems:"center", gap:7, padding:"7px 13px",
                    background:"var(--sur)", border:"1px solid var(--sep)",
                    borderRadius:8, cursor:"pointer",
                  }}>
                    <span style={{ fontSize:12, color:"var(--mu)" }}>←</span>
                    <div style={{ textAlign:"left" }}>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:7, color:"var(--mu)" }}>
                        {TECH_ANALYSIS_ORDER.indexOf(prevKey) + 1} / 4
                      </div>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9,
                        color:prevP.color, fontWeight:700 }}>{prevP.label}</div>
                    </div>
                  </button>
                ) : <div/>}
                {nextP && (
                  <button onClick={() => selectPillar(nextKey)} style={{
                    display:"flex", alignItems:"center", gap:7, padding:"7px 13px",
                    background:`${nextP.color}10`, border:`1px solid ${nextP.color}30`,
                    borderRadius:8, cursor:"pointer",
                  }}>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:7, color:"var(--mu)" }}>
                        {TECH_ANALYSIS_ORDER.indexOf(nextKey) + 1} / 4
                      </div>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9,
                        color:nextP.color, fontWeight:700 }}>{nextP.label}</div>
                    </div>
                    <span style={{ fontSize:12, color:nextP.color }}>→</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tog({ label, active, color, tip, onChange }) {
  const btn = (
    <button onClick={onChange} style={{
      display:"flex", alignItems:"center", gap:5, borderRadius:8,
      border:`1px solid ${active?color+"55":"var(--card-bdr)"}`,
      background: active ? color+"14" : "transparent",
      padding:"5px 10px", fontSize:10, fontWeight:800,
      color: active ? color : "var(--mu)", transition:"all .12s", cursor:"pointer",
    }}>
      <span style={{ width:7, height:7, borderRadius:999, flexShrink:0,
        background: active ? color : "var(--sep)" }}/>
      {label}
    </button>
  );
  return tip ? <Tip {...(typeof tip==="string"?{body:tip}:tip)}>{btn}</Tip> : btn;
}


// ══════════════════════════════════════════════════════════════════
// 11c. COURS MODAL — Analyse Technique
// ══════════════════════════════════════════════════════════════════
function CourseModal({ onClose, isDark, d }) {
  const [activeChap, setActiveChap] = useState("intro");
  const [menuOpen, setMenuOpen] = useState(false);
  // doc archi générique §4/§5/§6 : COURS doit lire la même structure de
  // données que Main/Guide (une seule source), pas une prop distincte.
  const symbol = d?.symbol || d?.company_name || "NVDA";

  const CHAPTERS = [
    { id:"intro",     num:"0",  label:"Introduction" },
    { id:"postulats", num:"1",  label:"3 Postulats fondateurs" },
    { id:"structure", num:"2",  label:"Structure de Tendance" },
    { id:"momentum",  num:"3",  label:"Momentum" },
    { id:"volume",    num:"4",  label:"Volume & Wyckoff" },
    { id:"timing",    num:"5",  label:"Timing d'entrée" },
    { id:"tscore",    num:"6",  label:"TScore Invelps" },
    { id:"synth",     num:"7",  label:"Synthèse pratique" },
  ];

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", h); };
  }, [onClose]);

  useEffect(() => {
    const container = document.getElementById("tm-course-scroll");
    if (!container) return;
    const timer = setTimeout(() => {
      const handleScroll = () => {
        const sections = container.querySelectorAll("[data-csec]");
        let current = "intro";
        sections.forEach(el => {
          const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
          if (top <= 80) current = el.dataset.csec;
        });
        setActiveChap(prev => {
          if (prev === current) return prev;
          setTimeout(() => {
            document.getElementById(`tm-sc-${current}`)?.scrollIntoView({ behavior:"smooth", block:"nearest" });
          }, 0);
          return current;
        });
      };
      container.addEventListener("scroll", handleScroll, { passive:true });
      handleScroll();
      return () => container.removeEventListener("scroll", handleScroll);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = id => {
    const container = document.getElementById("tm-course-scroll");
    const el = container?.querySelector(`#tm-cs-${id}`);
    if (el && container) container.scrollTo({ top: el.offsetTop - 20, behavior:"smooth" });
    setActiveChap(id);
    setMenuOpen(false);
  };

  const navBg  = isDark ? "#0c0b09" : "#f5f2ec";
  const navBdr = isDark ? "#1e1e26" : "#d4cfc4";
  const bg     = isDark ? "#0d1117" : "#f8f7f4";
  const card   = isDark ? "rgba(15,23,42,.96)" : "#ffffff";
  const bdr    = isDark ? "rgba(148,163,184,.12)" : "rgba(30,40,34,.08)";
  const ts     = isDark ? "#e2e8f0" : "#1f2a24";
  const tx     = isDark ? "#cbd5e1" : "#2d3b34";
  const mu     = isDark ? "#64748b" : "#6f7b74";
  const go     = C.green;
  const am     = C.amber;
  const re     = C.red;
  const su     = isDark ? "rgba(255,255,255,.025)" : "rgba(0,0,0,.018)";
  const bo     = isDark ? "rgba(148,163,184,.12)" : "rgba(30,40,34,.07)";

  // ── Micro-composants ───────────────────────────────────────────
  const Sec = ({ id, num, tag, title }) => (
    <div id={`tm-cs-${id}`} data-csec={id} style={{ paddingTop:"2rem", scrollMarginTop:"60px", maxWidth:780 }}>
      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:".43rem", letterSpacing:".2em",
        textTransform:"uppercase", color:go, marginBottom:".25rem" }}>{num} — {tag}</div>
      <h2 style={{ fontFamily:"Georgia,serif", fontSize:"clamp(1.05rem,2.4vw,1.35rem)",
        color:ts, fontWeight:400, margin:"0 0 .65rem",
        borderBottom:`2px solid ${go}28`, paddingBottom:".3rem" }}>{title}</h2>
    </div>
  );
  const P = ({ children }) => (
    <p style={{ fontSize:".81rem", color:tx, lineHeight:1.8, margin:"0 0 .55rem", maxWidth:720 }}>{children}</p>
  );
  const B = ({ children }) => <strong style={{ color:ts }}>{children}</strong>;
  const Ref = ({ src }) => (
    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:".43rem", color:go, marginLeft:4, opacity:.8 }}>[{src}]</span>
  );
  const Bx = ({ label, color, children }) => (
    <div style={{ margin:".5rem 0", padding:".65rem .88rem",
      background:`${color||go}0c`, border:`1px solid ${color||go}26`, borderRadius:8, maxWidth:780 }}>
      {label && <div style={{ fontFamily:"ui-monospace,monospace", fontSize:".42rem", color:color||go,
        textTransform:"uppercase", letterSpacing:".1em", marginBottom:".28rem", fontWeight:700 }}>{label}</div>}
      <div style={{ fontSize:".78rem", color:tx, lineHeight:1.72 }}>{children}</div>
    </div>
  );
  const Ind = ({ name, formula, seuil, note }) => (
    <div style={{ margin:".5rem 0", border:`1px solid ${bo}`, borderRadius:8, overflow:"hidden" }}>
      <div style={{ padding:"6px 12px", background:su, display:"flex", alignItems:"center", gap:8,
        borderBottom:`1px solid ${bo}` }}>
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:".62rem", fontWeight:700, color:ts }}>{name}</span>
        {formula && <span style={{ fontFamily:"ui-monospace,monospace", fontSize:".55rem", color:go, marginLeft:"auto" }}>{formula}</span>}
      </div>
      <div style={{ padding:"8px 12px" }}>
        {seuil && <div style={{ fontFamily:"ui-monospace,monospace", fontSize:".55rem", color:am, marginBottom:".3rem" }}>
          Seuils : {seuil}</div>}
        {note && <div style={{ fontSize:".75rem", color:tx, lineHeight:1.65, fontStyle:"italic" }}>{note}</div>}
      </div>
    </div>
  );
  // ── Exercices dynamiques (doc archi générique §4/§5) — réutilise le
  // pool QCM déjà écrit pour EduModal (LEVELS.*.modules[].qcm) + le même
  // mécanisme dyn()/QCM, appliqué ici dans la vraie vue Cours plutôt que
  // dans les bulles "?" séparées où il ne s'affichait pas jusqu'ici.
  const CoursExercices = ({ levelId, moduleId, anchorId }) => {
    const mod = LEVELS[levelId]?.modules?.find(m => m.id === moduleId);
    if (!mod?.qcm?.length) return null;
    return (
      <div style={{ marginTop:"1.5rem" }}>
        <div style={{ fontFamily:"ui-monospace,monospace", fontSize:9, textTransform:"uppercase",
          letterSpacing:".18em", color:mu, marginBottom:"1rem",
          paddingTop:"1.5rem", borderTop:`1px solid ${bo}` }}>
          Exercices — appliqués sur {symbol}
        </div>
        {mod.qcm.map((q, i) => (
          <QCM key={i} qid={`cours-${anchorId}-q${i+1}`} q={dyn(q.q, symbol)}
            choices={q.choices.map(ch => ({ ...ch, l:dyn(ch.l, symbol), f:dyn(ch.f, symbol) }))}/>
        ))}
      </div>
    );
  };
  const Q = ({ q, options, correct, expl }) => (
    <div style={{ margin:".65rem 0", border:`1px solid ${go}22`, borderRadius:8, overflow:"hidden" }}>
      <div style={{ padding:"8px 12px", background:`${go}09`,
        fontSize:".77rem", fontWeight:700, color:ts, borderBottom:`1px solid ${go}22` }}>{q}</div>
      <div style={{ padding:"6px 10px", display:"flex", flexDirection:"column", gap:4 }}>
        {options.map((o, i) => (
          <div key={i} style={{ display:"flex", gap:7, alignItems:"flex-start" }}>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:".55rem", fontWeight:800, flexShrink:0, marginTop:2,
              color: i === correct ? go : mu }}>{String.fromCharCode(65+i)}.</span>
            <span style={{ fontSize:".75rem", color: i === correct ? ts : mu,
              fontWeight: i === correct ? 700 : 400 }}>{o}</span>
            {i === correct && <span style={{ color:go, fontWeight:800, fontSize:".7rem", flexShrink:0 }}>✓</span>}
          </div>
        ))}
        {expl && (
          <div style={{ marginTop:6, paddingTop:6, borderTop:`1px dashed ${bo}`,
            fontSize:".72rem", color:mu, lineHeight:1.65, fontStyle:"italic" }}>{expl}</div>
        )}
      </div>
    </div>
  );

  return (
    // Corrigé : QCM/ModuleScore lisent useContext(ScoreCtx) qui vaut null
    // hors Provider ("Right side of assignment cannot be destructured").
    // CourseModal doit fournir son propre ScoreProv, comme EduModal.
    <ScoreProv>
    <div style={{ position:"fixed", inset:0, zIndex:10000,
      width:"100vw", height:"100vh",
      background:"rgba(0,0,0,.88)",
      backdropFilter:"blur(6px)", display:"flex", flexDirection:"column",
      boxSizing:"border-box" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      {/* ── Header compact ── */}
      <div style={{ flexShrink:0, background:navBg, borderBottom:`1px solid ${navBdr}`,
        display:"flex", alignItems:"center", padding:"10px 16px", gap:10 }}>
        <div style={{ width:32, height:32, borderRadius:8,
          background:`${go}18`, border:`1px solid ${go}28`,
          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={go}
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
            <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
          </svg>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9, fontWeight:800,
              color:go, textTransform:"uppercase", letterSpacing:".12em" }}>COURS 4.1</span>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9, color:mu }}>
              — Analyse Technique · Timing de Marché
            </span>
          </div>
          <div style={{ fontFamily:"ui-monospace,monospace", fontSize:7.5, color:mu, marginTop:2 }}>
            8 chapitres · Murphy · Moskowitz · Wyckoff · Wilder
          </div>
        </div>
        <button onClick={onClose} style={{
          width:34, height:34, flexShrink:0, display:"flex", alignItems:"center",
          justifyContent:"center",
          background:isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
          border:`1px solid ${navBdr}`, borderRadius:8, cursor:"pointer",
          fontSize:16, color:mu,
        }}>×</button>
      </div>

      {/* ── Contenu scroll pleine largeur ── */}
      <div id="tm-course-scroll" style={{ flex:1, overflowY:"auto", background:bg,
        padding:"clamp(16px,4vw,40px)" }}>
        <div style={{ maxWidth:800, margin:"0 auto" }}>

            {/* 0 — Introduction */}
            <Sec id="intro" num="0" tag="Introduction" title="L'analyse technique : lire le marché plutôt que l'entreprise"/>
            <P>L'analyse technique (AT) est l'étude des <B>cours boursiers et des volumes</B> dans le but de prévoir les mouvements futurs des prix. Elle repose sur un postulat radical : <B>tout ce qui est connaissable est déjà dans le prix</B>.</P>
            <Bx label="Pourquoi étudier l'analyse technique ?" color={go}>
              Pour un investisseur fondamental, l'AT répond à une question que les DCF ne peuvent pas résoudre : <B>quand entrer ?</B> Damodaran reconnaît qu'un titre peut être fondamentalement sous-évalué et continuer de baisser pendant 24 mois. L'AT fournit le timing que le fondamental ne donne pas. Murphy (ch. 1) : «&nbsp;le fondamental dit <em>quoi acheter</em>, le technique dit <em>quand acheter</em>.&nbsp;»
            </Bx>

            {/* 1 — Postulats */}
            <Sec id="postulats" num="1" tag="Fondements" title="Les 3 postulats fondateurs"/>
            <P><B>Postulat 1 — Le marché intègre tout.</B> Chaque prix de clôture reflète l'ensemble des informations disponibles. Murphy (ch. 1) : «&nbsp;le marché sait tout avant vous.&nbsp;» <Ref src="Murphy, TAOTFM ch. 1"/></P>
            <P><B>Postulat 2 — Les prix évoluent en tendances.</B> Un titre en hausse a statistiquement plus de chances de continuer à monter. C'est la base empirique de l'effet momentum documenté par Jegadeesh & Titman (1993). <Ref src="Jegadeesh & Titman, JoF 1993"/></P>
            <P><B>Postulat 3 — L'histoire se répète.</B> Les patterns reflètent la psychologie humaine — peur et cupidité — qui restent constants. <Ref src="Edwards & Magee, TAST 1948"/></P>
            <Q q="Murphy dit que 'le marché intègre tout'. Pourquoi une action peut-elle baisser sans aucune annonce officielle ?"
              options={[
                "Par hasard — les marchés sont aléatoires à court terme.",
                "Des acteurs informés vendent avant que l'information soit publique, laissant des traces dans le prix et le volume.",
                "Le cours suit toujours les SMAs avec un délai de 10 séances.",
                "L'analyse fondamentale a détecté un problème dans les comptes.",
              ]}
              correct={1}
              expl="Wyckoff : les institutions laissent inévitablement des empreintes dans le prix et le volume bien avant les annonces officielles."/>

            {/* 2 — Structure */}
            <Sec id="structure" num="2" tag="Pilier 1 · 40%" title="Structure de Tendance : direction, régularité, fiabilité"/>
            <P>La première question est <B>directionnelle</B> : le titre est-il au-dessus ou en dessous de sa SMA200 ? C'est la frontière universelle entre marché haussier et baissier. Murphy (ch. 9). <Ref src="Murphy, TAOTFM ch. 9"/></P>
            <Ind name="SMA 200 jours" formula="Moy. 200 derniers cours"
              seuil="Prix > SMA200 = bull · Prix < SMA200 = bear · Golden Cross (SMA50>SMA200) = confirmation"
              note="Référence universelle des institutionnels. Un Golden Cross (SMA50 > SMA200) est le signal de confirmation structurelle le plus suivi."/>
            <Ind name="Régression OLS + R²" formula="P_t = α + β·t"
              seuil="R² > 0.65 = fiable · 0.50–0.65 = exploitable · < 0.50 = trop erratique"
              note="β sans R² n'a aucune valeur. Un CAGR élevé avec R² = 0.17 signifie que 83% de la variance est du bruit — ce titre monte sur le papier mais descend souvent dans la réalité."/>
            <Ind name="Canal statistique ±1σ / ±2σ" formula="Droite ± écart-type des résidus"
              seuil="Prix dans ±1σ = normal · +2σ = sur-extension rare (~2.5% des observations)"
              note="95% des observations historiques se trouvent dans le canal ±2σ. Un prix à +2σ est statistiquement rare — signal de prudence, pas d'achat."/>
            <Bx label="Séquence d'analyse structure" color={C.green}>
              <B>(1)</B> SMA200 : au-dessus ou en dessous ? <B>(2)</B> Alignement SMA20 &gt; SMA50 &gt; SMA200 : le court terme confirme-t-il le long terme ? <B>(3)</B> R² : la tendance est-elle exploitable ? Un titre haussier avec alignement parfait et R² &gt; 0.65 représente la configuration la plus solide.
            </Bx>
            {d && <LiveCaseStudy pillar="structure" d={d} symbol={symbol}/>}
            <CoursExercices levelId="intermediaire" moduleId="i1" anchorId="structure"/>

            {/* 3 — Momentum */}
            <Sec id="momentum" num="3" tag="Pilier 2 · 25%" title="Force & Momentum : direction, qualité, force relative"/>
            <P>Moskowitz, Ooi & Pedersen (2012) ont prouvé la persistance du momentum sur <B>200 ans</B> d'histoire et plus de 40 marchés. Gray & Vogel : la <B>régularité</B> du momentum est tout aussi prédictive que sa direction. <Ref src="Moskowitz, Ooi & Pedersen, JFE 2012"/></P>
            <Ind name="TSMOM 12M" formula="(P_t / P_{t-252j}) − 1"
              seuil="TSMOM 12M > 0 = signal principal · 12M+6M+3M tous positifs = confirmation forte"
              note="Le signal le plus robuste et le plus documenté. La configuration 12M+/3M− signale un essoufflement à court terme dans une tendance de fond intacte."/>
            <Ind name="FIP — Frog In The Pan" formula="SIGN(TSMOM) × (% jours baissiers − % haussiers)"
              seuil="FIP ≤ 0 = smooth momentum (régulier) · FIP > 0 = erratique (fragile)"
              note="Da, Gurun & Warachka (2014) : un TSMOM 12M de +60% construit sur 5 séances exceptionnelles sous-performe de 3–5%/an vs smooth momentum. Le FIP qualifie la conviction derrière le chiffre."/>
            <Ind name="RS Ratio — Force Relative" formula="P_titre / P_indice (base 1)"
              seuil="RS en hausse = leadership croissant · RS > 1 et montant = force confirmée"
              note="Jegadeesh & Titman (1993) : les titres qui surperforment leur indice sur 3–12 mois tendent à continuer. TSMOM+ ET RS Ratio en hausse = double signal de momentum."/>
            {d && <LiveCaseStudy pillar="momentum" d={d} symbol={symbol}/>}
            <CoursExercices levelId="intermediaire" moduleId="i2" anchorId="momentum"/>

            {/* 4 — Volume */}
            <Sec id="volume" num="4" tag="Pilier 3 · 20%" title="Volume & Wyckoff : l'empreinte des mains fortes"/>
            <P>Wyckoff (1910) : Loi de l'Effort et du Résultat — <B>le volume (effort) doit être proportionnel au mouvement de prix (résultat)</B>. Murphy (ch. 7) : «&nbsp;le volume confirme la tendance — son absence la fragilise.&nbsp;» <Ref src="Murphy, TAOTFM ch. 7"/></P>
            <Ind name="Volume Ratio 20J / moy. 60J" formula="Vol_20j / Vol_60j"
              seuil="Ratio > 1.2 = participation élevée · 0.85–1.2 = normale · < 0.85 = faible"
              note="Hausse de prix + volume > moyenne = signal fort (institutionnels présents). Hausse de prix + volume faible = No Demand — la progression manque de conviction."/>
            <Bx label="Cycle de Wyckoff — 4 phases" color={C.olive}>
              <B>Accumulation</B> : institutions achètent discrètement, prix en range. <B>Markup</B> : tendance haussière établie. <B>Distribution</B> : institutions allègent dans l'euphorie — signal de danger. <B>Markdown</B> : baisse prolongée. L'<B>UTAD</B> (Upthrust After Distribution) est la fausse cassure haute conçue pour piéger les derniers acheteurs.
            </Bx>
            {d && <LiveCaseStudy pillar="volume" d={d} symbol={symbol}/>}
            <CoursExercices levelId="avance" moduleId="a1" anchorId="volume"/>

            {/* 5 — Timing */}
            <Sec id="timing" num="5" tag="Pilier 4 · 15%" title="Timing d'entrée : RSI, Z-Score, convergence SMAs"/>
            <P>Murphy (ch. 1) : «&nbsp;même dans la meilleure tendance, acheter au mauvais moment détruit le ratio risque/récompense.&nbsp;» Le timing ne contredit pas la tendance — il précise le <B>meilleur moment pour y entrer</B>.</P>
            <Ind name="RSI 14" formula="100 − [100 / (1 + RS)] · RS = moy.gains14j / moy.pertes14j"
              seuil="RSI > 70 = surachat · < 30 = survente · En tendance forte : zone 40–55 = zone d'achat optimale"
              note="La vraie puissance du RSI : la divergence baissière (prix↑ + RSI↓) signale un essoufflement interne. Ne pas appliquer mécaniquement 70 en tendance forte — le RSI peut rester > 60 pendant des mois."/>
            <Ind name="Z-Score (régression)" formula="(Prix − Droite OLS) / σ résiduel"
              seuil="Z > +2σ = sur-extension rare · 0 à +1σ = zone favorable · < −1σ = opportunité"
              note="Mesure l'éloignement du prix par rapport à sa trajectoire tendancielle. Un Z > +2σ : le retour vers la droite est probable, même si le délai est imprévisible. Combiner RSI (CT) + Z-Score (LT)."/>
            <Bx label="Timing optimal — confluence des 3 signaux" color={C.orange}>
              <B>Configuration idéale</B> : RSI entre 40 et 60 (zone de force, non surachat) + Z-Score entre 0 et +1σ (pas sur-étendu) + pullback sur SMA20 ou SMA50 (support dynamique testé). Cette confluence est rare — c'est précisément pour cela qu'elle est précieuse.
            </Bx>
            {d && <LiveCaseStudy pillar="timing" d={d} symbol={symbol}/>}
            <CoursExercices levelId="avance" moduleId="a2" anchorId="timing"/>

            {/* 6 — TScore */}
            <Sec id="tscore" num="6" tag="Synthèse Invelps" title="TScore : la synthèse en un seul chiffre"/>
            <P>Le TScore Invelps est un <B>score composite 0–100</B> qui agrège les 4 piliers avec leurs pondérations : Structure (40%) + Momentum (25%) + Volume (20%) + Timing (15%).</P>
            <Bx label="Interprétation du TScore" color={go}>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {[
                  { range:"≥ 75", label:"Configuration solide · Signal d'entrée ou de renforcement.", col:go },
                  { range:"55–74", label:"Configuration acceptable · Surveiller avant d'agir.", col:am },
                  { range:"40–54", label:"Configuration mixte · Patienter ou alléger.", col:am },
                  { range:"< 40", label:"Configuration défavorable · Éviter les positions longues.", col:re },
                ].map(r => (
                  <div key={r.range} style={{ display:"flex", gap:10, alignItems:"baseline" }}>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:".6rem", fontWeight:800, color:r.col, minWidth:40 }}>{r.range}</span>
                    <span style={{ fontSize:".75rem", color:tx }}>→ {r.label}</span>
                  </div>
                ))}
              </div>
            </Bx>

            {/* 7 — Synthèse pratique */}
            <Sec id="synth" num="7" tag="Méthode" title="Synthèse pratique : le filtre séquentiel"/>
            <Bx label="Ordre d'analyse recommandé" color={am}>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[
                  { n:"1", t:"Structure (filtre éliminatoire)", d:"SMA200 au-dessus ? R² > 0.50 ? Si non sur les deux : ne pas entrer, indépendamment du reste." },
                  { n:"2", t:"Momentum (confirmation)", d:"TSMOM 12M positif ? FIP ≤ 0 ? RS Ratio en hausse ? La convergence des trois est requise pour un signal fort." },
                  { n:"3", t:"Volume (validation)", d:"Volumes confirment-ils ? Wyckoff : quelle phase ? Distribution = sortie, Accumulation = attendre le markup." },
                  { n:"4", t:"Timing (optimisation)", d:"RSI entre 40–60 + Z-Score 0–+1σ + pullback SMA20. Attendre la confluence avant d'entrer." },
                ].map(s => (
                  <div key={s.n} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                    <span style={{ fontFamily:"ui-monospace,monospace", fontSize:".55rem", fontWeight:800,
                      color:"#fff", background:am, padding:"1px 6px", borderRadius:4, flexShrink:0, marginTop:2 }}>Étape {s.n}</span>
                    <div>
                      <div style={{ fontSize:".75rem", fontWeight:700, color:ts, marginBottom:2 }}>{s.t}</div>
                      <div style={{ fontSize:".72rem", color:tx, lineHeight:1.65 }}>{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Bx>
            <Q q="Un titre présente : SMA20 > SMA50 > SMA200, TSMOM 12M +45%, FIP = +0.4, RSI 72. Quel est le signal composite ?"
              options={[
                "BUY immédiat — structure et momentum excellents.",
                "Attendre — FIP > 0 (momentum erratique) et RSI en surachat. Patienter un pullback.",
                "SELL — RSI à 72 est toujours baissier.",
                "Analyser le volume d'abord — aucun signal sans données de volume.",
              ]}
              correct={1}
              expl="FIP = +0.4 : momentum construit sur pics isolés (erratique). RSI 72 : surachat (correction possible). Structure excellente mais timing et qualité du momentum insuffisants — patienter un repli vers SMA20/50."/>

            <div style={{ height:"3rem" }}/>
          </div>
        </div>

      {/* ── Sidebar overlay chapitres ── */}
      {menuOpen && (
        <div style={{
          position:"absolute", top:0, left:0, bottom:0,
          width:"min(280px,80vw)", zIndex:10200,
          background:navBg, borderRight:`1px solid ${navBdr}`,
          display:"flex", flexDirection:"column",
          boxShadow:"4px 0 24px rgba(0,0,0,.4)",
        }} onClick={e => e.stopPropagation()}>
          <div style={{ padding:"12px 16px 10px", borderBottom:`1px solid ${navBdr}`,
            display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:"7px", color:mu,
              textTransform:"uppercase", letterSpacing:".15em", fontWeight:700 }}>Chapitres</span>
            <button onClick={() => setMenuOpen(false)} style={{
              width:24, height:24, display:"flex", alignItems:"center", justifyContent:"center",
              background:"transparent", border:"none", cursor:"pointer", fontSize:14, color:mu,
            }}>×</button>
          </div>
          <div style={{ flex:1, overflowY:"auto", padding:"6px 0" }}>
            {CHAPTERS.map(c => (
              <button id={`tm-sc-${c.id}`} key={c.id} onClick={() => scrollTo(c.id)} style={{
                width:"100%", display:"flex", alignItems:"center", gap:10,
                padding:"10px 16px", border:"none", cursor:"pointer",
                background: activeChap === c.id ? `${go}12` : "transparent",
                borderLeft: activeChap === c.id ? `3px solid ${go}` : "3px solid transparent",
                transition:"all .12s",
              }}>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:"7px",
                  color: activeChap === c.id ? go : mu,
                  fontWeight:700, minWidth:14, textAlign:"right", flexShrink:0 }}>{c.num}</span>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:"10px", textAlign:"left",
                  fontWeight: activeChap === c.id ? 700 : 500,
                  color: activeChap === c.id ? go : tx }}>{c.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Backdrop ferme la sidebar ── */}
      {menuOpen && (
        <div style={{ position:"absolute", inset:0, zIndex:10100,
          background:"rgba(0,0,0,.35)" }}
          onClick={() => setMenuOpen(false)}/>
      )}

      {/* ── FAB ☰ bas-droite ── */}
      <button onClick={() => setMenuOpen(v => !v)} style={{
        position:"absolute", bottom:24, right:20, zIndex:10300,
        width:52, height:52, borderRadius:16,
        background: isDark ? "#1a2234" : "#f0ede6",
        border:`1px solid ${navBdr}`,
        boxShadow:"0 4px 20px rgba(0,0,0,.4)",
        display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", fontSize:22, color:go,
      }}>☰</button>

    </div>
    </ScoreProv>
  );
}

// ══════════════════════════════════════════════════════════════════
// 11bis. VERDICT BANNER — étape finale de l'architecture générique
// (doc §2 : Header → Questions → Sections → Verdict/Synthèse finale ;
//  §8 : VerdictBanner fait partie des composants réutilisables communs
//  à tous les modules Invelps — Timing, Market Structure, Fundamentals…)
// Contrairement au bloc "SYNTHÈSE D'ANALYSE" plus haut dans la carte
// (réponse synthétique AVANT les preuves, §9 règle 2), le VerdictBanner
// clôt la page APRÈS les 4 sections d'analyse détaillées : il synthétise
// l'ensemble (§9 règle 6), 100% dynamique — aucune donnée codée en dur.
// ══════════════════════════════════════════════════════════════════
function VerdictBanner({ d, company, scoreGlobal, marketBias, regimeCol, actionLabel, actionCol }) {
  const f = (v, def) => { const n = parseFloat(v ?? def); return isNaN(n) ? null : n; };
  const wyckoff   = d?.wyckoff_context ?? "Neutre";
  const wyEvent   = d?.wyckoff_event   ?? "None";
  const rsi14     = f(d?.rsi_14, null);
  const tsmom12   = f(d?.tsmom_12m, null);
  const zScore    = f(d?.z_score, null);
  const symbol    = company?.name || d?.symbol || "Le titre";

  const ACTION_VERBS = {
    ENTRER:     "renforcer ou initier une position",
    SURVEILLER: "surveiller avant tout renforcement",
    ATTENDRE:   "attendre un point d'entrée plus favorable",
    ÉVITER:     "éviter toute prise de position à ce stade",
  };
  const verb = ACTION_VERBS[actionLabel] || "surveiller l'évolution du titre";

  const clauses = [];
  if (tsmom12 != null) {
    clauses.push(tsmom12 >= 0
      ? `un momentum annuel positif (TSMOM 12M ${tsmom12 >= 0 ? "+" : ""}${tsmom12.toFixed(1)}%)`
      : `un momentum annuel négatif (TSMOM 12M ${tsmom12.toFixed(1)}%)`);
  }
  clauses.push(`une phase Wyckoff de ${wyckoff.toLowerCase()}${wyEvent && wyEvent !== "None" ? ` (${wyEvent})` : ""}`);
  if (rsi14 != null) {
    clauses.push(rsi14 > 70 ? `un RSI en zone de surachat (${rsi14.toFixed(1)})`
               : rsi14 < 30 ? `un RSI en zone de survente (${rsi14.toFixed(1)})`
               : `un RSI neutre (${rsi14.toFixed(1)})`);
  }
  if (zScore != null && Math.abs(zScore) > 1.5) {
    clauses.push(`une sur-extension statistique (Z-Score ${zScore >= 0 ? "+" : ""}${zScore.toFixed(2)}σ)`);
  }

  return (
    <div className="tm5-card" style={{ borderTop:`3px solid ${actionCol}` }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        gap:8, marginBottom:12, flexWrap:"wrap" }}>
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
          textTransform:"uppercase", letterSpacing:".12em", color:"var(--mu)" }}>
          VERDICT — SYNTHÈSE FINALE
        </span>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800, color:regimeCol }}>
            {marketBias.toUpperCase()}
          </span>
          <span style={{
            display:"inline-flex", alignItems:"center", gap:4, borderRadius:999, padding:"3px 10px",
            fontSize:11, fontWeight:800, background:`${actionCol}18`, border:`1px solid ${actionCol}44`,
            color:actionCol, fontFamily:"ui-monospace,monospace",
          }}>
            {actionLabel}
          </span>
        </div>
      </div>

      <p style={{ fontSize:12.5, lineHeight:1.75, color:"var(--ts)", margin:0 }}>
        {symbol} affiche un score technique global de <strong style={{ color:getScoreColor(scoreGlobal) }}>{scoreGlobal}/100</strong>{" "}
        porté par {clauses.length > 1 ? clauses.slice(0, -1).join(", ") + " et " + clauses[clauses.length - 1] : (clauses[0] || "un diagnostic technique neutre")}.{" "}
        Sur la base des 4 piliers (structure, momentum, volume, timing), la lecture d'ensemble invite à{" "}
        <strong style={{ color:actionCol }}>{verb}</strong>.
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 12. MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
export default function TimingMarcheCard({ d: dProp, th, nav }) {
  // API live (DetailPages) : dProp = req1 + priceChart / rsChart / statsChart
  const hasApi = !!(dProp && (dProp.symbol || dProp.priceChart || dProp.statsChart));
  const d = useMemo(() => (hasApi ? buildTimingMeta(dProp) : TM_META), [hasApi, dProp]);

  const liveDaily = useMemo(() => {
    const rows = dProp?.priceChart;
    if (!Array.isArray(rows) || !rows.length) return null;
    return buildDailySeries(rows.map((r) => ({
      date: String(r.date || "").slice(0, 10),
      open: r.open, high: r.high, low: r.low, close: r.close, volume: r.volume,
      sma20: r.sma20, sma50: r.sma50, sma200: r.sma200,
    })).filter((r) => r.date));
  }, [dProp]);

  const liveMonthly = useMemo(() => {
    const rows = dProp?.statsChart;
    if (!Array.isArray(rows) || !rows.length) return null;
    return buildMonthlySeries(rows.map((r) => ({
      date_month: String(r.date_month || "").slice(0, 7),
      stockprice: r.stockprice ?? r.stockprice_usd,
      lr_value: r.lr_value,
      sigma: r.sigma,
      z_score: r.z_score,
      lr_r_squared: r.lr_r_squared,
    })).filter((r) => r.date_month));
  }, [dProp]);

  const liveRs = useMemo(() => {
    const rows = dProp?.rsChart;
    if (!Array.isArray(rows) || !rows.length) return null;
    return buildRSSeries(rows.map((r) => ({
      date_month: String(r.date_month || r.date || "").slice(0, 7),
      rs_ratio: r.rs_ratio,
      rs_return_12m: r.rs_return_12m,
      rs_trend_label: r.rs_trend_label,
    })).filter((r) => r.date_month));
  }, [dProp]);

  const DAILY_LIVE = useMemo(() => {
    const src = liveDaily?.length ? liveDaily : RAW_DAILY;
    const reg = calcRegression(src);
    const z = calcZScore(src);
    return src.map((row, i) => ({ ...row, ...reg[i], z: z[i] }));
  }, [liveDaily]);

  const MONTHLY_LIVE = useMemo(
    () => (liveMonthly?.length ? liveMonthly : RAW_MONTHLY),
    [liveMonthly]
  );

  const seriesValue = useMemo(
    () => ({ daily: DAILY_LIVE, monthly: MONTHLY_LIVE, rs: liveRs?.length ? liveRs : RS_DATA }),
    [DAILY_LIVE, MONTHLY_LIVE, liveRs]
  );

  const { darkMode: themeDark } = useInvelpsTheme();
  const [isDarkLocal, setIsDarkLocal] = useState(() => {
    try {
      const saved = localStorage.getItem("invelps-dark");
      if (saved !== null) return saved === "true";
    } catch(_) {}
    if (th) return th.appBg === "#0b1220";
    const h = new Date().getHours();
    return h < 6 || h >= 20;
  });
  const isDark = themeDark != null ? !!themeDark : isDarkLocal;

  const toggleDark = useCallback(() => {
    setIsDarkLocal(v => {
      const next = !v;
      try { localStorage.setItem("invelps-dark", String(next)); } catch(_) {}
      return next;
    });
  }, []);

  useEffect(() => {
    applyTheme(isDark);
  }, [isDark]);

  // Inject minimal CSS
  useEffect(() => {
    if (!document.getElementById("tm5-base-css")) {
      const s = document.createElement("style");
      s.id = "tm5-base-css";
      s.textContent = `
        .tm5 *{box-sizing:border-box;margin:0;padding:0;}
        .tm5{
          font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;
          width:100%;
          max-width:none;
          min-width:0;
        }
        .tm5 button{font-family:inherit;}
        .tm5 svg{max-width:100%;}
        .tm5 img{max-width:100%;height:auto;}
        /* Prevent horizontal scroll at document level */
        html,body{overflow-x:hidden;width:100%;margin:0;padding:0;}
        body:has(.tm5) #root{max-width:none;width:100%;margin:0;padding:0;}
        body:has(.tm5) main{max-width:none;}
        .tm5-page{
          width:100%;
          max-width:1820px;
          margin:0 auto;
          padding:16px 20px 40px;
          display:flex;
          flex-direction:column;
          gap:12px;
          box-sizing:border-box;
        }
        @media(min-width:640px){.tm5-page{padding:16px 20px 40px;gap:12px;}}
        @media(min-width:1024px){.tm5-page{padding:16px 20px 40px;gap:12px;}}
        .tm5-card{
          background:var(--card-bg);
          border:1px solid var(--card-bdr);
          border-radius:16px;
          padding:16px 20px;
          min-width:0;
          width:100%;
          overflow:hidden;
        }
        @media(min-width:640px){.tm5-card{padding:16px;}}

        /* ── Diagnostic: 4 blocs CÔTE À CÔTE ── */
        .tm9-diag-grid{
          display:grid;
          grid-template-columns:1fr;
          gap:0;
          width:100%;
        }
        .tm9-diag-grid > *{min-width:0;}
        @media(min-width:640px){
          .tm9-diag-grid{grid-template-columns:1fr 1fr;}
        }
        @media(min-width:1080px){
          .tm9-diag-grid{grid-template-columns:repeat(4,1fr);}
        }
        @media(max-width:639px){
          .tm9-diag-grid{grid-template-columns:1fr;}
          .tm9-diag-grid > *{
            border-right:none !important;
            border-bottom:1px solid var(--sep);
            padding-left:0 !important;
            padding-right:0 !important;
            padding-top:12px;
            padding-bottom:12px;
          }
          .tm9-diag-grid > *:first-child{padding-top:0;}
          .tm9-diag-grid > *:last-child{border-bottom:none;padding-bottom:0;}
        }

        /* ── ProfilDescriptionRadarCard ──
           Mobile (<640px) : 1 colonne, sans bordures latérales
           Tablet (640–820px) : 2 colonnes
           Desktop (>820px) : 3 colonnes
        */
        .pdrc-grid{
          display:grid;
          grid-template-columns:1fr;
          gap:16px;
          width:100%;
        }
        .pdrc-grid > *{min-width:0;}
        /* Sur mobile : pas de padding/bordure latérale */
        .pdrc-col-sep{border-right:none !important; padding-right:0 !important;}
        .pdrc-col-mid{padding-left:0 !important; padding-right:0 !important; border-right:none !important;}
        .pdrc-col-last{padding-left:0 !important;}
        @media(min-width:640px){
          .pdrc-grid{grid-template-columns:1fr 1fr;gap:0;}
          .pdrc-col-sep{border-right:1px solid var(--sep) !important; padding-right:20px !important;}
          .pdrc-col-mid{padding:0 20px !important; border-right:1px solid var(--sep) !important;}
          .pdrc-col-last{padding-left:20px !important;}
        }
        @media(min-width:1024px){
          .pdrc-grid{grid-template-columns:minmax(220px,.9fr) minmax(0,1.35fr) minmax(220px,.9fr);gap:0;}
        }
        .tm5-diag{display:flex;flex-direction:column;gap:12px;}
        @media(min-width:480px){.tm5-diag{flex-direction:row;align-items:flex-start;gap:16px;}}
        .tm5-diag-right{flex:1;min-width:0;display:flex;flex-direction:column;gap:12px;}
        @media(min-width:768px){.tm5-diag-right{flex-direction:row;gap:16px;}}
        .tm5-sbars-m{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:12px;padding-top:12px;border-top:1px solid var(--sep);}
        @media(min-width:560px){.tm5-sbars-m{display:none;}}
        .tm5-sbars-d{display:none;}
        @media(min-width:768px){.tm5-sbars-d{display:block;flex:1;min-width:0;}}
        .tm5-pillars{display:grid;grid-template-columns:1fr;gap:10px;}
        @media(min-width:480px){.tm5-pillars{grid-template-columns:1fr 1fr;}}
        @media(min-width:1024px){.tm5-pillars{grid-template-columns:repeat(4,1fr);}}
        .tm5-indic-grid{display:grid;grid-template-columns:1fr;gap:8px;}
        @media(min-width:560px){.tm5-indic-grid{grid-template-columns:1fr 1fr;}}
        .tm5-indic-grid > *{min-width:0;height:100%;}
        .tm5-bottom{display:grid;grid-template-columns:1fr;gap:12px;}
        @media(min-width:768px){.tm5-bottom{grid-template-columns:1fr 1fr;}}
        .tm5-toggles{display:flex;flex-wrap:wrap;gap:5px;align-items:center;}
        .tm5-chart-type{margin-left:auto;display:flex;gap:4px;flex-shrink:0;}
        .tm5-legend{display:flex;flex-wrap:wrap;gap:5px 12px;padding:10px 0 2px;}
        @media(max-width:560px){
          .tm5-page{padding-left:8px;padding-right:8px;}
          .tm5-card{padding:12px;border-radius:12px;}
          .tm5-toggles{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:4px;}
          .tm5-toggles > *{width:100%;min-width:0;justify-content:center;padding:4px 8px!important;}
          .tm5-chart-type{grid-column:1/-1;margin-left:0;width:100%;}
          .tm5-chart-type button{flex:1;}
          .tm5-legend{display:grid;grid-template-columns:1fr;gap:6px;}
        }
        .tm5-murphy{display:flex;overflow-x:auto;gap:0;-webkit-overflow-scrolling:touch;}
        .tm5-murphy::-webkit-scrollbar{display:none;}
        /* ── CompassModal responsive ── */
        .compass-overlay{padding:clamp(6px,3vw,20px)!important;}
        .compass-modal{width:min(960px,98vw)!important;max-height:92dvh!important;}
        .compass-seqbar{display:flex!important;}
        @media(max-width:720px){
          .compass-overlay{align-items:stretch!important;padding:0!important;}
          .compass-modal{width:100vw!important;max-height:100dvh!important;height:100dvh!important;border-radius:0!important;}
          .compass-seqbar{display:none!important;}
        }
        /* CourseModal responsive */
        .tm-course-sidebar{display:flex!important;}
        .tm-course-fab{display:none!important;}
        @media(max-width:640px){
          .tm-course-sidebar{display:none!important;}
          .tm-course-fab{display:flex!important;}
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const [showGuide, setShowGuide] = useState(null); // null ou clé de pilier technique
  const [showCours, setShowCours] = useState(false);
  const [period,    setPeriod   ] = useState("1Y");
  const [showSMA20, setSMA20    ] = useState(true);
  const [showSMA50, setSMA50    ] = useState(false);
  const [showSMA200,setSMA200   ] = useState(false);
  const [showReg,   setReg      ] = useState(true);
  const [showBands, setBands    ] = useState(true);
  const [showZScore,setZScore   ] = useState(true);
  const [showVolume,setVolume   ] = useState(true);
  const [showRSI,   setRSI      ] = useState(true);
  const [showLevels,setLevels   ] = useState(true);
  const [chartType, setChartType] = useState("line");

  // ── Education modal
  const [eduModal, setEduModal] = useState(null);
  const openEdu  = useCallback(anchor => setEduModal({ anchor }), []);
  const closeEdu = useCallback(() => setEduModal(null), []);

  // SMAs only relevant for ≤1Y
  const isShortRange = ["3M","6M","1Y"].includes(period);

  // Horizons longs (3Y/5Y/MAX) : source mensuelle réelle
  // (monthly_histo_price_calculation). timing_marche_chart ne garde
  // pas assez d'historique daily pour ces vues — cf. mapping v3.
  const isMonthlySource = period==="3Y" || period==="5Y" || period==="MAX";

  // Filter data by period
  const chartData = useMemo(() => {
    if (isMonthlySource) {
      const src = MONTHLY_LIVE?.length ? MONTHLY_LIVE : RAW_MONTHLY;
      if (!src.length) return [];
      const cutoff = new Date(src[src.length-1].d + "-01");
      if (period==="3Y") cutoff.setFullYear(cutoff.getFullYear()-3);
      else if (period==="5Y") cutoff.setFullYear(cutoff.getFullYear()-5);
      const filtered = period==="MAX"
        ? src
        : src.filter(row => new Date(row.d + "-01") >= cutoff);
      const reg = calcRegression(filtered);
      return filtered.map((row, i) => ({ ...row, ...reg[i] }));
    }

    const allData = DAILY_LIVE?.length ? DAILY_LIVE : DAILY;
    if (!allData.length) return [];
    const lastDate = new Date(allData[allData.length-1].d);
    const cutoff = new Date(lastDate);
    if (period==="3M")  cutoff.setMonth(cutoff.getMonth()-3);
    else if (period==="6M") cutoff.setMonth(cutoff.getMonth()-6);
    else if (period==="1Y") cutoff.setFullYear(cutoff.getFullYear()-1);

    const filtered = allData.filter(row => new Date(row.d) >= cutoff);
    if (!filtered.length) return [];

    const closes = filtered.map(row => row.c);
    const zScores = closes.map((_, i) => {
      const w   = Math.min(60, i + 1);
      const sl  = closes.slice(i - w + 1, i + 1);
      const zm  = sl.reduce((a,b) => a+b, 0) / sl.length;
      const zs  = Math.sqrt(sl.reduce((s,y) => s + (y-zm)**2, 0) / sl.length) || 1;
      return +((closes[i] - zm) / zs).toFixed(3);
    });

    const base0 = filtered[0];
    const pxBase = base0.c;
    const ndxBase = base0._ndx || 1;
    const reg = calcRegression(filtered);

    return filtered.map((row, i) => ({
      ...row,
      ...reg[i],
      z: zScores[i],
      rs: row._ndx != null
        ? +((row.c / pxBase) / (row._ndx / ndxBase) * 100).toFixed(2)
        : null,
    }));
  }, [period, isMonthlySource, MONTHLY_LIVE, DAILY_LIVE]);

  const last     = chartData[chartData.length-1];
  const sma20v   = chartData.filter(d=>d.s20!=null).slice(-1)[0]?.s20;
  const sma50v   = chartData.filter(d=>d.s50!=null).slice(-1)[0]?.s50;
  const sma200v  = chartData.filter(d=>d.s200!=null).slice(-1)[0]?.s200;

  // ── Données société — StockDashboard ────────────────────────────────────
  // Plus de fallback NVDA : d = TM_META vient toujours de RAW_TM_DATA.req1,
  // jamais undefined (même principe que COMPANY_META dans EtatsFinanciersCard).
  const company = {
    name:           d.symbol,
    fullName:       d.company_name,
    exchange:       d.exchange_code,
    sector:         d.sector,
    industry:       d.industry,
    region:         d.region,
    marketCapClass: d.market_cap_class,
    style:          d.stock_style,
    cyclicals:      d.peter_lynch_category,
    price:          d.last_price,
    mtdPct:         d.last_return_m1,
    ytdPct:         d.ytd_pct,
    description:    d.description,
  };

  // ── IScore + Radar — StockDashboard_Decisions ────────────────────────────
  const iscore = parseFloat(d.iscore);
  const radarData = [
    { axis:"Fondamentaux", value: parseFloat(d.radar_fondamentaux)      },
    { axis:"Valorisation", value: parseFloat(d.radar_valorisation)      },
    { axis:"Marché",       value: parseFloat(d.radar_marche)            },
    { axis:"Momentum",     value: parseFloat(d.radar_momentum)          },
    { axis:"Risque/Rend.", value: parseFloat(d.radar_risque_rendement)  },
  ];

  // ── SR Levels — timing_marche_summary ───────────────────────────────────
  const SR_LEVELS = {
    support: {
      price: parseFloat(d?.support_level    ?? SR_LEVELS_DEFAULT.support.price),
      label: "Support",
      tests: d?.support_touches             ?? SR_LEVELS_DEFAULT.support.tests,
      volR:  parseFloat(d?.support_vol_ratio ?? 1.043),
      col:   "#6f8a5b",
    },
    resistance: {
      price: parseFloat(d?.resist_level     ?? SR_LEVELS_DEFAULT.resistance.price),
      label: "Résistance",
      tests: d?.resist_touches              ?? SR_LEVELS_DEFAULT.resistance.tests,
      volR:  parseFloat(d?.resist_vol_ratio  ?? 1.377),
      col:   "#c6523f",
    },
  };

  // ── Scores 4 piliers — timing_marche_summary ────────────────────────────
  const scoreStructure = Math.round(parseFloat(d?.trend_score    ?? 65.2));
  const scoreMomentum  = Math.round(parseFloat(d?.dd_momentum_score ?? d?.momentum_score ?? 53.86));
  const scoreVolume    = Math.round(parseFloat(d?.volume_score   ?? 68));
  const scoreTiming    = Math.round(parseFloat(d?.opportunity_score ?? 40)); // scoreTiming ← tms.opportunity_score (cf. mapping .md §2a)

  // TScore technique = moyenne pondérée des 4 piliers (structure 40% · momentum 25% · volume 20% · timing 15%)
  const scoreGlobal = d?.tscore_global != null
    ? Math.round(parseFloat(d.tscore_global))
    : Math.round(scoreStructure * 0.40 + scoreMomentum * 0.25 + scoreVolume * 0.20 + scoreTiming * 0.15);

  const momentumDir    = d?.momentum_direction ?? "Retournement baissier";

  // ── Diagnostic régime/signal/action ─────────────────────────────────────
  const marketBias   = d?.market_bias_label ?? "Pression baissière";
  const tscoreSignal = d?.tscore_signal     ?? (scoreGlobal >= 65 ? "Signal fort · configuration technique solide" : scoreGlobal >= 50 ? "Signal modéré · surveiller" : "Signal faible · patienter");

  // actionLabel dérivé du TScore technique si interp_action absent
  const actionInterp = d?.interp_action ?? "";
  const actionLabel  = actionInterp.includes("ENTRER")     ? "ENTRER"
                     : actionInterp.includes("SURVEILLER") ? "SURVEILLER"
                     : actionInterp.includes("ATTENDRE")   ? "ATTENDRE"
                     : actionInterp.includes("ÉVITER")     ? "ÉVITER"
                     : scoreGlobal >= 70 ? "ENTRER"
                     : scoreGlobal >= 55 ? "SURVEILLER"
                     : scoreGlobal >= 42 ? "ATTENDRE"
                     : "ÉVITER";
  const actionCol    = getStatusColor(
                         actionLabel === "ENTRER" ? "good" : actionLabel === "ÉVITER" ? "bad" : "neutral"
                       );
  const regimeCol    = getStatusColor(
                         marketBias.toLowerCase().includes("haussier") ? "good"
                         : marketBias.toLowerCase().includes("baissier") ? "bad" : "neutral"
                       );

  // ── last_updated ─────────────────────────────────────────────────────────
  const lastUpdated = d?.updated_at ?? "2026-06-03T14:07:20";

  const sC = v => v>=75?C.green:v>=55?C.amber:C.red;

  // Legend items below chart
  const legendItems = [
    { dot:RSI_COL.line,    label:`Prix : ${last?.c.toFixed(2)||"—"}` },
    ...(showSMA20&&isShortRange  ? [{ dot:SMA_COL.s20,  label:`SMA 20 : ${sma20v?.toFixed(2)||"—"}`,  tipKey:"legend_sma20"  }] : []),
    ...(showSMA50&&isShortRange  ? [{ dot:SMA_COL.s50,  label:`SMA 50 : ${sma50v?.toFixed(2)||"—"}`,  tipKey:"legend_sma50"  }] : []),
    ...(showSMA200&&isShortRange ? [{ dot:SMA_COL.s200, label:`SMA 200 : ${sma200v?.toFixed(2)||"—"}`, tipKey:"legend_sma200" }] : []),
    ...(showReg    ? [{ dot:SIG.reg.col,  label:`Régression : ${last?.reg?.toFixed(2)||"—"}`, dash:SIG.reg.dash, tipKey:"legend_reg" }] : []),
    ...(showBands  ? [
      { dot:SIG.b2p.col, label:`+2σ : ${last?.b2p?.toFixed(2)||"—"}`, dash:SIG.b2p.dash, tipKey:"legend_b2" },
      { dot:SIG.b1p.col, label:`+1σ : ${last?.b1p?.toFixed(2)||"—"}`, dash:SIG.b1p.dash, tipKey:"legend_b1" },
      { dot:SIG.b1n.col, label:`-1σ : ${last?.b1n?.toFixed(2)||"—"}`, dash:SIG.b1n.dash, tipKey:"legend_b1" },
      { dot:SIG.b2n.col, label:`-2σ : ${last?.b2n?.toFixed(2)||"—"}`, dash:SIG.b2n.dash, tipKey:"legend_b2" },
    ] : []),
    ...(showZScore ? [{ dot:C.amber, label:`Z-Score : ${last?.z!=null?(last.z>=0?"+":"")+last.z.toFixed(2):"—"}`, tipKey:"toggle_zscore" }] : []),
    ...(showVolume ? [{ dot:C.green, label:"Volume", tipKey:"toggle_volume" }] : []),
    ...(showRSI && !isMonthlySource ? [{ dot:RSI_COL.line, label:"RSI 14", tipKey:"toggle_rsi" }] : []),
    ...(showLevels && !isMonthlySource ? [
      { dot:SR_LEVELS.support.col,    label:`Support ${SR_LEVELS.support.price}`,    dash:"5 3", tipKey:"legend_support" },
      { dot:SR_LEVELS.resistance.col, label:`Résistance ${SR_LEVELS.resistance.price}`, dash:"5 3", tipKey:"legend_resist"  },
    ] : []),
  ];

  return (
    <TimingSeriesContext.Provider value={seriesValue}>
    <React.Fragment>
    <div className="tm5" style={{
      background:"var(--bg)", minHeight:"100vh", color:"var(--ts)", width:"100%",
    }}>
    <div className="tm5-page">

      {/* ══ DARK MODE TOGGLE ══ */}
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:-4 }}>
        <button
          onClick={toggleDark}
          title={isDark ? "Passer en mode jour" : "Passer en mode nuit"}
          style={{
            display:"flex", alignItems:"center", gap:6,
            padding:"6px 12px", borderRadius:999,
            border:`1px solid ${isDark ? "rgba(111,138,91,.30)" : "rgba(30,40,34,.15)"}`,
            background: isDark ? "rgba(111,138,91,.10)" : "rgba(30,40,34,.05)",
            color: isDark ? "#8da57a" : "#6f7b74",
            fontSize:11, fontWeight:700, cursor:"pointer",
            fontFamily:"ui-sans-serif,system-ui,sans-serif",
            transition:"all .18s ease",
            letterSpacing:".04em",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = isDark ? "rgba(111,138,91,.18)" : "rgba(30,40,34,.10)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = isDark ? "rgba(111,138,91,.10)" : "rgba(30,40,34,.05)";
          }}
        >
          <span style={{ fontSize:14, lineHeight:1 }}>{isDark ? "☀" : "☾"}</span>
          <span>{isDark ? "Mode jour" : "Mode nuit"}</span>
        </button>
      </div>

      {/* ══ PROFIL / DESCRIPTION / RADAR ══ */}
      <ProfilDescriptionRadarCard
        company={company}
        iscore={iscore}
        radarData={radarData}
        onOpenEdu={openEdu}
        onOpenGuide={(pillarKey) => setShowGuide(pillarKey || "structure")}
        onOpenCours={() => setShowCours(true)}
      />

      {/* ══ DIAGNOSTIC TECHNIQUE GLOBAL ══ */}
      {(() => {
        // ── Extraction des métriques AT — champs exacts de timing_marche_summary ──
        const _f   = (v, def) => { const n = parseFloat(v ?? def); return isNaN(n) ? null : n; };
        const lastPr   = _f(d?.last_price,        199.57);   // last_price
        const sma200v  = _f(d?.sma200,             183.72);  // sma200
        const sma50v   = _f(d?.sma50,              186.94);  // sma50
        const sma20v   = _f(d?.sma20,              196.17);  // sma20
        const r2v      = _f(d?.r_squared,           0.84378); // r_squared
        // tsmom stocké en % bruts dans la DB : 60.12 = +60.12%, -6.49 = -6.49%
        const tsmom12v = _f(d?.tsmom_12m,          60.12);   // tsmom_12m
        const tsmom6v  = _f(d?.tsmom_6m,           -6.53);   // tsmom_6m
        const tsmom3v  = _f(d?.tsmom_3m,           -6.49);   // tsmom_3m
        const fipv     = _f(d?.fip_score,          -0.0342); // fip_score
        const volR20v  = _f(d?.volume_ratio_20d,   1.4767);  // volume_ratio_20d
        const wyckoffv = d?.wyckoff_context  ?? "Distribution"; // wyckoff_context
        const wyEventv = d?.wyckoff_event    ?? "UTAD";          // wyckoff_event
        const rsi14v   = _f(d?.rsi_14,             60.81);   // rsi_14
        const zScorev  = _f(d?.z_score,            1.317);   // z_score
        const momDirv  = d?.momentum_direction ?? "Retournement baissier"; // momentum_direction

        // ── Réponses depuis diag_q* (Timing_marche_interpretations v2) ──────────
        // diag_q1_ok / q2_ok / q3_ok / q4_ok : 1=Oui · 0=Partiellement · -1=Non
        // Fallback calcul dynamique si champs absents (déploiement SP en attente)
        const _okToStatus = v => v === 1 ? true : v === -1 ? false : "mid";

        const diagResults = {
          structure: (() => {
            if (d?.diag_q1_tendance) return {
              ok:  _okToStatus(parseInt(d.diag_q1_ok ?? 0)),
              txt: d.diag_q1_tendance,
            };
            // Fallback
            if (!sma200v || !lastPr) return null;
            const above   = lastPr > sma200v;
            const aligned = sma20v && sma50v ? sma20v > sma50v && sma50v > sma200v : null;
            const r2txt   = r2v != null ? `, R² = ${(r2v*100).toFixed(0)}%` : "";
            if (!above)
              return { ok:false, txt:`Non — cours ${lastPr.toFixed(2)} < SMA200 ${sma200v.toFixed(2)} : tendance primaire baissière${r2txt}. Murphy ch. 9.` };
            if (above && aligned === true)
              return { ok:true,  txt:`Oui — cours ${lastPr.toFixed(2)} > SMA200 ${sma200v.toFixed(2)}, alignement SMA20 (${sma20v?.toFixed(1)}) > SMA50 (${sma50v?.toFixed(1)}) > SMA200${r2txt}.` };
            if (above && aligned === false && sma20v && sma50v)
              return { ok:"mid", txt:`Partiellement — cours ${lastPr.toFixed(2)} > SMA200 ${sma200v.toFixed(2)}${r2txt}, mais ${sma20v > sma50v ? "SMA50 > SMA200 non vérifiée" : "SMA20 > SMA50 non vérifiée"}.` };
            return { ok:"mid", txt:`Partiellement — cours au-dessus de la SMA200 (${sma200v.toFixed(2)}) mais alignement SMA incomplet.` };
          })(),
          momentum: (() => {
            if (d?.diag_q2_momentum) return {
              ok:  _okToStatus(parseInt(d.diag_q2_ok ?? 0)),
              txt: d.diag_q2_momentum,
            };
            // Fallback
            if (tsmom12v < 0)
              return { ok:false, txt:`Non — TSMOM 12M = ${tsmom12v.toFixed(1)}% : biais baissier annuel. Moskowitz (2012) : ne pas entrer à contre-courant du momentum annuel.` };
            if (tsmom12v > 0 && tsmom3v < 0)
              return { ok:"mid", txt:`Il s'essouffle — TSMOM 12M = +${tsmom12v.toFixed(1)}% (tendance annuelle positive) mais TSMOM 3M = ${tsmom3v.toFixed(1)}% (baisse récente). Murphy ch. 10 : signal d'essoufflement.` };
            if (tsmom12v > 0 && fipv != null && fipv <= 0)
              return { ok:true,  txt:`Il accélère — TSMOM 12M = +${tsmom12v.toFixed(1)}%${fipv != null ? `, FIP = ${fipv.toFixed(3)} (≤ 0)` : ""} : momentum positif et régulier. Gray & Vogel : smooth momentum, configuration idéale.` };
            return { ok: tsmom12v > 0 ? "mid" : false, txt:`TSMOM 12M = ${tsmom12v >= 0 ? "+" : ""}${tsmom12v.toFixed(1)}%, 3M = ${tsmom3v >= 0 ? "+" : ""}${tsmom3v.toFixed(1)}%${fipv != null ? `, FIP = ${fipv.toFixed(3)}` : ""}.` };
          })(),
          volume: (() => {
            if (d?.diag_q3_volume) return {
              ok:  _okToStatus(parseInt(d.diag_q3_ok ?? 0)),
              txt: d.diag_q3_volume,
            };
            // Fallback
            const isDistrib = wyckoffv && (wyckoffv.includes("Distribution") || wyckoffv.includes("Markdown"));
            if (isDistrib)
              return { ok:false, txt:`Non — phase Wyckoff ${wyckoffv}${wyEventv ? ` (${wyEventv})` : ""} : les institutionnels allègent. Volume Ratio 20J = ${volR20v.toFixed(2)}×. Villahermosa ch. 20 : signal baissier majeur.` };
            if (volR20v >= 1.2)
              return { ok:true,  txt:`Oui — Volume Ratio 20J = ${volR20v.toFixed(2)}× la moyenne (+${((volR20v-1)*100).toFixed(0)}%)${wyckoffv ? `, phase Wyckoff ${wyckoffv}` : ""}. Murphy ch. 7 : le volume confirme la tendance.` };
            if (volR20v < 0.85)
              return { ok:"mid", txt:`Partiellement — Volume Ratio 20J = ${volR20v.toFixed(2)}× (sous la normale). Wyckoff : No Demand — mouvement sans conviction institutionnelle.` };
            return { ok:"mid", txt:`Partiellement — Volume Ratio 20J = ${volR20v.toFixed(2)}×${wyckoffv ? `, phase Wyckoff ${wyckoffv}` : ""}. Volume dans la norme.` };
          })(),
          timing: (() => {
            if (d?.diag_q4_timing) return {
              ok:  _okToStatus(parseInt(d.diag_q4_ok ?? 0)),
              txt: d.diag_q4_timing,
            };
            // Fallback
            const rsiOB   = rsi14v > 70, rsiOS = rsi14v < 30;
            const rsiForce= rsi14v >= 40 && rsi14v <= 60;
            const zExt    = zScorev > 2,  zNeut = zScorev >= -1 && zScorev <= 1;
            if (rsiOB && zExt)
              return { ok:false, txt:`Non — double sur-extension : RSI 14 = ${rsi14v.toFixed(1)} (surachat) ET Z-Score +${zScorev.toFixed(2)}σ. Ratio risque/rendement dégradé. Attendre un repli${sma20v ? ` vers SMA20 (${sma20v.toFixed(2)})` : ""}.` };
            if (rsiOS)
              return { ok:true,  txt:`Oui — RSI 14 = ${rsi14v.toFixed(1)} (survente < 30) : zone de rebond fiable (Wilder 1978). Z-Score = ${zScorev >= 0 ? "+" : ""}${zScorev.toFixed(2)}σ.` };
            if (rsiForce && zNeut)
              return { ok:true,  txt:`Oui — RSI 14 = ${rsi14v.toFixed(1)} (zone de force 40–60) ET Z-Score = ${zScorev >= 0 ? "+" : ""}${zScorev.toFixed(2)}σ (près de la droite OLS) : confluence idéale. Murphy ch. 15.` };
            if (rsiOB)
              return { ok:"mid", txt:`Pas encore — RSI 14 = ${rsi14v.toFixed(1)} (surachat > 70). Wilder : patienter un repli vers RSI 40–55. SMA20 = ${sma20v?.toFixed(2) ?? "—"} est la cible naturelle.` };
            if (zExt)
              return { ok:"mid", txt:`Pas encore — Z-Score +${zScorev.toFixed(2)}σ : cours trop éloigné de la droite OLS. RSI 14 = ${rsi14v.toFixed(1)}. Attendre Z-Score 0/+1σ.` };
            return { ok:"mid", txt:`Acceptable — RSI 14 = ${rsi14v.toFixed(1)}, Z-Score = ${zScorev >= 0 ? "+" : ""}${zScorev.toFixed(2)}σ. Conditions correctes sans être optimales.` };
          })(),
        };
        const pillarsDisp = [
          { key:"structure", label:"Structure de Tendance", icon:"◈", score:scoreStructure, color:C.green,  weight:"40%", indic:"SMA200 · Alignement · R²" },
          { key:"momentum",  label:"Force / Momentum",      icon:"⚡", score:scoreMomentum,  color:C.amber,  weight:"25%", indic:"TSMOM 12M · FIP · RS Ratio" },
          { key:"volume",    label:"Participation / Volume", icon:"◎", score:scoreVolume,    color:C.olive,  weight:"20%", indic:"Vol. Ratio 20J · Wyckoff" },
          { key:"timing",    label:"Timing / Opportunité",  icon:"⊕", score:scoreTiming,    color:C.orange, weight:"15%", indic:"RSI 14 · Z-Score · SMAs" },
        ];

        return (
          <div className="tm5-card">
            {/* ── Header ── */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              gap:8, marginBottom:14, paddingBottom:11, borderBottom:"1px solid var(--sep)",
              flexWrap:"wrap" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
                  textTransform:"uppercase", letterSpacing:".12em", color:"var(--mu)" }}>
                  DIAGNOSTIC TECHNIQUE GLOBAL
                </span>
                <TipIcon text={TIPS.score_global}/>
              </div>
              {/* Badges clés AT en ligne */}
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {rsi14v != null && (() => {
                  const rStatus = rsi14v > 70 ? "bad" : rsi14v < 30 ? "good" : rsi14v >= 40 && rsi14v <= 60 ? "good" : "neutral";
                  const rc = getStatusColor(rStatus);
                  return (
                    <div style={{ padding:"3px 9px", borderRadius:8, background:`${rc}0d`, border:`1px solid ${rc}28` }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800, color:rc }}>
                        RSI {rsi14v.toFixed(1)} · {rsi14v > 70 ? "Surachat" : rsi14v < 30 ? "Survente" : rsi14v >= 40 && rsi14v <= 60 ? "Force" : "Neutre"}
                      </span>
                    </div>
                  );
                })()}
                {zScorev != null && (() => {
                  const zStatus = Math.abs(zScorev) > 2 ? "bad" : Math.abs(zScorev) > 1 ? "neutral" : "good";
                  const zc = getStatusColor(zStatus);
                  return (
                    <div style={{ padding:"3px 9px", borderRadius:8, background:`${zc}0d`, border:`1px solid ${zc}28` }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800, color:zc }}>
                        Z-Score {zScorev >= 0 ? "+" : ""}{zScorev.toFixed(2)}σ
                      </span>
                    </div>
                  );
                })()}
                {wyckoffv && (() => {
                  const wc = getPhaseColor(wyckoffv);
                  return (
                    <div style={{ padding:"3px 9px", borderRadius:8, background:`${wc}0d`, border:`1px solid ${wc}28` }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800, color:wc }}>
                        {wyckoffv}{wyEventv && wyEventv !== "None" ? ` · ${wyEventv}` : ""}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ── Top row : Gauge + barres piliers + Régime/Signal/Action ── */}
            <div style={{ width:"100%" }} className="tm9-diag-grid">
              {/* Gauge */}
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
                justifyContent:"center", paddingRight:16, borderRight:"1px solid var(--sep)" }}>
                <Gauge value={scoreGlobal/100} labelTop={`${scoreGlobal} / 100`} labelBottom="TScore"/>
              </div>

              {/* Barres 4 piliers */}
              <div style={{ padding:"0 16px", borderRight:"1px solid var(--sep)",
                display:"flex", flexDirection:"column", justifyContent:"center" }}>
                <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
                  letterSpacing:".1em", color:"var(--mu)", marginBottom:12 }}>
                  RÉPARTITION DES SCORES
                </div>
                {pillarsDisp.map(s => (
                  <div key={s.key} style={{ marginBottom:9 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                      <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
                        <span style={{ fontSize:10, color:"var(--mu)", fontWeight:600 }}>{s.label}</span>
                        {s.key === "momentum" && momentumDir && (
                          <Tip {...TIPS.mom_direction}>
                            <span style={{ fontSize:8, fontWeight:700, color:C.orange,
                              fontFamily:"ui-monospace,monospace", letterSpacing:".04em", cursor:"default" }}>
                              {momentumDir}
                            </span>
                          </Tip>
                        )}
                      </div>
                      <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                          fontWeight:800, color:colorForPct(s.score) }}>{s.score}/100</span>
                        <span style={{ fontSize:9, color:"var(--mu)" }}>{s.weight}</span>
                      </div>
                    </div>
                    <div style={{ height:6, borderRadius:999, background:"var(--sep)", overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${s.score}%`,
                        background:`linear-gradient(90deg,${colorForPct(s.score)}cc,${colorForPct(s.score)})`,
                        borderRadius:999, transition:"width .5s ease" }}/>
                    </div>
                  </div>
                ))}
              </div>

              {/* Régime / Signal / Action */}
              <div style={{ padding:"0 16px", borderRight:"1px solid var(--sep)",
                display:"flex", flexDirection:"column", justifyContent:"center", gap:8 }}>
                {[
                  { sub:"RÉGIME TECHNIQUE", val:marketBias.toUpperCase(),                        col:regimeCol, tipKey:"regime" },
                  { sub:"SIGNAL PRINCIPAL", val:tscoreSignal.split("·")[0].trim().toUpperCase(), col:C.amber,   tipKey:"signal" },
                  { sub:"ACTION SUGGÉRÉE",  val:actionLabel,                                     col:actionCol, tipKey:"action" },
                ].map(({sub,val,col,tipKey}) => (
                  <div key={sub} style={{ padding:"9px 12px", borderRadius:8,
                    background:`${col}0e`, border:`1px solid ${col}28` }}>
                    <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
                      letterSpacing:".09em", color:"var(--mu)", marginBottom:3,
                      display:"flex", alignItems:"center", gap:4 }}>
                      {sub}<TipIcon text={TIPS[tipKey]}/>
                    </div>
                    <div style={{ fontFamily:"ui-monospace,monospace", fontSize:11,
                      fontWeight:800, color:col, lineHeight:1.3 }}>{val}</div>
                  </div>
                ))}
              </div>


              {/* 4ème colonne : Arguments positifs / Points de vigilance */}
              {(() => {
                const _lp   = parseFloat(d?.timing_last_price ?? d?.price ?? 199.57);
                const _s200 = parseFloat(d?.timing_sma200 ?? d?.sma200 ?? 183.72);
                const _s50  = parseFloat(d?.timing_sma50  ?? d?.sma50  ?? 186.94);
                const _s20  = parseFloat(d?.timing_sma20  ?? d?.sma20  ?? 196.17);
                const _r2   = parseFloat(d?.r_squared ?? 0.84);
                const _t12  = parseFloat(d?.tsmom_12m ?? 60.12);
                const _t3   = parseFloat(d?.tsmom_3m  ?? -6.49);
                const _fip  = parseFloat(d?.fip_score ?? -0.034);
                const _vr   = parseFloat(d?.volume_ratio_20d ?? 1.4767);
                const _wyk  = d?.wyckoff_context ?? "Distribution";
                const _wev  = d?.wyckoff_event   ?? "UTAD";
                const _rsi  = parseFloat(d?.rsi_14  ?? 60.81);
                const _z    = parseFloat(d?.z_score ?? 1.317);

                const above   = _lp > _s200;
                const aligned = _s20 > _s50 && _s50 > _s200;
                const isDistrib = _wyk.includes("Distribution") || _wyk.includes("Markdown");
                const isAccum   = _wyk.includes("Accumulation") || _wyk.includes("Markup");

                const pos = [];
                if (above && aligned)   pos.push("Tendance primaire robuste");
                else if (above)         pos.push("Cours au-dessus de la SMA200");
                if (_r2 >= 0.65)        pos.push(`Tendance régulière (R² ${(_r2*100).toFixed(0)}%)`);
                if (_t12 > 20)          pos.push(`Momentum annuel solide (TSMOM 12M +${_t12.toFixed(1)}%)`);
                if (_fip <= 0 && _t12 > 0) pos.push("Momentum régulier (FIP ≤ 0)");
                if (_vr >= 1.2 && !isDistrib) pos.push(`Volumes élevés (+${((_vr-1)*100).toFixed(0)}% vs moyenne)`);
                if (isAccum)            pos.push(`Phase Wyckoff : ${_wyk}`);
                if (_rsi >= 40 && _rsi <= 60) pos.push(`RSI en zone de force (${_rsi.toFixed(1)})`);
                if (_rsi < 30)          pos.push(`RSI en survente — opportunité (${_rsi.toFixed(1)})`);
                if (scoreMomentum >= 65) pos.push("Leadership sectoriel confirmé");

                const vig = [];
                if (!above)             vig.push("Cours sous la SMA200 — tendance baissière");
                if (!aligned && above)  vig.push("Alignement SMA incomplet");
                if (_t12 > 0 && _t3 < 0) vig.push(`Momentum court terme en repli (3M ${_t3.toFixed(1)}%)`);
                if (_t12 < 0)           vig.push(`TSMOM 12M négatif (${_t12.toFixed(1)}%)`);
                if (isDistrib)          vig.push(`Phase Wyckoff ${_wyk}${_wev && _wev !== "None" ? ` — ${_wev}` : ""}`);
                if (_fip > 0.3)         vig.push("Momentum erratique (FIP > 0)");
                if (_rsi > 70)          vig.push(`RSI en surachat (${_rsi.toFixed(1)})`);
                if (_z > 1.5)           vig.push(`Prix au-dessus de sa droite OLS (Z +${_z.toFixed(2)}σ)`);
                if (_vr < 0.85)         vig.push("Volumes faibles — No Demand");
                if (scoreTiming < 40)   vig.push("Timing sous-optimal");

                return (
                  <div style={{ paddingLeft:16, display:"flex", flexDirection:"column", gap:12 }}>
                    {pos.length > 0 && (
                      <div>
                        <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
                          letterSpacing:".1em", color:C.green, marginBottom:7 }}>
                          ARGUMENTS POSITIFS
                        </div>
                        {pos.slice(0,4).map((p,i) => (
                          <div key={i} style={{ display:"flex", alignItems:"flex-start",
                            gap:6, marginBottom:5 }}>
                            <span style={{ color:C.green, fontSize:11, flexShrink:0 }}>✓</span>
                            <span style={{ fontSize:9.5, color:"var(--ts)", lineHeight:1.4 }}>{p}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {vig.length > 0 && (
                      <div>
                        <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
                          letterSpacing:".1em", color:C.orange, marginBottom:7 }}>
                          POINTS DE VIGILANCE
                        </div>
                        {vig.slice(0,4).map((v,i) => (
                          <div key={i} style={{ display:"flex", alignItems:"flex-start",
                            gap:6, marginBottom:5 }}>
                            <span style={{ color:C.orange, fontSize:11, flexShrink:0 }}>⚠</span>
                            <span style={{ fontSize:9.5, color:"var(--ts)", lineHeight:1.4 }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>{/* fin tm9-diag-grid */}
            {/* ── Ce que les indicateurs révèlent + Lecture croisée (pleine largeur) ── */}
            <div style={{ marginTop:16, paddingTop:14, borderTop:"1px solid var(--sep)" }}>
              <div style={{ marginBottom:10 }}>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
                  color:"var(--ts)", marginBottom:3 }}>
                  Ce que les indicateurs révèlent
                </div>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:8, color:"var(--mu)" }}>
                  4 lectures — tendance · force · volume · timing
                </div>
              </div>
              <div className="tm5-indic-grid">
                {pillarsDisp.filter(p => diagResults[p.key] != null).map(p => {
                  const res  = diagResults[p.key];
                  const col  = getStatusColor(res.ok === true ? "good" : res.ok === false ? "bad" : "neutral");
                  const icon = res.ok === true ? "✓" : res.ok === false ? "✗" : "~";
                  return (
                    <div key={p.key} style={{ display:"flex", gap:8, alignItems:"flex-start",
                      padding:"9px 12px", borderRadius:8,
                      background:`${col}07`, border:`1px solid ${col}22` }}>
                      <span style={{ fontSize:14, color:p.color, flexShrink:0, marginTop:1 }}>{p.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", marginBottom:3 }}>
                          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8,
                            fontWeight:700, color:p.color }}>{p.label}</span>
                          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:7,
                            color:"var(--mu)", opacity:.7 }}>· {p.indic}</span>
                        </div>
                        <p style={{ margin:"0 0 4px", fontSize:9.5, fontStyle:"italic",
                          color:"var(--mu)", lineHeight:1.4 }}>
                          {PILLAR_QUESTIONS[p.key]}
                        </p>
                        <p style={{ margin:0, fontSize:"clamp(10px,1.3vw,11.5px)",
                          fontWeight:700, color:col, lineHeight:1.55 }}>
                          {res.txt}
                        </p>
                      </div>
                      <button onClick={() => setShowGuide(p.key)} title={`Guide ${p.label}`}
                        style={{ width:22, height:22, flexShrink:0, display:"flex",
                          alignItems:"center", justifyContent:"center",
                          background:"transparent", border:`1px solid ${p.color}28`,
                          borderRadius:4, cursor:"pointer", fontSize:9, color:p.color }}>🧭</button>
                    </div>
                  );
                })}
              </div>

              {/* ── Lecture croisée ── */}
              {(() => {
                // Priorité : champs DB (lecture_croisee_*)
                if (d?.lecture_croisee_titre && d?.lecture_croisee_txt) {
                  const okVal = parseInt(d.lecture_croisee_ok ?? 0);
                  const col   = okVal === 1 ? C.green : okVal === -1 ? C.red : C.orange;
                  const icon  = okVal === 1 ? "✓" : okVal === -1 ? "✗" : "⚠";
                  return (
                    <div style={{ marginTop:10, padding:"10px 14px", borderRadius:10,
                      background:`${col}08`, border:`1px solid ${col}28` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                        <span style={{ fontSize:13, color:col }}>{icon}</span>
                        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800,
                          color:col, textTransform:"uppercase", letterSpacing:".1em" }}>
                          {d.lecture_croisee_titre}
                        </span>
                      </div>
                      <p style={{ margin:0, fontSize:"clamp(10px,1.3vw,11.5px)",
                        color:"var(--tx)", lineHeight:1.72 }}>{d.lecture_croisee_txt}</p>
                    </div>
                  );
                }

                // Fallback : calcul dynamique
                const t12pos    = tsmom12v > 0;
                const t3neg     = tsmom3v  < 0;
                const isDistrib = wyckoffv && (wyckoffv.includes("Distribution") || wyckoffv.includes("Markdown"));
                const isAccum   = wyckoffv && (wyckoffv.includes("Accumulation") || wyckoffv.includes("Markup"));
                const volFort   = volR20v >= 1.2;
                const volFaible = volR20v < 0.85;
                const rsiOB     = rsi14v > 70;
                const rsiOS     = rsi14v < 30;
                const above     = lastPr && sma200v && lastPr > sma200v;

                let lecture = null;

                if (t12pos && t3neg && isDistrib)
                  lecture = { col:C.orange, icon:"⚠", titre:"Lecture croisée — signal de transition",
                    txt:`Le momentum ralentit (+${tsmom12v.toFixed(1)}% sur 12M mais −${Math.abs(tsmom3v).toFixed(1)}% sur 3M) pendant que les institutionnels allègent (phase Wyckoff ${wyckoffv}${wyEventv && wyEventv !== "None" ? ` — ${wyEventv}` : ""}). Ces deux signaux racontent la même histoire : les mains fortes sortent progressivement en profitant du flux des acheteurs tardifs. Le prix peut encore monter brièvement, mais la pression vendeuse augmente en coulisse.` };

                else if (t12pos && !t3neg && volFort && isAccum && above)
                  lecture = { col:C.green, icon:"✓", titre:"Lecture croisée — configuration solide",
                    txt:`Tendance haussière, momentum positif sur les deux horizons, et institutionnels en accumulation avec des volumes élevés. Les trois piliers se confirment mutuellement : direction, force et participation sont alignées.` };

                else if (above && t12pos && t3neg && !isDistrib && !volFaible)
                  lecture = { col:C.amber, icon:"~", titre:"Lecture croisée — consolidation dans une tendance haussière",
                    txt:`La tendance de fond reste haussière et les volumes ne montrent pas d'alerte institutionnelle. L'essoufflement du momentum à court terme (−${Math.abs(tsmom3v).toFixed(1)}% sur 3M dans une progression annuelle de +${tsmom12v.toFixed(1)}%) ressemble à une consolidation normale plutôt qu'à un retournement. Ces phases de respiration peuvent offrir un meilleur point d'entrée que d'acheter en pleine accélération.` };

                else if (t12pos && volFaible && !isDistrib)
                  lecture = { col:C.amber, icon:"~", titre:"Lecture croisée — hausse sans conviction volumétrique",
                    txt:`Le momentum est positif mais les volumes sont sous la normale (${volR20v.toFixed(2)}× la moyenne). Une hausse sans volume est fragile — elle peut se retourner rapidement faute de participation institutionnelle. Wyckoff nomme cela "No Demand".` };

                else if (rsiOS && above)
                  lecture = { col:C.green, icon:"✓", titre:"Lecture croisée — opportunité dans la tendance",
                    txt:`Le titre est en survente (RSI ${rsi14v.toFixed(1)}) à l'intérieur d'une tendance haussière confirmée. C'est le type d'entrée idéal : un repli temporaire dans une tendance de fond intacte. Le ratio risque/rendement est favorable.` };

                else if (rsiOB && t3neg)
                  lecture = { col:C.red, icon:"✗", titre:"Lecture croisée — double signal de prudence",
                    txt:`Le RSI (${rsi14v.toFixed(1)}) signale un surachat ET le momentum à 3 mois est négatif (${tsmom3v.toFixed(1)}%). Ces deux signaux indiquent que le titre a monté trop vite et commence à perdre sa dynamique. Ce n'est pas le moment d'entrer.` };

                if (!lecture) return null;
                return (
                  <div style={{ marginTop:10, padding:"10px 14px", borderRadius:10,
                    background:`${lecture.col}08`, border:`1px solid ${lecture.col}28` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                      <span style={{ fontSize:13, color:lecture.col }}>{lecture.icon}</span>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800,
                        color:lecture.col, textTransform:"uppercase", letterSpacing:".1em" }}>
                        {lecture.titre}
                      </span>
                    </div>
                    <p style={{ margin:0, fontSize:"clamp(10px,1.3vw,11.5px)",
                      color:"var(--tx)", lineHeight:1.72 }}>{lecture.txt}</p>
                  </div>
                );
              })()}
            </div>

            {/* Mobile fallback bars */}
            <div className="tm5-sbars-m">
              {pillarsDisp.map(s => (
                <div key={s.key} style={{ borderRadius:10, padding:"9px 10px",
                  background:"var(--sur)", border:"1px solid var(--sep)" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:10, color:"var(--mu)", fontWeight:600 }}>{s.label}</span>
                    <span style={{ fontSize:9, color:"var(--mu)" }}>{s.weight}</span>
                  </div>
                  <div style={{ fontFamily:"ui-monospace,monospace", fontSize:18,
                    fontWeight:800, color:colorForPct(s.score), lineHeight:1, marginBottom:4 }}>{s.score}</div>
                  <div style={{ height:4, borderRadius:999, background:"var(--sep)", overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${s.score}%`,
                      background:colorForPct(s.score), borderRadius:999 }}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ══ BANDEAU NARRATIF (Niveau 2) ══ */}
      {(() => {
        // ── Génération dynamique cohérente avec la lecture croisée ──────────
        const _f = (v, def) => { const n = parseFloat(v ?? def); return isNaN(n) ? null : n; };
        const nLastPr   = _f(d?.last_price, d?.price ?? 199.57);
        const nSma200   = _f(d?.sma200,     183.72);
        const nSma20    = _f(d?.sma20,      196.17);
        const nSma50    = _f(d?.sma50,      186.94);
        const nT12      = _f(d?.tsmom_12m,  60.12) / 100;
        const nT6       = _f(d?.tsmom_6m,   -6.53) / 100;
        const nT3       = _f(d?.tsmom_3m,   -6.49) / 100;
        const nRsi      = _f(d?.rsi_14,     60.81);
        const nZ        = _f(d?.z_score,    1.317);
        const nVolR     = _f(d?.volume_ratio_20d, 1.4767);
        const nWyckoff  = d?.wyckoff_context ?? null;
        const nWyEv     = d?.wyckoff_event   ?? null;
        const company   = d?.company_name ?? d?.symbol ?? "Le titre";

        const isDistrib = nWyckoff && (nWyckoff.includes("Distribution") || nWyckoff.includes("Markdown"));
        const isAccum   = nWyckoff && (nWyckoff.includes("Accumulation") || nWyckoff.includes("Markup"));
        const above200  = nLastPr && nSma200 && nLastPr > nSma200;
        const aligned   = nSma20 && nSma50 && nSma200 ? nSma20 > nSma50 && nSma50 > nSma200 : null;
        const momEssouf = nT12 > 0 && nT3 < 0;
        const momAcc    = nT12 > 0 && nT3 >= 0;

        // Phrase 1 : Tendance
        const p1 = above200
          ? `La tendance de fond est haussière${aligned ? ", confirmée par l'alignement SMA20 > SMA50 > SMA200" : " mais l'alignement des moyennes mobiles n'est pas encore complet"}.`
          : `La tendance de fond est baissière — le cours (${nLastPr?.toFixed(2)}) est sous la SMA200 (${nSma200?.toFixed(2)}).`;

        // Phrase 2 : Momentum — 3 horizons, formulé en clair
        const p2 = nT12 < 0
          ? `Le momentum est négatif sur les trois horizons : 12 mois ${(nT12*100).toFixed(1)}%, 6 mois ${(nT6*100).toFixed(1)}%, 3 mois ${(nT3*100).toFixed(1)}%. Le mouvement de fond est baissier.`
          : nT12 > 0 && nT6 < 0 && nT3 < 0
          ? `Le momentum annuel reste positif (+${(nT12*100).toFixed(1)}% sur 12 mois), mais il s'essouffle sur les horizons intermédiaire et court : ${(nT6*100).toFixed(1)}% sur 6 mois et ${(nT3*100).toFixed(1)}% sur 3 mois. Le retournement est profond.`
          : nT12 > 0 && nT6 >= 0 && nT3 < 0
          ? `Le momentum annuel reste positif (+${(nT12*100).toFixed(1)}% sur 12 mois, +${(nT6*100).toFixed(1)}% sur 6 mois), mais il s'essouffle récemment : ${(nT3*100).toFixed(1)}% sur 3 mois. Essoufflement de court terme dans une tendance de fond intacte.`
          : nT12 > 0 && nT6 < 0 && nT3 >= 0
          ? `Signal mixte : +${(nT12*100).toFixed(1)}% sur 12 mois, ${(nT6*100).toFixed(1)}% sur 6 mois, +${(nT3*100).toFixed(1)}% sur 3 mois. Rebond récent après une phase de correction intermédiaire.`
          : nT12 > 0
          ? `Le momentum est positif sur les trois horizons : +${(nT12*100).toFixed(1)}% sur 12 mois, ${nT6 >= 0 ? "+" : ""}${(nT6*100).toFixed(1)}% sur 6 mois, ${nT3 >= 0 ? "+" : ""}${(nT3*100).toFixed(1)}% sur 3 mois.`
          : `Momentum : ${(nT12*100).toFixed(1)}% sur 12 mois, ${(nT6*100).toFixed(1)}% sur 6 mois.`;

        // Phrase 3 : Volume / Wyckoff
        const p3 = isDistrib
          ? `Les institutionnels sont en phase de distribution${nWyEv && nWyEv !== "None" ? ` (${nWyEv})` : ""} : ils allègent leurs positions pendant que le public achète.`
          : isAccum
          ? `Les volumes confirment : phase Wyckoff ${nWyckoff}${nWyEv && nWyEv !== "None" ? ` (${nWyEv})` : ""}, les institutionnels s'accumulent.`
          : nVolR >= 1.2
          ? `Les volumes sont élevés (${nVolR.toFixed(2)}× la moyenne) — la participation institutionnelle est présente.`
          : nVolR < 0.85
          ? `Les volumes sont faibles (${nVolR.toFixed(2)}× la moyenne) — la hausse manque de conviction institutionnelle.`
          : `Les volumes sont dans la norme${nWyckoff ? ` (phase Wyckoff : ${nWyckoff})` : ""}.`;

        // Phrase 4 : Timing
        const p4 = nRsi > 70 && nZ > 2
          ? `RSI 14 à ${nRsi.toFixed(1)} (surachat) et Z-Score à +${nZ.toFixed(2)}σ : double sur-extension — ce n'est pas le bon moment pour entrer.`
          : nRsi < 30
          ? `RSI 14 à ${nRsi.toFixed(1)} (survente) : zone de rebond statistiquement favorable.`
          : nRsi >= 40 && nRsi <= 60 && Math.abs(nZ) <= 1
          ? `RSI 14 à ${nRsi.toFixed(1)} et Z-Score à ${nZ >= 0 ? "+" : ""}${nZ.toFixed(2)}σ : confluence idéale pour entrer.`
          : `RSI 14 à ${nRsi.toFixed(1)}, Z-Score à ${nZ >= 0 ? "+" : ""}${nZ.toFixed(2)}σ${nRsi > 60 ? " — légèrement en surachat, attendre un repli" : nRsi < 40 ? " — légèrement en survente" : ""}.`;

        const diag  = d?.narrative_diagnostic  ?? [p1, p2, p3, p4].join(" ");
        const concl = d?.narrative_conclusion  ?? null;
        const decSumm = d?.decision_summary    ?? "";
        const bdrCol  = actionCol;
        return (
          <div style={{
            borderRadius:12, border:`1px solid ${bdrCol}33`,
            background:`${bdrCol}08`, padding:"12px 14px",
          }}>
            {/* Badge signal */}
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, gap:8}}>
              <div style={{display:"flex", alignItems:"center", gap:6}}>
                <span style={{fontFamily:"ui-monospace,monospace", fontSize:9, fontWeight:800,
                  textTransform:"uppercase", letterSpacing:".1em", color:"var(--mu)"}}>
                  SYNTHÈSE D'ANALYSE
                </span>
                <TipIcon text="Synthèse narrative générée à partir des 4 piliers. Lire de haut en bas : diagnostic → conclusion → action."/>
              </div>
              <span style={{
                display:"inline-flex", alignItems:"center", gap:4,
                borderRadius:999, padding:"3px 10px", fontSize:10, fontWeight:800,
                background:`${bdrCol}18`, border:`1px solid ${bdrCol}44`, color:bdrCol,
                fontFamily:"ui-monospace,monospace",
              }}>
                {actionLabel}
              </span>
            </div>
            {/* Diagnostic */}
            <div style={{fontSize:11, lineHeight:1.75, color:"var(--ts)", marginBottom:6}}>
              {diag}
            </div>
            {/* Conclusion SQL si disponible */}
            {concl && <>
              <div style={{height:1, background:"var(--sep)", margin:"8px 0"}}/>
              <div style={{fontSize:11, lineHeight:1.65, color:bdrCol, fontWeight:600}}>
                {concl}
              </div>
            </>}
            {decSumm && (
              <div style={{marginTop:6, fontSize:10, color:"var(--mu)", fontWeight:600,
                lineHeight:1.5, borderTop:"1px solid var(--sep)", paddingTop:6}}>
                {decSumm}
              </div>
            )}
          </div>
        );
      })()}

      {/* ══ 2. CHART ══ */}
      <div className="tm5-card">
        {/* Header + controls */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:9 }}>
            <Tip {...TIPS.regression}>
              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
                textTransform:"uppercase", letterSpacing:".12em", color:"var(--mu)" }}>PRIX &amp; TENDANCE</span>
            </Tip>
          </div>
          {/* Periods */}
          <div style={{ display:"flex", gap:4, marginBottom:9, flexWrap:"wrap" }}>
            {[
              { p:"3M",  tip:TIPS.period_3m  },
              { p:"6M",  tip:TIPS.period_6m  },
              { p:"1Y",  tip:TIPS.period_1y  },
              { p:"3Y",  tip:TIPS.period_3y  },
              { p:"5Y",  tip:TIPS.period_5y  },
              { p:"MAX", tip:TIPS.period_max },
            ].map(({p,tip})=>(
              <Tip key={p} {...tip}>
                <button onClick={()=>setPeriod(p)} style={{
                  borderRadius:8,
                  border:`1px solid ${period===p?C.amber+"66":"var(--card-bdr)"}`,
                  background: period===p ? "rgba(202,168,94,.14)" : "transparent",
                  padding:"4px 11px", fontSize:11, fontWeight:800, cursor:"pointer",
                  color: period===p ? C.amber : "var(--mu)",
                  transition:"all .12s",
                }}>{p}</button>
              </Tip>
            ))}
          </div>
          {/* Toggles */}
          <div className="tm5-toggles" style={{ marginBottom:6 }}>
            <Tog label="SMA 20"  active={showSMA20&&isShortRange}  color={SMA_COL.s20}
              tip={isShortRange ? TIPS.toggle_sma20 : "SMAs affichées uniquement pour 3M, 6M, 1Y"}
              onChange={()=>isShortRange&&setSMA20(v=>!v)}/>
            <Tog label="SMA 50"  active={showSMA50&&isShortRange}  color={SMA_COL.s50}
              tip={isShortRange ? TIPS.toggle_sma50 : "SMAs affichées uniquement pour 3M, 6M, 1Y"}
              onChange={()=>isShortRange&&setSMA50(v=>!v)}/>
            <Tog label="SMA 200" active={showSMA200&&isShortRange} color={SMA_COL.s200}
              tip={isShortRange ? TIPS.toggle_sma200 : "SMAs affichées uniquement pour 3M, 6M, 1Y"}
              onChange={()=>isShortRange&&setSMA200(v=>!v)}/>
            <Tog label="Régression" active={showReg}    color={SIG.reg.col}  tip={TIPS.toggle_reg}     onChange={()=>setReg(v=>!v)}/>
            <Tog label="Canal σ"    active={showBands}  color={SIG.b2p.col}  tip={TIPS.toggle_canal}   onChange={()=>setBands(v=>!v)}/>
            <Tog label="Z-Score"    active={showZScore} color={C.amber}      tip={TIPS.toggle_zscore}  onChange={()=>setZScore(v=>!v)}/>
            <Tog label="Volume"     active={showVolume} color={C.green}      tip={TIPS.toggle_volume}  onChange={()=>setVolume(v=>!v)}/>
            <Tog label="RSI 14"     active={showRSI && !isMonthlySource} color={C.orange}
              tip={isMonthlySource ? "RSI 14 calculé sur données daily — non disponible au-delà de 1Y" : TIPS.toggle_rsi}
              onChange={()=>!isMonthlySource && setRSI(v=>!v)}/>
            <Tog label="S/R"        active={showLevels && !isMonthlySource} color="#8b6f9a"
              tip={isMonthlySource ? "Support/Résistance calculés sur 365j glissants — non pertinents au-delà de 1Y" : TIPS.toggle_sr}
              onChange={()=>!isMonthlySource && setLevels(v=>!v)}/>
            <div className="tm5-chart-type">
              {[["candle","🕯 Bougies","toggle_candle"],["line","〜 Ligne","toggle_line"]].map(([t,l,tk])=>(
                <Tip key={t} {...TIPS[tk]}>
                  <button onClick={()=>setChartType(t)} style={{
                    padding:"5px 11px", borderRadius:8, fontSize:10, fontWeight:800, cursor:"pointer",
                    border:`1px solid ${chartType===t?C.amber+"66":"var(--card-bdr)"}`,
                    background: chartType===t?"rgba(202,168,94,.14)":"transparent",
                    color: chartType===t?C.amber:"var(--mu)",
                  }}>{l}</button>
                </Tip>
              ))}
            </div>
          </div>
        </div>

        {/* Chart */}
        <CandleChart
          data={chartData} range={period}
          showSMA20={showSMA20} showSMA50={showSMA50} showSMA200={showSMA200}
          showReg={showReg} showBands={showBands} showZScore={showZScore}
          showVolume={showVolume} showRSI={showRSI && !isMonthlySource} showLevels={showLevels && !isMonthlySource}
          srLevels={SR_LEVELS}
          chartType={chartType} isDark={isDark}
        />

        {/* Legend below */}
        <div style={{ borderTop:"1px solid var(--sep)", marginTop:8 }}>
          <div className="tm5-legend">
            {legendItems.map((it,i)=>{
              const tipKey = it.tipKey;
              const tip = tipKey ? TIPS[tipKey] : null;
              const inner = (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:5, cursor: tip ? "help" : "default" }}>
                  {it.dash ? (
                    <svg width={16} height={10} style={{flexShrink:0}}>
                      <line x1={0} y1={5} x2={16} y2={5} stroke={it.dot}
                        strokeWidth={2} strokeDasharray={it.dash}/>
                    </svg>
                  ) : (
                    <div style={{ width:8, height:8, borderRadius:999, background:it.dot, flexShrink:0 }}/>
                  )}
                  <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                    fontWeight:600, color:"var(--mu)" }}>{it.label}</span>
                </div>
              );
              return tip ? <Tip key={i} {...tip}>{inner}</Tip> : inner;
            })}
          </div>
          {!isShortRange && (showSMA20||showSMA50||showSMA200) && (
            <div style={{ fontSize:10, color:C.amber, fontWeight:600, marginTop:4, opacity:.8 }}>
              ⓘ SMAs affichées uniquement pour 3M, 6M et 1Y (données journalières)
            </div>
          )}
        </div>
      </div>

      {/* ══ 3. RELATIVE STRENGTH HISTORIQUE ══ */}
      <div className="tm5-card">
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:14,
          paddingBottom:11, borderBottom:"1px solid var(--sep)" }}>
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
            textTransform:"uppercase", letterSpacing:".12em", color:"var(--mu)" }}>
            RELATIVE STRENGTH vs INDICE DE RÉFÉRENCE
          </span>
          <TipIcon text={TIPS.rs}/>
        </div>
        <RSChart isDark={isDark} symbol={d?.symbol}/>
      </div>

      {/* ══ 4. QUATRE PILIERS ══ */}
      <div className="tm5-pillars">

        {/* Structure */}
        {(() => {
          const trendDir  = d?.trend_direction       ?? "Haussière";
          const ltScore   = parseFloat(d?.lt_trend_score  ?? 94.4);
          const stScore   = parseFloat(d?.st_trend_score  ?? 55.3);
          const tqs       = Math.round(parseFloat(d?.trend_quality_score ?? 46));
          const tss       = Math.round(parseFloat(d?.trend_score         ?? 65.2));
          const compScore = Math.round(parseFloat(d?.dd_value_creation_score ?? 90.26));
          const r2val     = parseFloat(d?.r_squared   ?? 0.844);
          const cagrVal   = parseFloat(d?.cagr        ?? 37.3);
          const sma20v    = parseFloat(d?.sma20       ?? 196.171);
          const sma50v    = parseFloat(d?.sma50       ?? 186.943);
          const sma200v   = parseFloat(d?.sma200      ?? 183.721);
          const smaAlign  = d?.sma_alignment          ?? "Alignement haussier parfait";
          const arrow     = trendDir === "Haussière" ? "↑" : trendDir === "Baissière" ? "↓" : "→";
          const tCol      = trendDir === "Haussière" ? C.green : trendDir === "Baissière" ? C.red : C.amber;
          const ltLbl     = ltScore >= 70 ? `${arrow} Haussière forte` : ltScore >= 50 ? `${arrow} Haussière` : "→ Neutre";
          const stLbl     = stScore >= 70 ? `${arrow} Haussière forte` : stScore >= 50 ? `${arrow} Haussière` : stScore >= 30 ? "→ Correction" : "↓ Faiblesse CT";
          const smaShort  = smaAlign.includes("parfait") ? "Fort ↗" : smaAlign.includes("haussier") ? "Haussier ↗" : smaAlign.includes("baissier") ? "Baissier ↘" : "Mixte →";
          const tipLt     = d?.interp_lt_trend_score      ?? TIPS.tendance_5ans;
          const tipSt     = d?.interp_st_trend_score      ?? TIPS.tendance_1an;
          const tipTqs    = d?.interp_trend_quality_score ?? TIPS.trend_quality;
          const tipTss    = d?.interp_trend_score         ?? TIPS.trend_strength;
          const tipR2     = d?.interp_r_squared           ?? TIPS.r2;
          const tipCagr   = d?.interp_cagr                ?? TIPS.cagr_5y;
          const tipAlign  = d?.interp_sma_alignment       ?? TIPS.alignement;
          const tw = t => typeof t === "string" ? { body: t } : t;
          return (
        <PillarCard number={1} title="STRUCTURE DE TENDANCE" score={tss}
          color={colorForPct(tss)} pillar="structure" d={d} onGuide={() => setShowGuide("structure")}
          tip="Analyse de la direction du prix sur 3 horizons + qualité/force + niveaux clés.">
          {/* ── Ligne de synthèse pilier 1 ── */}
          {(() => {
            const synth = d?.interp_trend_global ?? `Structure ${trendDir.toLowerCase()} — CAGR +${cagrVal.toFixed(1)}%, R² ${r2val.toFixed(2)}. ${tss >= 75 ? "Tendance de haute qualité." : tss >= 55 ? "Tendance globalement positive." : "Structure sous pression."}`;
            const col   = colorForPct(tss);
            return (
              <div style={{borderRadius:8, padding:"7px 10px", marginBottom:10,
                background:`${col}0d`, border:`1px solid ${col}33`,
                fontSize:10, fontWeight:600, color:"var(--ts)", lineHeight:1.55}}>
                {synth}
              </div>
            );
          })()}
          {[
            { label:"Tendance primaire", value:`${arrow} ${trendDir}`,      color:tCol,                       tip:TIPS.tendance_prim  },
            { label:"Tendance 5 ans",   value:ltLbl,                        color:tCol,                       tip:tw(tipLt)           },
            { label:"Tendance 1 an",    value:stLbl,                        color:stScore>=50?tCol:C.amber,   tip:tw(tipSt)           },
          ].map(r=><MRow key={r.label} {...r}/>)}
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>
          <MRow label="Trend Quality Score"  value={`${tqs}`}                   tip={tw(tipTqs)}  />
          <MRow label="Trend Strength Score" value={`${tss}`}                   tip={tw(tipTss)}  />
          <MRow label="Compounder Score"     value={`${compScore}`}             tip={TIPS.compounder} />
          <MRow label="R² (log, all)"        value={r2val.toFixed(3)}           tip={tw(tipR2)}   />
          <MRow label="CAGR"                 value={`+${cagrVal.toFixed(1)}%`}  tip={tw(tipCagr)} />
          {isShortRange && (
            <>
              <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>
              <MRow label="SMA 20"  value={sma20v?.toFixed(2)  ?? "—"} color={SMA_COL.s20}  tip={TIPS.sma20}  />
              <MRow label="SMA 50"  value={sma50v?.toFixed(2)  ?? "—"} color={SMA_COL.s50}  tip={TIPS.sma50}  />
              <MRow label="SMA 200" value={sma200v?.toFixed(2) ?? "—"} color={SMA_COL.s200} tip={TIPS.sma200} />
            </>
          )}
          <div style={{fontSize:10,fontWeight:700,color:"var(--mu)",margin:"7px 0",
            display:"flex",alignItems:"center",gap:5}}>
            <Tip {...tw(tipAlign)}>Alignement SMA</Tip>
            <span style={{color:C.green,fontWeight:800}}>{smaShort}</span>
          </div>
        </PillarCard>
          );
        })()}

        {/* Momentum */}
        <PillarCard number={2} title="FORCE / MOMENTUM" score={scoreMomentum}
          color={colorForPct(scoreMomentum)} pillar="momentum" d={d} onGuide={() => setShowGuide("momentum")}
          tip="Analyse du momentum absolu et relatif : TSMOM, leadership, force interne.">

          {/* ── Ligne de synthèse pilier 2 ── */}
          {(() => {
            const synth = d?.interp_momentum_global ?? `Momentum ${d?.momentum_direction ?? "en retournement baissier"}. TSMOM 12M ${parseFloat(d?.tsmom_12m??60.12)>=0?"+":""}${parseFloat(d?.tsmom_12m??60.12).toFixed(1)}% / 3M ${parseFloat(d?.tsmom_3m??-6.49)>=0?"+":""}${parseFloat(d?.tsmom_3m??-6.49).toFixed(1)}%.`;
            const col   = colorForPct(scoreMomentum);
            return (
              <div style={{borderRadius:8, padding:"7px 10px", marginBottom:10,
                background:`${col}0d`, border:`1px solid ${col}33`,
                fontSize:10, fontWeight:600, color:"var(--ts)", lineHeight:1.55}}>
                {synth}
              </div>
            );
          })()}

          {/* ── Badge momentum_direction ── */}
          {(() => {
            const dir = d?.momentum_direction ?? "Retournement baissier";
            const pat = d?.momentum_pattern   ?? "Transition / Mixte";
            const str = d?.momentum_strength  ?? "Faible";
            const dirMeta = {
              "Haussier":              { col:C.green,  icon:"↑", bg:`${C.green}12`  },
              "Baissier":              { col:C.red,    icon:"↓", bg:`${C.red}12`    },
              "Retournement haussier": { col:C.green,  icon:"⟳", bg:`${C.green}12`  },
              "Retournement baissier": { col:C.orange, icon:"⚠", bg:`${C.orange}12` },
              "Neutre / Range":        { col:C.amber,  icon:"→", bg:`${C.amber}10`  },
              "Mixte / Indéterminé":   { col:"var(--mu)", icon:"~", bg:"var(--sur)" },
            };
            const strMeta = {
              "Fort":  { col:C.green,  dot:"●" },
              "Moyen": { col:C.amber,  dot:"●" },
              "Faible":{ col:C.red,    dot:"●" },
            };
            const m = dirMeta[dir] || dirMeta["Mixte / Indéterminé"];
            const s = strMeta[str] || strMeta["Moyen"];
            return (
              <div style={{marginBottom:11}}>
                {/* Ligne principale : direction + force */}
                <Tip {...TIPS.mom_direction}>
                  <div style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"8px 11px", borderRadius:8,
                    background:m.bg, border:`1px solid ${m.col}30`,
                    cursor:"default",
                  }}>
                    <div style={{display:"flex", alignItems:"center", gap:7}}>
                      <span style={{fontSize:16, lineHeight:1, color:m.col}}>{m.icon}</span>
                      <div>
                        <div style={{fontSize:9, fontWeight:700, textTransform:"uppercase",
                          letterSpacing:".09em", color:"var(--mu)", marginBottom:1}}>
                          DIRECTION MOMENTUM
                        </div>
                        <div style={{fontSize:12, fontWeight:800, color:m.col}}>{dir}</div>
                      </div>
                    </div>
                    {/* Intensité à droite */}
                    <Tip {...TIPS.mom_strength}>
                      <div style={{
                        display:"flex", flexDirection:"column", alignItems:"flex-end", gap:1,
                        cursor:"default",
                      }}>
                        <div style={{fontSize:8, fontWeight:700, textTransform:"uppercase",
                          letterSpacing:".08em", color:"var(--mu)"}}>INTENSITÉ</div>
                        <div style={{display:"flex", alignItems:"center", gap:4}}>
                          <span style={{fontSize:10, color:s.col}}>{s.dot}</span>
                          <span style={{fontSize:11, fontWeight:800, color:s.col,
                            fontFamily:"ui-monospace,monospace"}}>{str}</span>
                        </div>
                      </div>
                    </Tip>
                  </div>
                </Tip>
                {/* Ligne pattern */}
                <Tip {...TIPS.mom_pattern}>
                  <div style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"5px 11px", marginTop:4, borderRadius:8,
                    background:"var(--sur)", border:"1px solid var(--sep)",
                    cursor:"default",
                  }}>
                    <span style={{fontSize:9, fontWeight:700, textTransform:"uppercase",
                      letterSpacing:".08em", color:"var(--mu)"}}>PATTERN</span>
                    <span style={{fontSize:10, fontWeight:700,
                      color:"var(--tx)", fontFamily:"ui-monospace,monospace"}}>{pat}</span>
                  </div>
                </Tip>
              </div>
            );
          })()}

          {/* ── Barres TSMOM ── */}
          {(() => {
            const t12 = parseFloat(d?.tsmom_12m ?? 60.12);
            const t6  = parseFloat(d?.tsmom_6m  ?? -6.53);
            const t3  = parseFloat(d?.tsmom_3m  ?? -6.49);
            const bar = v => Math.max(2, Math.min(98, 50 + v));
            const tw  = t => typeof t === "string" ? { body: t } : t;
            return [
              { label:"TSMOM 12M", value:`${t12>=0?"+":""}${t12.toFixed(1)}%`, color:t12>=0?C.green:C.red, bar:bar(t12), tip:tw(d?.interp_tsmom_12m ?? TIPS.tsmom12) },
              { label:"TSMOM 6M",  value:`${t6>=0?"+":""}${t6.toFixed(1)}%`,   color:t6>=0?C.green:C.red,  bar:bar(t6),  tip:tw(d?.interp_tsmom_6m  ?? TIPS.tsmom6)  },
              { label:"TSMOM 3M",  value:`${t3>=0?"+":""}${t3.toFixed(1)}%`,   color:t3>=0?C.green:C.red,  bar:bar(t3),  tip:tw(d?.interp_tsmom_3m  ?? TIPS.tsmom3)  },
            ].map(r=>(
            <div key={r.label} style={{marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                <Tip {...(typeof r.tip==="string"?{body:r.tip}:r.tip)}>
                  <span style={{fontSize:11,color:"var(--mu)",fontWeight:600}}>{r.label}</span>
                </Tip>
                <span style={{fontFamily:"ui-monospace,monospace",fontSize:11,
                  fontWeight:800,color:r.color}}>{r.value}</span>
              </div>
              <div style={{height:5,borderRadius:999,background:"var(--sep)",overflow:"hidden"}}>
                <div style={{height:"100%",width:`${r.bar}%`,background:r.color,borderRadius:999}}/>
              </div>
            </div>
          ));
          })()}

          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>
          {(() => {
            const tw = t => typeof t === "string" ? { body: t } : t;
            const momScore  = parseFloat(d?.dd_momentum_score ?? d?.momentum_score ?? 53.86);
            const ldScore   = Math.round(parseFloat(d?.dd_momentum_score ?? 53.86));
            const fip       = parseFloat(d?.fip_score ?? -0.034);
            const joursPos  = parseFloat(d?.positive_days_pct ?? 51.71);
            const m1        = parseFloat(d?.last_return_m1 ?? 14.43);
            const ytd       = parseFloat(d?.ytd_pct ?? 7.01);
            return (<>
              <MRow label="Momentum Score"     value={`${Math.round(momScore)}`}          tip={tw(d?.interp_momentum_score    ?? TIPS.mom_score)}  />
              <MRow label="Leadership Score"   value={`${ldScore}`}                        tip={TIPS.leadership}                                    />
              <MRow label="FIP (Qualité)"      value={fip.toFixed(3)}                      tip={tw(d?.interp_fip_score         ?? TIPS.fip)}         />
              <MRow label="Jours positifs (%)" value={`${joursPos.toFixed(1)}%`}           tip={tw(d?.interp_positive_days_pct ?? TIPS.jours_pos)}   />
              <MRow label="M1 Return"          value={`${m1>=0?"+":""}${m1.toFixed(1)}%`}  tip={tw(d?.interp_last_return_m1    ?? TIPS.m1_return)}   />
              <MRow label="YTD Return"         value={`${ytd>=0?"+":""}${ytd.toFixed(1)}%`} tip={TIPS.ytd}                                           />
            </>);
          })()}
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>

          {/* ── Bloc divergence ── */}
          {(() => {
            const t12pos     = parseFloat(d?.tsmom_12m ?? 60.12) > 0;
            const t3pos      = parseFloat(d?.tsmom_3m  ?? -6.49) > 0;
            const isDiverg   = t12pos && !t3pos;
            const divText    = d?.interp_momentum_divergence ?? null;
            const dir        = d?.momentum_direction ?? "Retournement baissier";
            return isDiverg ? (
              <div style={{borderRadius:8,border:`1px solid ${C.orange}44`,background:`${C.orange}0d`,padding:"9px 10px"}}>
                <div style={{fontSize:10,fontWeight:800,color:C.orange,marginBottom:3,display:"flex",alignItems:"center",gap:5}}>
                  Divergence : OUI ⚠ <TipIcon text={TIPS.divergence}/>
                </div>
                <div style={{fontSize:10,color:"var(--mu)",fontWeight:600,lineHeight:1.5}}>
                  {divText ?? `TSMOM 12M positif mais CT négatif — ${dir.toLowerCase()}.`}
                </div>
              </div>
            ) : (
              <div style={{borderRadius:8,border:"1px solid var(--sep)",background:"var(--sur)",padding:"9px 10px"}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--mu)",display:"flex",alignItems:"center",gap:5}}>
                  Cohérence momentum ✓
                </div>
                <div style={{fontSize:10,color:"var(--mu)",fontWeight:600,lineHeight:1.5}}>
                  Les horizons 3M / 6M / 12M convergent dans la même direction.
                </div>
              </div>
            );
          })()}
        </PillarCard>

        {/* Volume */}
        {(() => {
          const tw        = t => typeof t === "string" ? { body: t } : t;
          const vScore    = Math.round(parseFloat(d?.volume_score         ?? 68));
          const oppScore  = parseFloat(d?.opportunity_score               ?? 9.21);
          const liqScore  = Math.round(parseFloat(d?.liquidity_score      ?? 0.9995) * 100);
          const r20d      = parseFloat(d?.volume_ratio_20d                ?? 1.4767);
          const r60d      = parseFloat(d?.volume_ratio_60d                ?? 1.2805);
          const suppVR    = parseFloat(d?.support_vol_ratio               ?? 1.043);
          const resVR     = parseFloat(d?.resist_vol_ratio                ?? 1.377);
          const absorption= d?.absorption_distribution                    ?? "Neutral";
          const wyckoff   = d?.wyckoff_context                            ?? "Distribution";
          const wyckoffEv = d?.wyckoff_event                              ?? "UTAD";
          const priceM1   = parseFloat(d?.last_return_m1                  ?? 14.43);
          // volume_trend_20_60 : si absent, déduire depuis les ratios (20d > 60d = Rising)
          const _rawTrend  = d?.volume_trend_20_60 ?? null;
          const trend20_60 = _rawTrend ?? (r20d >= r60d * 1.05 ? "Rising" : r20d <= r60d * 0.95 ? "Falling" : "Neutral");
          // Divergence : prix monte MAIS volumes en baisse relative ET ratio 20J sous la normale
          const volDiverg = priceM1 > 2 && r20d < 0.85;
          const rCol      = v => v >= 1.2 ? C.green : v >= 0.85 ? C.amber : C.red;
          const absLabel  = { Absorption:"Absorption", Distribution:"Distribution", Neutral:"Neutre" }[absorption] || absorption;
          const absCol    = absorption === "Absorption" ? C.green : absorption === "Distribution" ? C.red : "var(--mu)";
          const wyCol     = wyckoff === "Distribution" ? C.orange : wyckoff === "Accumulation" ? C.green : "var(--mu)";
          const wyLabel   = wyckoffEv && wyckoffEv !== "None" ? `${wyckoff} — ${wyckoffEv}` : wyckoff;

          return (
        <PillarCard number={3} title="PARTICIPATION / VOLUME" score={vScore}
          color={colorForPct(vScore)} pillar="volume" d={d} onGuide={() => setShowGuide("volume")}
          tip="Analyse de la participation des volumes à la tendance. Phase Wyckoff.">
          {/* ── Ligne de synthèse pilier 3 ── */}
          {(() => {
            const synth = d?.interp_volume_global ?? `Phase ${wyckoff}${wyckoffEv&&wyckoffEv!=="None"?" — "+wyckoffEv:""}. Ratio 20J : ${r20d.toFixed(2)}x. ${wyckoff==="Distribution"?"Signal de prudence sur les entrées.":""}`;
            const col   = colorForPct(vScore);
            return (
              <div style={{borderRadius:8, padding:"7px 10px", marginBottom:10,
                background:`${col}0d`, border:`1px solid ${col}33`,
                fontSize:10, fontWeight:600, color:"var(--ts)", lineHeight:1.55}}>
                {synth}
              </div>
            );
          })()}
          <MRow label="Volume Score"    value={vScore}             tip={tw(d?.interp_volume_global    ?? TIPS.vol_score)} />
          <MRow label="Liquidité Score" value={`${liqScore}`}      tip={tw(d?.interp_liquidity        ?? TIPS.liq_score)} />
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>
          <MRow label="Ratio Volume 20J" value={`${r20d.toFixed(2)}x`} color={rCol(r20d)} tip={tw(d?.interp_volume_ratio_20j ?? TIPS.vol_20j)} />
          <MRow label="Ratio Volume 60J" value={`${r60d.toFixed(2)}x`} color={rCol(r60d)} tip={tw(d?.interp_volume_ratio_60j ?? TIPS.vol_60j)} />
          <MRow label="Support Vol. Ratio"
                value={suppVR != null ? suppVR.toFixed(2) : "—"}
                color={suppVR >= 1 ? C.green : C.red}
                tip={TIPS.supp_vol}  />
          <MRow label="Résistance Vol. Ratio"
                value={resVR != null ? resVR.toFixed(2) : "—"}
                tip={TIPS.res_vol}   />
          <MRow label="Absorption Signal" value={absLabel} color={absCol} tip={tw(d?.interp_absorption  ?? TIPS.absorption)} />
          <MRow label="Phase Wyckoff"     value={wyLabel}  color={wyCol}  tip={tw(d?.interp_wyckoff_phase ?? TIPS.wyckoff)}   />
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>

          {/* Prix / Volume divergence */}
          {/* Prix / Volume — contexte Wyckoff */}
          {(() => {
            const isDistrib = wyckoff === "Distribution" || wyckoff === "Markdown";
            const trendCol  = isDistrib && trend20_60 === "Rising"
              ? C.orange
              : trend20_60 === "Rising"  ? C.green
              : trend20_60 === "Falling" ? C.red
              : C.amber;
            const trendIcon  = trend20_60 === "Rising" ? "↑" : trend20_60 === "Falling" ? "↓" : "→";
            const showAlert  = volDiverg || (isDistrib && trend20_60 === "Rising");
            const alertLabel = volDiverg ? "⚠ Divergence" : "⚠ Contexte";
            return (
              <div style={{borderRadius:8,
                border:`1px solid ${showAlert ? C.orange+"44" : "var(--sep)"}`,
                background: showAlert ? `${C.orange}0d` : "var(--sur)",
                padding:"9px 10px"}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".09em",
                  color:"var(--mu)",marginBottom:6,display:"flex",alignItems:"center",gap:4}}>
                  PRIX / VOLUME <TipIcon text={TIPS.prix_vol}/>
                </div>
                <div style={{display:"flex",gap:14,alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:9,color:"var(--mu)",fontWeight:600}}>Perf. M1</div>
                    <div style={{fontFamily:"ui-monospace,monospace",fontSize:13,fontWeight:800,
                      color: priceM1 >= 0 ? C.green : C.red}}>
                      {priceM1 >= 0 ? "↑" : "↓"} {priceM1 >= 0 ? "+" : ""}{priceM1.toFixed(1)}%
                    </div>
                  </div>
                  <div style={{width:1,height:28,background:"var(--sep)"}}/>
                  <div>
                    <div style={{fontSize:9,color:"var(--mu)",fontWeight:600}}>Tendance Vol. 20/60J</div>
                    <div style={{fontFamily:"ui-monospace,monospace",fontSize:13,fontWeight:800,
                      color: trendCol}}>
                      {trendIcon} {trend20_60}
                    </div>
                  </div>
                  {showAlert && (
                    <div style={{marginLeft:"auto",fontSize:9,fontWeight:800,color:C.orange}}>
                      {alertLabel}
                    </div>
                  )}
                </div>
                {isDistrib && trend20_60 === "Rising" && (
                  <p style={{margin:"8px 0 0",fontSize:9.5,color:`${C.orange}dd`,
                    lineHeight:1.6,fontStyle:"italic"}}>
                    Volume élevé en phase de Distribution : les institutionnels allègent sur la demande forte de la foule. Le volume confirme la distribution, pas la hausse.
                  </p>
                )}
              </div>
            );
          })()}
        </PillarCard>
          );
        })()}

        {/* Timing */}
        {(() => {
          const tw          = t => typeof t === "string" ? { body: t } : t;
          const rsi14       = parseFloat(d?.rsi_14            ?? 60.81);
          const zScore      = parseFloat(d?.z_score           ?? 1.317);
          const oppScore    = parseFloat(d?.opportunity_score ?? 9.21);
          const timingScore = parseFloat(d?.opportunity_score ?? 40); // ← tms.opportunity_score, pas timing_quality (texte)
          const tscore      = parseFloat(d?.tscore            ?? 32.3);
          const coherence   = parseInt(d?.coherence_score     ?? -1);
          const coherenceLbl= d?.coherence_label              ?? "Marché en tension — point de bascule";
          const crossSignal = d?.crossover_signal             ?? "+1σ breakout";
          const valStatus   = d?.valuation_status             ?? "Surévalué";
          const tscorelbl   = tscore >= 75 ? "ENTRER"
                            : tscore >= 60 ? "SURVEILLER"
                            : tscore >= 45 ? "ATTENDRE"
                            : "ÉVITER";
          const tscoreCol   = tscore >= 75 ? C.green
                            : tscore >= 60 ? C.green
                            : tscore >= 45 ? C.amber
                            : C.red;
          const oppPct      = `${Math.round(oppScore)}%`;
          const coherenceCol= coherence >= 3 ? C.green
                            : coherence >= 1 ? C.green
                            : coherence >= -1 ? C.amber
                            : C.red;
          // Jauge position (0–100 → 0%–100% left)
          const gaugePos    = `${Math.max(2, Math.min(96, oppScore))}%`;

          return (
        <PillarCard number={4} title="TIMING / OPPORTUNITÉ" score={Math.round(timingScore)}
          color={colorForPct(Math.round(timingScore))} pillar="timing" d={d} onGuide={() => setShowGuide("timing")}
          tip="Analyse du point d'entrée optimal : RSI, Z-Score, cohérence inter-timeframes.">
          {/* ── Ligne de synthèse pilier 4 ── */}
          {(() => {
            const synth = d?.interp_action
              ? d.interp_action
              : `RSI ${rsi14.toFixed(1)} — ${rsi14>=70?"surachat":rsi14>=55?"zone de force":rsi14<=30?"survente":"neutre"}. Z-Score ${zScore>=0?"+":""}${zScore.toFixed(2)}. Opp. Score ${oppScore.toFixed(0)}/100.`;
            const col   = colorForPct(Math.round(timingScore));
            return (
              <div style={{borderRadius:8, padding:"7px 10px", marginBottom:10,
                background:`${col}0d`, border:`1px solid ${col}33`,
                fontSize:10, fontWeight:600, color:"var(--ts)", lineHeight:1.55}}>
                {synth}
              </div>
            );
          })()}
          <MRow label="RSI (14)"
                value={rsi14.toFixed(1)}
                color={rsi14 >= 70 ? C.red : rsi14 >= 55 ? C.green : rsi14 <= 30 ? C.red : C.amber}
                tip={tw(d?.interp_rsi_14 ?? TIPS.rsi)} />
          <MRow label="Z-Score (régression)"
                value={`${zScore >= 0 ? "+" : ""}${zScore.toFixed(2)}`}
                color={zScore >= 2 ? C.red : zScore >= 1 ? C.amber : zScore <= -1 ? C.green : "var(--tx)"}
                tip={tw(d?.interp_z_score_timing ?? TIPS.zscore)} />
          <MRow label="Opportunity Score"
                value={`${oppScore.toFixed(0)}/100`}
                color={oppScore >= 65 ? C.green : oppScore >= 50 ? C.amber : C.red}
                tip={tw(d?.interp_opportunity_score ?? TIPS.opp_score)} />
          <MRow label="Timing Score"
                value={`${Math.round(timingScore)}/100`}
                tip={tw(d?.interp_timing_score ?? TIPS.timing_score)} />
          <MRow label="TScore"
                value={`${tscore.toFixed(0)}/100`}
                color={tscoreCol}
                tip={TIPS.tscore} />
          <MRow label="Cohérence inter-piliers"
                value={`${coherence >= 0 ? "+" : ""}${coherence}/6`}
                color={coherenceCol}
                tip={tw(d?.interp_coherence ?? TIPS.coherence)} />
          <MRow label="Signal sigma"
                value={crossSignal || "Zone neutre"}
                color={crossSignal && crossSignal.startsWith("+2") ? C.red
                     : crossSignal && crossSignal.startsWith("+1") ? C.amber
                     : crossSignal && crossSignal.startsWith("-") ? C.green
                     : "var(--mu)"}
                tip={TIPS.cross_sma} />
          <MRow label="Valorisation (régression)"
                value={valStatus}
                color={valStatus === "Surévalué" ? C.orange : valStatus === "Sous-évalué" ? C.green : C.amber}
                tip={TIPS.val_status} />
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>

          {/* Jauge d'opportunité */}
          <div style={{borderRadius:8,
            border:`1px solid ${tscoreCol}33`,
            background:`${tscoreCol}08`,padding:"10px"}}>
            <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".09em",
              color:"var(--mu)",marginBottom:6,display:"flex",alignItems:"center",gap:4}}>
              JAUGE D'OPPORTUNITÉ <TipIcon text={TIPS.jauge}/>
            </div>
            <div style={{position:"relative",height:11,borderRadius:999,
              background:`linear-gradient(to right,${C.red},${C.amber},${C.green})`,
              marginBottom:5,overflow:"visible"}}>
              <div style={{position:"absolute",top:-3,left:gaugePos,transform:"translateX(-50%)",
                width:7,height:17,borderRadius:4,background:"#fff",
                boxShadow:"0 0 0 1.5px rgba(0,0,0,.18)",zIndex:2}}/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:8,
              color:"var(--mu)",fontWeight:700,marginBottom:8}}>
              {["Faible","Neutre","Bon","Excellent"].map(l=><span key={l}>{l}</span>)}
            </div>
            <div style={{fontSize:10,fontWeight:800,color:tscoreCol,marginBottom:2}}>
              Action suggérée : {tscorelbl}
            </div>
            <div style={{fontSize:10,color:"var(--mu)",fontWeight:600,lineHeight:1.5}}>
              {coherenceLbl}
            </div>
          </div>
        </PillarCard>
          );
        })()}
      </div>

      {/* ── Verdict / Synthèse finale (doc archi générique §2 et §9.6) ── */}
      <VerdictBanner d={d} company={company} scoreGlobal={scoreGlobal}
        marketBias={marketBias} regimeCol={regimeCol}
        actionLabel={actionLabel} actionCol={actionCol}/>

    </div>
    </div>

    {/* ══ EDUCATION MODAL ══ */}
    {eduModal && <EduModal anchor={eduModal.anchor} onClose={closeEdu} isDark={isDark} d={d}/>}
    {/* ══ COURS MODAL ══ */}
    {showCours && <CourseModal onClose={() => setShowCours(false)} isDark={isDark} d={d}/>}
    {/* ══ GUIDE MODAL ══ */}
    {showGuide && <GuideModal currentPillar={showGuide} onClose={() => setShowGuide(null)} d={d}/>}
  </React.Fragment>
    </TimingSeriesContext.Provider>
  );
}
